/**
 * Wire types for the Business OS backend.
 *
 * Field names mirror the .NET API exactly (camelCase on the wire). Anything the
 * backend can legitimately omit is typed `| null`, not optional, because the API
 * always emits the key.
 */

/** Every endpoint — success or failure — responds with this envelope. */
export interface ApiEnvelope<T> {
  /** Present only on success; absent for 204-style endpoints. */
  data?: T;
  success: boolean;
  message: string | null;
  /** Machine-readable code, e.g. `auth.invalid_credentials`. */
  errorCode: string | null;
  /** Field-level validation failures, keyed by field name. */
  errors: Record<string, string[]> | null;
  correlationId: string | null;
  /** ISO-8601 timestamp. */
  timestamp: string;
}

export interface ApiErrorInit {
  status: number;
  message: string;
  errorCode?: string | null;
  errors?: Record<string, string[]> | null;
  correlationId?: string | null;
  /** Request path, kept for logging — never shown to end users. */
  path?: string | null;
}

/**
 * Thrown for any non-2xx response, any `success: false` envelope, and for
 * transport failures (which use `status: 0`).
 */
export class ApiError extends Error {
  readonly status: number;
  readonly errorCode: string | null;
  readonly errors: Record<string, string[]> | null;
  readonly correlationId: string | null;
  readonly path: string | null;

  constructor(init: ApiErrorInit) {
    super(init.message);
    this.name = "ApiError";
    this.status = init.status;
    this.errorCode = init.errorCode ?? null;
    this.errors = init.errors ?? null;
    this.correlationId = init.correlationId ?? null;
    this.path = init.path ?? null;
  }

  /** True when the request never reached the server. */
  get isNetworkError(): boolean {
    return this.status === 0;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  /** First message for a field, if the backend reported one. */
  fieldError(field: string): string | null {
    return this.errors?.[field]?.[0] ?? null;
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

/** `User.preferredLanguage` is a backend enum; only these two are in use today. */
export const PreferredLanguage = {
  English: 1,
  Arabic: 2,
} as const;

export type PreferredLanguageValue =
  (typeof PreferredLanguage)[keyof typeof PreferredLanguage];

export interface User {
  id: string;
  userName: string;
  email: string;
  fullName: string;
  fullNameAr: string | null;
  phoneNumber: string | null;
  /** Backend enum value. */
  status: number;
  /** Backend enum value; see `PreferredLanguage`. */
  preferredLanguage: number;
  isSystemAdministrator: boolean;
  emailConfirmed: boolean;
  mustChangePassword: boolean;
  lastLoginAt: string | null;
  defaultCompanyId: string | null;
  defaultBranchId: string | null;
  roles: string[];
  /** ISO-8601. Lifecycle columns the users table always carried; no password material. */
  createdAt: string;
  passwordChangedAt: string | null;
  /** Set while brute-force lockout is in force; sign-in is refused until it passes. */
  lockedOutUntil: string | null;
  failedLoginAttempts: number;
}

export interface CompanyAccess {
  companyId: string;
  companyCode: string;
  companyName: string;
  companyNameAr: string | null;
  branchId: string | null;
  branchCode: string | null;
  branchName: string | null;
  isDefault: boolean;
}

export interface AuthResult {
  accessToken: string;
  refreshToken: string;
  /** ISO-8601. */
  expiresAt: string;
  /** ISO-8601. */
  refreshTokenExpiresAt: string;
  /** Always `"Bearer"` today. */
  tokenType: string;
  user: User;
  permissions: string[];
  roles: string[];
  companies: CompanyAccess[];
  tenantId: string;
  tenantCode: string | null;
  tenantName: string | null;
  companyId: string | null;
  branchId: string | null;
  mustChangePassword: boolean;
}

export interface UserProfile {
  user: User;
  permissions: string[];
  roles: string[];
  companies: CompanyAccess[];
  tenantId: string;
  tenantCode: string | null;
  tenantName: string | null;
  currentCompanyId: string | null;
  currentBranchId: string | null;
}

export interface ReportCategory {
  /** Stable backend enum value (1..10). */
  category: number;
  /** Kebab-case identifier, e.g. `trial-balance`. */
  code: string;
  name: string;
  nameAr: string;
  displayOrder: number;
  reportCount: number;
}

export interface ReportsCenterResult {
  categories: ReportCategory[];
  totalCategories: number;
}

/** One row in a category's report list. Carries no parameter metadata by design. */
export interface ReportListItem {
  /** Globally unique kebab-case key; the URL segment for the parameter screen. */
  code: string;
  name: string;
  nameAr: string;
  displayOrder: number;
}

/** A category plus the reports the signed-in user may open. */
export interface ReportCategoryDetail {
  category: number;
  code: string;
  name: string;
  nameAr: string;
  reports: ReportListItem[];
  /**
   * Reports defined before permission filtering. A non-zero total against an empty
   * `reports` array means "hidden from you", which reads differently from "not built yet".
   */
  totalReports: number;
}

export interface LoginRequest {
  tenantCode?: string | null;
  userNameOrEmail: string;
  password: string;
}

export interface RefreshRequest {
  refreshToken: string;
}

export interface LogoutRequest {
  refreshToken: string;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

// ─────────────────────────────────────────────── Accounting master data

/** Mirrors the backend AccountType enum. Values are stable and persisted. */
export const ACCOUNT_TYPE = {
  Asset: 1,
  Liability: 2,
  Equity: 3,
  Revenue: 4,
  Expense: 5,
} as const;
export type AccountType = (typeof ACCOUNT_TYPE)[keyof typeof ACCOUNT_TYPE];

/** The side an account normally carries its balance on. */
export const ACCOUNT_NATURE = { Debit: 1, Credit: 2 } as const;
export type AccountNature = (typeof ACCOUNT_NATURE)[keyof typeof ACCOUNT_NATURE];

export const FINANCIAL_STATEMENT = { BalanceSheet: 1, IncomeStatement: 2 } as const;
export type FinancialStatement = (typeof FINANCIAL_STATEMENT)[keyof typeof FINANCIAL_STATEMENT];

export const RELATED_PARTY_TYPE = { None: 0, Customer: 1, Supplier: 2, Employee: 3 } as const;
export type RelatedPartyType = (typeof RELATED_PARTY_TYPE)[keyof typeof RELATED_PARTY_TYPE];

export interface Currency {
  id: string;
  code: string;
  name: string;
  nameAr: string | null;
  symbol: string | null;
  decimalPlaces: number;
  /** Rate in force today; 1 for the base currency, `null` when none has been entered. */
  exchangeRate: number | null;
  rateEffectiveDate: string | null;
  isBaseCurrency: boolean;
  isActive: boolean;
  /** End of the current rate's window, inclusive. Null means open-ended. */
  rateEffectiveTo: string | null;
  /** Rate rows on file. History is only offered when there is something to show. */
  rateHistoryCount: number;
  /** Something already references it, so Delete is refused. */
  isInUse: boolean;
  createdAt: string;
  updatedAt: string | null;
  iconUrl: string | null;

  /** Amount-in-words nouns. All optional — a currency posts fine before anyone spells it. */
  wordsArSingular: string | null;
  wordsArDual: string | null;
  wordsArPlural: string | null;
  wordsArFractionSingular: string | null;
  wordsArFractionDual: string | null;
  wordsArFractionPlural: string | null;
  wordsEnSingular: string | null;
  wordsEnPlural: string | null;
  wordsEnFractionSingular: string | null;
  wordsEnFractionPlural: string | null;

  /** 1,234.56 spelled with this currency's own configuration; null when unconfigured. */
  sampleWordsAr: string | null;
  sampleWordsEn: string | null;
}

/** One window of a currency's rate history. Append-only: a rate is never edited in place. */
export interface ExchangeRateRow {
  id: string;
  currencyId: string;
  currencyCode: string;
  rate: number;
  effectiveFrom: string;
  effectiveTo: string | null;
  isCurrent: boolean;
  createdAt: string;
  createdBy: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface ExchangeRateInput {
  rate: number;
  effectiveFrom: string;
  effectiveTo?: string | null;
}

export interface CurrencyInput {
  code?: string;
  name: string;
  nameAr?: string | null;
  symbol?: string | null;
  decimalPlaces: number;
  /**
   * Only sent when creating. Rates are append-only, so an edit never carries one — changing a
   * rate is `addExchangeRate`, which adds a dated window and leaves earlier ones readable.
   */
  exchangeRate?: number;
  effectiveFrom?: string;
  effectiveTo?: string;
  isBaseCurrency: boolean;
  isActive: boolean;
  iconUrl?: string | null;

  wordsArSingular?: string | null;
  wordsArDual?: string | null;
  wordsArPlural?: string | null;
  wordsArFractionSingular?: string | null;
  wordsArFractionDual?: string | null;
  wordsArFractionPlural?: string | null;
  wordsEnSingular?: string | null;
  wordsEnPlural?: string | null;
  wordsEnFractionSingular?: string | null;
  wordsEnFractionPlural?: string | null;
}

export interface CostCenter {
  id: string;
  companyId: string;
  code: string;
  name: string;
  nameAr: string | null;
  parentId: string | null;
  parentCode: string | null;
  parentName: string | null;
  level: number;
  path: string | null;
  /** A group aggregates its children and cannot be posted to. */
  isGroup: boolean;
  isActive: boolean;
  notes: string | null;
  hasChildren: boolean;
  hasTransactions: boolean;
  createdAt: string;
  updatedAt: string | null;
}

export interface CostCenterTreeNode {
  id: string;
  parentId: string | null;
  code: string;
  name: string;
  nameAr: string | null;
  level: number;
  path: string | null;
  isGroup: boolean;
  isActive: boolean;
  children: CostCenterTreeNode[];
}

export interface CostCenterInput {
  code: string;
  name: string;
  nameAr?: string | null;
  parentId?: string | null;
  isGroup: boolean;
  isActive: boolean;
  notes?: string | null;
}

export interface AccountCategory {
  id: string;
  code: string;
  name: string;
  nameAr: string | null;
  displayOrder: number;
  isActive: boolean;
}

export interface Account {
  accountClassificationId: string | null;
  accountClassificationCode: string | null;
  accountClassificationName: string | null;
  id: string;
  companyId: string;
  accountNumber: string;
  name: string;
  nameAr: string | null;
  accountType: AccountType;
  nature: AccountNature;
  financialStatement: FinancialStatement;
  parentId: string | null;
  parentAccountNumber: string | null;
  parentName: string | null;
  level: number;
  path: string | null;
  /** Postable leaves only; the screen shows this as "allow posting". */
  isPostable: boolean;
  isActive: boolean;
  /** Seeded and referenced by the posting engine; code, type and existence are locked. */
  isSystemAccount: boolean;
  currencyId: string | null;
  currencyCode: string | null;
  requiresCostCenter: boolean;
  /** The cost centre pre-filled when this account is posted to. */
  defaultCostCenterId: string | null;
  defaultCostCenterCode: string | null;
  defaultCostCenterName: string | null;
  requiredPartyType: RelatedPartyType;
  notes: string | null;

  /** Tenant-defined classification, separate from accountType. */
  accountCategoryId: string | null;
  accountCategoryName: string | null;
  accountCategoryNameAr: string | null;

  openingDate: string | null;
  referenceNumber: string | null;

  /** Closed accounts reject new postings but keep their balances and history. */
  isClosed: boolean;
  closedAt: string | null;

  /** Cost centres postings are restricted to. Empty means unrestricted. */
  allowedCostCenterIds: string[];

  /** Something has posted to this account, so type and nature are frozen. */
  hasTransactions: boolean;
  openingDebit: number;
  openingCredit: number;
  hasChildren: boolean;
  createdAt: string;
  updatedAt: string | null;
}

export interface AccountTreeNode {
  id: string;
  parentId: string | null;
  accountNumber: string;
  name: string;
  nameAr: string | null;
  accountType: AccountType;
  nature: AccountNature;
  financialStatement: FinancialStatement;
  level: number;
  path: string | null;
  isPostable: boolean;
  isActive: boolean;
  isSystemAccount: boolean;
  currencyId: string | null;
  currencyCode: string | null;
  requiresCostCenter: boolean;
  requiredPartyType: RelatedPartyType;
  children: AccountTreeNode[];
}

export interface AccountInput {
  accountClassificationId?: string | null;
  accountNumber: string;
  name: string;
  nameAr?: string | null;
  parentId?: string | null;
  accountType: AccountType;
  nature: AccountNature;
  /** Which statement the account is reported on. Required, and must match the account type. */
  financialStatement: FinancialStatement;
  isGroup: boolean;
  currencyId?: string | null;
  requiresCostCenter: boolean;
  /** The cost centre pre-filled when this account is posted to. */
  defaultCostCenterId?: string | null;
  requiredPartyType: RelatedPartyType;
  isActive: boolean;
  notes?: string | null;
  accountCategoryId?: string | null;
  openingDate?: string | null;
  referenceNumber?: string | null;
  isClosed?: boolean;
  allowedCostCenterIds?: string[];
}

// ---------------------------------------------------------------- Journal entries

/**
 * Journal lifecycle. Draft, Rejected and Audited are all "unposted" to the accountant — none of
 * them has reached the ledger — but they are kept distinct because the audit and posting screens
 * act on the difference.
 */
export const JOURNAL_STATUS = {
  Draft: 1,
  Audited: 2,
  Posted: 3,
  Rejected: 4,
  Cancelled: 5,
  Reversed: 6,
} as const;

export type JournalStatus = (typeof JOURNAL_STATUS)[keyof typeof JOURNAL_STATUS];

/** True while the entry has not reached the General Ledger. */
export function isUnposted(status: JournalStatus): boolean {
  return status !== JOURNAL_STATUS.Posted && status !== JOURNAL_STATUS.Reversed;
}

export interface BranchOption {
  id: string;
  code: string;
  name: string;
  nameAr: string | null;
  isMain: boolean;
}

/** The fiscal year and period a voucher date falls into, and whether they accept postings. */
export interface FiscalPeriodResolution {
  found: boolean;
  fiscalYearId: string | null;
  fiscalYearCode: string | null;
  fiscalYearName: string | null;
  fiscalYearNameAr: string | null;
  fiscalYearStatus: number | null;
  fiscalPeriodId: string | null;
  periodNumber: number | null;
  periodName: string | null;
  periodNameAr: string | null;
  periodStatus: number | null;
  allowsPosting: boolean;
  reason: string | null;
}

/** Everything a blank Journal Entry screen needs before the user types anything. */
export interface JournalEntryContext {
  companyId: string;
  companyName: string;
  companyNameAr: string | null;
  voucherTypeId: string;
  voucherTypeCode: string;
  voucherTypeName: string;
  voucherTypeNameAr: string | null;
  currentUserId: string;
  currentUserName: string;
  currentUserFullName: string | null;
  defaultBranchId: string | null;
  branches: BranchOption[];
  baseCurrencyId: string | null;
  baseCurrencyCode: string | null;
  baseCurrencyDecimalPlaces: number;
  today: string;
  defaultPeriod: FiscalPeriodResolution | null;
  /** Company setting: Notes becomes mandatory on new and edited Journal Entries. */
  requireJournalEntryNotes: boolean;
}

/** An account the line grid may target, with the rules it imposes on the line. */
export interface PostingAccount {
  id: string;
  accountNumber: string;
  name: string;
  nameAr: string | null;
  accountType: AccountType;
  nature: AccountNature;
  currencyId: string | null;
  currencyCode: string | null;
  /** False for a group header. Only groups are returned when the caller asks for them. */
  isPostable: boolean;
  requiresCostCenter: boolean;
  /** Cost centres this account permits. EMPTY means unrestricted. */
  allowedCostCenterIds: string[];
  requiredPartyType: RelatedPartyType;
  currentBalance: number;
  currentBalanceType: number;
}

export interface SubledgerParty {
  id: string;
  code: string;
  name: string;
  nameAr: string | null;
  partnerType: RelatedPartyType;
  currencyId: string | null;
}

/**
 * A resolved rate. `found: false` means no rate covers the voucher date — the screen must say so
 * rather than substituting 1, which would silently misstate every local amount on the line.
 */
export interface ExchangeRateResolution {
  currencyId: string;
  currencyCode: string;
  isBaseCurrency: boolean;
  found: boolean;
  rate: number;
  effectiveFrom: string | null;
  effectiveTo: string | null;
  reason: string | null;
}

export interface JournalLine {
  id: string;
  lineNumber: number;
  accountId: string;
  accountNumber: string;
  accountName: string;
  accountNameAr: string | null;
  relatedPartyType: RelatedPartyType;
  relatedPartyId: string | null;
  relatedPartyName: string | null;
  costCenterId: string | null;
  costCenterCode: string | null;
  costCenterName: string | null;
  currencyId: string;
  currencyCode: string | null;
  exchangeRate: number;
  debit: number;
  credit: number;
  localDebit: number;
  localCredit: number;
  balance: number;
  balanceType: number;
  notes: string | null;
  lineReference: string | null;
  dueDate: string | null;
}

export interface JournalAttachment {
  id: string;
  fileName: string;
  contentType: string | null;
  fileSizeBytes: number;
  description: string | null;
  uploadedAt: string;
  uploadedBy: string | null;
  uploadedByName: string | null;
}

export interface JournalVoucher {
  id: string;
  companyId: string;
  voucherTypeId: string;
  voucherTypeCode: string;
  voucherTypeName: string;
  voucherTypeNameAr: string | null;
  status: JournalStatus;
  voucherNumber: string;
  voucherSerial: number;
  voucherDate: string;
  referenceNumber: string | null;
  referenceDate: string | null;
  referenceSequence: number | null;
  salespersonId: string | null;
  salespersonName: string | null;
  createdByUserId: string;
  createdByUserName: string | null;
  fiscalYearId: string;
  fiscalYearCode: string;
  fiscalPeriodId: string | null;
  fiscalPeriodNumber: number | null;
  branchId: string;
  branchCode: string | null;
  branchName: string | null;
  branchNameAr: string | null;
  notes: string | null;
  description: string | null;
  printStamps: boolean;
  showCostCentersInPrint: boolean;
  showCurrencyInPrint: boolean;
  totalDebit: number;
  totalCredit: number;
  totalLocalDebit: number;
  totalLocalCredit: number;
  difference: number;
  isBalanced: boolean;
  isEditable: boolean;
  auditedAt: string | null;
  postedAt: string | null;
  rejectedAt: string | null;
  rejectionReason: string | null;
  /** The auditor's review notes, kept separate from the accountant's notes. */
  auditNotes: string | null;
  /** The voucher this one reverses. The ONLY authoritative link a journal carries. */
  reversalOfVoucherId: string | null;
  reversalOfVoucherNumber: string | null;
  /** The counter-entry that reversed this voucher. */
  reversedByVoucherId: string | null;
  reversedByVoucherNumber: string | null;
  createdAt: string;
  updatedAt: string | null;
  lines: JournalLine[];
  attachments: JournalAttachment[];
}

/** One row as the client sends it. Local amounts are absent by design — the server derives them. */
export interface JournalLineInput {
  accountId: string;
  relatedPartyId?: string | null;
  costCenterId?: string | null;
  currencyId?: string | null;
  /** Omit to take the rate effective on the voucher date. A value is an override. */
  exchangeRate?: number | null;
  debit: number;
  credit: number;
  notes?: string | null;
  lineReference?: string | null;
  dueDate?: string | null;
}

export interface JournalVoucherInput {
  voucherDate: string;
  branchId: string;
  referenceNumber?: string | null;
  referenceDate?: string | null;
  referenceSequence?: number | null;
  salespersonId?: string | null;
  notes?: string | null;
  description?: string | null;
  printStamps: boolean;
  showCostCentersInPrint: boolean;
  showCurrencyInPrint: boolean;
  lines: JournalLineInput[];
}

export interface JournalVoucherListItem {
  id: string;
  voucherNumber: string;
  voucherSerial: number;
  status: JournalStatus;
  voucherDate: string;
  referenceNumber: string | null;
  referenceDate: string | null;
  branchName: string | null;
  branchNameAr: string | null;
  createdByUserName: string | null;
  salespersonName: string | null;
  fiscalYearCode: string;
  totalDebit: number;
  totalCredit: number;
  totalLocalDebit: number;
  totalLocalCredit: number;
  isBalanced: boolean;
  isEditable: boolean;
  lineCount: number;
  attachmentCount: number;
  notes: string | null;
}

export interface JournalVoucherPage {
  items: JournalVoucherListItem[];
  totalCount: number;
  page: number;
  pageSize: number;
  filteredTotalLocalDebit: number;
  filteredTotalLocalCredit: number;
}

/** Every filter on the inquiry screen. All optional; they compose. */
export interface JournalVoucherFilter {
  sequenceNumber?: string;
  dateFrom?: string;
  dateTo?: string;
  referenceNumber?: string;
  referenceDate?: string;
  branchId?: string;
  createdByUserId?: string;
  salespersonId?: string;
  status?: JournalStatus;
  accountId?: string;
  relatedPartyId?: string;
  fiscalYearId?: string;
  searchTerm?: string;
  page?: number;
  pageSize?: number;
}

/** What part of the journal an audit entry concerns. */
export const JOURNAL_AUDIT_CATEGORY = { Header: 1, Line: 2, Attachment: 3 } as const;

export type JournalAuditCategory =
  (typeof JOURNAL_AUDIT_CATEGORY)[keyof typeof JOURNAL_AUDIT_CATEGORY];

/** One row of the Audit History tab, read from the existing audit trail. */
export interface JournalAuditEntry {
  id: string;
  /** AuditAction: 1 Created, 2 Updated, 3 Deleted, others are platform events. */
  action: number;
  category: JournalAuditCategory;
  entityName: string;
  summary: string | null;
  oldValues: string | null;
  newValues: string | null;
  affectedColumns: string | null;
  occurredAt: string;
  userId: string | null;
  userName: string | null;
}

/** Whether an entry may be posted, and every reason it may not. */
export interface JournalPostingEligibility {
  journalVoucherId: string;
  canPost: boolean;
  isAudited: boolean;
  isPosted: boolean;
  isBalanced: boolean;
  periodOpen: boolean;
  hasPostPermission: boolean;
  blockers: JournalPostingBlocker[];
}

// ─────────────────────────────── Daily Transactions Inquiry

/** Every filter the inquiry accepts. Mirrors the server's filter record. */
export interface DailyTransactionsFilter {
  accountId?: string;
  includeChildAccounts?: boolean;
  dateFrom?: string;
  dateTo?: string;
  fiscalYearId?: string;
  branchId?: string;
  voucherNumberFrom?: string;
  voucherNumberTo?: string;
  referenceNumber?: string;
  referenceDateFrom?: string;
  referenceDateTo?: string;
  userId?: string;
  salespersonId?: string;
  costCenterId?: string;
  relatedPartyId?: string;
  currencyId?: string;
  auditStatus?: JournalStatus;
  isPosted?: boolean;
  descriptionSearch?: string;
  sortBy?: string;
  sortDescending?: boolean;
  page?: number;
  pageSize?: number;
}

/** One movement: a single journal line on the inquired account. */
export interface DailyTransactionRow {
  journalVoucherId: string;
  journalVoucherLineId: string;
  voucherDate: string;
  voucherNumber: string;
  voucherSerial: number;
  voucherTypeCode: string;
  voucherTypeName: string;
  voucherTypeNameAr: string | null;
  accountId: string;
  accountNumber: string;
  accountName: string;
  accountNameAr: string | null;
  description: string | null;
  relatedPartyType: RelatedPartyType;
  relatedPartyId: string | null;
  relatedPartyName: string | null;
  currencyCode: string | null;
  exchangeRate: number;
  debit: number;
  credit: number;
  localDebit: number;
  localCredit: number;
  costCenterId: string | null;
  costCenterCode: string | null;
  costCenterName: string | null;
  userName: string | null;
  branchId: string;
  branchName: string | null;
  branchNameAr: string | null;
  referenceNumber: string | null;
  referenceDate: string | null;
  status: JournalStatus;
  isPosted: boolean;
  sourceModule: string;
  sourceReference: string | null;
}

/** Totals across the whole filtered set, not just the page on screen. */
export interface DailyTransactionsTotals {
  movementCount: number;
  totalDebit: number;
  totalCredit: number;
  totalLocalDebit: number;
  totalLocalCredit: number;
  netMovement: number;
}

export interface DailyTransactionsResult {
  rows: DailyTransactionRow[];
  totals: DailyTransactionsTotals;
  totalCount: number;
  page: number;
  pageSize: number;
  accountNumber: string | null;
  accountName: string | null;
  accountNameAr: string | null;
}

// ─────────────────────────────── Account Movements Inquiry

/** Which voucher produced a General Ledger movement. Mirrors the server's VoucherKind. */
export const VOUCHER_KIND = {
  JournalVoucher: 1,
  ReceiptVoucher: 2,
  PaymentVoucher: 3,
  OpeningBalance: 4,
  ClosingEntry: 5,
  Adjustment: 6,
  Reversal: 7,
} as const;
export type VoucherKind = (typeof VOUCHER_KIND)[keyof typeof VOUCHER_KIND];

/** A debit-positive balance, classified by its sign. */
export const BALANCE_TYPE = { Balanced: 0, Debit: 1, Credit: 2 } as const;
export type BalanceType = (typeof BALANCE_TYPE)[keyof typeof BALANCE_TYPE];

export const MOVEMENT_POSTING_STATUS = { Posted: 1, Unposted: 2, All: 3 } as const;
export type MovementPostingStatus =
  (typeof MOVEMENT_POSTING_STATUS)[keyof typeof MOVEMENT_POSTING_STATUS];

export interface AccountMovementsFilter {
  accountId?: string;
  includeChildAccounts?: boolean;
  dateFrom?: string;
  dateTo?: string;
  fiscalYearId?: string;
  branchId?: string;
  voucherKind?: VoucherKind;
  sourceModule?: string;
  voucherNumberFrom?: string;
  voucherNumberTo?: string;
  referenceNumber?: string;
  referenceDateFrom?: string;
  referenceDateTo?: string;
  userId?: string;
  salespersonId?: string;
  partyType?: RelatedPartyType;
  relatedPartyId?: string;
  costCenterId?: string;
  currencyId?: string;
  auditStatus?: JournalStatus;
  postingStatus?: MovementPostingStatus;
  descriptionSearch?: string;
  includeOpeningBalance?: boolean;
  sortBy?: string;
  sortDescending?: boolean;
  page?: number;
  pageSize?: number;
}

export interface AccountMovementRow {
  movementId: string;
  journalVoucherId: string;
  journalVoucherLineId: string;
  movementDate: string;
  voucherNumber: string;
  voucherSerial: number;
  lineNumber: number;
  voucherKind: VoucherKind;
  voucherTypeCode: string;
  voucherTypeName: string;
  voucherTypeNameAr: string | null;
  sourceModule: string;
  sourceReference: string | null;
  accountId: string;
  accountNumber: string;
  accountName: string;
  accountNameAr: string | null;
  description: string | null;
  relatedPartyType: RelatedPartyType;
  relatedPartyId: string | null;
  relatedPartyName: string | null;
  costCenterId: string | null;
  costCenterCode: string | null;
  costCenterName: string | null;
  currencyCode: string | null;
  exchangeRate: number;
  debit: number;
  credit: number;
  localDebit: number;
  localCredit: number;
  /** Null where a running balance would be meaningless — see the server query. */
  runningBalance: number | null;
  runningBalanceType: BalanceType | null;
  userName: string | null;
  branchId: string;
  branchName: string | null;
  branchNameAr: string | null;
  referenceNumber: string | null;
  referenceDate: string | null;
  status: JournalStatus;
  isPosted: boolean;
  isReversal: boolean;
}

/** Where the account stood, what moved, and where it ended. Over the whole filtered set. */
export interface AccountMovementsSummary {
  movementCount: number;
  openingBalance: number;
  openingBalanceType: BalanceType;
  totalDebit: number;
  totalCredit: number;
  totalLocalDebit: number;
  totalLocalCredit: number;
  netMovement: number;
  closingBalance: number;
  closingBalanceType: BalanceType;
  unpostedCount: number;
  unpostedLocalDebit: number;
  unpostedLocalCredit: number;
}

export interface AccountMovementsResult {
  rows: AccountMovementRow[];
  summary: AccountMovementsSummary;
  totalCount: number;
  page: number;
  pageSize: number;
  accountNumber: string | null;
  accountName: string | null;
  accountNameAr: string | null;
  accountNature: AccountNature;
  runningBalanceAvailable: boolean;
  showsSubledger: boolean;
  showsCostCenters: boolean;
}

// ─────────────────────────────── Cost Center Account Inquiry

export interface CostCenterAccountFilter {
  costCenterId?: string;
  includeChildCostCenters?: boolean;
  accountId?: string;
  includeChildAccounts?: boolean;
  dateFrom?: string;
  dateTo?: string;
  fiscalYearId?: string;
  branchId?: string;
  voucherKind?: VoucherKind;
  sourceModule?: string;
  voucherNumberFrom?: string;
  voucherNumberTo?: string;
  referenceNumber?: string;
  userId?: string;
  currencyId?: string;
  auditStatus?: JournalStatus;
  postingStatus?: MovementPostingStatus;
  descriptionSearch?: string;
  includeOpeningBalance?: boolean;
  sortBy?: string;
  sortDescending?: boolean;
  page?: number;
  pageSize?: number;
}

export interface CostCenterAccountRow {
  movementId: string;
  journalVoucherId: string;
  journalVoucherLineId: string;
  movementDate: string;
  voucherNumber: string;
  voucherSerial: number;
  lineNumber: number;
  voucherKind: VoucherKind;
  voucherTypeCode: string;
  voucherTypeName: string;
  voucherTypeNameAr: string | null;
  sourceModule: string;
  sourceReference: string | null;
  costCenterId: string;
  costCenterCode: string;
  costCenterName: string;
  costCenterNameAr: string | null;
  accountId: string;
  accountNumber: string;
  accountName: string;
  accountNameAr: string | null;
  description: string | null;
  relatedPartyType: RelatedPartyType;
  relatedPartyId: string | null;
  relatedPartyName: string | null;
  currencyCode: string | null;
  exchangeRate: number;
  debit: number;
  credit: number;
  localDebit: number;
  localCredit: number;
  /** The share of the line charged to this cost centre. Always 100 in the current data model. */
  allocationPercentage: number;
  allocatedDebit: number;
  allocatedCredit: number;
  allocatedLocalDebit: number;
  allocatedLocalCredit: number;
  runningBalance: number | null;
  runningBalanceType: BalanceType | null;
  userName: string | null;
  branchId: string;
  branchName: string | null;
  branchNameAr: string | null;
  referenceNumber: string | null;
  referenceDate: string | null;
  status: JournalStatus;
  isPosted: boolean;
  isReversal: boolean;
}

export interface CostCenterAccountSummary {
  movementCount: number;
  openingBalance: number;
  openingBalanceType: BalanceType;
  totalAllocatedDebit: number;
  totalAllocatedCredit: number;
  totalAllocatedLocalDebit: number;
  totalAllocatedLocalCredit: number;
  netMovement: number;
  closingBalance: number;
  closingBalanceType: BalanceType;
  unpostedCount: number;
  unpostedLocalDebit: number;
  unpostedLocalCredit: number;
}

export interface CostCenterAccountResult {
  rows: CostCenterAccountRow[];
  summary: CostCenterAccountSummary;
  totalCount: number;
  page: number;
  pageSize: number;
  costCenterCode: string | null;
  costCenterName: string | null;
  costCenterNameAr: string | null;
  accountNumber: string | null;
  accountName: string | null;
  accountNameAr: string | null;
  accountNature: AccountNature;
  runningBalanceAvailable: boolean;
  showsSubledger: boolean;
  /** How many cost centres the inquiry covered. More than one means a group roll-up. */
  costCenterCount: number;
}

// ─────────────────────────────── Cash Subledger Movements Inquiry

/**
 * Which slice of the cash ledger to show.
 *
 * Values are deliberately NOT the `RelatedPartyType` numbers: this enum has a value that one
 * cannot express (`All`), and sharing 0–3 would let a mistaken cast turn "No Party" into
 * "Employee" silently.
 */
export const CASH_PARTY_SCOPE = { All: 1, Customer: 2, Supplier: 3, NoParty: 4 } as const;
export type CashPartyScope = (typeof CASH_PARTY_SCOPE)[keyof typeof CASH_PARTY_SCOPE];

/**
 * How a cash transaction was settled.
 *
 * Only Cash, BankTransfer and Mixed are ever returned: settlement is recorded as a movement on a
 * General Ledger account, and an account says whether money went through a cash box or a bank —
 * never whether the bank movement was a card, a transfer or a cheque. The other three are declared
 * so the column, filter and export already have a place for them.
 */
export const CASH_PAYMENT_METHOD = {
  Cash: 1,
  BankTransfer: 2,
  Card: 3,
  Cheque: 4,
  ImmediateSettlement: 5,
  Mixed: 6,
} as const;
export type CashPaymentMethod = (typeof CASH_PAYMENT_METHOD)[keyof typeof CASH_PAYMENT_METHOD];

export const CASH_POSTING_STATUS = { Posted: 1, Unposted: 2, All: 3 } as const;
export type CashPostingStatus = (typeof CASH_POSTING_STATUS)[keyof typeof CASH_POSTING_STATUS];

export interface CashSubledgerFilter {
  partyScope?: CashPartyScope;
  customerId?: string;
  supplierId?: string;
  dateFrom?: string;
  dateTo?: string;
  fiscalYearId?: string;
  branchId?: string;
  voucherKind?: VoucherKind;
  sourceModule?: string;
  paymentMethod?: CashPaymentMethod;
  documentNumberFrom?: string;
  documentNumberTo?: string;
  referenceNumber?: string;
  currencyId?: string;
  cashboxAccountId?: string;
  bankAccountId?: string;
  userId?: string;
  salespersonId?: string;
  costCenterId?: string;
  documentStatus?: JournalStatus;
  postingStatus?: CashPostingStatus;
  descriptionSearch?: string;
  includeCancelledDocuments?: boolean;
  sortBy?: string;
  sortDescending?: boolean;
  page?: number;
  pageSize?: number;
}

export interface CashSubledgerRow {
  movementId: string;
  journalVoucherId: string;
  journalVoucherLineId: string;
  movementDate: string;
  documentNumber: string;
  documentSerial: number;
  lineNumber: number;
  voucherKind: VoucherKind;
  documentTypeCode: string;
  documentTypeName: string;
  documentTypeNameAr: string | null;
  sourceModule: string;

  partyType: RelatedPartyType;
  partyId: string | null;
  partyCode: string | null;
  partyName: string | null;
  partyNameAr: string | null;

  paymentMethod: CashPaymentMethod;
  cashboxAccountNumber: string | null;
  cashboxAccountName: string | null;
  /** 0 none, 1 exactly one (named), 2 several (counted, never guessed). */
  cashboxAccountCount: number;
  bankAccountNumber: string | null;
  bankAccountName: string | null;
  bankAccountCount: number;

  accountId: string;
  accountNumber: string;
  accountName: string;
  accountNameAr: string | null;
  description: string | null;
  currencyCode: string | null;
  exchangeRate: number;

  netAmount: number;
  /** Always null: this system records no tax source. Rendered as "not available", never as zero. */
  taxAmount: number | null;
  totalAmount: number;
  paidAmount: number;
  /** Zero on every qualifying row. Anything else is a classification defect, and is flagged. */
  outstandingAmount: number;
  localAmount: number;

  costCenterId: string | null;
  costCenterCode: string | null;
  costCenterName: string | null;
  userName: string | null;
  salespersonName: string | null;
  branchId: string;
  branchName: string | null;
  branchNameAr: string | null;
  referenceNumber: string | null;
  isPosted: boolean;
  documentStatus: JournalStatus;
  isExcludedFromTotals: boolean;
}

export interface CashSubledgerSummary {
  movementCount: number;
  documentCount: number;
  totalNetAmount: number;
  totalTaxAmount: number | null;
  totalGrossAmount: number;
  totalPaidAmount: number;
  totalOutstandingAmount: number;
  totalLocalAmount: number;
  totalCashAmount: number;
  totalBankAmount: number;
  totalCardAmount: number | null;
  totalChequeAmount: number | null;
  totalReturns: number | null;
  noPartyMovementCount: number;
  totalNoPartyAmount: number;
  excludedCount: number;
  /** Unposted rows the reader asked for. These ARE inside the totals, and the screen says so. */
  unpostedCount: number;
  totalUnpostedAmount: number;
}

export interface CashSubledgerResult {
  rows: CashSubledgerRow[];
  summary: CashSubledgerSummary;
  totalCount: number;
  page: number;
  pageSize: number;
  partyScope: CashPartyScope;
  partyCode: string | null;
  partyName: string | null;
  partyNameAr: string | null;
  showsSubledger: boolean;
  showsCostCenters: boolean;
  showsCashboxes: boolean;
  showsBankAccounts: boolean;
  showsCustomers: boolean;
  showsSuppliers: boolean;
  showsNoParty: boolean;
  /** False when no account is classified Cash or Bank — the screen must say so, not show a blank. */
  cashAccountsConfigured: boolean;
}

// ─────────────────────────────── Unaudited Journal Notes Inquiry

/**
 * Where a journal entry stands in the audit conversation.
 *
 * DERIVED on the server, not stored. The workflow keeps four statuses and this screen needs six;
 * the two extra are distinctions the record can draw even though the status column cannot —
 * "returned for correction" is what a rejection MEANS here, and "resubmitted" is a rejected entry
 * that has been edited since.
 */
export const UNAUDITED_JOURNAL_STATE = {
  PendingAudit: 1,
  ReturnedForCorrection: 2,
  Resubmitted: 3,
  Audited: 4,
  Posted: 5,
  Cancelled: 6,
  Reversed: 7,
} as const;
export type UnauditedJournalState =
  (typeof UNAUDITED_JOURNAL_STATE)[keyof typeof UNAUDITED_JOURNAL_STATE];

/** Which entries to list. The default deliberately excludes finished work. */
export const UNAUDITED_NOTES_SCOPE = {
  Outstanding: 1,
  PendingAudit: 2,
  ReturnedForCorrection: 3,
  Resubmitted: 4,
  WithUnresolvedNotes: 5,
  All: 6,
} as const;
export type UnauditedNotesScope =
  (typeof UNAUDITED_NOTES_SCOPE)[keyof typeof UNAUDITED_NOTES_SCOPE];

/** Someone who has entered or reviewed journals in this company. Not the user directory. */
export interface JournalParticipant {
  id: string;
  userName: string;
  fullName: string | null;
}

export interface JournalParticipants {
  creators: JournalParticipant[];
  auditors: JournalParticipant[];
}

export interface UnauditedJournalNotesFilter {
  scope?: UnauditedNotesScope;
  dateFrom?: string;
  dateTo?: string;
  fiscalYearId?: string;
  branchId?: string;
  voucherKind?: VoucherKind;
  voucherNumber?: string;
  voucherNumberFrom?: string;
  voucherNumberTo?: string;
  referenceNumber?: string;
  referenceDateFrom?: string;
  referenceDateTo?: string;
  accountId?: string;
  includeChildAccounts?: boolean;
  costCenterId?: string;
  createdByUserId?: string;
  auditorUserId?: string;
  salespersonId?: string;
  auditStatus?: JournalStatus;
  isPosted?: boolean;
  auditNoteSearch?: string;
  journalNotesSearch?: string;
  returnedMoreThanOnce?: boolean;
  includeCancelled?: boolean;
  includeReversed?: boolean;
  unbalancedOnly?: boolean;
  sortBy?: string;
  sortDescending?: boolean;
  page?: number;
  pageSize?: number;
}

export interface UnauditedJournalNoteRow {
  journalVoucherId: string;
  voucherNumber: string;
  voucherSerial: number;
  voucherDate: string;
  voucherKind: VoucherKind;
  voucherTypeCode: string;
  voucherTypeName: string;
  voucherTypeNameAr: string | null;
  referenceNumber: string | null;
  referenceDate: string | null;

  createdByUserId: string;
  createdByName: string | null;
  /** Null when the reader may not see who reviewed the entry. */
  auditorUserId: string | null;
  auditorName: string | null;

  status: JournalStatus;
  state: UnauditedJournalState;
  isPosted: boolean;

  /** Null when the reader lacks permission to read audit notes. */
  auditNotes: string | null;
  rejectionReason: string | null;
  /** The accountant's own notes — never overwritten by a review, never permission-gated. */
  journalNotes: string | null;
  hasUnresolvedNotes: boolean;

  lastAuditAction: number | null;
  lastAuditActionAt: string | null;

  returnCount: number;
  resubmissionCount: number;

  totalDebit: number;
  totalCredit: number;
  difference: number;
  isBalanced: boolean;
  lineCount: number;

  branchId: string;
  branchName: string | null;
  branchNameAr: string | null;
  fiscalYearId: string;
  fiscalYearCode: string | null;
  createdAt: string;
  updatedAt: string | null;

  /** True when this reader may open the entry and correct it. */
  canEdit: boolean;
}

export interface UnauditedJournalNotesSummary {
  entryCount: number;
  pendingAuditCount: number;
  returnedForCorrectionCount: number;
  resubmittedCount: number;
  unresolvedNotesCount: number;
  unbalancedCount: number;
  totalDebit: number;
  totalCredit: number;
  totalDifference: number;
}

export interface UnauditedJournalNotesResult {
  rows: UnauditedJournalNoteRow[];
  summary: UnauditedJournalNotesSummary;
  totalCount: number;
  page: number;
  pageSize: number;
  scope: UnauditedNotesScope;
  showsAuditNotes: boolean;
  showsAuditorIdentity: boolean;
  /** False when the reader sees only entries they created. The screen says so rather than implying. */
  showsAllEntries: boolean;
  showsAuditHistory: boolean;
}

// ─────────────────────────────── Tax Types (Jordan Tax module)

/**
 * What the system must know about a tax in order to treat it correctly.
 *
 * An enum while the taxes themselves are rows: a category names a BEHAVIOUR the code implements,
 * and behaviours are fixed by code. The catalogue of taxes is data, and a tenant may add as many
 * as they like in any of these categories without a release.
 *
 * `ZeroRated` and `Exempt` are separate because a tax return keeps them separate — both charge
 * nothing, and only zero-rated supplies leave the input tax reclaimable.
 */
export const TAX_CATEGORY = {
  SalesTax: 1,
  PurchaseTax: 2,
  AdditionalTax: 3,
  SpecialTax: 4,
  ZeroRated: 5,
  Exempt: 6,
  NonRecoverablePurchaseTax: 7,
  WithholdingTax: 8,
  Other: 9,
} as const;
export type TaxCategory = (typeof TAX_CATEGORY)[keyof typeof TAX_CATEGORY];

export interface TaxType {
  id: string;
  code: string;
  name: string;
  nameAr: string;
  category: TaxCategory;
  /**
   * Which side of the ledger this classification sits on, when the row says so itself.
   * Null means the row predates the marker and its category decides — the same rule the
   * server's TaxDirection applies. Filtering by category alone is NOT equivalent: zero-rated
   * and exempt describe a treatment that a purchase carries as readily as a sale.
   */
  side: TaxDirectionSide | null;
  effectiveFrom: string;
  effectiveTo: string | null;
  isActive: boolean;
  /** Active AND inside its effective window today — the two reasons a tax may be unpickable. */
  isSelectableToday: boolean;

  descriptionAr: string | null;
  descriptionEn: string | null;

  debitAccountId: string | null;
  debitAccountNumber: string | null;
  debitAccountName: string | null;
  creditAccountId: string | null;
  creditAccountNumber: string | null;
  creditAccountName: string | null;

  allowManualRateOverride: boolean;
  isRecoverable: boolean;
  affectsItemCost: boolean;
  requiresTaxRegistration: boolean;
  requiresNationalNumber: boolean;

  sortOrder: number;
  notes: string | null;

  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
  activatedAt: string | null;
  activatedByName: string | null;
  deactivatedAt: string | null;
  deactivatedByName: string | null;
}

export interface TaxTypeInput {
  code: string;
  name: string;
  nameAr: string;
  category: TaxCategory;
  effectiveFrom?: string;
  effectiveTo?: string | null;
  isActive: boolean;
  descriptionAr?: string | null;
  descriptionEn?: string | null;
  debitAccountId?: string | null;
  creditAccountId?: string | null;
  allowManualRateOverride: boolean;
  isRecoverable: boolean;
  affectsItemCost: boolean;
  requiresTaxRegistration: boolean;
  requiresNationalNumber: boolean;
  sortOrder: number;
  notes?: string | null;
}

export interface TaxTypeFilter {
  search?: string;
  category?: TaxCategory;
  isActive?: boolean;
  /** Only taxes that may be chosen on a new document today. */
  selectableOnly?: boolean;
}

// ─────────────────────────────── Tax Rates (Jordan Tax module)

/**
 * Where a rate stands relative to today.
 *
 * Derived server-side from the dates and the active flag, never stored — a stored "is current"
 * flag needs recomputing at midnight, and the day nobody recomputes it the screen shows last
 * year's rate as the one in force.
 */
export const TAX_RATE_STATUS = { Current: 1, Future: 2, Expired: 3, Inactive: 4 } as const;
export type TaxRateStatus = (typeof TAX_RATE_STATUS)[keyof typeof TAX_RATE_STATUS];

export interface TaxRate {
  id: string;
  taxTypeId: string;
  taxTypeCode: string;
  taxTypeName: string;
  taxTypeNameAr: string;
  taxCategory: TaxCategory;
  /** False when the owning tax type is retired. The rate stays visible regardless. */
  taxTypeIsActive: boolean;

  /** A percentage: 16 means 16%. */
  percentage: number;
  effectiveFrom: string;
  effectiveTo: string | null;
  isActive: boolean;
  status: TaxRateStatus;

  referenceNumber: string | null;
  legalReference: string | null;
  decisionNumber: string | null;
  descriptionAr: string | null;
  descriptionEn: string | null;
  notes: string | null;
  sortOrder: number;

  /** True once a document has valued something at this rate. Frozen from then on. */
  isInUse: boolean;
  /** True when the rate has no end date and so covers every future document. */
  isOpenEnded: boolean;

  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
  activatedAt: string | null;
  activatedByName: string | null;
  deactivatedAt: string | null;
  deactivatedByName: string | null;
  closedAt: string | null;
  closedByName: string | null;
}

export interface TaxRateInput {
  taxTypeId: string;
  percentage: number;
  effectiveFrom: string;
  effectiveTo?: string | null;
  isActive: boolean;
  referenceNumber?: string | null;
  legalReference?: string | null;
  decisionNumber?: string | null;
  descriptionAr?: string | null;
  descriptionEn?: string | null;
  notes?: string | null;
  sortOrder: number;
  /** Consent to close an open-ended predecessor. Never sent without the user confirming. */
  closeOpenEndedPredecessor?: boolean;
  /** True when amending the rate in force — the server picks the stricter permission. */
  editingCurrent?: boolean;
}

export interface TaxRateFilter {
  search?: string;
  taxTypeId?: string;
  category?: TaxCategory;
  status?: TaxRateStatus;
  isActive?: boolean;
  effectiveFrom?: string;
  effectiveTo?: string;
}

/** The rate in force for one tax on one date, as the resolution service returns it. */
export interface ResolvedTaxRate {
  taxRateId: string;
  taxTypeId: string;
  taxTypeCode: string;
  percentage: number;
  effectiveFrom: string;
  effectiveTo: string | null;
}

// ─────────────────────────────── General Ledger Settings

/** How a user keys a manual Cost Center distribution on a journal line. */
export const MANUAL_COST_CENTER_ENTRY_METHOD = { Amount: 1, Percentage: 2 } as const;
export type ManualCostCenterEntryMethod =
  (typeof MANUAL_COST_CENTER_ENTRY_METHOD)[keyof typeof MANUAL_COST_CENTER_ENTRY_METHOD];

/** Company-level General Ledger configuration. Exactly one per company. */
export interface GeneralLedgerSettings {
  companyId: string;
  requireDefaultCostCenterOnAccounts: boolean;
  requireJournalEntryNotes: boolean;
  aggregateChildSubledgerOpeningBalances: boolean;
  /** A percentage: 16 means 16%. No behavior reads this value yet. */
  costPercentage: number;
  manualCostCenterEntryMethod: ManualCostCenterEntryMethod;
  updatedAt: string | null;
  updatedByUserId: string | null;
  updatedByUserName: string | null;
}

export interface UpdateGeneralLedgerSettingsInput {
  requireDefaultCostCenterOnAccounts: boolean;
  requireJournalEntryNotes: boolean;
  aggregateChildSubledgerOpeningBalances: boolean;
  costPercentage: number;
  manualCostCenterEntryMethod: ManualCostCenterEntryMethod;
  /** Consent to enable RequireDefaultCostCenterOnAccounts while non-compliant accounts exist. */
  acknowledgeNonCompliantAccounts?: boolean;
}

export interface NonCompliantAccountsCount {
  count: number;
}

export interface GeneralLedgerSettingsAuditEntry {
  id: string;
  /** AuditAction: 1 Created, 2 Updated, 3 Deleted, others are platform events. */
  action: number;
  summary: string | null;
  oldValues: string | null;
  newValues: string | null;
  affectedColumns: string | null;
  occurredAt: string;
  userId: string | null;
  userName: string | null;
}

// ─────────────────────────────── Financial Position Inquiry

/**
 * Which side a balance ACTUALLY fell on.
 *
 * Distinct from the nature an account type expects: expectation is a property of the account,
 * this is a property of the arithmetic, and collapsing the two is what makes every asset render
 * as "Debit" whatever the ledger says.
 */
export const BALANCE_NATURE = { Debit: 1, Credit: 2, Zero: 3 } as const;
export type BalanceNature = (typeof BALANCE_NATURE)[keyof typeof BALANCE_NATURE];

/** Whether the balance sits on the side its account type expects. Zero is never abnormal. */
export const BALANCE_NORMALITY = { Normal: 1, Abnormal: 2 } as const;
export type BalanceNormality = (typeof BALANCE_NORMALITY)[keyof typeof BALANCE_NORMALITY];

export const BALANCE_NORMALITY_FILTER = { All: 1, NormalOnly: 2, AbnormalOnly: 3 } as const;
export type BalanceNormalityFilter =
  (typeof BALANCE_NORMALITY_FILTER)[keyof typeof BALANCE_NORMALITY_FILTER];

export const FP_COMPARISON = {
  None: 1,
  PreviousPeriod: 2,
  PreviousFiscalYear: 3,
  CustomDate: 4,
} as const;
export type FinancialPositionComparison = (typeof FP_COMPARISON)[keyof typeof FP_COMPARISON];

/** One node of the position. Rows arrive FLAT and pre-ordered, carrying their own depth. */
export interface FinancialPositionRow {
  accountId: string;
  accountNumber: string;
  accountName: string;
  accountNameAr: string | null;
  level: number;
  parentId: string | null;
  parentAccountNumber: string | null;
  accountType: AccountType;
  accountCategoryId: string | null;
  accountCategoryName: string | null;
  accountCategoryNameAr: string | null;
  currencyCode: string | null;

  isGroup: boolean;
  isPostable: boolean;
  isActive: boolean;
  isClosed: boolean;
  hasHiddenDescendants: boolean;

  debit: number;
  credit: number;
  /** Always non-negative; the side it fell on is `balanceNature`. */
  balance: number;
  /** Signed, debit-positive. */
  rawBalance: number;
  balanceNature: BalanceNature;
  expectedNature: BalanceNature;
  normality: BalanceNormality;
  percentageOfTotalAssets: number | null;

  comparativeBalance: number | null;
  comparativeRawBalance: number | null;
  comparativeNature: BalanceNature | null;
  changeAmount: number | null;
  changePercentage: number | null;

  /** True for the Current Period Profit or Loss line, which no posting produced. */
  isSystemCalculated: boolean;
}

export interface FinancialPositionSummary {
  totalAssets: number;
  totalCurrentAssets: number;
  totalNonCurrentAssets: number;
  totalLiabilities: number;
  totalCurrentLiabilities: number;
  totalNonCurrentLiabilities: number;
  totalPostedEquity: number;
  currentPeriodResult: number;
  totalEquity: number;
  totalLiabilitiesAndEquity: number;
  difference: number;
  isBalanced: boolean;
  displayedAccountCount: number;
  abnormalBalanceCount: number;
}

export interface CurrentPeriodResult {
  amount: number;
  revenue: number;
  expenses: number;
  periodAlreadyClosed: boolean;
  isSystemCalculated: boolean;
}

export interface FinancialPositionResult {
  rows: FinancialPositionRow[];
  summary: FinancialPositionSummary;
  currentPeriod: CurrentPeriodResult;
  asOfDate: string;
  fiscalYearId: string | null;
  fiscalYearName: string | null;
  fiscalYearNameAr: string | null;
  baseCurrencyCode: string;
  baseCurrencyDecimals: number;
  branchName: string | null;
  branchNameAr: string | null;
  isConsolidated: boolean;
  consolidatedBranches: string[];
  includesUnpostedMovements: boolean;
  comparativeDate: string | null;
  comparison: FinancialPositionComparison;
}

export interface FinancialPositionFilter {
  asOfDate?: string;
  fiscalYearId?: string;
  branchId?: string;
  consolidateBranches?: boolean;

  levelFrom?: number;
  levelTo?: number;
  parentAccountId?: string;
  showChildren?: boolean;

  accountFrom?: string;
  accountTo?: string;
  accountCategoryId?: string;
  accountType?: AccountType;
  costCenterId?: string;
  costCenterParentId?: string;
  currencyId?: string;

  includeZeroBalances?: boolean;
  includeClosedAccounts?: boolean;
  includeInactiveAccounts?: boolean;
  includeUnpostedMovements?: boolean;

  normality?: BalanceNormalityFilter;
  comparison?: FinancialPositionComparison;
  comparativeDate?: string;
  comparativeFiscalYearId?: string;
  showPercentageOfTotal?: boolean;
  search?: string;
}

// ─────────────────────────────── Financial Link Settings

/** The eight predefined financial-account slots a company maps. */
export const FINANCIAL_LINK_TYPE = {
  ProfitAndLoss: 1,
  CurrencyDifferenceDebit: 2,
  CurrencyDifferenceCredit: 3,
  PropertyTaxExpense: 4,
  RealizedRevaluationLoss: 5,
  RealizedRevaluationGain: 6,
  UnrealizedRevaluationLoss: 7,
  UnrealizedRevaluationGain: 8,

  // ── Receivables ──
  CreditCardCommission: 9,
  CreditCardCommissionTrust: 10,
  PromissoryNoteInterestRevenue: 11,
  DoubtfulDebtExpense: 12,
  AllowanceForDoubtfulDebts: 13,
  ExpectedLegalCasesExpense: 14,
  ProvisionForExpectedLegalCases: 15,
  UnearnedInterestRevenue: 16,
  PromissoryNoteIssuance: 17,
  /** إيرادات خدمات مدفوعة مقدماً — where a CONTRACT service sale is credited. */
  DeferredServiceRevenue: 18,
} as const;
export type FinancialLinkType = (typeof FINANCIAL_LINK_TYPE)[keyof typeof FINANCIAL_LINK_TYPE];

/**
 * Which settings screen owns a slot. One table and one engine hold every mapping; this only says
 * which grid a row belongs on.
 */
export const FINANCIAL_LINK_MODULE =
  { GeneralLedger: 1, Receivables: 2, CashAndBanks: 3, FixedAssets: 4, Inventory: 5 } as const;
export type FinancialLinkModule =
  (typeof FINANCIAL_LINK_MODULE)[keyof typeof FINANCIAL_LINK_MODULE];

/**
 * What a mapping is scoped to — and therefore which entity lookup the row offers.
 *
 * The level is the choice; the lookup follows from it. That ordering is the design: one grid whose
 * entity column changes what it offers, not one permanent grid per level.
 */
export const FINANCIAL_LINK_LEVEL = {
  User: 1,
  Company: 2,
  Branch: 3,
  Warehouse: 4,
  ItemGroup: 5,
  Item: 6,
  InventoryTransactionType: 7,
} as const;
export type FinancialLinkLevel = (typeof FINANCIAL_LINK_LEVEL)[keyof typeof FINANCIAL_LINK_LEVEL];

/** The levels whose module has shipped. The rest stay selectable and say so when chosen. */
export const AVAILABLE_LINK_LEVELS: readonly FinancialLinkLevel[] = [
  FINANCIAL_LINK_LEVEL.User,
  FINANCIAL_LINK_LEVEL.Company,
  FINANCIAL_LINK_LEVEL.Branch,
];

export interface FinancialLinkRow {
  linkId: string | null;
  linkType: FinancialLinkType;
  linkTypeName: string;
  linkTypeNameAr: string;
  module: FinancialLinkModule;
  /** True when the scope is fixed at Company and the level is not the user's to choose. */
  isCompanyOnly: boolean;
  /** The account type this slot must use, or null where nothing is enforced. */
  requiredAccountType: AccountType | null;
  linkLevel: FinancialLinkLevel;
  linkLevelName: string;
  linkLevelNameAr: string;
  entityId: string | null;
  entityName: string | null;
  entityNameAr: string | null;
  entityCode: string | null;
  accountId: string | null;
  accountNumber: string | null;
  accountName: string | null;
  accountNameAr: string | null;
  /** The stored branch scope. Set only at Branch level, and derived there by the server. */
  branchId: string | null;
  isActive: boolean;
  updatedAt: string | null;
  updatedByName: string | null;
}

/** One recorded change to a mapping. Reads the same audit log every entity writes to. */
export interface FinancialLinkAuditEntry {
  id: string;
  action: number;
  userName: string | null;
  occurredAt: string;
  summary: string | null;
  affectedColumns: string | null;
  oldValues: string | null;
  newValues: string | null;
}

export interface FinancialLinkResult {
  rows: FinancialLinkRow[];
  totalCount: number;
  linkedCount: number;
  /**
   * The General Ledger Settings this screen OBEYS, echoed read-only.
   *
   * Read from the one settings row per company on every request. This screen keeps no copy and
   * shows no control that could change one — General Ledger Settings remains the only editor.
   */
  settings: FinancialLinkSettingsContext;
}

export interface FinancialLinkSettingsContext {
  /** When true, an account with no Default Cost Center cannot be linked. */
  requireDefaultCostCenterOnAccounts: boolean;
  /** Context for a future allocation consumer. Nothing acts on it yet. */
  manualCostCenterEntryMethod: ManualCostCenterEntryMethod;
}

export interface LinkLevelEntity {
  id: string;
  name: string;
  nameAr: string | null;
  code: string | null;
  isActive: boolean;
}

export interface LinkLevelEntities {
  level: FinancialLinkLevel;
  entities: LinkLevelEntity[];
  /** False for Warehouse, Item Group, Item and Inventory Transaction Type. */
  isAvailable: boolean;
}

export interface SaveFinancialLinkInput {
  linkType: FinancialLinkType;
  linkLevel: FinancialLinkLevel;
  entityId: string;
  /**
   * Null registers the scope without an account yet.
   *
   * An approved setup routinely fixes the level for every slot up front and leaves some accounts
   * to be chosen later. Requiring one here would force a placeholder that then had to be cleared,
   * recording a mapping nobody intended.
   */
  accountId: string | null;
  branchId?: string | null;
}

// ─────────────────────────────── Financial Statement Setup

/**
 * One financial statement definition — the heading a structure will later hang from.
 *
 * Carries both descriptions rather than one resolved to the caller's locale, because the approved
 * screen shows both fields side by side and a form needs both to fill itself.
 */
export interface FinancialStatementDefinition {
  id: string;
  code: string;
  descriptionAr: string;
  descriptionEn: string;
  statementType: FinancialStatement;
  statementTypeName: string;
  statementTypeNameAr: string;
  isActive: boolean;
  updatedAt: string | null;
}

export interface FinancialStatementList {
  rows: FinancialStatementDefinition[];
  totalCount: number;
}

export interface CreateFinancialStatementInput {
  code: string;
  descriptionAr: string;
  descriptionEn: string;
  statementType: FinancialStatement;
}

export interface UpdateFinancialStatementInput extends CreateFinancialStatementInput {
  isActive: boolean;
}

// ─────────────────────────────── Voucher Types

/**
 * Yearly, Monthly or Continuous — the sequence type the setup screen offers.
 *
 * These are the server's existing `NumberSequenceResetPolicy` values under the screen's own name:
 * "Continuous" is a counter that never resets, which is what `Never` already meant.
 */
export const VOUCHER_SEQUENCE_TYPE = { Continuous: 0, Yearly: 1, Monthly: 2 } as const;
export type VoucherSequenceType =
  (typeof VOUCHER_SEQUENCE_TYPE)[keyof typeof VOUCHER_SEQUENCE_TYPE];

export const VOUCHER_TAX_INCLUSION = { Included: 1, NotIncluded: 2 } as const;
export type VoucherTaxInclusion =
  (typeof VOUCHER_TAX_INCLUSION)[keyof typeof VOUCHER_TAX_INCLUSION];

export const VOUCHER_NUMBERING_METHOD = { Unified: 1, PerBranch: 2, PerCompany: 3 } as const;
export type VoucherNumberingMethod =
  (typeof VOUCHER_NUMBERING_METHOD)[keyof typeof VOUCHER_NUMBERING_METHOD];

export const VOUCHER_REPORT_DESTINATION = { Screen: 1, Printer: 2, Pdf: 3 } as const;
export type VoucherReportDestination =
  (typeof VOUCHER_REPORT_DESTINATION)[keyof typeof VOUCHER_REPORT_DESTINATION];

/**
 * Which settings screen maintains a voucher type. A classification, never a behaviour: the
 * catalogue is one company-wide table and every type keeps working for whatever raises it.
 */
export const VOUCHER_TYPE_MODULE = {
  GeneralLedger: 1,
  Receivables: 2,
  CashAndBanks: 3,
  Payments: 4,
} as const;
export type VoucherTypeModule = (typeof VOUCHER_TYPE_MODULE)[keyof typeof VOUCHER_TYPE_MODULE];

/** آلي · يدوي · موافقة — appended, never renumbered: the stored value is the meaning. */
export const VOUCHER_EFFECT_TYPE = { Automatic: 1, Manual: 2, Approval: 3 } as const;
export type VoucherEffectType = (typeof VOUCHER_EFFECT_TYPE)[keyof typeof VOUCHER_EFFECT_TYPE];

export interface VoucherType {
  id: string;
  code: string;
  descriptionAr: string;
  descriptionEn: string;
  sequenceType: VoucherSequenceType;
  taxInclusion: VoucherTaxInclusion;
  numberingMethod: VoucherNumberingMethod;
  reportDestination: VoucherReportDestination;
  numberOfCopies: number;
  effectType: VoucherEffectType;
  isActive: boolean;
  /** A type the product depends on: renameable, never retirable. */
  isSystemType: boolean;
  /** How many vouchers already carry this type. Zero means it is safe to retire. */
  usageCount: number;
  createdAt: string | null;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
  activatedAt: string | null;
  activatedByName: string | null;
  deactivatedAt: string | null;
  deactivatedByName: string | null;
}

export interface VoucherTypeList {
  rows: VoucherType[];
  totalCount: number;
  activeCount: number;
}

export interface VoucherTypeUsageRow {
  id: string;
  voucherNumber: string;
  voucherDate: string;
  status: number;
}

export interface VoucherTypeUsage {
  voucherTypeId: string;
  code: string;
  descriptionAr: string;
  descriptionEn: string;
  journalVoucherCount: number;
  numberSequenceCount: number;
  recent: VoucherTypeUsageRow[];
}

export interface VoucherTypeAuditEntry {
  id: string;
  action: number;
  userName: string | null;
  occurredAt: string;
  summary: string | null;
  affectedColumns: string | null;
}

export interface SaveVoucherTypeInput {
  /** Which screen raised it, and therefore which one maintains it. Create only. */
  module?: VoucherTypeModule;
  code: string;
  descriptionAr: string;
  descriptionEn: string;
  sequenceType: VoucherSequenceType;
  taxInclusion: VoucherTaxInclusion;
  numberingMethod: VoucherNumberingMethod;
  reportDestination: VoucherReportDestination;
  numberOfCopies: number;
  effectType: VoucherEffectType;
}

// ─────────────────────────────── Account Opening Balances

export const OPENING_BALANCE_ROW_STATUS = {
  NotEntered: 1, Zero: 2, Entered: 3, Invalid: 4, Locked: 5,
} as const;
export type OpeningBalanceRowStatus =
  (typeof OPENING_BALANCE_ROW_STATUS)[keyof typeof OPENING_BALANCE_ROW_STATUS];

export const OPENING_BALANCE_BATCH_STATUS = {
  Draft: 1, Balanced: 2, Unbalanced: 3, Locked: 4, Posted: 5,
} as const;
export type OpeningBalanceBatchStatus =
  (typeof OPENING_BALANCE_BATCH_STATUS)[keyof typeof OPENING_BALANCE_BATCH_STATUS];

export interface OpeningBalanceRow {
  id: string | null;
  accountId: string;
  accountNumber: string;
  accountName: string;
  accountNameAr: string;
  /** Carried so a caller can narrow the sheet to one account type. Read-only. */
  accountType: AccountType;
  debit: number;
  credit: number;
  fiscalYearId: string;
  fiscalYearCode: string;
  currencyId: string;
  currencyCode: string;
  currencyDecimalPlaces: number;
  /** Null — never a guess — when a foreign currency has no approved rate yet. */
  exchangeRate: number | null;
  localDebit: number;
  localCredit: number;
  notes: string | null;
  status: OpeningBalanceRowStatus;
  isLocked: boolean;
  isEditable: boolean;
  readOnlyReason: string | null;
  readOnlyReasonAr: string | null;
  accountIsActive: boolean;
  updatedAt: string | null;
  updatedByName: string | null;
}

export interface OpeningBalanceTotals {
  totalDebit: number;
  totalCredit: number;
  totalLocalDebit: number;
  totalLocalCredit: number;
  difference: number;
  isBalanced: boolean;
}

export interface OpeningBalanceScope {
  fiscalYearId: string;
  fiscalYearCode: string;
  fiscalYearStatus: number;
  fiscalYearIsOpen: boolean;
  branchId: string | null;
  branchName: string | null;
  branchNameAr: string | null;
  baseCurrencyCode: string;
  baseCurrencyDecimalPlaces: number;
}

export interface OpeningBalanceSheet {
  rows: OpeningBalanceRow[];
  totalCount: number;
  totals: OpeningBalanceTotals;
  batchStatus: OpeningBalanceBatchStatus;
  scope: OpeningBalanceScope;
}

export interface OpeningBalanceFiscalYear {
  id: string;
  code: string;
  name: string;
  nameAr: string | null;
  status: number;
  isCurrent: boolean;
  isOpen: boolean;
}

/**
 * What the screen declares for one account.
 *
 * Local debit and local credit are absent on purpose: they are calculated by the server from the
 * rate, and a client that could state its own would be able to state one the rate does not produce.
 */
export interface SaveOpeningBalanceRowInput {
  accountId: string;
  currencyId: string;
  debit: number;
  credit: number;
  exchangeRate: number;
  notes: string | null;
}

export interface SaveOpeningBalancesInput {
  fiscalYearId: string;
  branchId?: string | null;
  rows: SaveOpeningBalanceRowInput[];
}

export interface SaveOpeningBalancesResult {
  savedCount: number;
  createdCount: number;
  updatedCount: number;
}

export interface OpeningBalanceFilter {
  fiscalYearId?: string;
  branchId?: string;
  search?: string;
  accountType?: number;
  financialStatement?: number;
  currencyId?: string;
  status?: number;
  showZeroBalances?: boolean;
  showInactiveAccounts?: boolean;
  /** 0 = whole chart, 1 = accounts named by a bank account, 2 = accounts named by a cashbox. */
  scope?: number;
}

/**
 * Which slice of the chart an opening-balance sheet covers.
 *
 * Derived server-side from what REFERENCES the account — a bank account's GL account, a cashbox's
 * cash account — so nothing depends on an account-code convention.
 */
export const OPENING_BALANCE_SCOPE = { All: 0, BankAccounts: 1, Cashboxes: 2 } as const;

// ─────────────────────────────── Cost Center Groups

/** One cost center inside a group. `id` is the MEMBERSHIP, not the cost center. */
export interface CostCenterGroupMember {
  id: string;
  costCenterId: string;
  costCenterCode: string;
  costCenterName: string;
  costCenterNameAr: string;
  /** The cost center's own state, shown but never edited from the group screen. */
  isActive: boolean;
  /** True when the center is a non-postable node of the cost center tree. */
  isTreeGroup: boolean;
  /** Read from the cost center's own master-data definition — never stored on the membership. */
  parentCode: string | null;
  parentName: string | null;
  parentNameAr: string | null;
  level: number;
  /** Materialised path of codes, e.g. `ALL/ADMIN`. */
  path: string | null;
  notes: string | null;
}

export interface CostCenterGroup {
  id: string;
  code: string;
  descriptionAr: string;
  descriptionEn: string;
  isActive: boolean;
  memberCount: number;
  /** Only populated by the single-group read; the list leaves it empty. */
  members: CostCenterGroupMember[];
  updatedAt: string | null;
}

export interface CostCenterGroupList {
  rows: CostCenterGroup[];
  totalCount: number;
}

export interface SaveCostCenterGroupInput {
  code: string;
  descriptionAr: string;
  descriptionEn: string;
  isActive: boolean;
}

// ─────────────────────────────── Payment Methods

/** Where a method puts the money: nowhere by itself, a cashbox, or a bank account. */
export const SETTLEMENT_TARGET = { None: 0, Cashbox: 1, Bank: 2 } as const;
export type SettlementTarget = (typeof SETTLEMENT_TARGET)[keyof typeof SETTLEMENT_TARGET];

export interface PaymentMethod {
  id: string;
  code: string;
  descriptionAr: string;
  descriptionEn: string;
  isActive: boolean;

  settlementTarget: SettlementTarget;

  cashboxId: string | null;
  cashboxCode: string | null;
  cashboxName: string | null;
  cashboxNameAr: string | null;

  bankId: string | null;
  bankCode: string | null;
  bankName: string | null;
  bankNameAr: string | null;

  bankBranchId: string | null;
  bankBranchName: string | null;
  bankBranchNameAr: string | null;

  bankAccountId: string | null;

  /** The GL account money reaches — the cashbox's own, or the chosen bank account. Read-only. */
  settlementAccountId: string | null;
  settlementAccountNumber: string | null;
  settlementAccountName: string | null;
  settlementAccountNameAr: string | null;

  /** Facility expense lines pointing here. Non-zero forbids deletion. */
  usageCount: number;
  updatedAt: string | null;
}

export interface PaymentMethodList {
  rows: PaymentMethod[];
  totalCount: number;
}

export interface SavePaymentMethodInput {
  id?: string | null;
  code: string;
  descriptionAr: string;
  descriptionEn: string;
  isActive: boolean;
  settlementTarget: SettlementTarget;
  cashboxId?: string | null;
  bankId?: string | null;
  bankBranchId?: string | null;
  bankAccountId?: string | null;
}

// ─────────────────────────────── Banks

export interface BankBranch {
  id: string;
  bankId: string;
  code: string;
  descriptionAr: string;
  descriptionEn: string;
  phone: string | null;
  fax: string | null;
  mobile: string | null;
  address: string | null;
  email: string | null;
  postalAddress: string | null;
  isDefault: boolean;
  isActive: boolean;
}

export interface Bank {
  id: string;
  code: string;
  descriptionAr: string;
  descriptionEn: string;
  swiftCode: string | null;
  bankCode: string | null;
  /** A free-text code until a cheque-template module exists to point at. */
  chequePrintTemplateCode: string | null;
  isDefault: boolean;
  isActive: boolean;
  branchCount: number;
  accountCount: number;
  /** Only the single-bank read fills these. */
  branches: BankBranch[];
  accounts: BankAccount[];
  updatedAt: string | null;
}

export interface BankAccount {
  id: string;
  bankId: string;
  bankBranchId: string | null;
  branchCode: string | null;
  branchName: string | null;
  branchNameAr: string | null;
  descriptionAr: string;
  descriptionEn: string;
  accountNumber: string | null;
  iban: string | null;
  currencyId: string;
  currencyCode: string;
  currencyName: string | null;
  currencyNameAr: string | null;
  /** The chart row the balance sits in. One bank account per chart row. */
  glAccountId: string;
  glAccountNumber: string;
  glAccountName: string;
  glAccountNameAr: string | null;
  isMain: boolean;
  isActive: boolean;
}

export interface SaveBankAccountInput {
  id?: string | null;
  bankBranchId: string | null;
  descriptionAr: string;
  descriptionEn: string;
  accountNumber: string | null;
  iban: string | null;
  currencyId: string;
  glAccountId: string;
  isMain: boolean;
  isActive: boolean;
}

export interface BankList {
  rows: Bank[];
  totalCount: number;
}

export interface SaveBankInput {
  code: string;
  descriptionAr: string;
  descriptionEn: string;
  swiftCode: string | null;
  bankCode: string | null;
  chequePrintTemplateCode: string | null;
  isDefault: boolean;
  isActive: boolean;
}

export interface SaveBankBranchInput {
  id?: string | null;
  code: string;
  descriptionAr: string;
  descriptionEn: string;
  phone: string | null;
  fax: string | null;
  mobile: string | null;
  address: string | null;
  email: string | null;
  postalAddress: string | null;
  isDefault: boolean;
  isActive: boolean;
}

// ─────────────────────────────── Cash Deposit

/** One share of the deposit, carried by a cost centre. */
export interface CashDepositLine {
  costCenterId: string | null;
  costCenterCode: string | null;
  costCenterName: string | null;
  costCenterNameAr: string | null;
  amount: number;
  description: string | null;
}

/** One charge the bank made on the deposit. */
export interface CashDepositExpense {
  expenseAccountId: string;
  expenseAccountNumber: string;
  expenseAccountName: string;
  expenseAccountNameAr: string | null;
  costCenterId: string | null;
  costCenterCode: string | null;
  costCenterName: string | null;
  amount: number;
  description: string | null;
}

/** One receipt or invoice the deposit covers. */
export interface CashDepositDocument {
  id: string;
  documentNumber: string;
  documentDate: string | null;
  amount: number;
  description: string | null;
}

export interface CashDeposit {
  id: string;
  /** The voucher this deposit is. Attachments, print and audit history all key on it. */
  journalVoucherId: string;
  voucherNumber: string;
  documentDate: string;
  status: JournalStatus;

  cashboxId: string;
  cashboxCode: string;
  cashboxName: string;
  cashboxNameAr: string;
  /** The cashbox's own cash account — the credit side. */
  cashboxAccountId: string;
  cashboxAccountNumber: string;
  cashboxAccountName: string;

  bankId: string;
  bankCode: string;
  bankName: string;
  bankNameAr: string;
  bankBranchId: string | null;
  bankBranchName: string | null;
  bankBranchNameAr: string | null;
  bankAccountId: string;
  bankAccountName: string;
  bankAccountNameAr: string;

  /** The chart row the bank account posts to — the debit side. */
  glAccountId: string;
  glAccountNumber: string;
  glAccountName: string;
  glAccountNameAr: string | null;

  currencyId: string;
  currencyCode: string;
  currencyDecimalPlaces: number;
  branchId: string;
  branchName: string;
  branchNameAr: string | null;
  fiscalYearId: string;
  fiscalYearName: string;

  amount: number;
  exchangeRate: number;
  localAmount: number;
  description: string | null;
  /** What actually reached the bank: the amount less the charges. Read-only. */
  amountReachingBank: number;

  lines: CashDepositLine[];
  expenses: CashDepositExpense[];
  documents: CashDepositDocument[];
  attachmentCount: number;
  postedAt: string | null;
  updatedAt: string | null;
}

export interface CashDepositList {
  rows: CashDeposit[];
  totalCount: number;
}

export interface SaveCashDepositLineInput {
  costCenterId: string;
  amount: number;
  description: string | null;
}

export interface SaveCashDepositInput {
  id?: string | null;
  documentDate: string;
  cashboxId: string;
  bankId: string;
  bankBranchId: string | null;
  bankAccountId: string;
  currencyId: string;
  branchId: string;
  amount: number;
  exchangeRate: number;
  description: string | null;
  lines: SaveCashDepositLineInput[];
  expenses: SaveCashDepositExpenseInput[];
  documents: SaveCashDepositDocumentInput[];
}

export interface SaveCashDepositExpenseInput {
  expenseAccountId: string;
  costCenterId: string | null;
  amount: number;
  description: string | null;
}

export interface SaveCashDepositDocumentInput {
  documentNumber: string;
  documentDate: string | null;
  amount: number;
  description: string | null;
}

// ─────────────────────────────── Cash Withdrawal

/** One share of the withdrawal, carried by a cost centre. */
export interface CashWithdrawalLine {
  costCenterId: string | null;
  costCenterCode: string | null;
  costCenterName: string | null;
  costCenterNameAr: string | null;
  amount: number;
  description: string | null;
}

export interface CashWithdrawal {
  id: string;
  journalVoucherId: string;
  voucherNumber: string;
  documentDate: string;
  status: JournalStatus;

  cashboxId: string;
  cashboxCode: string;
  cashboxName: string;
  cashboxNameAr: string;
  /** The cashbox's own cash account — the debit side. */
  cashboxAccountId: string;
  cashboxAccountNumber: string;
  cashboxAccountName: string;

  bankId: string;
  bankCode: string;
  bankName: string;
  bankNameAr: string;
  bankBranchId: string | null;
  bankBranchName: string | null;
  bankBranchNameAr: string | null;
  bankAccountId: string;
  bankAccountName: string;
  bankAccountNameAr: string;

  /** The chart row the bank account posts to — the credit side. */
  glAccountId: string;
  glAccountNumber: string;
  glAccountName: string;
  glAccountNameAr: string | null;

  currencyId: string;
  currencyCode: string;
  currencyDecimalPlaces: number;
  branchId: string;
  branchName: string;
  branchNameAr: string | null;
  fiscalYearId: string;
  fiscalYearName: string;

  amount: number;
  exchangeRate: number;
  localAmount: number;
  description: string | null;

  lines: CashWithdrawalLine[];
  attachmentCount: number;
  postedAt: string | null;
  updatedAt: string | null;
}

export interface CashWithdrawalList {
  rows: CashWithdrawal[];
  totalCount: number;
}

export interface SaveCashWithdrawalLineInput {
  costCenterId: string;
  amount: number;
  description: string | null;
}

export interface SaveCashWithdrawalInput {
  id?: string | null;
  documentDate: string;
  cashboxId: string;
  bankId: string;
  bankBranchId: string | null;
  bankAccountId: string;
  currencyId: string;
  branchId: string;
  amount: number;
  exchangeRate: number;
  description: string | null;
  lines: SaveCashWithdrawalLineInput[];
}

// ─────────────────────────────── Cashbox Transactions

/** من / إلى — which way the cash moved relative to the cashbox. */
export const CASH_DIRECTION = { From: 1, To: 2 } as const;
export type CashDirection = (typeof CASH_DIRECTION)[keyof typeof CASH_DIRECTION];

/** What the cashbox moved against. */
export const CASH_PARTY_TYPE = { Cashbox: 1, Bank: 2 } as const;
export type CashPartyType = (typeof CASH_PARTY_TYPE)[keyof typeof CASH_PARTY_TYPE];

/** إيداع · صفر الصندوق · تغذية صندوق · محضر جرد صندوق · محاسبة مندوبين */
export const CASH_MOVEMENT_TYPE = {
  Deposit: 1,
  Zeroing: 2,
  CashboxFunding: 3,
  CountMinutes: 4,
  RepresentativeSettlement: 5,
} as const;
export type CashMovementType = (typeof CASH_MOVEMENT_TYPE)[keyof typeof CASH_MOVEMENT_TYPE];

export interface CashboxTransactionLine {
  costCenterId: string | null;
  costCenterCode: string | null;
  costCenterName: string | null;
  costCenterNameAr: string | null;
  amount: number;
  description: string | null;
}

export interface CashboxTransaction {
  id: string;
  journalVoucherId: string;
  voucherNumber: string;
  documentDate: string;
  status: JournalStatus;

  direction: CashDirection;
  partyType: CashPartyType;
  movementType: CashMovementType;
  movementTime: string | null;
  counterCashboxId: string | null;
  counterAccountId: string | null;
  counterAccountNumber: string | null;
  counterAccountName: string | null;

  cashboxId: string;
  cashboxCode: string;
  cashboxName: string;
  cashboxNameAr: string;
  cashboxAccountId: string;
  cashboxAccountNumber: string;
  cashboxAccountName: string;

  bankId: string | null;
  bankCode: string;
  bankName: string;
  bankNameAr: string;
  bankBranchId: string | null;
  bankBranchName: string | null;
  bankBranchNameAr: string | null;
  bankAccountId: string | null;
  bankAccountName: string;
  bankAccountNameAr: string;

  glAccountId: string | null;
  glAccountNumber: string;
  glAccountName: string;
  glAccountNameAr: string | null;

  currencyId: string;
  currencyCode: string;
  currencyDecimalPlaces: number;
  branchId: string;
  branchName: string;
  branchNameAr: string | null;
  fiscalYearId: string;
  fiscalYearName: string;

  amount: number;
  exchangeRate: number;
  localAmount: number;
  description: string | null;

  lines: CashboxTransactionLine[];
  attachmentCount: number;
  postedAt: string | null;
  updatedAt: string | null;
}

export interface CashboxTransactionList {
  rows: CashboxTransaction[];
  totalCount: number;
}

export interface SaveCashboxTransactionLineInput {
  costCenterId: string;
  amount: number;
  description: string | null;
}

export interface SaveCashboxTransactionInput {
  id?: string | null;
  documentDate: string;
  movementTime: string | null;
  direction: CashDirection;
  partyType: CashPartyType;
  movementType: CashMovementType;
  counterCashboxId: string | null;
  counterAccountId: string | null;
  cashboxId: string;
  bankId: string | null;
  bankBranchId: string | null;
  bankAccountId: string | null;
  currencyId: string;
  branchId: string;
  amount: number;
  exchangeRate: number;
  description: string | null;
  lines: SaveCashboxTransactionLineInput[];
}

// ─────────────────────────────── Bank Facilities

/** قرض · سحب على المكشوف · اعتماد مستندي · كفالة بنكية · شيكات برسم التحصيل · حسم كمبيالات */
export const FACILITY_TYPE = {
  Loan: 1,
  Overdraft: 2,
  LetterOfCredit: 3,
  LetterOfGuarantee: 4,
  ChequesForCollection: 5,
  BillDiscounting: 6,
} as const;
export type FacilityType = (typeof FACILITY_TYPE)[keyof typeof FACILITY_TYPE];

/** How the loan is repaid. */
export const LOAN_REPAYMENT_TYPE = {
  EqualInstalments: 1,
  DecreasingInstalments: 2,
  LumpSumAtMaturity: 3,
  InterestOnlyThenPrincipal: 4,
} as const;
export type LoanRepaymentType = (typeof LOAN_REPAYMENT_TYPE)[keyof typeof LOAN_REPAYMENT_TYPE];

/** What the interest is charged on. */
export const INTEREST_RESTRICTION_BASIS = {
  OutstandingBalance: 1,
  ApprovedCeiling: 2,
  UtilisedAmount: 3,
  DailyBalance: 4,
} as const;
export type InterestRestrictionBasis =
  (typeof INTEREST_RESTRICTION_BASIS)[keyof typeof INTEREST_RESTRICTION_BASIS];

/** One charge on the facility: which expense account it posts to, and how it is settled. */
export interface FacilityExpense {
  id: string;
  expenseAccountId: string;
  expenseAccountNumber: string;
  expenseAccountName: string;
  expenseAccountNameAr: string;
  /** When given, the amount is computed from the ceiling and the keyed amount is ignored. */
  percentage: number | null;
  amount: number;
  paymentMethodId: string | null;
  paymentMethodName: string | null;
  paymentMethodNameAr: string | null;
}

/** A bank / branch / account triple, resolved for display. */
export interface FacilityAccountSide {
  bankId: string | null;
  bankName: string | null;
  bankNameAr: string | null;
  bankBranchId: string | null;
  branchName: string | null;
  branchNameAr: string | null;
  accountId: string | null;
  accountNumber: string | null;
  accountName: string | null;
  accountNameAr: string | null;
}

export interface BankFacility {
  id: string;
  code: string;
  facilityType: FacilityType;
  currencyId: string;
  currencyCode: string;
  currencyDecimalPlaces: number;
  facilityCeiling: number;
  interestRatePercent: number;
  loanType: LoanRepaymentType;
  graceDays: number;
  approvedYearDays: number;
  numberOfPayments: number;
  interestRestrictionBasis: InterestRestrictionBasis;
  isActive: boolean;
  ceilingExceededAllowed: boolean;
  isExternalParty: boolean;
  loanAccount: FacilityAccountSide;
  currentAccount: FacilityAccountSide;
  expenseCount: number;
  /** Only the single-facility read fills this. */
  expenses: FacilityExpense[];
  updatedAt: string | null;
}

export interface BankFacilityList {
  rows: BankFacility[];
  totalCount: number;
}

export interface SaveBankFacilityInput {
  id?: string | null;
  code: string;
  facilityType: FacilityType;
  currencyId: string;
  facilityCeiling: number;
  interestRatePercent: number;
  loanType: LoanRepaymentType;
  graceDays: number;
  approvedYearDays: number;
  numberOfPayments: number;
  interestRestrictionBasis: InterestRestrictionBasis;
  isActive: boolean;
  ceilingExceededAllowed: boolean;
  isExternalParty: boolean;
  loanBankId: string | null;
  loanBankBranchId: string | null;
  loanAccountId: string | null;
  currentBankId: string | null;
  currentBankBranchId: string | null;
  currentAccountId: string | null;
}

export interface SaveFacilityExpenseInput {
  id?: string | null;
  expenseAccountId: string;
  percentage: number | null;
  amount: number;
  paymentMethodId: string | null;
}

// ─────────────────────────────── Cashboxes

/** An account a cashbox posts to, carried with enough of the account to render it. */
export interface CashboxAccountRef {
  id: string | null;
  accountNumber: string | null;
  name: string | null;
  nameAr: string | null;
}

export interface CashboxTreeNode {
  id: string;
  parentId: string | null;
  code: string;
  name: string;
  nameAr: string;
  level: number;
  /** The cashbox's OWN flags — what the maintenance form edits. */
  isActive: boolean;
  isClosed: boolean;
  /** Closed itself, or sitting beneath a closed cashbox. Decides whether it can be used. */
  isEffectivelyClosed: boolean;
  isEffectivelyInactive: boolean;
  /** The cashbox above that closed or withdrew it; null when it stands on its own. */
  blockedBy: string | null;
  hasChildren: boolean;
}

export interface Cashbox {
  id: string;
  parentId: string | null;
  parentCode: string | null;
  parentName: string | null;
  parentNameAr: string | null;
  level: number;
  path: string | null;
  code: string;
  descriptionAr: string;
  descriptionEn: string;
  isOpeningCashbox: boolean;
  isClosingCashbox: boolean;
  cashAccount: CashboxAccountRef;
  chequesInCollectionAccount: CashboxAccountRef;
  differencesAccount: CashboxAccountRef;
  exchangeAccount: CashboxAccountRef;
  returnedChequesLegalAccount: CashboxAccountRef;
  returnedChequesCustomersAccount: CashboxAccountRef;
  creationDate: string | null;
  /** Null while the cashbox is open. */
  closingDate: string | null;
  isActive: boolean;
  isOpen: boolean;
  hasChildren: boolean;
  updatedAt: string | null;

  /** The group this cashbox is administered under, or null for an ungrouped one. */
  cashboxGroupId: string | null;
  cashboxGroupCode: string | null;
  cashboxGroupName: string | null;
  cashboxGroupNameAr: string | null;
}

export interface SaveCashboxInput {
  parentId: string | null;
  code: string;
  descriptionAr: string;
  descriptionEn: string;
  isOpeningCashbox: boolean;
  isClosingCashbox: boolean;
  /** The group this cashbox is administered under, or null for an ungrouped one. */
  cashboxGroupId?: string | null;
  cashAccountId: string | null;
  chequesInCollectionAccountId: string | null;
  differencesAccountId: string | null;
  exchangeAccountId: string | null;
  returnedChequesLegalAccountId: string | null;
  returnedChequesCustomersAccountId: string | null;
  creationDate: string | null;
  closingDate: string | null;
  isActive: boolean;
}

// ─────────────────────────────── Account Types

/** Local or international. A fixed enum: code reasons about these two values. */
export const ACCOUNT_SCOPE = { Local: 1, International: 2 } as const;
export type AccountScope = (typeof ACCOUNT_SCOPE)[keyof typeof ACCOUNT_SCOPE];

export interface AccountTypeRow {
  id: string;
  code: string;
  descriptionAr: string;
  descriptionEn: string;
  scope: AccountScope;
  scopeName: string;
  scopeNameAr: string;
  isActive: boolean;
  /** How many accounts point at this type. Non-zero forbids deletion. */
  usageCount: number;
  updatedAt: string | null;
}

export interface AccountTypeList {
  rows: AccountTypeRow[];
  totalCount: number;
}

export interface SaveAccountTypeInput {
  code: string;
  descriptionAr: string;
  descriptionEn: string;
  scope: AccountScope;
}

// ─────────────────────────────── Account Categories

export interface AccountCategoryRow {
  id: string;
  code: string;
  descriptionAr: string;
  descriptionEn: string;
  isActive: boolean;
  /** How many accounts carry this category. Non-zero forbids deletion. */
  usageCount: number;
  /** Orders the Financial Position subtotals. Carried, never edited here. */
  displayOrder: number;
  updatedAt: string | null;
}

export interface AccountCategoryList {
  rows: AccountCategoryRow[];
  totalCount: number;
}

export interface SaveAccountCategoryInput {
  code: string;
  descriptionAr: string;
  descriptionEn: string;
}

// ─────────────────────────────── Freeze Transactions

export interface FreezeMember {
  id: string;
  code: string;
  name: string;
  nameAr: string;
}

export interface TransactionFreeze {
  id: string;
  code: string;
  /** The date the decision was recorded — not part of the blocked window. */
  freezeDate: string;
  /** First blocked transaction date, inclusive. */
  fromDate: string;
  /** Last blocked transaction date, inclusive. */
  toDate: string;
  description: string | null;
  isActive: boolean;
  branches: FreezeMember[];
  voucherTypes: FreezeMember[];
  updatedAt: string | null;
}

export interface TransactionFreezeList {
  rows: TransactionFreeze[];
  totalCount: number;
}

export interface SaveTransactionFreezeInput {
  id?: string | null;
  code: string;
  freezeDate: string;
  fromDate: string;
  toDate: string;
  description?: string | null;
  branchIds: string[];
  voucherTypeIds: string[];
}

// ─────────────────────────────── Financial Statement Lines

export const STATEMENT_LINE_TYPE = {
  Header: 1, Account: 2, Group: 3, Total: 4,
  Calculated: 5, Spacer: 6, Note: 7, CostCenter: 8,
} as const;
export type StatementLineType = (typeof STATEMENT_LINE_TYPE)[keyof typeof STATEMENT_LINE_TYPE];

export const STATEMENT_EFFECT_NATURE = { Add: 1, Subtract: 2, NoEffect: 3 } as const;
export type StatementEffectNature =
  (typeof STATEMENT_EFFECT_NATURE)[keyof typeof STATEMENT_EFFECT_NATURE];

export const BALANCE_CALCULATION_METHOD = {
  AccountBalance: 1, SumOfChildren: 2, DebitMinusCredit: 3, CreditMinusDebit: 4, NoBalance: 5,
} as const;
export type BalanceCalculationMethod =
  (typeof BALANCE_CALCULATION_METHOD)[keyof typeof BALANCE_CALCULATION_METHOD];

export interface FinancialStatementLine {
  id: string;
  parentId: string | null;
  lineCode: string;
  lineType: StatementLineType;
  accountId: string | null;
  accountNumber: string | null;
  accountName: string | null;
  costCenterId: string | null;
  costCenterCode: string | null;
  costCenterName: string | null;
  descriptionAr: string;
  descriptionEn: string;
  disclosure: string | null;
  effectNature: StatementEffectNature;
  balanceMethod: BalanceCalculationMethod;
  displaySequence: number;
  isActive: boolean;
  isBold: boolean;
  isPrinted: boolean;
  level: number;
  childCount: number;
}

export interface SaveFinancialStatementLineInput {
  id?: string | null;
  parentId?: string | null;
  lineCode: string;
  lineType: StatementLineType;
  accountId?: string | null;
  costCenterId?: string | null;
  descriptionAr: string;
  descriptionEn: string;
  disclosure?: string | null;
  effectNature: StatementEffectNature;
  balanceMethod: BalanceCalculationMethod;
  displaySequence: number;
  isActive: boolean;
  isBold: boolean;
  isPrinted: boolean;
}

// ─────────────────────────────── Business Partners (Customers · Suppliers)

/**
 * The eleven partner vocabularies, all stored in ONE table keyed by this discriminator.
 *
 * Eleven separate master-data screens would have been eleven tables saying the same thing:
 * a code, an Arabic description, an English description and a flag. The type is what keeps
 * `VIP` legitimately usable as both a category and an importance without either shadowing
 * the other.
 */
export const PARTNER_LOOKUP_TYPE = {
  Category: 1,
  Importance: 2,
  PartnerType: 3,
  Class: 4,
  Title: 5,
  Nature: 6,
  Source: 7,
  Nationality: 8,
  ParentClassification: 9,
  ReportTemplate: 10,
  SalePriceType: 11,
  CommercialActivity: 12,
  /** نوع المستند — the vocabulary the partner document register draws from. */
  DocumentType: 13,
} as const;
export type PartnerLookupType = (typeof PARTNER_LOOKUP_TYPE)[keyof typeof PARTNER_LOOKUP_TYPE];

/** How the partner's prices carry tax. */
export const PARTNER_TAX_TREATMENT = { Inclusive: 1, Exclusive: 2, Exempt: 3 } as const;
export type PartnerTaxTreatment = (typeof PARTNER_TAX_TREATMENT)[keyof typeof PARTNER_TAX_TREATMENT];

/** نقدي / آجل — whether the partner settles on the spot or on account. */
export const PARTNER_PAYMENT_METHOD = { Cash: 1, Credit: 2 } as const;
export type PartnerPaymentMethod = (typeof PARTNER_PAYMENT_METHOD)[keyof typeof PARTNER_PAYMENT_METHOD];

export interface PartnerLookup {
  id: string;
  lookupType: PartnerLookupType;
  code: string;
  descriptionEn: string;
  descriptionAr: string;
  isActive: boolean;
  /** The collector this value hands a new customer card. Nature of Business only. */
  collectorUserId: string | null;
  collectorName: string | null;
  /** The tax code. Commercial Activity only, and optional there. */
  taxCode: string | null;
  /** How many partner cards point here. Non-zero forbids deletion. */
  usageCount: number;
  updatedAt: string | null;
}

export interface PartnerLookupList {
  rows: PartnerLookup[];
  totalCount: number;
}

export interface SavePartnerLookupInput {
  id?: string | null;
  lookupType: PartnerLookupType;
  code: string;
  descriptionAr: string;
  descriptionEn: string;
  isActive: boolean;
  /** One of the existing users, or null. Nature of Business only. */
  collectorUserId?: string | null;
  /** Optional tax code. Commercial Activity only. */
  taxCode?: string | null;
}

/** One person a partner card may name. Read from the existing users. */
export interface PartnerStaff {
  id: string;
  userName: string;
  name: string;
  nameAr: string | null;
}

export interface BusinessPartner {
  id: string;
  code: string;
  name: string;
  nameAr: string;
  partnerType: RelatedPartyType;
  startDate: string | null;

  controlAccountId: string | null;
  controlAccountNumber: string | null;
  controlAccountName: string | null;
  currencyId: string | null;
  currencyCode: string | null;
  creditLimit: number;
  taxTreatment: PartnerTaxTreatment;
  paymentMethod: PartnerPaymentMethod;
  salePriceTypeId: string | null;

  categoryId: string | null;
  importanceId: string | null;
  nationalityId: string | null;
  partnerTypeLookupId: string | null;
  classId: string | null;
  titleId: string | null;
  natureId: string | null;
  sourceId: string | null;
  parentClassificationId: string | null;
  reportTemplateId: string | null;
  parentPartnerId: string | null;
  parentPartnerName: string | null;

  salesRepresentativeUserId: string | null;
  marketerUserId: string | null;
  collectorUserId: string | null;
  employeeUserId: string | null;
  ownerUserId: string | null;

  nationalIdIndividual: string | null;
  nationalIdCompany: string | null;
  commercialRegistration: string | null;
  referenceNumber: string | null;
  taxNumber: string | null;
  turnoverRate: number | null;
  /** طريقة الدفع الافتراضية — a reference into the existing payment-method catalogue. */
  defaultPaymentMethodId: string | null;
  notes: string | null;

  phone: string | null;
  mobile: string | null;
  fax: string | null;
  email: string | null;
  website: string | null;
  contactPerson: string | null;

  country: string | null;
  city: string | null;
  area: string | null;
  /** The controlled selection, beside the legacy text above. */
  countryRegionId: string | null;
  countryRegionName: string | null;
  countryRegionNameAr: string | null;
  cityRegionId: string | null;
  cityRegionName: string | null;
  cityRegionNameAr: string | null;
  street: string | null;
  building: string | null;
  postalCode: string | null;
  poBox: string | null;
  address: string | null;
  addressNotes: string | null;
  latitude: number | null;
  longitude: number | null;

  isBlacklisted: boolean;
  blacklistReason: string | null;
  isActive: boolean;
  updatedAt: string | null;
}

export interface BusinessPartnerList {
  rows: BusinessPartner[];
  totalCount: number;
}

/**
 * Everything the save accepts. Mirrors the card exactly, minus the fields the server resolves
 * for itself — the control account's number and name, the currency code, the parent's name.
 */
export interface SaveBusinessPartnerInput {
  id?: string | null;
  code: string;
  name: string;
  nameAr: string;
  partnerType: RelatedPartyType;
  startDate: string | null;

  controlAccountId: string | null;
  currencyId: string | null;
  creditLimit: number;
  taxTreatment: PartnerTaxTreatment;
  paymentMethod: PartnerPaymentMethod;
  salePriceTypeId: string | null;

  categoryId: string | null;
  importanceId: string | null;
  nationalityId: string | null;
  partnerTypeLookupId: string | null;
  classId: string | null;
  titleId: string | null;
  natureId: string | null;
  sourceId: string | null;
  parentClassificationId: string | null;
  reportTemplateId: string | null;
  parentPartnerId: string | null;

  salesRepresentativeUserId: string | null;
  marketerUserId: string | null;
  collectorUserId: string | null;
  employeeUserId: string | null;
  ownerUserId: string | null;

  nationalIdIndividual: string | null;
  nationalIdCompany: string | null;
  commercialRegistration: string | null;
  referenceNumber: string | null;
  taxNumber: string | null;
  turnoverRate: number | null;
  /** طريقة الدفع الافتراضية — a reference into the existing payment-method catalogue. */
  defaultPaymentMethodId: string | null;
  notes: string | null;

  phone: string | null;
  mobile: string | null;
  fax: string | null;
  email: string | null;
  website: string | null;
  contactPerson: string | null;

  country: string | null;
  city: string | null;
  area: string | null;
  /** The controlled selection, beside the legacy text above. */
  countryRegionId: string | null;
  cityRegionId: string | null;
  street: string | null;
  building: string | null;
  postalCode: string | null;
  poBox: string | null;
  address: string | null;
  addressNotes: string | null;
  latitude: number | null;
  longitude: number | null;

  isBlacklisted: boolean;
  blacklistReason: string | null;
  isActive: boolean;
}

/** One recorded change to a partner card. Reads the same audit log every entity writes to. */
export interface BusinessPartnerAuditEntry {
  id: string;
  action: number;
  userName: string | null;
  occurredAt: string;
  summary: string | null;
  affectedColumns: string | null;
}

// ─────────────────────────────── Receivables System Settings

/** نوع التمويل — ثابت / متناقص. */
export const CUSTOMER_FINANCING_TYPE = { Fixed: 1, Declining: 2 } as const;
export type CustomerFinancingType =
  (typeof CUSTOMER_FINANCING_TYPE)[keyof typeof CUSTOMER_FINANCING_TYPE];

/**
 * نوع احتساب كلفة التمويل. The approved reference shows one value; the rest of the list is not
 * invented here, and adding one later is an enum member rather than a schema change.
 */
export const FINANCING_COST_BASIS = { Currencies: 1 } as const;
export type FinancingCostBasis = (typeof FINANCING_COST_BASIS)[keyof typeof FINANCING_COST_BASIS];

/** آلية احتساب قيمة الفائدة المؤجلة والمجدولة. One value in the approved reference. */
export const DEFERRED_INTEREST_BASIS = { Monthly: 1 } as const;
export type DeferredInterestBasis =
  (typeof DEFERRED_INTEREST_BASIS)[keyof typeof DEFERRED_INTEREST_BASIS];

/** اعتماد طريقة الدفع للعميل. One value in the approved reference. */
export const PAYMENT_METHOD_ENFORCEMENT_SOURCE = { CustomerCard: 1 } as const;
export type PaymentMethodEnforcementSource =
  (typeof PAYMENT_METHOD_ENFORCEMENT_SOURCE)[keyof typeof PAYMENT_METHOD_ENFORCEMENT_SOURCE];

/** آلية إحتساب تواريخ الفواتير في عمر الذمة. */
export const AGING_INVOICE_DATE_BASIS = { InvoiceDate: 1, DueDate: 2 } as const;
export type AgingInvoiceDateBasis =
  (typeof AGING_INVOICE_DATE_BASIS)[keyof typeof AGING_INVOICE_DATE_BASIS];

/** Every Receivables setting for the current company. One row, read and written whole. */
export interface ReceivablesSettings {
  requireNationalOrTaxNumber: boolean;
  requireCustomerCategory: boolean;
  requireAddressAndMobile: boolean;
  requireCustomerTypeClassAndNature: boolean;
  defaultCustomerCreditLimit: number;
  defaultCustomerPaymentMethod: PartnerPaymentMethod;
  defaultCustomerPriceTaxTreatment: PartnerTaxTreatment;

  hideCollectorFromVouchers: boolean;
  linkReferenceWithCollector: boolean;
  electronicPaymentSalesRepresentativeUserId: string | null;
  electronicPaymentSalesRepresentativeName: string | null;
  enableReceiptCommissionEntry: boolean;
  allowedSettlementDifference: number;
  maximumDeliveriesToSamePerson: number | null;
  minimumCustomerResponsePercentage: number;
  legalSettlementAlertDays: number | null;
  paymentMethodEnforcementSource: PaymentMethodEnforcementSource;
  createCustomerReceivablesForChequesInCollection: boolean;

  customerAgingGracePeriodDays: number;
  receivablesOpeningBalanceDate: string | null;
  useOpeningBalanceDateAsAgingStart: boolean;
  agingInvoiceDateBasis: AgingInvoiceDateBasis;
  showNotYetDueAndAdvancePaymentsInAging: boolean;

  customerFinancingType: CustomerFinancingType;
  accountingFinancingType: CustomerFinancingType;
  financingCostBasis: FinancingCostBasis;
  deferredInterestBasis: DeferredInterestBasis;
  decliningInterestMultiplier: number;
  decliningInterestOnBalanceBeforePayment: boolean;
  createIntermediateAccountInFinancingRequest: boolean;

  promissoryNoteDelayPenalty: number | null;
  hideCommissionFieldsInScheduling: boolean;

  updatedAt: string | null;
  updatedByName: string | null;
}

/** The save payload: every setting, minus the two names the server resolves for itself. */
export type SaveReceivablesSettingsInput = Omit<
  ReceivablesSettings,
  "electronicPaymentSalesRepresentativeName" | "updatedAt" | "updatedByName"
>;

export interface ReceivablesSettingsAuditEntry {
  id: string;
  action: number;
  userName: string | null;
  occurredAt: string;
  summary: string | null;
  affectedColumns: string | null;
  /** The before and after images the interceptor recorded, as stored JSON. */
  oldValues: string | null;
  newValues: string | null;
}

// ─────────────────────────────── Workflow Actions

/** One defined step in the company's working procedure. Master data only. */
export interface WorkflowAction {
  id: string;
  code: string;
  descriptionEn: string;
  descriptionAr: string;
  sequence: number;
  isMandatory: boolean;
  isActive: boolean;
  updatedAt: string | null;
  updatedByName: string | null;
}

export interface WorkflowActionList {
  rows: WorkflowAction[];
  totalCount: number;
}

export interface SaveWorkflowActionInput {
  id?: string | null;
  code: string;
  descriptionAr: string;
  descriptionEn: string;
  sequence: number;
  isMandatory: boolean;
  isActive: boolean;
}

export interface WorkflowActionAuditEntry {
  id: string;
  action: number;
  userName: string | null;
  occurredAt: string;
  summary: string | null;
  affectedColumns: string | null;
  oldValues: string | null;
  newValues: string | null;
}

// ─────────────────────────────── Customer Opening Balances

/** The six approved receivable classifications. Each stays a distinct balance. */
export const CUSTOMER_OPENING_BALANCE_TYPE = {
  CustomerReceivables: 1,
  UnbilledReceivables: 2,
  CustomerAdvancePayments: 3,
  PromissoryNoteReceivables: 4,
  SupplierReceivables: 5,
  ChequesInCollection: 6,
} as const;
export type CustomerOpeningBalanceType =
  (typeof CUSTOMER_OPENING_BALANCE_TYPE)[keyof typeof CUSTOMER_OPENING_BALANCE_TYPE];

export interface CustomerOpeningBalanceRow {
  id: string | null;
  businessPartnerId: string;
  customerCode: string;
  customerName: string;
  customerNameAr: string;
  customerIsActive: boolean;
  categoryId: string | null;
  fiscalYearId: string;
  fiscalYearName: string;
  currencyId: string;
  currencyCode: string;
  isBaseCurrency: boolean;
  exchangeRate: number;
  debit: number;
  credit: number;
  localDebit: number;
  localCredit: number;
  notes: string | null;
  status: OpeningBalanceRowStatus;
  balanceType: CustomerOpeningBalanceType;
  controlAccountId: string | null;
  controlAccountNumber: string | null;
  controlAccountName: string | null;
}

export interface CustomerOpeningBalanceTotals {
  debit: number;
  credit: number;
  localDebit: number;
  localCredit: number;
  difference: number;
  enteredCount: number;
  zeroCount: number;
  notEnteredCount: number;
}

export interface CustomerOpeningBalanceSheet {
  balanceType: CustomerOpeningBalanceType;
  rows: CustomerOpeningBalanceRow[];
  totals: CustomerOpeningBalanceTotals;
  /** Said plainly when a classification asks for something the system cannot express yet. */
  advisory: string | null;
  advisoryAr: string | null;
}

export interface CustomerOpeningBalanceInput {
  businessPartnerId: string;
  currencyId: string;
  exchangeRate: number;
  debit: number;
  credit: number;
  notes?: string | null;
}

export interface SaveCustomerOpeningBalancesInput {
  balanceType: CustomerOpeningBalanceType;
  fiscalYearId: string;
  rows: CustomerOpeningBalanceInput[];
}

export interface SaveCustomerOpeningBalancesResult {
  created: number;
  updated: number;
}

// ─────────────────────────────── Service Sales Types

/** One approved kind of service the company sells, with its accounting treatment. */
export interface ServiceSalesTypeRow {
  id: string;
  code: string;
  descriptionAr: string;
  descriptionEn: string;
  accountId: string;
  accountNumber: string | null;
  accountName: string | null;
  accountNameAr: string | null;
  isTaxable: boolean;
  taxTypeId: string | null;
  taxTypeCode: string | null;
  taxTypeName: string | null;
  taxTypeNameAr: string | null;
  isActive: boolean;
  updatedAt: string | null;
  updatedByName: string | null;
}

export interface ServiceSalesTypeList {
  rows: ServiceSalesTypeRow[];
  totalCount: number;
}

export interface SaveServiceSalesTypeInput {
  id?: string | null;
  code: string;
  descriptionAr: string;
  descriptionEn: string;
  accountId: string;
  isTaxable: boolean;
  taxTypeId: string | null;
  isActive: boolean;
}

/**
 * What a NEW sales or service document may name, with the defaults it takes from the type.
 * Active types only — the server offers no way to ask for the withdrawn ones here.
 */
export interface ServiceSalesTypeLookupRow {
  id: string;
  code: string;
  name: string;
  nameAr: string;
  accountId: string;
  accountNumber: string;
  accountName: string;
  accountNameAr: string | null;
  isTaxable: boolean;
  taxTypeId: string | null;
  taxTypeCode: string | null;
  taxTypeName: string | null;
  taxTypeNameAr: string | null;
}

export interface ServiceSalesTypeAuditEntry {
  id: string;
  action: number;
  userName: string | null;
  occurredAt: string;
  summary: string | null;
  affectedColumns: string | null;
}

// ─────────────────────────────── Discount Types

/** نوع الخصم — نسبة / مبلغ ثابت. */
export const DISCOUNT_CALCULATION_TYPE = { Percentage: 1, FixedAmount: 2 } as const;
export type DiscountCalculationType =
  (typeof DISCOUNT_CALCULATION_TYPE)[keyof typeof DISCOUNT_CALCULATION_TYPE];

export interface DiscountType {
  id: string;
  code: string;
  descriptionEn: string;
  descriptionAr: string;
  calculationType: DiscountCalculationType;
  accountId: string;
  accountNumber: string | null;
  accountName: string | null;
  accountNameAr: string | null;
  isDefault: boolean;
  isActive: boolean;
  updatedAt: string | null;
  updatedByName: string | null;
}

export interface DiscountTypeList {
  rows: DiscountType[];
  totalCount: number;
}

export interface SaveDiscountTypeInput {
  id?: string | null;
  code: string;
  descriptionAr: string;
  descriptionEn: string;
  calculationType: DiscountCalculationType;
  accountId: string;
  isDefault: boolean;
  isActive: boolean;
}

export interface DiscountTypeAuditEntry {
  id: string;
  action: number;
  userName: string | null;
  occurredAt: string;
  summary: string | null;
  affectedColumns: string | null;
}

// ─────────────────────────────── Electronic Payment Policy

export interface PaymentCardType {
  id: string;
  code: string;
  descriptionEn: string;
  descriptionAr: string;
  isActive: boolean;
  /** How many commission policies name it. Non-zero forbids removal. */
  usageCount: number;
  updatedAt: string | null;
}

export interface PaymentCardTypeList {
  rows: PaymentCardType[];
  totalCount: number;
}

export interface SavePaymentCardTypeInput {
  id?: string | null;
  code: string;
  descriptionAr: string;
  descriptionEn: string;
  isActive: boolean;
}

export interface PaymentCardCommissionTier {
  id?: string | null;
  fromAmount: number;
  /** Null is the open-ended final range. No maximum is invented. */
  toAmount: number | null;
  commissionPercent: number;
  commissionAmount: number;
  sequence: number;
}

export interface PaymentCardCommissionPolicy {
  id: string;
  paymentCardTypeId: string;
  paymentCardTypeCode: string;
  paymentCardTypeName: string;
  paymentCardTypeNameAr: string;
  branchId: string;
  branchName: string | null;
  branchNameAr: string | null;
  isActive: boolean;
  tiers: PaymentCardCommissionTier[];
  updatedAt: string | null;
}

export interface PaymentCardCommissionPolicyList {
  rows: PaymentCardCommissionPolicy[];
  totalCount: number;
  /** Set when the commission account is not linked yet — the policy stores but cannot post. */
  advisory: string | null;
  advisoryAr: string | null;
}

export interface SavePaymentCardCommissionPolicyInput {
  id?: string | null;
  paymentCardTypeId: string;
  branchId: string;
  isActive: boolean;
  tiers: PaymentCardCommissionTier[];
}

// ─────────────────────────────── SMS Reminder Settings

export interface SmsReminderSettings {
  sendDayOfMonth: number;
  sendHour: number;
  sendMinute: number;
  startMonth: number;
  endMonth: number;
  messageText: string | null;
  isAutomaticSending: boolean;
  isActive: boolean;
  /** True while this build has no way to actually send. The screen says so. */
  sendingUnavailable: boolean;
  updatedAt: string | null;
  updatedByName: string | null;
}

export type SaveSmsReminderSettingsInput = Omit<
  SmsReminderSettings,
  "sendingUnavailable" | "updatedAt" | "updatedByName"
>;

export interface SmsReminderSettingsAuditEntry {
  id: string;
  action: number;
  userName: string | null;
  occurredAt: string;
  summary: string | null;
  affectedColumns: string | null;
  oldValues: string | null;
  newValues: string | null;
}

// ─────────────────────────────── User-Partner Access (Customers · Suppliers)

/**
 * The three levels a partner may be granted at. Independent by construction: holding a
 * partner at one level says nothing about the other two.
 */
export const SupplierAccessScope = {
  DailyTransaction: 1,
  Inquiry: 2,
  Report: 3,
} as const;

export type SupplierAccessScopeValue =
  (typeof SupplierAccessScope)[keyof typeof SupplierAccessScope];

export interface PartnerGrant {
  id: string;
  partnerId: string;
  partnerCode: string;
  partnerName: string;
  partnerNameAr: string | null;
  /** The partner itself. A deactivated partner keeps its grant but confers nothing. */
  partnerIsActive: boolean;
  /** The grant. False suspends it without losing it. */
  isActive: boolean;
  grantedAt: string;
}

export interface UserPartnerAccess {
  userId: string;
  userName: string;
  fullName: string;
  fullNameAr: string | null;
  userIsActive: boolean;
  partyType: RelatedPartyType;
  dailyTransactions: PartnerGrant[];
  inquiries: PartnerGrant[];
  reports: PartnerGrant[];
}

export interface AssignablePartner {
  id: string;
  code: string;
  name: string;
  nameAr: string | null;
}

export interface UserPartnerAccessAuditEntry {
  id: string;
  action: number;
  userName: string | null;
  occurredAt: string;
  summary: string | null;
  affectedColumns: string | null;
}

// ─────────────────────────────── Countries & Regions

export interface CountryRegion {
  id: string;
  parentId: string | null;
  code: string;
  descriptionEn: string;
  descriptionAr: string;
  nationality: string | null;
  nationalityAr: string | null;
  currencyId: string | null;
  currencyCode: string | null;
  timeZoneId: string | null;
  shortCode: string | null;
  internationalCode: string | null;
  mobileDigitCount: number;
  documentDigitCount: number;
  hasFlag: boolean;
  flagFileName: string | null;
  isActive: boolean;
  /** Derived from the parent server-side, never stored. */
  isChild: boolean;
  level: number;
  childCount: number;
  updatedAt: string | null;
}

export interface CountryRegionTree {
  /** Depth-first, parents before children. */
  rows: CountryRegion[];
  totalCount: number;
}

export interface SaveCountryRegionInput {
  id: string | null;
  parentId: string | null;
  code: string;
  descriptionEn: string;
  descriptionAr: string;
  nationality: string | null;
  nationalityAr: string | null;
  currencyId: string | null;
  timeZoneId: string | null;
  shortCode: string | null;
  internationalCode: string | null;
  mobileDigitCount: number;
  documentDigitCount: number;
  isActive: boolean;
}

export interface TimeZoneOption {
  id: string;
  displayName: string;
  offset: string;
}

export interface CountryRegionAuditEntry {
  id: string;
  action: number;
  userName: string | null;
  occurredAt: string;
  summary: string | null;
  affectedColumns: string | null;
}

// ─────────────────────────────── Custody Categories

export interface CustodyCategory {
  id: string;
  code: string;
  descriptionAr: string;
  descriptionEn: string;
  isActive: boolean;
  updatedAt: string | null;
}

export interface CustodyCategoryList {
  rows: CustodyCategory[];
  totalCount: number;
  activeCount: number;
}

export interface SaveCustodyCategoryInput {
  id: string | null;
  code: string;
  descriptionAr: string;
  descriptionEn: string;
  isActive: boolean;
}

export interface CustodyCategoryAuditEntry {
  id: string;
  action: number;
  userName: string | null;
  occurredAt: string;
  summary: string | null;
  affectedColumns: string | null;
}

/**
 * Payments › Master Data › Collectors, Drivers, Coordinators, Sales Representatives.
 *
 * One shape for the four lists. `userId` is the SAME users.id that receipt orders and partner
 * cards already carry for a collector or a sales representative — this record says who that
 * user is, it does not become a second thing for a document to point at.
 */
export const PERSONNEL_ROLE_TYPE = {
  Collector: 1,
  Driver: 2,
  Coordinator: 3,
  SalesRepresentative: 4,
} as const;
export type PersonnelRoleType = (typeof PERSONNEL_ROLE_TYPE)[keyof typeof PERSONNEL_ROLE_TYPE];

export interface PersonnelRole {
  id: string;
  roleType: PersonnelRoleType;
  code: string;
  nameAr: string;
  nameEn: string;
  userId: string | null;
  userName: string | null;
  mobile: string | null;
  phone: string | null;
  email: string | null;
  branchId: string | null;
  branchName: string | null;
  notes: string | null;
  isActive: boolean;
  updatedAt: string | null;
}

export interface PersonnelRoleList {
  rows: PersonnelRole[];
  /** The whole list, not the filtered view. */
  totalCount: number;
  activeCount: number;
  /** How many rows the current filter matched. */
  matchedCount: number;
}

export interface SavePersonnelRoleInput {
  id: string | null;
  roleType: PersonnelRoleType;
  code: string;
  nameAr: string;
  nameEn: string;
  userId: string | null;
  mobile: string | null;
  phone: string | null;
  email: string | null;
  branchId: string | null;
  notes: string | null;
  isActive: boolean;
}

export interface PersonnelOption {
  id: string;
  name: string;
  nameAr: string | null;
}

export interface PersonnelOptions {
  users: PersonnelOption[];
  branches: PersonnelOption[];
}

/**
 * Payments › Daily Transactions › طلب شراء خدمات — Service Purchase Request.
 *
 * A PRE-ACCOUNTING document: it posts nothing at any stage. There is deliberately no audit or
 * post action on it, and no journal reference anywhere in this shape.
 */
export const SERVICE_PURCHASE_REQUEST_STATUS = { Draft: 1, Cancelled: 2 } as const;
export type ServicePurchaseRequestStatus =
  (typeof SERVICE_PURCHASE_REQUEST_STATUS)[keyof typeof SERVICE_PURCHASE_REQUEST_STATUS];

export interface ServicePurchaseRequestLine {
  id: string;
  lineNumber: number;
  description: string;
  unit: string | null;
  quantity: number;
  unitPrice: number;
  discountTypeId: string | null;
  discountTypeName: string | null;
  discountTypeNameAr: string | null;
  discountValue: number;
  discountPercent: number;
  taxTypeId: string | null;
  taxTypeCode: string | null;
  taxTypeName: string | null;
  taxTypeNameAr: string | null;
  taxPercent: number;
  costCenterId: string | null;
  costCenterCode: string | null;
  costCenterName: string | null;
  grossAmount: number;
  discountAmount: number;
  taxableAmount: number;
  taxAmount: number;
  netAmount: number;
}

export interface ServicePurchaseRequest {
  id: string;
  requestNumber: string;
  requestSerial: number;
  requestDate: string;
  requiredDate: string | null;
  supplierId: string;
  supplierCode: string;
  supplierName: string;
  supplierNameAr: string | null;
  currencyId: string;
  currencyCode: string;
  exchangeRate: number;
  paymentMethodId: string | null;
  paymentMethodName: string | null;
  requestedByUserId: string | null;
  requestedByUserName: string | null;
  costCenterId: string | null;
  costCenterCode: string | null;
  branchId: string;
  branchName: string | null;
  fiscalYearId: string;
  fiscalYearName: string | null;
  reference: string | null;
  notes: string | null;
  isTaxInclusive: boolean;
  status: ServicePurchaseRequestStatus;
  isEditable: boolean;
  cancelledAt: string | null;
  cancellationReason: string | null;
  subtotal: number;
  discountTotal: number;
  taxTotal: number;
  netTotal: number;
  createdAt: string;
  createdByName: string | null;
  lines: ServicePurchaseRequestLine[];
  attachmentCount: number;
}

export interface ServicePurchaseRequestList {
  rows: ServicePurchaseRequest[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalNet: number;
  baseCurrencyCode: string;
}

export interface SaveServicePurchaseRequestLineInput {
  description: string;
  unit: string | null;
  quantity: number;
  unitPrice: number;
  discountTypeId: string | null;
  discountValue: number;
  discountPercent: number;
  taxTypeId: string | null;
  costCenterId: string | null;
}

export interface SaveServicePurchaseRequestInput {
  id: string | null;
  requestDate: string;
  requiredDate: string | null;
  supplierId: string;
  currencyId: string;
  exchangeRate: number;
  branchId: string;
  paymentMethodId: string | null;
  requestedByUserId: string | null;
  costCenterId: string | null;
  reference: string | null;
  notes: string | null;
  isTaxInclusive: boolean;
  lines: SaveServicePurchaseRequestLineInput[];
}

/**
 * Payments › Daily Transactions › فاتورة شراء خدمات — Service Purchase Invoice.
 *
 * An ACCOUNTING document. Its lifecycle is the journal voucher's — Draft → Audited → Posted —
 * so `status` is a JournalStatus, not a status of the invoice's own.
 *
 * Each line names the expense account it is bought into. The sales catalogue is deliberately
 * NOT used: its account is a revenue account, and a purchase must never land there.
 */
export interface ServicePurchaseInvoiceLine {
  id: string;
  lineNumber: number;
  expenseAccountId: string;
  expenseAccountNumber: string | null;
  expenseAccountName: string | null;
  description: string;
  unit: string | null;
  quantity: number;
  unitPrice: number;
  discountTypeId: string | null;
  discountValue: number;
  discountPercent: number;
  taxTypeId: string | null;
  taxTypeName: string | null;
  taxTypeNameAr: string | null;
  taxPercent: number;
  costCenterId: string | null;
  costCenterCode: string | null;
  grossAmount: number;
  discountAmount: number;
  taxableAmount: number;
  taxAmount: number;
  netAmount: number;
  returnedAmount: number;
}

export interface ServicePurchaseInvoice {
  id: string;
  journalVoucherId: string;
  voucherNumber: string;
  invoiceDate: string;
  status: JournalStatus;
  isEditable: boolean;
  supplierId: string;
  supplierCode: string;
  supplierName: string;
  supplierNameAr: string | null;
  supplierInvoiceNumber: string | null;
  sourceRequestId: string | null;
  sourceRequestNumber: string | null;
  currencyId: string;
  currencyCode: string;
  exchangeRate: number;
  paymentMethodId: string | null;
  costCenterId: string | null;
  branchId: string;
  notes: string | null;
  isTaxInclusive: boolean;
  subtotal: number;
  discountTotal: number;
  taxTotal: number;
  netTotal: number;
  settledAmount: number;
  paymentStatus: number;
  createdAt: string;
  attachmentCount: number;
  lines: ServicePurchaseInvoiceLine[];
}

export interface ServicePurchaseInvoiceList {
  rows: ServicePurchaseInvoice[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalNet: number;
  baseCurrencyCode: string;
}

export interface SaveServicePurchaseInvoiceLineInput {
  id: string;
  expenseAccountId: string;
  description: string;
  unit: string | null;
  quantity: number;
  unitPrice: number;
  discountTypeId: string | null;
  discountValue: number;
  discountPercent: number;
  taxTypeId: string | null;
  costCenterId: string | null;
}

export interface SaveServicePurchaseInvoiceInput {
  id: string | null;
  invoiceDate: string;
  supplierId: string;
  supplierInvoiceNumber: string | null;
  sourceRequestId: string | null;
  currencyId: string;
  exchangeRate: number;
  branchId: string | null;
  paymentMethodId: string | null;
  costCenterId: string | null;
  notes: string | null;
  isTaxInclusive: boolean;
  lines: SaveServicePurchaseInvoiceLineInput[];
}

/**
 * Payments › Daily Transactions › مردود شراء خدمات — Service Purchase Return.
 *
 * Invoice-bound: the supplier, currency, rate, accounts and tax all come from the source
 * invoice. The caller chooses only WHICH lines and HOW MUCH of each.
 */
export interface ServicePurchaseReturnLine {
  id: string;
  lineNumber: number;
  sourceInvoiceLineId: string;
  returnQuantity: number;
  originalQuantity: number;
  expenseAccountId: string;
  expenseAccountNumber: string | null;
  description: string | null;
  unitPrice: number;
  taxPercent: number;
  discountAmount: number;
  netAmount: number;
  taxAmount: number;
  totalAmount: number;
}

export interface ServicePurchaseReturn {
  id: string;
  journalVoucherId: string;
  voucherNumber: string;
  returnDate: string;
  status: JournalStatus;
  isEditable: boolean;
  sourceInvoiceId: string;
  sourceInvoiceNumber: string;
  supplierId: string;
  supplierCode: string;
  supplierName: string;
  supplierNameAr: string | null;
  currencyCode: string;
  exchangeRate: number;
  referenceNumber: string | null;
  notes: string | null;
  netAmount: number;
  taxAmount: number;
  totalAmount: number;
  createdAt: string;
  lines: ServicePurchaseReturnLine[];
}

export interface ServicePurchaseReturnList {
  rows: ServicePurchaseReturn[];
  totalCount: number;
  totalReturned: number;
  baseCurrencyCode: string;
}

/** What is still returnable on each line of a source invoice. */
export interface ServicePurchaseReturnAvailabilityLine {
  sourceInvoiceLineId: string;
  lineNumber: number;
  description: string;
  expenseAccountId: string;
  expenseAccountNumber: string | null;
  originalQuantity: number;
  returnedQuantity: number;
  remainingQuantity: number;
  unitPrice: number;
  taxPercent: number;
}

export interface ServicePurchaseReturnAvailability {
  sourceInvoiceId: string;
  invoiceNumber: string;
  invoiceDate: string;
  supplierId: string;
  supplierCode: string;
  supplierName: string;
  currencyCode: string;
  isPosted: boolean;
  lines: ServicePurchaseReturnAvailabilityLine[];
}

export interface SaveServicePurchaseReturnLineInput {
  sourceInvoiceLineId: string;
  returnQuantity: number;
  description: string | null;
}

export interface SaveServicePurchaseReturnInput {
  id: string | null;
  sourceInvoiceId: string;
  returnDate: string;
  branchId: string | null;
  costCenterId: string | null;
  referenceNumber: string | null;
  notes: string | null;
  lines: SaveServicePurchaseReturnLineInput[];
}

/**
 * Payments › Daily Transactions › دفاتر الصرف — Payment Books.
 *
 * Stationery under control: no accounting at any stage, and a book never allocates a voucher
 * number. Its movement history is append-only.
 */
export const PAYMENT_BOOK_STATUS = { Available: 1, Assigned: 2, Closed: 3 } as const;
export type PaymentBookStatus =
  (typeof PAYMENT_BOOK_STATUS)[keyof typeof PAYMENT_BOOK_STATUS];

export const PAYMENT_BOOK_ACTION = {
  Received: 1, Assigned: 2, ReturnedFromHolder: 3, Closed: 4,
} as const;
export type PaymentBookAction =
  (typeof PAYMENT_BOOK_ACTION)[keyof typeof PAYMENT_BOOK_ACTION];

export interface PaymentBookMovement {
  id: string;
  action: PaymentBookAction;
  fromHolderId: string | null;
  fromHolderName: string | null;
  toHolderId: string | null;
  toHolderName: string | null;
  movementDate: string;
  performedByName: string | null;
  notes: string | null;
  createdAt: string;
}

export interface PaymentBook {
  id: string;
  bookNumber: string;
  fromNumber: number;
  toNumber: number;
  voucherCount: number;
  status: PaymentBookStatus;
  branchId: string | null;
  branchName: string | null;
  currentHolderId: string | null;
  currentHolderCode: string | null;
  currentHolderName: string | null;
  currentHolderNameAr: string | null;
  receivedDate: string;
  assignedDate: string | null;
  returnedDate: string | null;
  notes: string | null;
  createdAt: string;
  movements: PaymentBookMovement[];
}

export interface PaymentBookList {
  rows: PaymentBook[];
  totalCount: number;
  availableCount: number;
  assignedCount: number;
  totalNumbers: number;
}

export interface SavePaymentBookInput {
  id: string | null;
  bookNumber: string;
  fromNumber: number;
  toNumber: number;
  branchId: string | null;
  receivedDate: string;
  notes: string | null;
}

/** Payments › Daily Transactions › أمر صرف — an instruction; PaidAmount is DERIVED. */
export const PAYMENT_ORDER_STATUS = { Open: 1, Cancelled: 2 } as const;
export type PaymentOrderStatus =
  (typeof PAYMENT_ORDER_STATUS)[keyof typeof PAYMENT_ORDER_STATUS];

export interface PaymentOrderVoucher {
  id: string;
  voucherNumber: string;
  paymentDate: string;
  status: JournalStatus;
  amount: number;
  /** Only a POSTED voucher counts toward the paid total. */
  isEffective: boolean;
}

export interface PaymentOrder {
  id: string;
  orderNumber: string;
  orderDate: string;
  status: PaymentOrderStatus;
  isEditable: boolean;
  supplierId: string;
  supplierCode: string;
  supplierName: string;
  supplierNameAr: string | null;
  requestedAmount: number;
  paidAmount: number;
  remainingAmount: number;
  isFullyPaid: boolean;
  isPartiallyPaid: boolean;
  canPay: boolean;
  currencyId: string;
  currencyCode: string;
  exchangeRate: number;
  expectedPaymentDate: string | null;
  expectedPaymentMethodId: string | null;
  expectedPaymentMethodName: string | null;
  responsibleUserId: string | null;
  responsibleUserName: string | null;
  branchId: string;
  branchName: string | null;
  referenceNumber: string | null;
  notes: string | null;
  cancellationReason: string | null;
  createdAt: string;
  vouchers: PaymentOrderVoucher[];
}

export interface PaymentOrderList {
  rows: PaymentOrder[];
  totalCount: number;
  totalRequested: number;
  totalPaid: number;
  baseCurrencyCode: string;
}

export interface SavePaymentOrderInput {
  id: string | null;
  orderDate: string;
  supplierId: string;
  requestedAmount: number;
  currencyId: string;
  exchangeRate: number;
  branchId: string | null;
  expectedPaymentDate: string | null;
  expectedPaymentMethodId: string | null;
  responsibleUserId: string | null;
  referenceNumber: string | null;
  notes: string | null;
}

/** Payments › Daily Transactions › سند صرف — the accounting document. */
export const PAYMENT_VOUCHER_MODE = { Supplier: 1, DirectAccount: 2 } as const;
export type PaymentVoucherMode =
  (typeof PAYMENT_VOUCHER_MODE)[keyof typeof PAYMENT_VOUCHER_MODE];

export interface PaymentVoucherLine {
  id: string;
  lineNumber: number;
  paymentMethodId: string;
  paymentMethodName: string | null;
  amount: number;
  cashboxId: string | null;
  cashboxCode: string | null;
  bankAccountId: string | null;
  bankAccountNumber: string | null;
  notes: string | null;
}

/** One supplier invoice a payment voucher settles. */
export interface PaymentVoucherAllocation {
  outstandingJournalVoucherId: string;
  documentNumber: string;
  supplierInvoiceNumber: string | null;
  documentDate: string | null;
  dueDate: string | null;
  currencyCode: string | null;
  originalAmount: number;
  /** What the invoice still owed before this voucher's own settlement. */
  outstandingBefore: number;
  amount: number;
  /** True once the voucher posted and the reconciliation engine accepted it. */
  isEffective: boolean;
}

/** A supplier document a payment can still be applied to. */
export interface PayableOpenInvoice {
  outstandingJournalVoucherId: string;
  documentNumber: string;
  documentType: string;
  supplierInvoiceNumber: string | null;
  documentDate: string;
  dueDate: string | null;
  currencyId: string;
  currencyCode: string | null;
  originalAmount: number;
  settledAmount: number;
  outstandingAmount: number;
  status: string;
}

export interface PayableOpenInvoices {
  rows: PayableOpenInvoice[];
  totalOutstanding: number;
}

export interface PaymentVoucher {
  id: string;
  journalVoucherId: string;
  voucherNumber: string;
  paymentDate: string;
  status: JournalStatus;
  isEditable: boolean;
  mode: PaymentVoucherMode;
  supplierId: string | null;
  supplierCode: string | null;
  supplierName: string | null;
  supplierNameAr: string | null;
  directAccountId: string | null;
  directAccountNumber: string | null;
  directAccountName: string | null;
  paymentOrderId: string | null;
  paymentOrderNumber: string | null;
  currencyId: string;
  currencyCode: string;
  exchangeRate: number;
  totalAmount: number;
  costCenterId: string | null;
  branchId: string;
  referenceNumber: string | null;
  notes: string | null;
  createdAt: string;
  lines: PaymentVoucherLine[];
  allocations: PaymentVoucherAllocation[];
  /** The part of the payment applied to no invoice. A payment on account, not an error. */
  unappliedAmount: number;
}

export interface PaymentVoucherList {
  rows: PaymentVoucher[];
  totalCount: number;
  totalPaid: number;
  baseCurrencyCode: string;
}

export interface SavePaymentVoucherLineInput {
  paymentMethodId: string;
  amount: number;
  cashboxId: string | null;
  bankAccountId: string | null;
  /** Sent so a cheque attempt is REFUSED by name; nothing stores it. */
  chequeNumber: string | null;
  notes: string | null;
}

// ── Payments › Daily Transactions › إشعار مدين للمورّد (#7) ────────────────────────────────
//
// An adjustment that DEBITS a supplier: it reduces what is owed. Free-standing, unlike the
// purchase return, which must name an invoice and is capped by what remains returnable on it.

export interface SupplierDebitNoteLine {
  id: string;
  lineNumber: number;
  description: string;
  offsetAccountId: string;
  offsetAccountNumber: string | null;
  offsetAccountName: string | null;
  offsetAccountNameAr: string | null;
  amount: number;
  taxTypeId: string | null;
  taxTypeCode: string | null;
  taxTypeName: string | null;
  taxTypeNameAr: string | null;
  taxPercent: number;
  costCenterId: string | null;
  costCenterCode: string | null;
  costCenterName: string | null;
  notes: string | null;
  netAmount: number;
  taxAmount: number;
  totalAmount: number;
}

export interface SupplierDebitNote {
  id: string;
  noteNumber: string;
  noteDate: string;
  status: JournalStatus;
  journalVoucherId: string;
  supplierId: string;
  supplierCode: string | null;
  supplierName: string | null;
  supplierNameAr: string | null;
  /** Lineage only. The invoice itself is never altered by this document. */
  sourceServicePurchaseInvoiceId: string | null;
  sourceInvoiceNumber: string | null;
  currencyId: string;
  currencyCode: string;
  exchangeRate: number;
  isTaxInclusive: boolean;
  costCenterId: string | null;
  branchId: string;
  branchName: string | null;
  referenceNumber: string | null;
  notes: string | null;
  netAmount: number;
  taxAmount: number;
  totalAmount: number;
  isEditable: boolean;
  updatedAt: string | null;
  createdBy: string | null;
  createdByName: string | null;
  lines: SupplierDebitNoteLine[];
}

export interface SupplierDebitNoteList {
  rows: SupplierDebitNote[];
  totalCount: number;
  totalPosted: number;
  baseCurrencyCode: string;
}

export interface SaveSupplierDebitNoteLineInput {
  id?: string | null;
  description: string;
  offsetAccountId: string;
  amount: number;
  taxTypeId?: string | null;
  costCenterId?: string | null;
  notes?: string | null;
}

export interface SaveSupplierDebitNoteInput {
  id?: string | null;
  noteDate: string;
  supplierId: string;
  sourceServicePurchaseInvoiceId?: string | null;
  currencyId: string;
  exchangeRate?: number | null;
  costCenterId?: string | null;
  branchId?: string | null;
  referenceNumber?: string | null;
  notes?: string | null;
  /** Null takes the supplier's own approved treatment. */
  isTaxInclusive?: boolean | null;
  lines: SaveSupplierDebitNoteLineInput[];
}

// ── Payments › Daily Transactions › إشعار دائن للمورّد (#8) ────────────────────────────────
//
// An adjustment that CREDITS a supplier: it increases what is owed. The exact inverse of the
// debit note — an undercharge corrected, an agreed price rise, a charge raised after the fact.

export interface SupplierCreditNoteLine {
  id: string;
  lineNumber: number;
  description: string;
  offsetAccountId: string;
  offsetAccountNumber: string | null;
  offsetAccountName: string | null;
  offsetAccountNameAr: string | null;
  amount: number;
  taxTypeId: string | null;
  taxTypeCode: string | null;
  taxTypeName: string | null;
  taxTypeNameAr: string | null;
  taxPercent: number;
  costCenterId: string | null;
  costCenterCode: string | null;
  costCenterName: string | null;
  notes: string | null;
  netAmount: number;
  taxAmount: number;
  totalAmount: number;
}

export interface SupplierCreditNote {
  id: string;
  noteNumber: string;
  noteDate: string;
  status: JournalStatus;
  journalVoucherId: string;
  supplierId: string;
  supplierCode: string | null;
  supplierName: string | null;
  supplierNameAr: string | null;
  /** Lineage only. The invoice itself is never altered by this document. */
  sourceServicePurchaseInvoiceId: string | null;
  sourceInvoiceNumber: string | null;
  currencyId: string;
  currencyCode: string;
  exchangeRate: number;
  isTaxInclusive: boolean;
  costCenterId: string | null;
  branchId: string;
  branchName: string | null;
  referenceNumber: string | null;
  notes: string | null;
  netAmount: number;
  taxAmount: number;
  totalAmount: number;
  isEditable: boolean;
  updatedAt: string | null;
  createdBy: string | null;
  createdByName: string | null;
  lines: SupplierCreditNoteLine[];
}

export interface SupplierCreditNoteList {
  rows: SupplierCreditNote[];
  totalCount: number;
  totalPosted: number;
  baseCurrencyCode: string;
}

export interface SaveSupplierCreditNoteLineInput {
  id?: string | null;
  description: string;
  offsetAccountId: string;
  amount: number;
  taxTypeId?: string | null;
  costCenterId?: string | null;
  notes?: string | null;
}

export interface SaveSupplierCreditNoteInput {
  id?: string | null;
  noteDate: string;
  supplierId: string;
  sourceServicePurchaseInvoiceId?: string | null;
  currencyId: string;
  exchangeRate?: number | null;
  costCenterId?: string | null;
  branchId?: string | null;
  referenceNumber?: string | null;
  notes?: string | null;
  /** Null takes the supplier's own approved treatment. */
  isTaxInclusive?: boolean | null;
  lines: SaveSupplierCreditNoteLineInput[];
}

export interface SavePaymentVoucherInput {
  id: string | null;
  paymentDate: string;
  mode: PaymentVoucherMode;
  supplierId: string | null;
  directAccountId: string | null;
  paymentOrderId: string | null;
  currencyId: string;
  exchangeRate: number;
  branchId: string | null;
  costCenterId: string | null;
  referenceNumber: string | null;
  notes: string | null;
  lines: SavePaymentVoucherLineInput[];
  allocations: SavePaymentVoucherAllocationInput[];
}

/** An invoice this voucher is asked to settle. Takes effect only when the voucher posts. */
export interface SavePaymentVoucherAllocationInput {
  outstandingJournalVoucherId: string;
  amount: number;
}

export interface PersonnelRoleAuditEntry {
  id: string;
  action: number;
  userName: string | null;
  occurredAt: string;
  summary: string | null;
  affectedColumns: string | null;
}

// ─────────────────────────────── Receivables Aging Periods

export interface ReceivablesAgingPeriod {
  id: string;
  sequence: number;
  fromDay: number;
  toDay: number;
  updatedAt: string | null;
}

export interface ReceivablesAgingPeriodList {
  rows: ReceivablesAgingPeriod[];
  totalCount: number;
}

export interface SaveReceivablesAgingPeriodInput {
  id: string | null;
  sequence: number;
  fromDay: number;
  toDay: number;
}

export interface ReceivablesAgingPeriodAuditEntry {
  id: string;
  action: number;
  userName: string | null;
  occurredAt: string;
  summary: string | null;
  affectedColumns: string | null;
}

// ─────────────────────────────── Sales & Purchase Tax Types

/** جهة الضريبة — 1 Sales, 2 Purchase. Null leaves the category deciding, as it always did. */
export const TAX_DIRECTION_SIDE = { Sales: 1, Purchase: 2 } as const;

export type TaxDirectionSide = (typeof TAX_DIRECTION_SIDE)[keyof typeof TAX_DIRECTION_SIDE];

export interface SalesPurchaseTaxType {
  id: string;
  code: string;
  descriptionAr: string;
  descriptionEn: string;
  category: number;
  /** Set only when the category cannot say which side the tax belongs to. */
  side: TaxDirectionSide | null;
  /** Whether the tax may be charged on a sale, after the marker is taken into account. */
  isSalesSide: boolean;
  fromDate: string | null;
  toDate: string | null;
  rate: number | null;
  rateId: string | null;
  debitAccountId: string | null;
  creditAccountId: string | null;
  /** The account the grid shows: credit for sales, debit for purchases. */
  accountNumber: string | null;
  accountName: string | null;
  accountNameAr: string | null;
  isActive: boolean;
  isDefaultForSales: boolean;
  updatedAt: string | null;
}

export interface SalesPurchaseTaxTypeList {
  rows: SalesPurchaseTaxType[];
  totalCount: number;
  activeCount: number;
}

export interface SaveSalesPurchaseTaxTypeInput {
  id: string | null;
  code: string;
  descriptionAr: string;
  descriptionEn: string;
  category: number;
  side: TaxDirectionSide | null;
  fromDate: string;
  toDate: string | null;
  rate: number;
  debitAccountId: string | null;
  creditAccountId: string | null;
  isActive: boolean;
  isDefaultForSales: boolean;
}

// ─────────────────────────────── Service Sales Quotations

export const SERVICE_QUOTATION_STATUS = {
  Draft: 1,
  Cancelled: 2,
} as const;

export type ServiceQuotationStatusValue =
  (typeof SERVICE_QUOTATION_STATUS)[keyof typeof SERVICE_QUOTATION_STATUS];

export interface ServiceQuotationLine {
  id: string;
  lineNumber: number;
  serviceSalesTypeId: string;
  serviceCode: string;
  serviceName: string;
  serviceNameAr: string;
  description: string | null;
  quantity: number;
  unitPrice: number;
  discountPercent: number;
  /** The Receivables Settings discount type this line was discounted under, if any. */
  discountTypeId: string | null;
  discountTypeCode: string | null;
  discountTypeName: string | null;
  discountTypeNameAr: string | null;
  /** 1 Percentage, 2 Fixed amount — the TYPE decides how `discountValue` is read. */
  discountCalculation: DiscountCalculationType | null;
  discountValue: number;
  taxTypeId: string | null;
  taxTypeCode: string | null;
  /** The classification's own names, so a zero-charging line still says WHY. */
  taxTypeName: string | null;
  taxTypeNameAr: string | null;
  taxPercent: number;
  costCenterId: string | null;
  grossAmount: number;
  discountAmount: number;
  /** The tax base: the amount, net of tax, the tax relates to. */
  taxableAmount: number;
  taxAmount: number;
  netAmount: number;
}

export interface ServiceQuotation {
  id: string;
  quotationNumber: string;
  quotationDate: string;
  status: ServiceQuotationStatusValue;
  customerId: string;
  customerCode: string;
  customerName: string;
  customerNameAr: string | null;
  currencyId: string;
  currencyCode: string;
  exchangeRate: number;
  paymentMethod: number;
  /** طريقة الدفع — defaulted from the customer, then owned by the document. */
  paymentMethodId: string | null;
  salespersonUserId: string | null;
  salespersonName: string | null;
  costCenterId: string | null;
  branchId: string;
  /** الفرع — resolved for display; the save assigns the branch itself. */
  branchName: string | null;
  fiscalYearId: string;
  /** السنة — the fiscal year the number was allocated in. */
  fiscalYearName: string | null;
  /** فئة العميل — read from the customer's card, never stored on the quotation. */
  customerCategoryId: string | null;
  notes: string | null;
  /** ضريبة السعر: شامل / غير شامل — changes the arithmetic, not just the label. */
  isTaxInclusive: boolean;
  /** The customer's approved tax treatment: 1 Inclusive, 2 Exclusive, 3 Exempt. */
  customerTaxTreatment: number;
  subtotal: number;
  discountTotal: number;
  taxTotal: number;
  netTotal: number;
  isEditable: boolean;
  updatedAt: string | null;
  lines: ServiceQuotationLine[];
  /** الملفات المرفقة — metadata only; the bytes are fetched per file. */
  attachments: ServiceQuotationAttachment[];
}

/** One file attached to a quotation. Same shape as a journal attachment, same storage behind it. */
export interface ServiceQuotationAttachment {
  id: string;
  fileName: string;
  contentType: string | null;
  fileSizeBytes: number;
  description: string | null;
  uploadedAt: string;
  uploadedBy: string | null;
  uploadedByName: string | null;
}

/** نوع البيع — 1 One-Time, 2 Contract. Decides which account the revenue is credited to. */
export const SALE_NATURE = { OneTime: 1, Contract: 2 } as const;
export type SaleNature = (typeof SALE_NATURE)[keyof typeof SALE_NATURE];

// ─────────────────────────────── Receipt Voucher (سند القبض) + Cheques

export const RECEIPT_SOURCE_TYPE = { Customer: 1, GeneralAccount: 2 } as const;
export type ReceiptSourceType = (typeof RECEIPT_SOURCE_TYPE)[keyof typeof RECEIPT_SOURCE_TYPE];

export const CHEQUE_STATUS = {
  /** برسم التحصيل - in our hands, not yet at a bank. */
  UnderCollection: 1,
  /** مودع للتحصيل - lodged with a bank. */
  Deposited: 2,
  /** محصل - the money arrived. */
  Cleared: 3,
  /** مرتجع - the bank returned it unpaid. */
  Bounced: 4,
  /** مُعاد - handed back uncollected. */
  Returned: 5,

  // The paying side. A cheque WE wrote never holds one of the five above, and none of these five
  // ever belongs to one we received - which is why both live in one list without ever colliding.

  /** محرر غير مسلّم - written, still in our hands. */
  Issued: 6,
  /** مُسلّم - handed to the beneficiary. */
  Delivered: 7,
  /** مصروف - the bank took the money. */
  Paid: 8,
  /** مرتجع من البنك - our own cheque came back. */
  ReturnedUnpaid: 9,
  /** ملغى - voided. */
  Cancelled: 10,
} as const;
export type ChequeStatus = (typeof CHEQUE_STATUS)[keyof typeof CHEQUE_STATUS];

export interface ReceiptVoucherPayment {
  id: string;
  lineNumber: number;
  paymentMethodId: string;
  paymentMethodCode: string | null;
  paymentMethodName: string | null;
  paymentMethodNameAr: string | null;
  amount: number;
  cashboxId: string | null;
  cashboxName: string | null;
  bankAccountId: string | null;
  bankAccountName: string | null;
  chequeNumber: string | null;
  chequeBankId: string | null;
  chequeBankName: string | null;
  chequeDate: string | null;
  chequeDueDate: string | null;
  notes: string | null;
}

export interface ReceiptVoucherCheque {
  id: string;
  chequeNumber: string;
  bankName: string | null;
  chequeDate: string;
  dueDate: string;
  amount: number;
  status: ChequeStatus;
  drawerName: string | null;
  clearedDate: string | null;
}

export interface ReceiptVoucherAttachment {
  id: string;
  fileName: string;
  contentType: string | null;
  fileSizeBytes: number;
  description: string | null;
  uploadedAt: string;
  uploadedBy: string | null;
  uploadedByName: string | null;
}

export interface ReceiptVoucher {
  id: string;
  voucherNumber: string;
  voucherDate: string;
  status: JournalStatus;
  journalVoucherId: string;
  sourceType: ReceiptSourceType;
  customerId: string | null;
  customerCode: string | null;
  customerName: string | null;
  customerNameAr: string | null;
  sourceAccountId: string | null;
  sourceAccountName: string | null;
  /** أمر القبض المصدر — optional. A direct receipt leaves it null. */
  sourceReceiptOrderId: string | null;
  currencyId: string;
  currencyCode: string;
  exchangeRate: number;
  /** Derived server-side from the payment rows; never typed. */
  totalAmount: number;
  costCenterId: string | null;
  branchId: string;
  branchName: string | null;
  notes: string | null;
  referenceNumber: string | null;
  isEditable: boolean;
  updatedAt: string | null;
  createdBy: string | null;
  createdByName: string | null;
  payments: ReceiptVoucherPayment[];
  cheques: ReceiptVoucherCheque[];
  attachments: ReceiptVoucherAttachment[];
}

export interface ReceiptVoucherList {
  rows: ReceiptVoucher[];
  totalCount: number;
}

/**
 * One row of الشيكات المقبوضة.
 *
 * Wider than {@link ReceiptVoucherCheque}: the voucher panel shows the few particulars of a
 * cheque it just created, while the inquiry answers "where is this piece of paper and where did
 * it come from" — which needs the cashbox, the currency and the voucher it arrived on.
 */
export interface ChequeListItem {
  id: string;
  chequeNumber: string;
  drawerPartyId: string;
  drawerCode: string | null;
  drawerName: string | null;
  drawerNameAr: string | null;
  drawerBankId: string;
  bankName: string | null;
  bankNameAr: string | null;
  drawerBankBranchId: string | null;
  bankBranchName: string | null;
  chequeDate: string;
  dueDate: string;
  amount: number;
  currencyId: string;
  currencyCode: string | null;
  cashboxId: string;
  cashboxCode: string | null;
  cashboxName: string | null;
  status: ChequeStatus;
  receiptVoucherId: string;
  receiptVoucherNumber: string | null;
  clearedToBankAccountId: string | null;
  clearedToBankAccountName: string | null;
  clearedDate: string | null;
  settlementJournalVoucherId: string | null;
  settlementVoucherNumber: string | null;
  notes: string | null;
}

export interface ChequeList {
  rows: ChequeListItem[];
  totalCount: number;
}

export interface ChequeFilter {
  chequeNumber?: string;
  customerId?: string;
  bankId?: string;
  cashboxId?: string;
  status?: ChequeStatus;
  receiptVoucherId?: string;
  dueFrom?: string;
  dueTo?: string;
}

export interface SaveReceiptVoucherPaymentInput {
  id: string | null;
  paymentMethodId: string;
  amount: number;
  cashboxId: string | null;
  bankAccountId: string | null;
  chequeNumber: string | null;
  chequeBankId: string | null;
  chequeBankBranchId: string | null;
  chequeDate: string | null;
  chequeDueDate: string | null;
  notes: string | null;
}

export interface SaveReceiptVoucherInput {
  id: string | null;
  voucherDate: string;
  sourceType: ReceiptSourceType;
  customerId: string | null;
  sourceAccountId: string | null;
  /** أمر القبض المصدر — optional; the server enforces the order's ceiling when set. */
  sourceReceiptOrderId?: string | null;
  currencyId: string;
  exchangeRate: number | null;
  costCenterId: string | null;
  branchId: string | null;
  notes: string | null;
  referenceNumber: string | null;
  payments: SaveReceiptVoucherPaymentInput[];
}

// ─────────────────────────────── Debit Note (إشعار مدين)

export interface DebitNoteLine {
  id: string;
  lineNumber: number;
  description: string;
  /** The postable account credited against the customer's receivable. Chosen per line. */
  offsetAccountId: string;
  offsetAccountNumber: string | null;
  offsetAccountName: string | null;
  offsetAccountNameAr: string | null;
  amount: number;
  /** Kept even when the tax amount is zero — zero-rated, exempt, export and OOS stay distinct. */
  taxTypeId: string | null;
  taxTypeCode: string | null;
  taxTypeName: string | null;
  taxTypeNameAr: string | null;
  taxPercent: number;
  costCenterId: string | null;
  costCenterCode: string | null;
  costCenterName: string | null;
  notes: string | null;
  netAmount: number;
  taxAmount: number;
  totalAmount: number;
}

export interface DebitNoteAttachment {
  id: string;
  fileName: string;
  contentType: string | null;
  fileSizeBytes: number;
  description: string | null;
  uploadedAt: string;
  uploadedBy: string | null;
  uploadedByName: string | null;
}

export interface DebitNote {
  id: string;
  noteNumber: string;
  noteDate: string;
  status: JournalStatus;
  journalVoucherId: string;
  customerId: string;
  customerCode: string | null;
  customerName: string | null;
  customerNameAr: string | null;
  /** Lineage only: the invoice is never altered, allocated against or reopened. */
  sourceServiceSalesInvoiceId: string | null;
  sourceInvoiceNumber: string | null;
  currencyId: string;
  currencyCode: string;
  exchangeRate: number;
  isTaxInclusive: boolean;
  costCenterId: string | null;
  branchId: string;
  branchName: string | null;
  referenceNumber: string | null;
  notes: string | null;
  /** All three derived server-side from the persisted lines; never sent by the screen. */
  netAmount: number;
  taxAmount: number;
  totalAmount: number;
  isEditable: boolean;
  updatedAt: string | null;
  createdBy: string | null;
  createdByName: string | null;
  lines: DebitNoteLine[];
  attachments: DebitNoteAttachment[];
}

export interface DebitNoteList {
  rows: DebitNote[];
  totalCount: number;
}

export interface DebitNoteFilter {
  search?: string;
  customerId?: string;
  status?: number;
  currencyId?: string;
  branchId?: string;
  fromDate?: string;
  toDate?: string;
}

export interface SaveDebitNoteLineInput {
  id: string | null;
  description: string;
  offsetAccountId: string;
  amount: number;
  taxTypeId: string | null;
  costCenterId: string | null;
  notes: string | null;
}

export interface SaveDebitNoteInput {
  id: string | null;
  noteDate: string;
  customerId: string;
  sourceServiceSalesInvoiceId: string | null;
  currencyId: string;
  exchangeRate: number | null;
  costCenterId: string | null;
  branchId: string | null;
  referenceNumber: string | null;
  notes: string | null;
  /** Null takes the customer's own approved treatment. */
  isTaxInclusive: boolean | null;
  lines: SaveDebitNoteLineInput[];
}

// ─────────────────────────────── Credit Note (إشعار دائن)

export interface CreditNoteLine {
  id: string;
  lineNumber: number;
  description: string;
  /** The postable account DEBITED against the customer's receivable. Chosen per line. */
  offsetAccountId: string;
  offsetAccountNumber: string | null;
  offsetAccountName: string | null;
  offsetAccountNameAr: string | null;
  amount: number;
  /** Kept even when the tax amount is zero — zero-rated, exempt, export and OOS stay distinct. */
  taxTypeId: string | null;
  taxTypeCode: string | null;
  taxTypeName: string | null;
  taxTypeNameAr: string | null;
  taxPercent: number;
  costCenterId: string | null;
  costCenterCode: string | null;
  costCenterName: string | null;
  notes: string | null;
  netAmount: number;
  taxAmount: number;
  totalAmount: number;
}

export interface CreditNoteAttachment {
  id: string;
  fileName: string;
  contentType: string | null;
  fileSizeBytes: number;
  description: string | null;
  uploadedAt: string;
  uploadedBy: string | null;
  uploadedByName: string | null;
}

export interface CreditNote {
  id: string;
  noteNumber: string;
  noteDate: string;
  status: JournalStatus;
  journalVoucherId: string;
  customerId: string;
  customerCode: string | null;
  customerName: string | null;
  customerNameAr: string | null;
  /** Lineage only: the invoice is never altered, allocated against or reopened. */
  sourceServiceSalesInvoiceId: string | null;
  sourceInvoiceNumber: string | null;
  currencyId: string;
  currencyCode: string;
  exchangeRate: number;
  isTaxInclusive: boolean;
  costCenterId: string | null;
  branchId: string;
  branchName: string | null;
  referenceNumber: string | null;
  notes: string | null;
  /** All three derived server-side from the persisted lines; never sent by the screen. */
  netAmount: number;
  taxAmount: number;
  totalAmount: number;
  isEditable: boolean;
  updatedAt: string | null;
  createdBy: string | null;
  createdByName: string | null;
  lines: CreditNoteLine[];
  attachments: CreditNoteAttachment[];
}

export interface CreditNoteList {
  rows: CreditNote[];
  totalCount: number;
}

export interface CreditNoteFilter {
  search?: string;
  customerId?: string;
  status?: number;
  currencyId?: string;
  branchId?: string;
  fromDate?: string;
  toDate?: string;
}

export interface SaveCreditNoteLineInput {
  id: string | null;
  description: string;
  offsetAccountId: string;
  amount: number;
  taxTypeId: string | null;
  costCenterId: string | null;
  notes: string | null;
}

export interface SaveCreditNoteInput {
  id: string | null;
  noteDate: string;
  customerId: string;
  sourceServiceSalesInvoiceId: string | null;
  currencyId: string;
  exchangeRate: number | null;
  costCenterId: string | null;
  branchId: string | null;
  referenceNumber: string | null;
  notes: string | null;
  /** Null takes the customer's own approved treatment. */
  isTaxInclusive: boolean | null;
  lines: SaveCreditNoteLineInput[];
}

// ─────────────────────────────── Service Sales Return (مردود بيع خدمات)

/** One invoice line as the return screen offers it, with what is left to give back. */
export interface ReturnableInvoiceLine {
  sourceLineId: string;
  lineNumber: number;
  serviceSalesTypeId: string;
  serviceCode: string | null;
  serviceName: string | null;
  serviceNameAr: string | null;
  description: string | null;
  originalQuantity: number;
  /** Σ of POSTED returns. Drafts, audited returns and reversed ones do not count. */
  returnedQuantity: number;
  availableQuantity: number;
  unitPrice: number;
  discountAmount: number;
  taxTypeId: string | null;
  taxTypeCode: string | null;
  taxTypeName: string | null;
  taxTypeNameAr: string | null;
  taxPercent: number;
  costCenterId: string | null;
  netAmount: number;
  taxAmount: number;
  totalAmount: number;
}

export interface ReturnableInvoice {
  id: string;
  invoiceNumber: string;
  invoiceDate: string;
  customerId: string;
  customerCode: string | null;
  customerName: string | null;
  customerNameAr: string | null;
  currencyId: string;
  currencyCode: string;
  exchangeRate: number;
  netTotal: number;
  /** Derived from posted returns. The invoice itself stays POSTED regardless. */
  isFullyReturned: boolean;
  lines: ReturnableInvoiceLine[];
}

export interface ReturnableInvoiceList {
  rows: ReturnableInvoice[];
  totalCount: number;
}

export interface ServiceSalesReturnLine {
  id: string;
  lineNumber: number;
  sourceServiceSalesInvoiceLineId: string;
  serviceSalesTypeId: string;
  serviceCode: string | null;
  serviceName: string | null;
  serviceNameAr: string | null;
  description: string | null;
  originalQuantity: number;
  returnQuantity: number;
  unitPrice: number;
  taxTypeId: string | null;
  taxTypeCode: string | null;
  taxTypeName: string | null;
  taxTypeNameAr: string | null;
  taxPercent: number;
  costCenterId: string | null;
  costCenterCode: string | null;
  discountAmount: number;
  netAmount: number;
  taxAmount: number;
  totalAmount: number;
}

export interface ServiceSalesReturnAttachment {
  id: string;
  fileName: string;
  contentType: string | null;
  fileSizeBytes: number;
  description: string | null;
  uploadedAt: string;
  uploadedBy: string | null;
  uploadedByName: string | null;
}

export interface ServiceSalesReturn {
  id: string;
  returnNumber: string;
  returnDate: string;
  status: JournalStatus;
  journalVoucherId: string;
  /** REQUIRED. There is no free-entry service sales return. */
  sourceServiceSalesInvoiceId: string;
  sourceInvoiceNumber: string | null;
  sourceInvoiceDate: string | null;
  customerId: string;
  customerCode: string | null;
  customerName: string | null;
  customerNameAr: string | null;
  currencyId: string;
  currencyCode: string;
  exchangeRate: number;
  costCenterId: string | null;
  branchId: string;
  branchName: string | null;
  referenceNumber: string | null;
  notes: string | null;
  netAmount: number;
  taxAmount: number;
  totalAmount: number;
  isEditable: boolean;
  updatedAt: string | null;
  createdBy: string | null;
  createdByName: string | null;
  lines: ServiceSalesReturnLine[];
  attachments: ServiceSalesReturnAttachment[];
}

export interface ServiceSalesReturnList {
  rows: ServiceSalesReturn[];
  totalCount: number;
}

export interface ServiceSalesReturnFilter {
  search?: string;
  customerId?: string;
  sourceServiceSalesInvoiceId?: string;
  status?: number;
  currencyId?: string;
  branchId?: string;
  fromDate?: string;
  toDate?: string;
}

/** The only two things a client may send per line. Everything else comes from the invoice. */
export interface SaveServiceSalesReturnLineInput {
  id: string | null;
  sourceServiceSalesInvoiceLineId: string;
  returnQuantity: number;
}

export interface SaveServiceSalesReturnInput {
  id: string | null;
  returnDate: string;
  sourceServiceSalesInvoiceId: string;
  branchId: string | null;
  costCenterId: string | null;
  referenceNumber: string | null;
  notes: string | null;
  lines: SaveServiceSalesReturnLineInput[];
}

// ─────────────────────────────── Receipt Order (أمر القبض)

/** Operational only. NOT an accounting state — a receipt order posts nothing. */
export const RECEIPT_ORDER_STATUS = { Draft: 1, Open: 2, Cancelled: 3 } as const;
export type ReceiptOrderStatus =
  (typeof RECEIPT_ORDER_STATUS)[keyof typeof RECEIPT_ORDER_STATUS];

export interface LinkedReceiptVoucher {
  id: string;
  voucherNumber: string;
  voucherDate: string;
  totalAmount: number;
  status: JournalStatus;
  /** True only while POSTED — this is what counts toward the order. */
  countsTowardOrder: boolean;
  paymentSummary: string | null;
}

export interface ReceiptOrderAttachment {
  id: string;
  fileName: string;
  contentType: string | null;
  fileSizeBytes: number;
  description: string | null;
  uploadedAt: string;
  uploadedBy: string | null;
  uploadedByName: string | null;
}

export interface ReceiptOrder {
  id: string;
  orderNumber: string;
  orderDate: string;
  status: ReceiptOrderStatus;
  customerId: string;
  customerCode: string | null;
  customerName: string | null;
  customerNameAr: string | null;
  currencyId: string;
  currencyCode: string;
  exchangeRate: number;
  /** المبلغ المطلوب قبضه. */
  amount: number;
  /** المبلغ المقبوض — DERIVED from posted receipt vouchers, never stored. */
  collectedAmount: number;
  /** المبلغ المتبقي. */
  remainingAmount: number;
  isFullyCollected: boolean;
  isPartiallyCollected: boolean;
  canCollect: boolean;
  collectorUserId: string | null;
  collectorName: string | null;
  salesRepresentativeUserId: string | null;
  salesRepresentativeName: string | null;
  expectedCollectionDate: string | null;
  expectedPaymentMethodId: string | null;
  expectedPaymentMethodName: string | null;
  branchId: string;
  branchName: string | null;
  referenceNumber: string | null;
  notes: string | null;
  cancellationReason: string | null;
  isEditable: boolean;
  updatedAt: string | null;
  createdBy: string | null;
  createdByName: string | null;
  linkedReceiptVouchers: LinkedReceiptVoucher[];
  attachments: ReceiptOrderAttachment[];
}

export interface ReceiptOrderList {
  rows: ReceiptOrder[];
  totalCount: number;
}

export interface ReceiptOrderFilter {
  search?: string;
  customerId?: string;
  collectorUserId?: string;
  status?: number;
  currencyId?: string;
  branchId?: string;
  fromDate?: string;
  toDate?: string;
  expectedFrom?: string;
  expectedTo?: string;
  collectableOnly?: boolean;
}

export interface SaveReceiptOrderInput {
  id: string | null;
  orderDate: string;
  customerId: string;
  currencyId: string;
  exchangeRate: number | null;
  amount: number;
  collectorUserId: string | null;
  salesRepresentativeUserId: string | null;
  expectedCollectionDate: string | null;
  /** Planning only. It settles nowhere and creates nothing. */
  expectedPaymentMethodId: string | null;
  branchId: string | null;
  referenceNumber: string | null;
  notes: string | null;
}

// ─────────────────────────────── Customer Balance Reminder (تذكير بأرصدة العملاء)

/** Email only. There is no SMS transport in this system, so no SMS channel is offered. */
export const REMINDER_CHANNEL = { Email: 1 } as const;
export type ReminderChannel = (typeof REMINDER_CHANNEL)[keyof typeof REMINDER_CHANNEL];

export const REMINDER_BATCH_STATUS = {
  Prepared: 1,
  Completed: 2,
  CompletedWithErrors: 3,
  Failed: 4,
} as const;
export type ReminderBatchStatus =
  (typeof REMINDER_BATCH_STATUS)[keyof typeof REMINDER_BATCH_STATUS];

export const REMINDER_DELIVERY_STATUS = { Sent: 1, Skipped: 2, Failed: 3 } as const;
export type ReminderDeliveryStatus =
  (typeof REMINDER_DELIVERY_STATUS)[keyof typeof REMINDER_DELIVERY_STATUS];

export interface ReminderCandidate {
  customerId: string;
  code: string;
  name: string;
  nameAr: string | null;
  email: string | null;
  mobile: string | null;
  /** Read from PartyBalance — the same subledger every posting document updates. */
  balance: number;
  currencyCode: string;
  canReceiveEmail: boolean;
  lastReminderAt: string | null;
  isActive: boolean;
}

export interface ReminderCandidateList {
  rows: ReminderCandidate[];
  totalCount: number;
  totalBalance: number;
}

export interface ReminderCandidateFilter {
  search?: string;
  customerId?: string;
  categoryId?: string;
  currencyId?: string;
  collectorUserId?: string;
  salesRepresentativeUserId?: string;
  positiveBalanceOnly?: boolean;
  minimumBalance?: number;
}

export interface ReminderTemplateInfo {
  /** The company's approved wording, from its existing reminder settings. */
  message: string;
  isConfigured: boolean;
  companyName: string;
  placeholders: string[];
}

export interface ReminderPreview {
  customerId: string;
  customerName: string;
  destination: string | null;
  canReceive: boolean;
  blockedReason: string | null;
  subject: string;
  message: string;
  balance: number;
  currencyCode: string;
}

export interface ReminderRecipient {
  id: string;
  customerId: string;
  customerCode: string | null;
  customerName: string | null;
  customerNameAr: string | null;
  /** A snapshot taken at send time. Never rewritten when the balance later moves. */
  balanceSnapshot: number;
  currencyCode: string | null;
  destination: string | null;
  deliveryStatus: ReminderDeliveryStatus;
  renderedMessage: string | null;
  sentAt: string | null;
  errorReason: string | null;
}

export interface ReminderBatch {
  id: string;
  batchNumber: string;
  batchDate: string;
  channel: ReminderChannel;
  status: ReminderBatchStatus;
  subject: string | null;
  messageTemplate: string;
  filterSnapshot: string | null;
  recipientCount: number;
  sentCount: number;
  skippedCount: number;
  failedCount: number;
  totalBalance: number;
  createdAt: string;
  createdBy: string | null;
  createdByName: string | null;
  recipients: ReminderRecipient[];
}

export interface ReminderBatchList {
  rows: ReminderBatch[];
  totalCount: number;
}

export interface SendRemindersInput {
  /** Empty means "everyone the filter matched" — resolved by the server, never by the browser. */
  customerIds: string[];
  filter: ReminderCandidateFilter;
  subject: string;
  message: string;
}

// ─────────────────────────────── Receipt Books (دفاتر القبض)

export const RECEIPT_BOOK_STATUS = { Available: 1, Assigned: 2, Closed: 3 } as const;
export type ReceiptBookStatus = (typeof RECEIPT_BOOK_STATUS)[keyof typeof RECEIPT_BOOK_STATUS];

/** Only Employee resolves today: the system has no collector or sales-rep master file. */
export const RECEIPT_BOOK_HOLDER_TYPE = {
  Employee: 1,
  Collector: 2,
  SalesRepresentative: 3,
} as const;
export type ReceiptBookHolderType =
  (typeof RECEIPT_BOOK_HOLDER_TYPE)[keyof typeof RECEIPT_BOOK_HOLDER_TYPE];

export const RECEIPT_BOOK_MOVEMENT = {
  Received: 1,
  Assigned: 2,
  ReturnedFromHolder: 3,
  Closed: 4,
} as const;
export type ReceiptBookMovementAction =
  (typeof RECEIPT_BOOK_MOVEMENT)[keyof typeof RECEIPT_BOOK_MOVEMENT];

export interface ReceiptBookMovement {
  id: string;
  action: ReceiptBookMovementAction;
  fromHolderType: ReceiptBookHolderType | null;
  fromHolderId: string | null;
  fromHolderName: string | null;
  toHolderType: ReceiptBookHolderType | null;
  toHolderId: string | null;
  toHolderName: string | null;
  movementDate: string;
  returnedAt: string | null;
  performedByUserId: string;
  performedByName: string | null;
  notes: string | null;
  createdAt: string;
}

export interface ReceiptBook {
  id: string;
  bookNumber: string;
  fromReceiptNumber: number;
  toReceiptNumber: number;
  /** Derived server-side as To − From + 1; never sent by the client. */
  receiptCount: number;
  status: ReceiptBookStatus;
  currentHolderType: ReceiptBookHolderType | null;
  currentHolderId: string | null;
  currentHolderName: string | null;
  branchId: string | null;
  branchName: string | null;
  receivedDate: string;
  lastMovementDate: string | null;
  notes: string | null;
  updatedAt: string | null;
  movements: ReceiptBookMovement[];
}

export interface ReceiptBookList {
  rows: ReceiptBook[];
  totalCount: number;
}

export interface ReceiptBookHolder {
  id: string;
  code: string;
  name: string;
  nameAr: string | null;
}

export interface SaveReceiptBookInput {
  id: string | null;
  bookNumber: string;
  fromReceiptNumber: number;
  toReceiptNumber: number;
  branchId: string | null;
  receivedDate: string;
  notes: string | null;
}

export interface ServiceSalesInvoiceLine {
  id: string;
  lineNumber: number;
  serviceSalesTypeId: string;
  serviceCode: string;
  serviceName: string;
  serviceNameAr: string;
  description: string | null;
  quantity: number;
  unitPrice: number;
  discountPercent: number;
  discountTypeId: string | null;
  discountTypeCode: string | null;
  discountTypeName: string | null;
  discountTypeNameAr: string | null;
  discountCalculation: DiscountCalculationType | null;
  discountValue: number;
  taxTypeId: string | null;
  taxTypeCode: string | null;
  taxTypeName: string | null;
  taxTypeNameAr: string | null;
  taxPercent: number;
  costCenterId: string | null;
  grossAmount: number;
  discountAmount: number;
  taxableAmount: number;
  taxAmount: number;
  netAmount: number;
}

export interface ServiceSalesInvoiceAttachment {
  id: string;
  fileName: string;
  contentType: string | null;
  fileSizeBytes: number;
  description: string | null;
  uploadedAt: string;
  uploadedBy: string | null;
  uploadedByName: string | null;
}

export interface ServiceSalesInvoice {
  id: string;
  /** Allocated by the shared generator on the first save: SSI-{Year}-{Month}-{Serial}. */
  invoiceNumber: string;
  invoiceDate: string;
  status: JournalStatus;
  journalVoucherId: string;
  customerId: string;
  customerCode: string;
  customerName: string;
  customerNameAr: string | null;
  customerTaxTreatment: number;
  saleNature: SaleNature | null;
  sourceQuotationId: string | null;
  sourceQuotationNumber: string | null;
  currencyId: string;
  currencyCode: string;
  exchangeRate: number;
  /** Inherited from the customer card and read-only on the document. */
  paymentMethodId: string | null;
  paymentMethodCode: string | null;
  paymentMethodName: string | null;
  paymentMethodNameAr: string | null;
  costCenterId: string | null;
  branchId: string;
  branchName: string | null;
  fiscalYearId: string;
  fiscalYearName: string | null;
  notes: string | null;
  isTaxInclusive: boolean;
  subtotal: number;
  discountTotal: number;
  taxTotal: number;
  netTotal: number;
  /** Only a Draft may be amended. */
  isEditable: boolean;
  updatedAt: string | null;
  lines: ServiceSalesInvoiceLine[];
  attachments: ServiceSalesInvoiceAttachment[];
}

export interface ServiceSalesInvoiceList {
  rows: ServiceSalesInvoice[];
  totalCount: number;
}

export interface SaveServiceSalesInvoiceLineInput {
  id: string | null;
  serviceSalesTypeId: string;
  taxTypeId: string | null;
  description: string | null;
  quantity: number;
  unitPrice: number;
  discountPercent: number;
  discountTypeId: string | null;
  discountValue: number;
  costCenterId: string | null;
}

export interface SaveServiceSalesInvoiceInput {
  id: string | null;
  invoiceDate: string;
  customerId: string;
  saleNature: SaleNature | null;
  sourceQuotationId: string | null;
  currencyId: string;
  exchangeRate: number | null;
  costCenterId: string | null;
  branchId: string | null;
  notes: string | null;
  isTaxInclusive: boolean | null;
  lines: SaveServiceSalesInvoiceLineInput[];
}

export interface ServiceQuotationList {
  rows: ServiceQuotation[];
  totalCount: number;
}

export interface SaveServiceQuotationLineInput {
  /** The line this replaces, or null for one that has no row yet — including a duplicate. */
  id: string | null;
  serviceSalesTypeId: string;
  /** The sales tax classification for this line. Null falls back to the service's own. */
  taxTypeId: string | null;
  description: string | null;
  quantity: number;
  unitPrice: number;
  /** The plain percentage. Used only when no discount type is chosen. */
  discountPercent: number;
  /** A Receivables Settings discount type, or null to use `discountPercent`. */
  discountTypeId: string | null;
  /** Read as a percentage or as a flat amount according to the chosen type. */
  discountValue: number;
  costCenterId: string | null;
}

export interface SaveServiceQuotationInput {
  id: string | null;
  quotationDate: string;
  customerId: string;
  currencyId: string;
  exchangeRate: number | null;
  paymentMethod: number;
  paymentMethodId: string | null;
  salespersonUserId: string | null;
  costCenterId: string | null;
  branchId: string | null;
  notes: string | null;
  /** Null means "use the customer's own treatment". */
  isTaxInclusive: boolean | null;
  lines: SaveServiceQuotationLineInput[];
}

// ─────────────────────────────── Customer Categories (فئات العملاء)

/**
 * One approved customer category, as the settings module holds it.
 *
 * Only the identifying fields are typed here — the module carries a control account, credit
 * limit, grace period and more, and a picker has no business reading them.
 */
export interface CustomerCategoryOption {
  id: string;
  parentId: string | null;
  code: string;
  descriptionEn: string;
  descriptionAr: string;
  isActive: boolean;
}

export interface CustomerCategoryList {
  rows: CustomerCategoryOption[];
  totalCount: number;
}

// ─────────────────────────────────────────────── Advance Received Revenue
// الإيرادات المقبوضة مقدماً. Releases the deferred revenue a CONTRACT service sales invoice
// created, across the CONTRACT's own service period - never the collection date.

/** A posted contract invoice that could carry a schedule. */
export type RecognizableInvoice = {
  id: string;
  invoiceNumber: string;
  invoiceDate: string;
  customerId: string;
  customerCode: string | null;
  customerName: string | null;
  customerNameAr: string | null;
  currencyId: string;
  currencyCode: string;
  contractStartDate: string | null;
  contractEndDate: string | null;
  /** The NET credited to deferred service revenue. Tax excluded. */
  deferredAmount: number;
  isEligible: boolean;
  /** Why not, when it is not - missing dates, or a part month with no approved rule. */
  ineligibleReason: string | null;
};

export type RecognizableInvoiceList = { rows: RecognizableInvoice[] };

/** Every one of these is DERIVED at read time; none is stored. */
export type RecognitionPeriodStatus =
  | "Pending"
  | "Due"
  | "Drafted"
  | "Recognized"
  | "Reduced";

export type RecognitionPeriod = {
  id: string;
  periodNumber: number;
  periodStartDate: string;
  periodEndDate: string;
  /** The period's own end date - what puts the entry in the right fiscal year. */
  recognitionDate: string;
  scheduledAmount: number;
  recognizedAmount: number;
  status: RecognitionPeriodStatus;
  recognitionId: string | null;
  recognitionNumber: string | null;
  recognitionStatus: number | null;
};

/** Draft = 1, Active = 2, Completed = 3. */
export type RecognitionScheduleStatus = 1 | 2 | 3;

export type RecognitionSchedule = {
  id: string;
  sourceServiceSalesInvoiceId: string;
  invoiceNumber: string;
  invoiceDate: string;
  customerId: string;
  customerCode: string | null;
  customerName: string | null;
  customerNameAr: string | null;
  currencyId: string;
  currencyCode: string;
  exchangeRate: number;
  contractStartDate: string;
  contractEndDate: string;
  periodCount: number;
  status: RecognitionScheduleStatus;
  notes: string | null;
  /** recognized + returned + remaining always equals this. */
  originalDeferredAmount: number;
  recognizedAmount: number;
  returnedAmount: number;
  remainingAmount: number;
  duePendingAmount: number;
  duePendingCount: number;
  periods: RecognitionPeriod[];
};

export type RecognitionScheduleList = { rows: RecognitionSchedule[]; total: number };

export type RevenueRecognitionLine = {
  id: string;
  lineNumber: number;
  sourceServiceSalesInvoiceLineId: string;
  serviceSalesTypeId: string;
  serviceCode: string | null;
  serviceName: string | null;
  serviceNameAr: string | null;
  revenueAccountNumber: string | null;
  revenueAccountName: string | null;
  costCenterId: string | null;
  amount: number;
};

export type RevenueRecognition = {
  id: string;
  recognitionNumber: string;
  recognitionDate: string;
  status: number;
  scheduleId: string;
  schedulePeriodId: string;
  periodNumber: number;
  periodStartDate: string;
  periodEndDate: string;
  sourceServiceSalesInvoiceId: string;
  invoiceNumber: string;
  customerId: string;
  customerCode: string | null;
  customerName: string | null;
  customerNameAr: string | null;
  currencyCode: string;
  amount: number;
  notes: string | null;
  fiscalYearName: string | null;
  lines: RevenueRecognitionLine[];
};

export type RevenueRecognitionList = { rows: RevenueRecognition[]; total: number };

export type RecognizeDuePeriodsResult = {
  recognitionIds: string[];
  periodCount: number;
  totalAmount: number;
};

// ─────────────────────────────────────────────── Customer Transaction Transfer
// تحويل حركات عميل. Moves posted customer movements between customers by posting a NEW
// reclassification voucher — it never edits a posted GL row or a source document.

/** One selectable movement: a WHOLE posted voucher's effect on the customer who owns it now. */
export type TransferableMovement = {
  sourceJournalVoucherId: string;
  documentType: string;
  documentTypeCode: string;
  documentNumber: string;
  documentDate: string;
  reference: string | null;
  description: string | null;
  currencyId: string;
  currencyCode: string | null;
  debit: number;
  credit: number;
  /** Debit − Credit. Positive increased the receivable. */
  netCustomerEffect: number;
  currentCustomerId: string;
  currentCustomerCode: string | null;
  currentCustomerName: string | null;
  currentCustomerNameAr: string | null;
  /** The transfer that last moved it, when one did. */
  lastTransferNumber: string | null;
  isSelectable: boolean;
  /** Why not: a settled cheque, a receipt order, a reversal. */
  blockedReason: string | null;
};

export type TransferableMovementList = { rows: TransferableMovement[] };

export type CustomerTransactionTransferLine = {
  id: string;
  lineNumber: number;
  sourceJournalVoucherId: string;
  sourceVoucherTypeCode: string;
  sourceVoucherNumber: string;
  sourceVoucherDate: string;
  sourceReference: string | null;
  sourceDescription: string | null;
  sourceCustomerEffect: number;
  currencyCode: string | null;
  exchangeRate: number;
  /** True when the source voucher was reversed after this transfer posted. */
  sourceReversedAfterTransfer: boolean;
};

export type CustomerTransactionTransfer = {
  id: string;
  transferNumber: string;
  transferDate: string;
  status: number;
  oldCustomerId: string;
  oldCustomerCode: string | null;
  oldCustomerName: string | null;
  oldCustomerNameAr: string | null;
  newCustomerId: string;
  newCustomerCode: string | null;
  newCustomerName: string | null;
  newCustomerNameAr: string | null;
  fromDate: string;
  toDate: string;
  statement: string | null;
  currencyCode: string | null;
  totalEffect: number;
  movementCount: number;
  /** True when both customers share one receivable control account. */
  sameControlAccount: boolean;
  fiscalYearName: string | null;
  lines: CustomerTransactionTransferLine[];
};

export type CustomerTransactionTransferList = {
  rows: CustomerTransactionTransfer[];
  total: number;
};

export type SaveCustomerTransactionTransferInput = {
  id?: string | null;
  transferDate: string;
  oldCustomerId: string;
  newCustomerId: string;
  fromDate: string;
  toDate: string;
  statement?: string | null;
  movements: { sourceJournalVoucherId: string }[];
};

// ── Payments › Daily Transactions › تحويل حركات مورّد (#11) ──────────────────────────────
//
// The payables sibling of the customer transfer, with its own tables and its own STT
// voucher type. The arithmetic is the same sign-driven one; only the party differs.

export type TransferableSupplierMovement = {
  sourceJournalVoucherId: string;
  documentType: string;
  documentTypeCode: string;
  documentNumber: string;
  documentDate: string;
  reference: string | null;
  description: string | null;
  currencyId: string;
  currencyCode: string | null;
  debit: number;
  credit: number;
  /** Debit − Credit. Positive increased the receivable. */
  netSupplierEffect: number;
  currentSupplierId: string;
  currentSupplierCode: string | null;
  currentSupplierName: string | null;
  currentSupplierNameAr: string | null;
  /** The transfer that last moved it, when one did. */
  lastTransferNumber: string | null;
  isSelectable: boolean;
  /** Why not: a settled cheque, a receipt order, a reversal. */
  blockedReason: string | null;
};

export type TransferableSupplierMovementList = { rows: TransferableSupplierMovement[] };

export type SupplierTransactionTransferLine = {
  id: string;
  lineNumber: number;
  sourceJournalVoucherId: string;
  sourceVoucherTypeCode: string;
  sourceVoucherNumber: string;
  sourceVoucherDate: string;
  sourceReference: string | null;
  sourceDescription: string | null;
  sourceSupplierEffect: number;
  currencyCode: string | null;
  exchangeRate: number;
  /** True when the source voucher was reversed after this transfer posted. */
  sourceReversedAfterTransfer: boolean;
};

export type SupplierTransactionTransfer = {
  id: string;
  transferNumber: string;
  transferDate: string;
  status: number;
  oldSupplierId: string;
  oldSupplierCode: string | null;
  oldSupplierName: string | null;
  oldSupplierNameAr: string | null;
  newSupplierId: string;
  newSupplierCode: string | null;
  newSupplierName: string | null;
  newSupplierNameAr: string | null;
  fromDate: string;
  toDate: string;
  statement: string | null;
  currencyCode: string | null;
  totalEffect: number;
  movementCount: number;
  /** True when both suppliers share one receivable control account. */
  sameControlAccount: boolean;
  fiscalYearName: string | null;
  lines: SupplierTransactionTransferLine[];
};

export type SupplierTransactionTransferList = {
  rows: SupplierTransactionTransfer[];
  total: number;
};

export type SaveSupplierTransactionTransferInput = {
  id?: string | null;
  transferDate: string;
  oldSupplierId: string;
  newSupplierId: string;
  fromDate: string;
  toDate: string;
  statement?: string | null;
  movements: { sourceJournalVoucherId: string }[];
};

/** The filter an export runs — the SAME one the reminder grid ran. Informational only. */
export type ReminderCandidateExportFilter = {
  searchTerm?: string;
  customerId?: string;
  categoryId?: string;
  currencyId?: string;
  collectorUserId?: string;
  salesRepresentativeUserId?: string;
  positiveBalanceOnly?: boolean;
  minimumBalance?: number;
  locale?: string;
};



// ── Payments › Daily Transactions › تعديل بيانات المورد (#14) + سقف الائتمان (#15) ───────────
//
// Both non-accounting. #14 delegates every rule to the partner master; #15 keeps its own
// immutable history in its own table, leaving the customer implementation untouched.

/** The FIXED, typed set of fields this screen may change. Anything else is unreachable. */
export interface SupplierEditableFields {
  name?: string | null;
  nameAr?: string | null;
  startDate?: string | null;
  controlAccountId?: string | null;
  currencyId?: string | null;
  taxTreatment?: number | null;
  paymentMethod?: number | null;
  categoryId?: string | null;
  defaultPaymentMethodId?: string | null;
  nationalIdCompany?: string | null;
  commercialRegistration?: string | null;
  taxNumber?: string | null;
  referenceNumber?: string | null;
  phone?: string | null;
  mobile?: string | null;
  fax?: string | null;
  email?: string | null;
  website?: string | null;
  contactPerson?: string | null;
  country?: string | null;
  city?: string | null;
  area?: string | null;
  street?: string | null;
  building?: string | null;
  postalCode?: string | null;
  poBox?: string | null;
  address?: string | null;
  addressNotes?: string | null;
  notes?: string | null;
  isActive?: boolean | null;
  isBlacklisted?: boolean | null;
  blacklistReason?: string | null;
}

export interface SupplierInformationChange {
  id: string;
  supplierId: string;
  fieldName: string;
  oldValue: string | null;
  newValue: string | null;
  reason: string;
  changedAt: string;
  changedBy: string | null;
  changedByName: string | null;
}

export interface SupplierInformation extends SupplierEditableFields {
  supplierId: string;
  code: string;
  name: string;
  /** Shown, never editable here — the credit limit has its own screen and permission. */
  creditLimit: number;
  controlAccountNumber: string | null;
  controlAccountName: string | null;
  currencyCode: string | null;
  history: SupplierInformationChange[];
}

export interface SupplierCreditLimit {
  supplierId: string;
  code: string;
  name: string;
  nameAr: string | null;
  creditLimit: number;
  balance: number;
  currencyCode: string;
  balanceExceedsLimit: boolean;
  canChange: boolean;
}

export interface SupplierCreditLimitChange {
  id: string;
  previousCreditLimit: number;
  newCreditLimit: number;
  changeAmount: number;
  currencyCode: string;
  reason: string;
  balanceAtChange: number;
  balanceExceededNewLimit: boolean;
  changedAt: string;
  changedByName: string | null;
}

export interface SupplierCreditLimitHistory {
  rows: SupplierCreditLimitChange[];
}

// ── Payments › Daily Transactions › المصروفات المدفوعة مقدماً (#13) ──────────────────────────
//
// An ASSET already on the books becoming an EXPENSE over time. The schedule posts nothing; only
// recognition does, one voucher per period, and it never names a party.

/** Draft = 1, Active = 2, Cancelled = 3. The other two states are derived — see `state`. */
export const PREPAID_SCHEDULE_STATUS = { Draft: 1, Active: 2, Cancelled: 3 } as const;
export type PrepaidScheduleStatus =
  (typeof PREPAID_SCHEDULE_STATUS)[keyof typeof PREPAID_SCHEDULE_STATUS];

/** Monthly = 1. The only basis this build generates. */
export const PREPAID_RECOGNITION_BASIS = { Monthly: 1 } as const;
export type PrepaidRecognitionBasis =
  (typeof PREPAID_RECOGNITION_BASIS)[keyof typeof PREPAID_RECOGNITION_BASIS];

/** The five states the screen shows. Two of them are computed from what has effectively posted. */
export type PrepaidScheduleState =
  | "Draft" | "Active" | "PartiallyRecognized" | "FullyRecognized" | "Cancelled";

export interface PrepaidPeriod {
  id: string;
  periodNumber: number;
  periodStartDate: string;
  periodEndDate: string;
  recognitionDate: string;
  scheduledAmount: number;
  /** Zero once its voucher is reversed — the amount is read from the voucher, not stored. */
  recognizedAmount: number;
  remainingAmount: number;
  isDue: boolean;
  recognitionId: string | null;
  journalVoucherId: string | null;
  voucherNumber: string | null;
  voucherStatus: JournalStatus | null;
}

export interface PrepaidSchedule {
  id: string;
  scheduleNumber: string;
  scheduleDate: string;
  sourceInvoiceId: string;
  sourceInvoiceNumber: string | null;
  sourceInvoiceDate: string | null;
  supplierInvoiceNumber: string | null;
  sourceInvoiceLineId: string;
  sourceLineNumber: number;
  sourceLineDescription: string | null;
  sourceLineNetAmount: number;
  supplierId: string;
  supplierCode: string | null;
  supplierName: string | null;
  supplierNameAr: string | null;
  currencyId: string;
  currencyCode: string;
  exchangeRate: number;
  prepaidAccountId: string;
  prepaidAccountNumber: string | null;
  prepaidAccountName: string | null;
  prepaidAccountNameAr: string | null;
  expenseAccountId: string;
  expenseAccountNumber: string | null;
  expenseAccountName: string | null;
  expenseAccountNameAr: string | null;
  costCenterId: string | null;
  costCenterCode: string | null;
  costCenterName: string | null;
  coverageStartDate: string;
  coverageEndDate: string;
  recognitionBasis: PrepaidRecognitionBasis;
  periodCount: number;
  originalAmount: number;
  recognizedAmount: number;
  remainingAmount: number;
  status: PrepaidScheduleStatus;
  state: PrepaidScheduleState;
  isEditable: boolean;
  canRecognize: boolean;
  duePeriodCount: number;
  cancellationReason: string | null;
  cancelledAt: string | null;
  cancelledByName: string | null;
  notes: string | null;
  createdAt: string;
  createdBy: string | null;
  createdByName: string | null;
  periods: PrepaidPeriod[];
}

export interface PrepaidScheduleList {
  rows: PrepaidSchedule[];
  totalCount: number;
  totalOriginal: number;
  totalRecognized: number;
  baseCurrencyCode: string;
}

/** One line of a posted purchase invoice, with what is still unscheduled on it. */
export interface SchedulableLine {
  lineId: string;
  lineNumber: number;
  description: string;
  /** The account the line ACTUALLY debited — the prepaid account, if this line is scheduled. */
  postedAccountId: string;
  postedAccountNumber: string | null;
  postedAccountName: string | null;
  postedAccountNameAr: string | null;
  costCenterId: string | null;
  netAmount: number;
  scheduledAmount: number;
  returnedAmount: number;
  availableAmount: number;
}

export interface SchedulableLineList {
  invoiceId: string;
  invoiceNumber: string;
  invoiceDate: string;
  supplierId: string;
  supplierCode: string | null;
  supplierName: string | null;
  currencyId: string;
  currencyCode: string;
  exchangeRate: number;
  rows: SchedulableLine[];
}

export interface SavePrepaidScheduleInput {
  id?: string | null;
  scheduleDate: string;
  sourceInvoiceId: string;
  sourceInvoiceLineId: string;
  amount: number;
  expenseAccountId: string;
  costCenterId?: string | null;
  coverageStartDate: string;
  coverageEndDate: string;
  recognitionBasis: PrepaidRecognitionBasis;
  notes?: string | null;
}

export interface RecognizedPeriod {
  periodId: string;
  periodNumber: number;
  recognitionDate: string;
  amount: number;
  journalVoucherId: string;
  voucherNumber: string;
}

/** What a recognition run did, and what it deliberately left alone. */
export interface RecognizeDueResult {
  recognized: RecognizedPeriod[];
  skipped: string[];
}

// ── Payments › Daily Transactions › تذكير بأرصدة المورّدين (#12) ─────────────────────────
//
// Its own shapes because the wire fields differ: the server names the party supplierId,
// and Balance is stated from the COMPANY'S side — positive means the company owes.

export interface SupplierReminderCandidate {
  supplierId: string;
  code: string;
  name: string;
  nameAr: string | null;
  email: string | null;
  mobile: string | null;
  /** Read from PartyBalance — the same subledger every posting document updates. */
  balance: number;
  currencyCode: string;
  canReceiveEmail: boolean;
  lastReminderAt: string | null;
  isActive: boolean;
}

export interface SupplierReminderCandidateList {
  rows: SupplierReminderCandidate[];
  totalCount: number;
  totalBalance: number;
}

export interface SupplierReminderCandidateFilter {
  search?: string;
  supplierId?: string;
  categoryId?: string;
  currencyId?: string;
  collectorUserId?: string;
  salesRepresentativeUserId?: string;
  positiveBalanceOnly?: boolean;
  minimumBalance?: number;
}

export interface SupplierReminderTemplateInfo {
  /** The company's approved wording, from its existing reminder settings. */
  message: string;
  isConfigured: boolean;
  companyName: string;
  placeholders: string[];
}

export interface SupplierReminderPreview {
  supplierId: string;
  supplierName: string;
  destination: string | null;
  canReceive: boolean;
  blockedReason: string | null;
  subject: string;
  message: string;
  balance: number;
  currencyCode: string;
}

export interface SupplierReminderRecipient {
  id: string;
  supplierId: string;
  supplierCode: string | null;
  supplierName: string | null;
  supplierNameAr: string | null;
  /** A snapshot taken at send time. Never rewritten when the balance later moves. */
  balanceSnapshot: number;
  currencyCode: string | null;
  destination: string | null;
  deliveryStatus: ReminderDeliveryStatus;
  renderedMessage: string | null;
  sentAt: string | null;
  errorReason: string | null;
}

export interface SupplierReminderBatch {
  id: string;
  batchNumber: string;
  batchDate: string;
  channel: ReminderChannel;
  status: ReminderBatchStatus;
  subject: string | null;
  messageTemplate: string;
  filterSnapshot: string | null;
  recipientCount: number;
  sentCount: number;
  skippedCount: number;
  failedCount: number;
  totalBalance: number;
  createdAt: string;
  createdBy: string | null;
  createdByName: string | null;
  recipients: SupplierReminderRecipient[];
}

export interface SupplierReminderBatchList {
  rows: SupplierReminderBatch[];
  totalCount: number;
}

export interface SendSupplierRemindersInput {
  /** Empty means "everyone the filter matched" — resolved by the server, never by the browser. */
  supplierIds: string[];
  filter: SupplierReminderCandidateFilter;
  subject: string;
  message: string;
}

export type SupplierReminderCandidateExportFilter = {
  searchTerm?: string;
  supplierId?: string;
  categoryId?: string;
  currencyId?: string;
  collectorUserId?: string;
  salesRepresentativeUserId?: string;
  positiveBalanceOnly?: boolean;
  minimumBalance?: number;
  locale?: string;
};

// ─────────────────────────────────────────────── Customer credit limit
// سقف ائتمان العميل. Part of the customer card. Changing it has ZERO accounting effect.

export type CustomerCreditLimit = {
  customerId: string;
  code: string;
  name: string;
  nameAr: string | null;
  /** The approved ceiling, in company base currency. */
  creditLimit: number;
  currencyCode: string;
  /** From PartyBalance. Positive means the customer owes the company. */
  currentBalance: number;
  /** Reported, never enforced — no document in this system consults the limit today. */
  balanceExceedsLimit: boolean;
  canChange: boolean;
};

export type CustomerCreditLimitChange = {
  id: string;
  changedAt: string;
  previousCreditLimit: number;
  newCreditLimit: number;
  currencyCode: string;
  reason: string;
  balanceAtChange: number;
  balanceExceededNewLimit: boolean;
  changedByName: string | null;
};

export type CustomerCreditLimitHistory = { rows: CustomerCreditLimitChange[] };

export type ChangeCustomerCreditLimitInput = {
  newCreditLimit: number;
  reason: string;
  /** Staleness check only — the server never records this as the previous value. */
  expectedCurrentLimit?: number;
};

// ─────────────────────────────────────────────── Account Reconciliation
// مطابقة الحساب. Matches posted documents against each other. It POSTS NOTHING — no journal,
// no GL row, no party-balance movement — and cancelling a settlement does not cancel the invoice.

export type OpenItem = {
  journalVoucherId: string;
  documentType: string;
  documentNumber: string;
  documentDate: string;
  dueDate: string | null;
  reference: string | null;
  currencyCode: string | null;
  currencyId: string;
  originalAmount: number;
  /** Previously settled (outstanding side) or previously allocated (credit side). */
  allocatedAmount: number;
  /** Outstanding amount, or available amount, depending on the side. */
  remainingAmount: number;
  /** The base-currency half — what the settlement balance rule is judged on. */
  exchangeRate: number;
  originalLocalAmount: number;
  allocatedLocalAmount: number;
  remainingLocalAmount: number;
  branchName: string | null;
  fiscalYearName: string | null;
  status: "Unpaid" | "PartiallyPaid" | "Paid";
};

export type PartyOpenItems = {
  outstanding: OpenItem[];
  credits: OpenItem[];
  totalOutstanding: number;
  totalAvailable: number;
  totalOutstandingLocal: number;
  totalAvailableLocal: number;
};

export type ReconciliationLine = {
  id: string;
  reconciliationId: string;
  reconciliationNumber: string;
  reconciliationDate: string;
  outstandingDocumentNumber: string;
  outstandingDocumentType: string;
  creditDocumentNumber: string;
  creditDocumentType: string;
  appliedAmount: number;
  currencyCode: string | null;
  isActive: boolean;
  createdByName: string | null;
  createdAt: string;
  cancelledAt: string | null;
  cancelledByName: string | null;
  cancellationReason: string | null;
  action: "Reconciled" | "SettlementCancelled";
};

export type ReconciliationHistory = { rows: ReconciliationLine[] };

/** Unpaid = 1, PartiallyPaid = 2, Paid = 3. */
export type InvoicePaymentStatus = 1 | 2 | 3;

export type InvoiceSettlement = {
  invoiceId: string;
  invoiceNumber: string;
  invoiceTotal: number;
  settledAmount: number;
  outstandingAmount: number;
  paymentStatus: InvoicePaymentStatus;
  currencyCode: string | null;
  settlements: ReconciliationLine[];
};

export type ReconcileLineInput = {
  outstandingJournalVoucherId: string;
  creditJournalVoucherId: string;
  appliedAmount: number;
  /** Optional — the server derives it from the movement's posted rate when omitted. */
  appliedLocalAmount?: number;
  /** Staleness check only — the server recomputes every figure it acts on. */
  expectedOutstanding?: number;
};

export type CreateReconciliationInput = {
  partyId: string;
  partyType: number;
  reconciliationDate: string;
  notes?: string | null;
  lines: ReconcileLineInput[];
};

// ─────────────────────────────── Receivables Inquiries

/** إجمالي ذمم العملاء — one customer's standing on the receivables ledger. */
export interface CustomerReceivableRow {
  customerId: string;
  code: string;
  name: string;
  nameAr: string | null;
  categoryCode: string | null;
  categoryName: string | null;
  creditLimit: number;
  salesRepresentative: string | null;
  accountNumber: string | null;
  accountName: string | null;
  city: string | null;
  country: string | null;
  balance: number;
  /** "Debit" | "Credit" | "Balanced" — decided server-side from the sign. */
  balanceNature: string;
}

export interface CustomerReceivablesSummary {
  rows: CustomerReceivableRow[];
  baseCurrencyCode: string;
  totalDebit: number;
  totalCredit: number;
  netReceivables: number;
  customerCount: number;
  /**
   * True when the figures are the CURRENT stored balances; false when they were read from the
   * ledger up to a cut-off date. The screen turns this into "Current Balance" / "Balance as of
   * Date" — a reader needs to know which question was answered, not which table answered it.
   */
  fromPartyBalance: boolean;
}

export interface CustomerReceivablesFilter {
  upToDate?: string;
  customerId?: string;
  categoryId?: string;
  accountId?: string;
  salesRepresentativeUserId?: string;
  cityRegionId?: string;
  countryRegionId?: string;
  includeZeroBalances?: boolean;
  search?: string;
}

/** كشف حساب العميل — one movement, with the running balance the server computed. */
export interface CustomerStatementLine {
  entryDate: string;
  voucherType: string;
  voucherNumber: string;
  reference: string | null;
  description: string | null;
  debit: number;
  credit: number;
  runningBalance: number;
  balanceNature: string;
  currencyCode: string | null;
  currencyDebit: number;
  currencyCredit: number;
}

export interface CustomerStatement {
  customerId: string;
  code: string;
  name: string;
  nameAr: string | null;
  fromDate: string | null;
  toDate: string | null;
  baseCurrencyCode: string;
  openingBalance: number;
  periodDebit: number;
  periodCredit: number;
  closingBalance: number;
  closingBalanceNature: string;
  lines: CustomerStatementLine[];
}

export interface CustomerStatementFilter {
  customerId?: string;
  fromDate?: string;
  toDate?: string;
}

/** كشف إجمالي ذمم العملاء - مركز التكلفة — one customer's balance on one cost centre. */
export interface CustomerCostCenterRow {
  customerId: string;
  code: string;
  name: string;
  nameAr: string | null;
  categoryCode: string | null;
  categoryName: string | null;
  creditLimit: number;
  salesRepresentative: string | null;
  accountNumber: string | null;
  accountName: string | null;
  city: string | null;
  country: string | null;
  /** Null for movements posted without a cost centre — shown, never dropped. */
  costCenterId: string | null;
  costCenterCode: string | null;
  costCenterName: string | null;
  hasNoCostCenter: boolean;
  debit: number;
  credit: number;
  balance: number;
  balanceNature: string;
}

export interface CustomerReceivablesByCostCenter {
  rows: CustomerCostCenterRow[];
  baseCurrencyCode: string;
  totalDebit: number;
  totalCredit: number;
  netReceivables: number;
  customerCount: number;
  costCenterCount: number;
  unassignedRowCount: number;
  unassignedBalance: number;
  upToDate: string | null;
}

export interface CustomerCostCenterFilter {
  upToDate?: string;
  customerId?: string;
  categoryId?: string;
  accountId?: string;
  salesRepresentativeUserId?: string;
  cityRegionId?: string;
  countryRegionId?: string;
  costCenterId?: string;
  includeChildCostCenters?: boolean;
  onlyWithoutCostCenter?: boolean;
  includeZeroBalances?: boolean;
  search?: string;
}

// ─────────────────────────────── كشف حساب العميل / عملة

export interface CustomerLedgerCurrency {
  currencyId: string;
  code: string;
  name: string;
  nameAr: string | null;
  isBaseCurrency: boolean;
  movementCount: number;
}

export interface CustomerLedgerCurrencies {
  customerId: string;
  code: string;
  name: string;
  nameAr: string | null;
  currencies: CustomerLedgerCurrency[];
  /** Preselection only — the card can never override what was posted. */
  defaultCurrencyId: string | null;
  baseCurrencyCode: string;
}

/** One movement, in the currency it was posted in. */
export interface CustomerCurrencyStatementLine {
  entryDate: string;
  voucherType: string;
  voucherNumber: string;
  reference: string | null;
  description: string | null;
  debit: number;
  credit: number;
  runningBalance: number;
  /** The rate the movement was POSTED at — stored, never recomputed. */
  exchangeRate: number;
  /** The same movement in company base currency, as posted. Never converted here. */
  localDebit: number;
  localCredit: number;
  localRunningBalance: number;
}

export interface CustomerCurrencyStatement {
  customerId: string;
  code: string;
  name: string;
  nameAr: string | null;
  currencyId: string;
  /** Every figure on this object is in THIS currency. There is no second currency here. */
  currencyCode: string;
  currencyName: string | null;
  isBaseCurrency: boolean;
  fromDate: string | null;
  toDate: string | null;
  openingBalance: number;
  periodDebit: number;
  periodCredit: number;
  closingBalance: number;
  closingBalanceNature: string;
  lines: CustomerCurrencyStatementLine[];
}

export interface CustomerCurrencyStatementFilter {
  customerId?: string;
  currencyId?: string;
  fromDate?: string;
  toDate?: string;
}

// ─────────────────────────────── الحركات اليومية

/**
 * One customer movement.
 *
 * The four money fields follow the RECEIVABLES convention, not the GL one:
 * `debit`/`credit` are «مدين»/«دائن» in company base currency, and
 * `currencyDebit`/`currencyCredit` are «مدين عملة»/«دائن عملة» in the transaction currency.
 */
export interface CustomerTransactionRow {
  movementId: string;
  journalVoucherId: string;
  entryDate: string;
  voucherNumber: string;
  voucherType: string;
  voucherTypeId: string | null;
  customerId: string;
  customerCode: string;
  customerName: string;
  customerCategory: string | null;
  accountNumber: string | null;
  accountName: string | null;
  currencyCode: string | null;
  exchangeRate: number;
  debit: number;
  credit: number;
  currencyDebit: number;
  currencyCredit: number;
  /** Null for document types that carry no payment method. Never the customer default. */
  paymentMethod: string | null;
  notes: string | null;
  branchName: string | null;
  fiscalYearName: string | null;
  reference: string | null;
  isReversed: boolean;
}

export interface CustomerTransactionOption {
  id: string;
  label: string;
}

export interface CustomerTransactionsResult {
  rows: CustomerTransactionRow[];
  baseCurrencyCode: string;
  totalDebit: number;
  totalCredit: number;
  netMovement: number;
  /** Null when the result spans more than one currency — see the screen's note. */
  totalCurrencyDebit: number | null;
  totalCurrencyCredit: number | null;
  singleCurrencyCode: string | null;
  movementCount: number;
  customerCount: number;
  page: number;
  pageSize: number;
  totalCount: number;
  /** Voucher types and fiscal years that actually occur on customer movements. */
  voucherTypes: CustomerTransactionOption[];
  fiscalYears: CustomerTransactionOption[];
}

export interface CustomerTransactionsFilter {
  dateFrom?: string;
  dateTo?: string;
  branchId?: string;
  fiscalYearId?: string;
  customerId?: string;
  categoryId?: string;
  voucherTypeId?: string;
  voucherNumber?: string;
  accountId?: string;
  currencyId?: string;
  paymentMethodId?: string;
  search?: string;
  sortBy?: string;
  sortDescending?: boolean;
  page?: number;
  pageSize?: number;
}

// ─────────────────────────────── حركات السندات - خدمات

/** 1 = Quotation, 2 = Service Sales Invoice, 3 = Service Sales Return. */
export type ServiceDocumentKind = 1 | 2 | 3;

/** ONE SERVICE DOCUMENT. Never a journal line. */
export interface ServiceVoucherRow {
  documentId: string;
  kind: ServiceDocumentKind;
  /** Drill-down key; empty when no screen exists to open. */
  routeKey: string;
  voucherDate: string;
  voucherNumber: string;
  voucherType: string;
  voucherTypeId: string | null;
  /** From the document's own field. Null where the type carries none. */
  paymentMethod: string | null;
  partyType: number;
  partyId: string;
  partyCode: string;
  partyName: string;
  subtotal: number;
  /** Null for documents with no discount concept — distinct from a zero discount. */
  discount: number | null;
  tax: number;
  finalTotal: number;
  currencyCode: string;
  currencyId: string;
  supplierInvoiceNumber: string | null;
  project: string | null;
  notes: string | null;
  branchName: string | null;
  userName: string | null;
  fiscalYearName: string | null;
  status: string;
  /** True when the sign rule negated this row's amounts. */
  isReturn: boolean;
}

export interface ServiceVoucherCurrencyTotal {
  currencyCode: string;
  documentCount: number;
  subtotal: number;
  discount: number;
  tax: number;
  finalTotal: number;
}

export interface ServiceVoucherTypeOption {
  kind: ServiceDocumentKind;
  voucherTypeId: string | null;
  label: string;
}

export interface ServiceVoucherTransactionsResult {
  rows: ServiceVoucherRow[];
  /** Grouped by currency — there is deliberately no grand total across currencies. */
  totalsByCurrency: ServiceVoucherCurrencyTotal[];
  documentCount: number;
  documentTypes: ServiceVoucherTypeOption[];
  page: number;
  pageSize: number;
  totalCount: number;
}

export interface ServiceVoucherTransactionsFilter {
  dateFrom?: string;
  dateTo?: string;
  kind?: number;
  voucherNumber?: string;
  partyType?: number;
  partyId?: string;
  paymentMethodId?: string;
  currencyId?: string;
  branchId?: string;
  fiscalYearId?: string;
  createdByUserId?: string;
  status?: string;
  search?: string;
  sortBy?: string;
  sortDescending?: boolean;
  page?: number;
  pageSize?: number;
}

// ─────────────────────────────── حركات مادة عميل

/** 1 = Sale (بيع), 2 = Return (مردود). */
export type CustomerItemNature = 1 | 2;

/** ONE SALES DOCUMENT LINE. Never a header, never a GL entry. */
export interface CustomerItemTransactionRow {
  lineId: string;
  documentId: string;
  lineNumber: number;
  nature: CustomerItemNature;
  routeKey: string;
  voucherDate: string;
  voucherNumber: string;
  voucherType: string;
  customerId: string;
  customerCode: string;
  customerName: string;
  customerCategory: string | null;
  /** Which master the identity came from — one today, named so more can follow. */
  itemSourceType: string;
  itemId: string;
  itemCode: string;
  itemName: string;
  itemType: string;
  /** No unit-of-measure master exists in this system. */
  uom: string | null;
  /** Signed: positive for a sale, negative for a return. */
  quantity: number;
  /** The price on the line when it was invoiced — never the master's price today. */
  unitPrice: number;
  discount: number;
  netUnitPrice: number | null;
  tax: number;
  lineTotal: number;
  currencyCode: string;
  currencyId: string;
  exchangeRate: number;
  branchName: string | null;
  costCenterId: string | null;
  costCenterCode: string | null;
  costCenterName: string | null;
  /** Sales invoices and returns do not store one. */
  salesRepresentative: string | null;
  notes: string | null;
}

export interface CustomerItemCurrencyTotal {
  currencyCode: string;
  lineCount: number;
  quantity: number;
  discount: number;
  tax: number;
  netSales: number;
}

export interface CustomerItemTransactionsResult {
  rows: CustomerItemTransactionRow[];
  totalsByCurrency: CustomerItemCurrencyTotal[];
  customerCount: number;
  documentCount: number;
  lineCount: number;
  totalQuantity: number;
  page: number;
  pageSize: number;
  totalCount: number;
}

export interface CustomerItemTransactionsFilter {
  dateFrom?: string;
  dateTo?: string;
  customerId?: string;
  categoryId?: string;
  itemId?: string;
  itemType?: string;
  nature?: number;
  currencyId?: string;
  branchId?: string;
  costCenterId?: string;
  search?: string;
  sortBy?: string;
  sortDescending?: boolean;
  page?: number;
  pageSize?: number;
}

// ─────────────────────────────── الفواتير المسددة وغير المسددة

/** "Paid" | "PartiallyPaid" | "Unpaid" — the settlement engine's own three states. */
export type InvoiceSettlementState = "Paid" | "PartiallyPaid" | "Unpaid";

/** ONE CUSTOMER SALES INVOICE with its settlement state. */
export interface PaidUnpaidInvoiceRow {
  invoiceId: string;
  journalVoucherId: string;
  invoiceDate: string;
  invoiceNumber: string;
  invoiceType: string;
  customerId: string;
  customerCode: string;
  customerName: string;
  customerCategory: string | null;
  /** Every amount on this row is in THIS currency. */
  currencyCode: string;
  currencyId: string;
  invoiceTotal: number;
  /** The engine's own SettledAmount — the sum of ACTIVE allocations. */
  paidAmount: number;
  remainingAmount: number;
  paymentStatus: InvoiceSettlementState;
  /** Allocations exceed the invoice — an integrity problem, never clamped away. */
  isOverSettled: boolean;
  /** No invoice in this system carries a due date. Always null. */
  dueDate: string | null;
  /** Cannot be computed without a due date. Always null. */
  daysOverdue: number | null;
  paymentMethod: string | null;
  /** How it was settled; "Multiple" only when the settlements prove more than one. */
  settlementPaymentMethod: string | null;
  /** Sales invoices do not store a representative. Always null. */
  salesRepresentative: string | null;
  costCenterCode: string | null;
  costCenterName: string | null;
  branchName: string | null;
  reference: string | null;
  notes: string | null;
  settlementCount: number;
}

export interface PaidUnpaidCurrencyTotal {
  currencyCode: string;
  invoiceCount: number;
  invoiceTotal: number;
  paidAmount: number;
  remainingAmount: number;
}

export interface PaidUnpaidInvoicesResult {
  rows: PaidUnpaidInvoiceRow[];
  totalsByCurrency: PaidUnpaidCurrencyTotal[];
  invoiceCount: number;
  paidCount: number;
  partiallyPaidCount: number;
  unpaidCount: number;
  overSettledCount: number;
  page: number;
  pageSize: number;
  totalCount: number;
}

export interface PaidUnpaidInvoicesFilter {
  dateFrom?: string;
  dateTo?: string;
  customerId?: string;
  categoryId?: string;
  paymentStatus?: string;
  invoiceNumber?: string;
  currencyId?: string;
  paymentMethodId?: string;
  costCenterId?: string;
  branchId?: string;
  search?: string;
  sortBy?: string;
  sortDescending?: boolean;
  page?: number;
  pageSize?: number;
}

// ─────────────────────────────── أعمار ذمم العملاء

/** 1 = Due Date, 2 = Invoice/Document Date. */
export type AgingBasis = 1 | 2;

/** One configured period from الفترات الزمنية لعمر الذمة. */
export interface AgingPeriod {
  id: string;
  sequence: number;
  fromDay: number;
  toDay: number;
  /** Built from the range — the settings module stores no name. */
  label: string;
}

export interface CustomerAgingRow {
  customerId: string;
  customerCode: string;
  customerName: string;
  customerCategory: string | null;
  salesRepresentative: string | null;
  controlAccountNumber: string | null;
  controlAccountName: string | null;
  notDue: number;
  /** Amount per configured period, keyed by period id. */
  periods: Record<string, number>;
  /** Outstanding whose age no configured period covers. Never dropped. */
  unclassified: number;
  totalOutstanding: number;
  /** Credits with nothing left to settle — reported beside the aging, never offset into it. */
  unappliedCredits: number;
  creditLimit: number;
  maximumAgeDays: number | null;
  openItemCount: number;
}

export interface CustomerAgingDetailRow {
  journalVoucherId: string;
  documentDate: string;
  dueDate: string | null;
  voucherNumber: string;
  documentType: string;
  reference: string | null;
  originalAmount: number;
  settledAmount: number;
  remainingAmount: number;
  currencyCode: string | null;
  exchangeRate: number;
  originalTransactionAmount: number;
  ageDays: number;
  isNotDue: boolean;
  agingPeriodId: string | null;
  agingPeriodLabel: string;
  branchName: string | null;
  fiscalYearName: string | null;
}

export interface CustomerAgingSummary {
  customerCount: number;
  totalOutstanding: number;
  notDue: number;
  totalOverdue: number;
  unclassified: number;
  unappliedCredits: number;
  periods: Record<string, number>;
}

export interface CustomerAgingResult {
  periods: AgingPeriod[];
  rows: CustomerAgingRow[];
  summary: CustomerAgingSummary;
  baseCurrencyCode: string;
  asOfDate: string;
  basis: AgingBasis;
  /** Configuration and data problems — reported, never repaired. */
  warnings: string[];
  page: number;
  pageSize: number;
  totalCount: number;
}

export interface CustomerAgingFilter {
  asOfDate?: string;
  basis?: number;
  customerId?: string;
  categoryId?: string;
  salesRepresentativeUserId?: string;
  accountId?: string;
  branchId?: string;
  countryRegionId?: string;
  cityRegionId?: string;
  minimumOutstanding?: number;
  overdueOnly?: boolean;
  includeZeroBalances?: boolean;
  search?: string;
  sortBy?: string;
  sortDescending?: boolean;
  page?: number;
  pageSize?: number;
}

// ─────────────────────────────── تقرير أعمار الذمم التفصيلي

/** ONE OPEN RECEIVABLE ITEM. The same aging as the summary, one level down. */
export interface DetailedAgingRow {
  journalVoucherId: string;
  customerId: string;
  documentDate: string;
  voucherNumber: string;
  voucherType: string;
  voucherTypeId: string | null;
  customerCode: string;
  customerName: string;
  customerCategory: string | null;
  controlAccountNumber: string | null;
  controlAccountName: string | null;
  currencyCode: string | null;
  currencyId: string | null;
  exchangeRate: number;
  originalTransactionAmount: number;
  originalBaseAmount: number;
  settledBaseAmount: number;
  remainingBaseAmount: number;
  /** Null on every document today — this system records no invoice due date. */
  dueDate: string | null;
  ageDays: number;
  agingPeriodId: string | null;
  agingPeriodLabel: string;
  /** "NotDue" | "Overdue" | "Paid". */
  dueStatus: string;
  salesRepresentative: string | null;
  branchName: string | null;
  reference: string | null;
  notes: string | null;
  settlementCount: number;
}

export interface DetailedAgingSummary {
  openItemCount: number;
  customerCount: number;
  originalBaseAmount: number;
  settledBaseAmount: number;
  remainingBaseAmount: number;
  notDue: number;
  totalOverdue: number;
  unclassified: number;
  periods: Record<string, number>;
  unappliedCredits: number;
}

export interface DetailedAgingResult {
  periods: AgingPeriod[];
  rows: DetailedAgingRow[];
  summary: DetailedAgingSummary;
  baseCurrencyCode: string;
  asOfDate: string;
  basis: AgingBasis;
  warnings: string[];
  page: number;
  pageSize: number;
  totalCount: number;
}

export interface DetailedAgingSettlement {
  settlementDate: string;
  reconciliationNumber: string;
  creditDocumentNumber: string;
  appliedAmount: number;
  currencyCode: string | null;
  paymentMethod: string | null;
  /** Effective on the as-of date, but cancelled since. */
  cancelledLater: boolean;
  createdByName: string | null;
}

export interface DetailedAgingFilter {
  asOfDate?: string;
  basis?: number;
  documentDateFrom?: string;
  documentDateTo?: string;
  customerId?: string;
  categoryId?: string;
  voucherTypeId?: string;
  voucherNumber?: string;
  agingPeriodId?: string;
  dueStatus?: string;
  salesRepresentativeUserId?: string;
  accountId?: string;
  currencyId?: string;
  branchId?: string;
  countryRegionId?: string;
  cityRegionId?: string;
  minimumRemaining?: number;
  maximumRemaining?: number;
  showFullySettled?: boolean;
  search?: string;
  sortBy?: string;
  sortDescending?: boolean;
  page?: number;
  pageSize?: number;
}

// ─────────────────────────────── التسلسلات المفقودة في سندات القبض

export interface VoucherSequenceScope {
  sequenceId: string;
  voucherTypeId: string;
  voucherTypeCode: string;
  voucherType: string;
  year: number;
  month: number;
  resetPolicy: string;
  startNumber: number;
  lastNumber: number;
  issuedCount: number;
  missingCount: number;
  duplicateCount: number;
  label: string;
}

/** ONE MISSING NUMBER. */
export interface MissingVoucherNumber {
  sequenceId: string;
  serial: number;
  missingVoucherNumber: string;
  voucherType: string;
  year: number;
  month: number;
  sequenceLabel: string;
  gapFrom: number;
  gapTo: number;
  gapSize: number;
  previousVoucherNumber: string | null;
  previousVoucherDate: string | null;
  previousVoucherStatus: string | null;
  previousVoucherId: string | null;
  nextVoucherNumber: string | null;
  nextVoucherDate: string | null;
  nextVoucherStatus: string | null;
  nextVoucherId: string | null;
}

export interface MissingVoucherGap {
  sequenceId: string;
  sequenceLabel: string;
  missingFrom: number;
  missingTo: number;
  missingCount: number;
  missingFromNumber: string;
  missingToNumber: string;
  previousVoucherNumber: string | null;
  nextVoucherNumber: string | null;
}

export interface DuplicateVoucherNumber {
  sequenceLabel: string;
  serial: number;
  count: number;
  voucherNumbers: string;
}

// ─────────────────────────────── حركات السندات

// ─────────────────────────────── حساب العميل - مركز التكلفة

// ─────────────────────────────── الأعمار حسب الفئة / العملة

// ─────────────────────────────── the customer-master report family (#1 #4 #7 #9 #17)

// ─────────────────────────────── the remaining 13 receipts reports

/** #11 #13 #23 #26 share the party-ledger shape. */
export interface PartyLedgerRow {
  partyId: string; partyType: string; partyCode: string; partyName: string;
  category: string | null; controlAccountNumber: string | null; controlAccountName: string | null;
  salesRepresentative: string | null; isActive: boolean;
  openingDebit: number; openingCredit: number; openingBalance: number;
  periodDebit: number; periodCredit: number;
  closingDebit: number; closingCredit: number; closingBalance: number; balanceNature: string;
  movementCount: number; lastActivityDate: string | null; currencyCode: string | null;
  creditLimit: number; city: string | null; country: string | null;
}
export interface PartyLedgerResult {
  rows: PartyLedgerRow[]; partyCount: number;
  totalOpeningDebit: number; totalOpeningCredit: number;
  totalPeriodDebit: number; totalPeriodCredit: number;
  totalClosingDebit: number; totalClosingCredit: number;
  baseCurrencyCode: string; fromDate: string | null; toDate: string | null;
  warnings: string[]; page: number; pageSize: number; totalCount: number;
}

/** #34 */
export interface CustomerAccountByAccountLine {
  journalVoucherId: string; entryDate: string; voucherType: string; voucherTypeCode: string;
  voucherNumber: string; accountId: string; accountNumber: string; accountName: string;
  reference: string | null; description: string | null;
  debit: number; credit: number; runningBalance: number;
  currencyCode: string | null; exchangeRate: number;
  costCenterCode: string | null; branchName: string | null; notes: string | null;
}
export interface AccountSummaryRow {
  accountId: string; accountNumber: string; accountName: string;
  openingBalance: number; debit: number; credit: number; closingBalance: number; movementCount: number;
}
export interface CustomerAccountByAccountResult {
  customerId: string; customerCode: string; customerName: string; baseCurrencyCode: string;
  fromDate: string | null; toDate: string | null;
  openingBalance: number; totalDebit: number; totalCredit: number; closingBalance: number;
  lines: CustomerAccountByAccountLine[]; accountSummary: AccountSummaryRow[];
  page: number; pageSize: number; totalCount: number;
}

/** #2 */
export interface ReceiptSummaryRow {
  receiptId: string; journalVoucherId: string; receiptDate: string; receiptNumber: string;
  status: string; customerId: string | null; customerCode: string | null; customerName: string | null;
  currencyCode: string | null; exchangeRate: number; amount: number; amountLocal: number;
  paymentMethod: string | null; destination: string | null; collector: string | null;
  salesRepresentative: string | null; branchName: string | null; reference: string | null;
  notes: string | null; createdBy: string | null;
}
export interface ReceiptSummaryResult {
  rows: ReceiptSummaryRow[];
  totalsByCurrency: { currencyCode: string; receiptCount: number; amount: number }[];
  receiptCount: number; totalLocal: number; baseCurrencyCode: string;
  page: number; pageSize: number; totalCount: number;
}

/** #8 */
export interface ReceiptOrderRow {
  orderId: string; orderDate: string; orderNumber: string; status: string;
  customerId: string | null; customerCode: string | null; customerName: string | null;
  currencyCode: string | null; requestedAmount: number; collectedAmount: number;
  remainingAmount: number; expectedDate: string | null; collector: string | null;
  salesRepresentative: string | null; branchName: string | null;
  reference: string | null; notes: string | null;
}
export interface ReceiptOrderResult {
  rows: ReceiptOrderRow[]; orderCount: number;
  totalRequested: number; totalCollected: number; totalRemaining: number;
  baseCurrencyCode: string; warnings: string[];
  page: number; pageSize: number; totalCount: number;
}

/** #12 */
export interface CollectorTransactionRow {
  documentId: string; documentType: string; isAccounting: boolean;
  date: string; documentNumber: string; status: string;
  collector: string | null; customerCode: string | null; customerName: string | null;
  currencyCode: string | null; amount: number; paymentMethod: string | null;
  branchName: string | null; reference: string | null; notes: string | null;
}
export interface CollectorTransactionsResult {
  rows: CollectorTransactionRow[]; documentCount: number; collectorCount: number;
  totalCollectedReceipts: number; totalOrderedAmount: number;
  baseCurrencyCode: string; warnings: string[];
  page: number; pageSize: number; totalCount: number;
}

/** #14 */
export interface DueDeferredRevenueRow {
  scheduleId: string; periodId: string;
  customerId: string | null; customerCode: string | null; customerName: string | null;
  invoiceNumber: string | null; contractStart: string; contractEnd: string;
  periodNumber: number; periodStart: string; periodEnd: string; recognitionDate: string;
  scheduledAmount: number; recognisedAmount: number; dueAmount: number; status: string;
  currencyCode: string | null; originalDeferredAmount: number;
}
export interface DueDeferredRevenueResult {
  rows: DueDeferredRevenueRow[]; asOfDate: string;
  periodCount: number; scheduleCount: number;
  totalScheduled: number; totalRecognised: number; totalDue: number; totalNotYetDue: number;
  baseCurrencyCode: string; page: number; pageSize: number; totalCount: number;
}

/** #20 */
export interface ServiceInvoiceByTypeRow {
  invoiceId: string; invoiceDate: string; invoiceNumber: string; status: string;
  customerId: string; customerCode: string; customerName: string;
  serviceSalesType: string | null; saleNature: string; currencyCode: string | null;
  subtotal: number; discountTotal: number; taxTotal: number; netTotal: number;
  paymentMethod: string | null; salesRepresentative: string | null; branchName: string | null;
  reference: string | null; contractStart: string | null; contractEnd: string | null;
}
export interface ServiceInvoiceByTypeResult {
  rows: ServiceInvoiceByTypeRow[];
  totalsByType: { serviceSalesTypeId: string | null; serviceSalesType: string; invoiceCount: number; netTotal: number }[];
  invoiceCount: number; totalNet: number; totalTax: number; totalDiscount: number;
  baseCurrencyCode: string; warnings: string[];
  page: number; pageSize: number; totalCount: number;
}

/** #21 */
export interface ConsolidatedCustomerRow {
  customerId: string; customerCode: string; customerName: string;
  category: string | null; salesRepresentative: string | null;
  isParent: boolean; childCount: number;
  ownBalance: number; childrenBalance: number; consolidatedBalance: number;
  balanceNature: string; creditLimit: number;
}
export interface ConsolidatedCustomersResult {
  rows: ConsolidatedCustomerRow[]; customerCount: number; parentCount: number;
  totalConsolidated: number; baseCurrencyCode: string; warnings: string[];
  page: number; pageSize: number; totalCount: number;
}

/** #22 */
export interface PartyChangeRow {
  auditId: string; changedAt: string; action: string;
  partyType: string | null; partyCode: string | null; partyName: string | null;
  field: string; oldValue: string | null; newValue: string | null; changedBy: string | null;
}
export interface PartyChangesResult {
  rows: PartyChangeRow[]; changeCount: number; eventCount: number; partyCount: number;
  warnings: string[]; page: number; pageSize: number; totalCount: number;
}

/** #37 */
export interface ParentAccountMember {
  customerId: string; customerCode: string; customerName: string; isParent: boolean;
  debit: number; credit: number; balance: number; balanceNature: string; movementCount: number;
}
export interface ParentCustomerAccountResult {
  parentCustomerId: string; parentCustomerCode: string; parentCustomerName: string;
  fromDate: string | null; toDate: string | null; baseCurrencyCode: string;
  members: ParentAccountMember[]; childCount: number;
  parentOwnBalance: number; childrenBalance: number; consolidatedBalance: number;
  totalDebit: number; totalCredit: number; warnings: string[];
}

export interface ReceiptsReportFilter {
  [key: string]: string | number | boolean | undefined;
}

export interface CustomerMasterRow {
  customerId: string;
  customerCode: string;
  customerName: string;
  customerNameEn: string | null;
  category: string | null;
  isActive: boolean;
  isBlacklisted: boolean;
  blacklistReason: string | null;
  controlAccountNumber: string | null;
  controlAccountName: string | null;
  salesRepresentative: string | null;
  collector: string | null;
  defaultPaymentMethod: string | null;
  parentCustomerCode: string | null;
  parentCustomerName: string | null;
  taxNumber: string | null;
  commercialRegistration: string | null;
  phone: string | null;
  mobile: string | null;
  email: string | null;
  address: string | null;
  city: string | null;
  country: string | null;
  contactPerson: string | null;
  website: string | null;
  notes: string | null;
  creditLimit: number;
  currentBalance: number;
  balanceNature: string;
  exceededAmount: number;
  creditUtilisation: number | null;
  availableCredit: number | null;
  createdAt: string;
  createdBy: string | null;
}

export interface CustomerMasterResult {
  rows: CustomerMasterRow[];
  customerCount: number;
  totalCreditLimit: number;
  totalBalance: number;
  totalExceeded: number;
  baseCurrencyCode: string;
  page: number;
  pageSize: number;
  totalCount: number;
}

export interface CustomerMasterFilter {
  customerId?: string;
  categoryId?: string;
  salesRepresentativeUserId?: string;
  collectorUserId?: string;
  controlAccountId?: string;
  countryRegionId?: string;
  cityRegionId?: string;
  status?: string;
  search?: string;
  sortBy?: string;
  sortDescending?: boolean;
  withLimitOnly?: boolean;
  page?: number;
  pageSize?: number;
}

export interface AgingGroupRow {
  groupId: string | null;
  groupLabel: string;
  isUnassigned: boolean;
  customerCount: number;
  openItemCount: number;
  notDue: number;
  periods: Record<string, number>;
  unclassified: number;
  totalOutstanding: number;
}

export interface AgingGroupedResult {
  periods: AgingPeriod[];
  rows: AgingGroupRow[];
  dimension: string;
  baseCurrencyCode: string;
  asOfDate: string;
  totalNotDue: number;
  totalPeriods: Record<string, number>;
  totalUnclassified: number;
  totalOutstanding: number;
  warnings: string[];
}

export interface AgingGroupedFilter {
  asOfDate?: string;
  dimension?: string;
  basis?: number;
  categoryId?: string;
  branchId?: string;
  salesRepresentativeUserId?: string;
}

export type CostCenterFilterMode = "All" | "Specific" | "None";

/** ONE ENTRY = ONE EFFECTIVE ACCOUNTING MOVEMENT, with its own cost centre. */
export interface CustomerAccountCostCenterLine {
  journalVoucherId: string;
  entryDate: string;
  voucherType: string;
  voucherTypeCode: string;
  voucherNumber: string;
  reference: string | null;
  description: string | null;
  costCenterId: string | null;
  costCenterCode: string | null;
  costCenterName: string | null;
  noCostCenter: boolean;
  debit: number;
  credit: number;
  runningBalance: number;
  currencyCode: string | null;
  exchangeRate: number;
  currencyDebit: number;
  currencyCredit: number;
  branchName: string | null;
}

export interface CostCenterSummaryRow {
  costCenterId: string | null;
  costCenterCode: string | null;
  costCenterName: string;
  noCostCenter: boolean;
  openingBalance: number;
  debit: number;
  credit: number;
  closingBalance: number;
  movementCount: number;
}

export interface CustomerAccountCostCenterResult {
  customerId: string;
  customerCode: string;
  customerName: string;
  customerCategory: string | null;
  controlAccountCode: string | null;
  controlAccountName: string | null;
  fromDate: string | null;
  toDate: string | null;
  baseCurrencyCode: string;
  openingBalance: number;
  totalDebit: number;
  totalCredit: number;
  closingBalance: number;
  lines: CustomerAccountCostCenterLine[];
  costCenterSummary: CostCenterSummaryRow[];
  movementsWithCostCenter: number;
  movementsWithoutCostCenter: number;
  page: number;
  pageSize: number;
  totalCount: number;
}

export interface CustomerAccountCostCenterFilter {
  customerId?: string;
  fromDate?: string;
  toDate?: string;
  costCenterMode?: string;
  costCenterId?: string;
  voucherTypeCode?: string;
  voucherNumber?: string;
  branchId?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

export type VoucherNatureValue = "Debit" | "Credit" | "Neutral";
export type VoucherAccountingEffectValue =
  | "Posted" | "NotPosted" | "Reversed" | "NoAccountingEntry";

/** ONE ENTRY = ONE DOCUMENT. Never one per GL line. */
export interface VoucherTransactionRow {
  documentId: string;
  journalVoucherId: string;
  sourceModule: string;
  voucherDate: string;
  voucherNumber: string;
  voucherTypeCode: string;
  voucherType: string;
  status: string;
  accountingEffect: VoucherAccountingEffectValue;
  partyType: string | null;
  partyId: string | null;
  partyCode: string | null;
  partyName: string | null;
  currencyId: string | null;
  currencyCode: string | null;
  exchangeRate: number;
  voucherAmount: number;
  voucherAmountLocal: number;
  nature: VoucherNatureValue;
  paymentMethod: string | null;
  branchName: string | null;
  costCenterCode: string | null;
  costCenterName: string | null;
  reference: string | null;
  notes: string | null;
  createdBy: string | null;
  createdAt: string;
  reversalReference: string | null;
  isReversalEntry: boolean;
  counterpartyName: string | null;
}

export interface VoucherTransactionsCurrencyTotal {
  currencyCode: string;
  voucherCount: number;
  debitNatureTotal: number;
  creditNatureTotal: number;
  neutralTotal: number;
  effectiveDebitTotal: number;
  effectiveCreditTotal: number;
}

export interface VoucherTypeCount {
  voucherTypeCode: string;
  voucherType: string;
  sourceModule: string;
  count: number;
}

export interface VoucherTransactionsResult {
  rows: VoucherTransactionRow[];
  totalsByCurrency: VoucherTransactionsCurrencyTotal[];
  countsByType: VoucherTypeCount[];
  voucherCount: number;
  postedCount: number;
  unpostedCount: number;
  reversedCount: number;
  partyCount: number;
  effectiveNetLocal: number;
  historicalNetLocal: number;
  baseCurrencyCode: string;
  page: number;
  pageSize: number;
  totalCount: number;
}

export interface VoucherTransactionsFilter {
  dateFrom?: string;
  dateTo?: string;
  voucherTypeCode?: string;
  voucherNumber?: string;
  status?: string;
  accountingEffect?: string;
  partyId?: string;
  currencyId?: string;
  paymentMethodId?: string;
  branchId?: string;
  costCenterId?: string;
  createdBy?: string;
  nature?: string;
  search?: string;
  sortBy?: string;
  sortDescending?: boolean;
  page?: number;
  pageSize?: number;
}

// ─────────────────────────────── فواتير العملاء المستحقة

export type OutstandingInvoiceStatus = "Unpaid" | "PartiallyPaid";

/** ONE ENTRY = ONE OUTSTANDING INVOICE. Never one per customer. */
export interface OutstandingInvoiceRow {
  invoiceId: string;
  journalVoucherId: string;
  invoiceDate: string;
  invoiceNumber: string;
  customerId: string;
  customerCode: string;
  customerName: string;
  customerCategory: string | null;
  currencyId: string;
  currencyCode: string;
  invoiceTotal: number;
  settledAmount: number;
  remainingAmount: number;
  remainingLocalAmount: number;
  paymentStatus: OutstandingInvoiceStatus;
  dueDate: string | null;
  paymentMethod: string | null;
  salesRepresentative: string | null;
  branchName: string | null;
  reference: string | null;
  notes: string | null;
  settlementCount: number;
}

export interface OutstandingInvoiceCurrencyTotal {
  currencyCode: string;
  invoiceCount: number;
  invoiceTotal: number;
  settledAmount: number;
  remainingAmount: number;
}

export interface OutstandingInvoicesResult {
  rows: OutstandingInvoiceRow[];
  totalsByCurrency: OutstandingInvoiceCurrencyTotal[];
  invoiceCount: number;
  unpaidCount: number;
  partiallyPaidCount: number;
  customerCount: number;
  totalRemainingLocal: number;
  baseCurrencyCode: string;
  asOfDate: string;
  dueDateUnavailable: boolean;
  page: number;
  pageSize: number;
  totalCount: number;
}

export interface OutstandingInvoicesFilter {
  asOfDate?: string;
  dateFrom?: string;
  dateTo?: string;
  customerId?: string;
  categoryId?: string;
  paymentStatus?: string;
  invoiceNumber?: string;
  currencyId?: string;
  paymentMethodId?: string;
  salesRepresentativeUserId?: string;
  branchId?: string;
  minRemaining?: number;
  maxRemaining?: number;
  search?: string;
  sortBy?: string;
  sortDescending?: boolean;
  page?: number;
  pageSize?: number;
}

// ─────────────────────────────── سندات القبض بدون فواتير

export type ReceiptMatchingStatus =
  | "Unallocated"
  | "PartiallyAllocated"
  | "FullyAllocated";

export interface ReceiptWithoutInvoiceRow {
  receiptId: string;
  journalVoucherId: string;
  receiptDate: string;
  receiptNumber: string;
  customerId: string;
  customerCode: string;
  customerName: string;
  customerCategory: string | null;
  currencyId: string;
  currencyCode: string;
  receiptAmount: number;
  allocatedAmount: number;
  unallocatedAmount: number;
  unallocatedLocalAmount: number;
  matchingStatus: ReceiptMatchingStatus;
  isOverAllocated: boolean;
  paymentMethod: string | null;
  destination: string | null;
  reference: string | null;
  branchName: string | null;
  costCenterCode: string | null;
  costCenterName: string | null;
  collector: string | null;
  salesRepresentative: string | null;
  notes: string | null;
  createdBy: string | null;
  createdAt: string;
  allocationCount: number;
  fromReceiptOrder: boolean;
  chequeCount: number;
}

export interface ReceiptWithoutInvoiceCurrencyTotal {
  currencyCode: string;
  receiptCount: number;
  receiptAmount: number;
  allocatedAmount: number;
  unallocatedAmount: number;
}

export interface ReceiptVouchersWithoutInvoicesResult {
  rows: ReceiptWithoutInvoiceRow[];
  totalsByCurrency: ReceiptWithoutInvoiceCurrencyTotal[];
  receiptCount: number;
  unallocatedCount: number;
  partiallyAllocatedCount: number;
  fullyAllocatedCount: number;
  overAllocatedCount: number;
  totalUnallocatedLocal: number;
  baseCurrencyCode: string;
  page: number;
  pageSize: number;
  totalCount: number;
}

export interface ReceiptPaymentSplit {
  lineNumber: number;
  paymentMethod: string | null;
  amount: number;
  destination: string | null;
  chequeNumber: string | null;
  chequeDueDate: string | null;
  notes: string | null;
}

export interface ReceiptAllocationDetail {
  lineId: string;
  settlementDate: string;
  settlementNumber: string;
  documentJournalVoucherId: string;
  documentNumber: string;
  documentType: string;
  documentDate: string | null;
  customerName: string;
  allocatedAmount: number;
  currencyCode: string;
  status: "Active" | "Cancelled";
  cancellationReason: string | null;
  cancelledOn: string | null;
}

export interface ReceiptAllocationDetailResult {
  receiptId: string;
  receiptNumber: string;
  receiptDate: string;
  customerName: string;
  currencyCode: string;
  receiptAmount: number;
  allocatedAmount: number;
  unallocatedAmount: number;
  matchingStatus: ReceiptMatchingStatus;
  payments: ReceiptPaymentSplit[];
  allocations: ReceiptAllocationDetail[];
}

export interface ReceiptsWithoutInvoicesFilter {
  dateFrom?: string;
  dateTo?: string;
  customerId?: string;
  categoryId?: string;
  matchingStatus?: string;
  receiptNumber?: string;
  currencyId?: string;
  paymentMethodId?: string;
  branchId?: string;
  collectorUserId?: string;
  salesRepresentativeUserId?: string;
  minUnallocated?: number;
  maxUnallocated?: number;
  search?: string;
  showFullyAllocated?: boolean;
  sortBy?: string;
  sortDescending?: boolean;
  page?: number;
  pageSize?: number;
}

export interface MissingVoucherSequenceSummary {
  missingNumberCount: number;
  gapCount: number;
  firstMissingNumber: number | null;
  lastMissingNumber: number | null;
  scopeCount: number;
  duplicateCount: number;
}

export interface MissingReceiptVoucherSequencesResult {
  scopes: VoucherSequenceScope[];
  rows: MissingVoucherNumber[];
  gaps: MissingVoucherGap[];
  duplicates: DuplicateVoucherNumber[];
  summary: MissingVoucherSequenceSummary;
  warnings: string[];
  page: number;
  pageSize: number;
  totalCount: number;
}

export interface MissingVoucherSequenceFilter {
  sequenceId?: string;
  year?: number;
  missingNumberFrom?: number;
  missingNumberTo?: number;
  search?: string;
  sortBy?: string;
  sortDescending?: boolean;
  page?: number;
  pageSize?: number;
}

// ═══════════════════════════════════════════════════════════════════════════════════════════════
// Financial Management › Payments › Inquiries
//
// Nine READ-ONLY screens over the payables side. Every one of them is filtered, sorted, paged and
// totalled BY THE SERVER, under the caller's supplier Inquiry scope — so a row absent from a page
// is absent from the totals beneath it too, and neither can be widened from the browser.
//
// These are inquiries, not reports: Print, PDF and Excel are actions on the screen.
// ═══════════════════════════════════════════════════════════════════════════════════════════════

/** Everything the nine screens send. Paging and sorting are never done in the browser. */
export interface PagedInquiryFilter {
  sortBy?: string;
  sortDescending?: boolean;
  page?: number;
  pageSize?: number;
}

/**
 * The Payments name, kept because twenty-three screens reference it.
 *
 * Paging was never a Payments idea — it is the system's — so the contract now carries a neutral
 * name and this is an alias onto it rather than a second declaration.
 */
export type PaymentsInquiryFilter = PagedInquiryFilter;

/** Everything the nine screens receive. */
export interface PagedInquiryResult {
  totalCount: number;
  page: number;
  pageSize: number;
  baseCurrencyCode: string;
  /** Data limitations, stated by the server. Rendered verbatim; never suppressed. */
  warnings: string[];
}

export type PaymentsInquiryResult = PagedInquiryResult;

// ─────────────────────────────────────────── #1 إجمالي ذمم الموردين

export const SUPPLIER_BALANCE_TYPE = {
  All: 0,
  /** Owed TO the supplier — a credit balance on the control account. */
  Payable: 1,
  /** Paid ahead — a debit balance. Shown as its own figure, never netted away. */
  Advance: 2,
  Zero: 3,
} as const;

export type SupplierBalanceType =
  (typeof SUPPLIER_BALANCE_TYPE)[keyof typeof SUPPLIER_BALANCE_TYPE];

export interface TotalSupplierPayablesFilter extends PaymentsInquiryFilter {
  supplierId?: string;
  supplierCode?: string;
  isActive?: boolean;
  controlAccountId?: string;
  branchId?: string;
  currencyId?: string;
  balanceType?: SupplierBalanceType;
  minimumBalance?: number;
  maximumBalance?: number;
  hasOpenDocuments?: boolean;
  search?: string;
}

export interface SupplierPayableRow {
  supplierId: string;
  code: string;
  name: string;
  nameAr: string | null;
  isActive: boolean;
  controlAccountId: string | null;
  controlAccountNumber: string | null;
  controlAccountName: string | null;
  currencyCode: string;
  debitMovements: number;
  creditMovements: number;
  /** Σ(debit − credit) on the control account. Negative means money is owed. */
  accountingBalance: number;
  payableAmount: number;
  advanceAmount: number;
  openDocumentCount: number;
  unsettledPurchaseInvoices: number;
  unappliedPayments: number;
  lastMovementDate: string | null;
  creditLimit: number;
  availableCredit: number | null;
}

export interface TotalSupplierPayablesResult extends PaymentsInquiryResult {
  rows: SupplierPayableRow[];
  totalDebitMovements: number;
  totalCreditMovements: number;
  totalAccountingBalance: number;
  totalPayable: number;
  totalAdvance: number;
  supplierCount: number;
}

// ───────────────────────────── #2 حركات حساب مورد / #3 حركات الموردين

export interface SupplierAccountMovementsFilter extends PaymentsInquiryFilter {
  supplierId?: string;
  dateFrom?: string;
  dateTo?: string;
  fiscalYearId?: string;
  voucherTypeCode?: string;
  reference?: string;
  costCenterId?: string;
  branchId?: string;
  currencyId?: string;
  paymentMethodId?: string;
  postedOnly?: boolean;
  includeReversed?: boolean;
  /** "debit" | "credit" — one side of the ledger only. */
  side?: string;
  amountFrom?: number;
  amountTo?: number;
}

export interface SupplierMovementRow {
  journalVoucherId: string;
  /** رقم فاتورة المورد — only on rows that came from a service purchase invoice. */
  supplierInvoiceNumber: string | null;
  /** تاريخ فاتورة المورد — theirs, not ours. Null unless the invoice recorded one. */
  supplierInvoiceDate: string | null;
  /** تاريخ الاستحقاق — null on non-invoice rows and on invoices raised without terms. */
  dueDate: string | null;
  journalVoucherLineId: string;
  movementDate: string;
  postedAt: string | null;
  voucherNumber: string;
  voucherSerial: number;
  voucherTypeCode: string;
  documentType: string;
  documentTypeAr: string | null;
  documentNumber: string;
  sourceRoute: string | null;
  sourceDocumentId: string | null;
  reference: string | null;
  description: string | null;
  supplierId: string;
  supplierCode: string;
  supplierName: string;
  supplierNameAr: string | null;
  accountId: string;
  controlAccountNumber: string;
  controlAccountName: string | null;
  debit: number;
  credit: number;
  currencyCode: string;
  exchangeRate: number;
  localDebit: number;
  localCredit: number;
  /** Only on the single-supplier statement, where a running balance is meaningful. */
  runningBalance: number | null;
  costCenterId: string | null;
  costCenterCode: string | null;
  costCenterName: string | null;
  branchId: string;
  branchName: string | null;
  userName: string | null;
  postingStatus: JournalStatus;
  isReversed: boolean;
  isReversal: boolean;
  paymentMethodName: string | null;
}

export interface SupplierAccountMovementsResult extends PaymentsInquiryResult {
  rows: SupplierMovementRow[];
  supplierId: string | null;
  supplierCode: string | null;
  supplierName: string | null;
  controlAccountNumber: string | null;
  openingBalance: number;
  closingBalance: number;
  creditLimit: number | null;
  unusedCreditLimit: number | null;
  categoryName: string | null;
  categoryNameAr: string | null;
  gracePeriodDays: number | null;
  gracePeriodBasis: number | null;
  closingBalanceInWords: string | null;
  totalDebit: number;
  totalCredit: number;
  netMovement: number;
}

// ─────────────────────────────────────── #4 الحركات اليومية للمدفوعات

export interface PaymentsDailyTransactionsFilter extends PaymentsInquiryFilter {
  supplierId?: string;
  dateFrom?: string;
  dateTo?: string;
  /** One AP document family, by its code. Empty means every family. */
  documentFamily?: string;
  /** "accounting" | "non-accounting". */
  nature?: string;
  postedOnly?: boolean;
  branchId?: string;
  reference?: string;
  amountFrom?: number;
  amountTo?: number;
  search?: string;
}

export interface ApDocumentRow {
  documentId: string;
  documentDate: string;
  documentFamily: string;
  documentNumber: string;
  supplierId: string | null;
  supplierCode: string | null;
  supplierName: string | null;
  supplierNameAr: string | null;
  reference: string | null;
  amount: number;
  currencyCode: string;
  /** False for the instructions — a payment order moves no ledger. */
  isAccounting: boolean;
  status: string;
  postingStatus: JournalStatus | null;
  branchId: string | null;
  branchName: string | null;
  userName: string | null;
  createdAt: string;
  sourceRoute: string | null;
}

export interface PaymentsDailyTransactionsResult extends PaymentsInquiryResult {
  rows: ApDocumentRow[];
  accountingCount: number;
  nonAccountingCount: number;
  totalAccountingAmount: number;
}

// ─────────────────────────────────────────── #5 حركات سندات الدفع

export interface PaymentVoucherMovementsFilter extends PaymentsInquiryFilter {
  dateFrom?: string;
  dateTo?: string;
  voucherNumber?: string;
  supplierId?: string;
  paymentOrderId?: string;
  paymentMethodId?: string;
  cashboxId?: string;
  bankAccountId?: string;
  currencyId?: string;
  amountFrom?: number;
  amountTo?: number;
  status?: JournalStatus;
  postedOnly?: boolean;
  reversed?: boolean;
  branchId?: string;
  reference?: string;
  /** Vouchers paid to an account rather than a supplier. They carry no supplier scope. */
  includeDirectPayments?: boolean;
}

/** One settlement line of a voucher — the real split, never a fabricated single method. */
export interface PaymentVoucherComponent {
  id: string;
  lineNumber: number;
  paymentMethodName: string | null;
  cashboxCode: string | null;
  cashboxName: string | null;
  bankAccountNumber: string | null;
  amount: number;
  localAmount: number;
  notes: string | null;
}

export interface PaymentVoucherMovementRow {
  id: string;
  journalVoucherId: string;
  voucherNumber: string;
  voucherDate: string;
  mode: PaymentVoucherMode;
  supplierId: string | null;
  supplierCode: string | null;
  supplierName: string | null;
  supplierNameAr: string | null;
  directAccountNumber: string | null;
  directAccountName: string | null;
  paymentOrderId: string | null;
  paymentOrderNumber: string | null;
  amount: number;
  currencyCode: string;
  exchangeRate: number;
  localAmount: number;
  reference: string | null;
  notes: string | null;
  postingStatus: JournalStatus;
  isReversed: boolean;
  auditedAt: string | null;
  auditedByName: string | null;
  postedByName: string | null;
  createdByName: string | null;
  createdAt: string;
  branchId: string;
  branchName: string | null;
  paymentMethodSummary: string | null;
  componentCount: number;
  components: PaymentVoucherComponent[];
  sourceRoute: string | null;
}

export interface PaymentVoucherMovementsResult extends PaymentsInquiryResult {
  rows: PaymentVoucherMovementRow[];
  totalAmount: number;
  totalLocalAmount: number;
  postedCount: number;
  reversedCount: number;
}

// ──────────────────────────────────── #6 حركات مستندات شراء الخدمات

export interface ServicePurchaseDocumentsFilter extends PaymentsInquiryFilter {
  supplierId?: string;
  dateFrom?: string;
  dateTo?: string;
  /** "request" | "invoice" | "return". Empty means the whole chain. */
  documentType?: string;
  requestNumber?: string;
  invoiceNumber?: string;
  returnNumber?: string;
  supplierReference?: string;
  currencyId?: string;
  taxTypeId?: string;
  expenseAccountId?: string;
  costCenterId?: string;
  branchId?: string;
  postedOnly?: boolean;
  reversed?: boolean;
  amountFrom?: number;
  amountTo?: number;
  includeLines?: boolean;
}

export interface ServicePurchaseLine {
  id: string;
  lineNumber: number;
  description: string;
  unit: string | null;
  quantity: number;
  unitPrice: number;
  discountAmount: number;
  taxTypeId: string | null;
  taxTypeCode: string | null;
  /** The rate the document was posted at, not today's rate. */
  taxPercent: number;
  netAmount: number;
  taxAmount: number;
  totalAmount: number;
  expenseAccountId: string;
  expenseAccountNumber: string | null;
  expenseAccountName: string | null;
  costCenterId: string | null;
  costCenterCode: string | null;
  costCenterName: string | null;
}

export interface ServicePurchaseDocumentRow {
  id: string;
  documentDate: string;
  documentType: string;
  documentNumber: string;
  supplierId: string;
  supplierCode: string | null;
  supplierName: string | null;
  supplierNameAr: string | null;
  supplierReference: string | null;
  sourceRequestId: string | null;
  sourceRequestNumber: string | null;
  sourceInvoiceId: string | null;
  sourceInvoiceNumber: string | null;
  currencyCode: string;
  exchangeRate: number;
  netAmount: number;
  taxAmount: number;
  totalAmount: number;
  status: string;
  /** Null for a purchase REQUEST, which owns no journal voucher at all. */
  postingStatus: JournalStatus | null;
  isReversed: boolean;
  branchId: string | null;
  branchName: string | null;
  createdByName: string | null;
  createdAt: string;
  sourceRoute: string | null;
  lines: ServicePurchaseLine[];
}

export interface ServicePurchaseDocumentsResult extends PaymentsInquiryResult {
  rows: ServicePurchaseDocumentRow[];
  totalNet: number;
  totalTax: number;
  totalAmount: number;
  requestCount: number;
  invoiceCount: number;
  returnCount: number;
}

// ─────────────────────────────── #7 حركات المورد حسب مركز التكلفة

export interface SupplierCostCenterFilter extends PaymentsInquiryFilter {
  supplierId?: string;
  costCenterId?: string;
  /** Rolls the chosen centre's descendants in with it, by materialised path. */
  includeChildCostCenters?: boolean;
  dateFrom?: string;
  dateTo?: string;
  accountId?: string;
  voucherTypeCode?: string;
  currencyId?: string;
  branchId?: string;
  amountFrom?: number;
  amountTo?: number;
  /** On by default. Turning it off shows what carries NO centre — never invents one. */
  costCenterOnly?: boolean;
}

export interface SupplierCostCenterRow {
  journalVoucherId: string;
  journalVoucherLineId: string;
  postingDate: string;
  supplierId: string;
  supplierCode: string | null;
  supplierName: string | null;
  supplierNameAr: string | null;
  costCenterId: string | null;
  costCenterCode: string | null;
  costCenterName: string | null;
  parentCostCenterCode: string | null;
  parentCostCenterName: string | null;
  voucherTypeCode: string;
  documentType: string;
  documentNumber: string;
  accountId: string;
  accountNumber: string;
  accountName: string;
  description: string | null;
  debit: number;
  credit: number;
  netMovement: number;
  currencyCode: string;
  exchangeRate: number;
  localDebit: number;
  localCredit: number;
  branchId: string;
  branchName: string | null;
  sourceRoute: string | null;
}

export interface CostCenterGroupTotal {
  key: string;
  label: string;
  debit: number;
  credit: number;
  net: number;
  rowCount: number;
}

export interface SupplierCostCenterResult extends PaymentsInquiryResult {
  rows: SupplierCostCenterRow[];
  totalDebit: number;
  totalCredit: number;
  netMovement: number;
  bySupplier: CostCenterGroupTotal[];
  byCostCenter: CostCenterGroupTotal[];
  byAccount: CostCenterGroupTotal[];
  uncostedRowCount: number;
}

// ─────────────────────────────────────────── #8 دفاتر سندات الدفع

export interface PaymentBooksInquiryFilter extends PaymentsInquiryFilter {
  bookNumber?: string;
  numberFrom?: number;
  numberTo?: number;
  status?: PaymentBookStatus;
  holderId?: string;
  holderType?: PersonnelRoleType;
  branchId?: string;
  assignedFrom?: string;
  assignedTo?: string;
}

export interface PaymentBookMovementEntry {
  id: string;
  movementDate: string;
  action: number;
  fromHolderName: string | null;
  toHolderName: string | null;
  performedByName: string | null;
  notes: string | null;
  createdAt: string;
}

export interface PaymentBookInquiryRow {
  id: string;
  bookNumber: string;
  fromNumber: number;
  toNumber: number;
  voucherCount: number;
  status: PaymentBookStatus;
  currentHolderId: string | null;
  currentHolderName: string | null;
  currentHolderNameAr: string | null;
  holderType: PersonnelRoleType | null;
  receivedDate: string;
  assignedDate: string | null;
  returnedDate: string | null;
  branchId: string | null;
  branchName: string | null;
  lastMovementDate: string | null;
  notes: string | null;
  movements: PaymentBookMovementEntry[];
}

export interface PaymentBooksInquiryResult extends PaymentsInquiryResult {
  rows: PaymentBookInquiryRow[];
  totalBooks: number;
  totalVouchers: number;
}

// ────────────────────────────────────── #9 فجوات نطاقات الدفاتر

export interface MissingBookSequencesFilter extends PaymentsInquiryFilter {
  branchId?: string;
  bookNumber?: string;
  rangeFrom?: number;
  rangeTo?: number;
  status?: PaymentBookStatus;
}

/**
 * One hole between two REGISTERED book ranges.
 *
 * Not a missing voucher. Payment Books do not consume payment voucher numbers in this system,
 * so the only thing that can honestly be detected is a number range registered to no book —
 * which is what `detectionType` says, and what the screen prints.
 */
export interface MissingBookRange {
  branchId: string | null;
  branchName: string | null;
  previousBookNumber: string;
  previousRangeEnd: number;
  nextBookNumber: string;
  nextRangeStart: number;
  missingFrom: number;
  missingTo: number;
  missingCount: number;
  detectionType: string;
}

export interface MissingBookSequencesResult extends PaymentsInquiryResult {
  rows: MissingBookRange[];
  totalMissingNumbers: number;
  gapCount: number;
  booksExamined: number;
}

/**
 * One supplier a Payments inquiry picker may offer.
 *
 * Comes from the scoped endpoint, never from the general partner list: a picker that offers a
 * supplier the caller cannot read discloses the supplier file and invites a refusal.
 */
export interface InquirySupplierOption {
  id: string;
  code: string;
  name: string;
  nameAr: string | null;
  isActive: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════════════════════════
// Payments › Inquiries — the ten completing the AP catalogue
// ═══════════════════════════════════════════════════════════════════════════════════════════════

export const PURCHASE_ITEM_TYPE = {
  Service: 1,
  Goods: 2,
  /** نوع مصروف — an expense incurred, rather than a service or goods taken delivery of. */
  Expense: 3,
} as const;
export type PurchaseItemType = (typeof PURCHASE_ITEM_TYPE)[keyof typeof PURCHASE_ITEM_TYPE];

export const PURCHASE_ORDER_STATUS = {
  Draft: 1, Approved: 2, Closed: 3, Cancelled: 4,
} as const;
export type PurchaseOrderStatus =
  (typeof PURCHASE_ORDER_STATUS)[keyof typeof PURCHASE_ORDER_STATUS];

export const BOOK_KIND = { Receipt: 1, Payment: 2 } as const;
export type BookKind = (typeof BOOK_KIND)[keyof typeof BOOK_KIND];

// ── أوامر الصرف غير المدرجة

export interface UnsettledPaymentOrdersFilter extends PaymentsInquiryFilter {
  supplierId?: string;
  dateFrom?: string;
  dateTo?: string;
  branchId?: string;
  currencyId?: string;
  paymentMethodId?: string;
  reference?: string;
  status?: number;
  minimumRemaining?: number;
  includeFullyPaid?: boolean;
  includeCancelled?: boolean;
}

export interface UnsettledPaymentOrderRow {
  id: string;
  orderNumber: string;
  orderDate: string;
  supplierId: string;
  supplierCode: string | null;
  supplierName: string | null;
  supplierNameAr: string | null;
  currencyCode: string | null;
  exchangeRate: number;
  orderAmount: number;
  /** Σ of POSTED payment vouchers naming this order. A reversal removes itself. */
  paidAmount: number;
  remainingAmount: number;
  status: number;
  settlementState: string;
  expectedPaymentDate: string | null;
  paymentMethodName: string | null;
  branchName: string | null;
  referenceNumber: string | null;
  notes: string | null;
  voucherCount: number;
  sourceRoute: string | null;
}

export interface UnsettledPaymentOrdersResult extends PaymentsInquiryResult {
  rows: UnsettledPaymentOrderRow[];
  totalOrdered: number;
  totalPaid: number;
  totalRemaining: number;
  unpaidCount: number;
  partiallyPaidCount: number;
}

// ── بيانات المورد

export interface SupplierInformationInquiryFilter extends PaymentsInquiryFilter {
  supplierId?: string;
  isActive?: boolean;
  controlAccountId?: string;
  currencyId?: string;
  search?: string;
}

export interface SupplierInformationRow {
  id: string;
  code: string;
  name: string;
  nameAr: string | null;
  isActive: boolean;
  controlAccountNumber: string | null;
  controlAccountName: string | null;
  currencyCode: string | null;
  taxNumber: string | null;
  commercialRegistration: string | null;
  nationalId: string | null;
  phone: string | null;
  mobile: string | null;
  email: string | null;
  address: string | null;
  city: string | null;
  country: string | null;
  creditLimit: number;
  notes: string | null;
}

export interface SupplierInformationInquiryResult extends PaymentsInquiryResult {
  rows: SupplierInformationRow[];
  activeCount: number;
  inactiveCount: number;
}

// ── حساب المورد - عملة / محلي

export interface SupplierCurrencyStatementFilter extends PaymentsInquiryFilter {
  supplierId?: string;
  currencyId?: string;
  dateFrom?: string;
  dateTo?: string;
  branchId?: string;
  voucherTypeCode?: string;
  reference?: string;
  includeReversed?: boolean;
}

export interface SupplierLedgerCurrency {
  currencyId: string;
  currencyCode: string;
  isBaseCurrency: boolean;
  movementCount: number;
  foreignBalance: number;
  localBalance: number;
}

export interface SupplierCurrencyMovementRow {
  journalVoucherId: string;
  journalVoucherLineId: string;
  postingDate: string;
  documentType: string;
  documentNumber: string;
  voucherTypeCode: string;
  supplierId: string;
  supplierCode: string | null;
  supplierName: string | null;
  currencyCode: string;
  /** The rate the movement POSTED at. Never today's rate. */
  exchangeRate: number;
  foreignDebit: number;
  foreignCredit: number;
  foreignBalance: number;
  localDebit: number;
  localCredit: number;
  localBalance: number;
  description: string | null;
  reference: string | null;
  branchName: string | null;
  costCenterCode: string | null;
  sourceRoute: string | null;
  sourceDocumentId: string | null;
}

export interface SupplierCurrencyStatementResult extends PaymentsInquiryResult {
  rows: SupplierCurrencyMovementRow[];
  supplierId: string | null;
  supplierCode: string | null;
  supplierName: string | null;
  currencies: SupplierLedgerCurrency[];
  openingForeignBalance: number;
  openingLocalBalance: number;
  closingForeignBalance: number;
  closingLocalBalance: number;
  totalForeignDebit: number;
  totalForeignCredit: number;
  totalLocalDebit: number;
  totalLocalCredit: number;
}

// ── إجمالي أرصدة الموردين - مركز التكلفة

export interface SupplierBalancesByCostCenterFilter extends PaymentsInquiryFilter {
  supplierId?: string;
  costCenterId?: string;
  includeChildCostCenters?: boolean;
  upToDate?: string;
  branchId?: string;
  currencyId?: string;
  includeUnassigned?: boolean;
  includeZeroBalances?: boolean;
}

export interface SupplierCostCenterBalanceRow {
  costCenterId: string | null;
  costCenterCode: string | null;
  costCenterName: string | null;
  supplierId: string;
  supplierCode: string | null;
  supplierName: string | null;
  supplierNameAr: string | null;
  debit: number;
  credit: number;
  accountingBalance: number;
  payableAmount: number;
  advanceAmount: number;
  /** True for the bucket carrying no authoritative cost centre. Never inferred. */
  isUnassigned: boolean;
  currencyCode: string;
}

export interface SupplierBalancesByCostCenterResult extends PaymentsInquiryResult {
  rows: SupplierCostCenterBalanceRow[];
  totalDebit: number;
  totalCredit: number;
  totalAccountingBalance: number;
  totalPayable: number;
  totalAdvance: number;
  unassignedAmount: number;
  costCenterCount: number;
  supplierCount: number;
}

// ── أوامر الشراء بانتظار الدفع

export interface PurchaseOrdersAwaitingPaymentFilter extends PaymentsInquiryFilter {
  supplierId?: string;
  dateFrom?: string;
  dateTo?: string;
  branchId?: string;
  currencyId?: string;
  costCenterId?: string;
  reference?: string;
  minimumRemaining?: number;
  includeSettled?: boolean;
  includeNonOutstanding?: boolean;
}

export interface PurchaseOrderAwaitingPaymentRow {
  id: string;
  orderNumber: string;
  orderDate: string;
  expectedDate: string | null;
  paymentTermDays: number | null;
  supplierId: string;
  supplierCode: string | null;
  supplierName: string | null;
  supplierNameAr: string | null;
  currencyCode: string | null;
  exchangeRate: number;
  orderAmount: number;
  invoicedAmount: number;
  paidAmount: number;
  remainingAmount: number;
  uninvoicedAmount: number;
  status: PurchaseOrderStatus;
  costCenterCode: string | null;
  branchName: string | null;
  supplierReference: string | null;
  referenceNumber: string | null;
  sourceRoute: string | null;
}

export interface PurchaseOrdersAwaitingPaymentResult extends PaymentsInquiryResult {
  rows: PurchaseOrderAwaitingPaymentRow[];
  totalCommitted: number;
  totalInvoiced: number;
  totalPaid: number;
  totalRemaining: number;
  totalUninvoiced: number;
}

// ── دفاتر القبض والصرف

export interface CombinedBooksFilter extends PaymentsInquiryFilter {
  bookType?: BookKind;
  bookNumber?: string;
  numberFrom?: number;
  numberTo?: number;
  status?: number;
  branchId?: string;
  holderId?: string;
  assignedFrom?: string;
  assignedTo?: string;
}

export interface CombinedBookRow {
  id: string;
  bookType: BookKind;
  bookNumber: string;
  fromNumber: number;
  toNumber: number;
  voucherCount: number;
  status: number;
  currentHolderId: string | null;
  currentHolderName: string | null;
  currentHolderNameAr: string | null;
  receivedDate: string;
  assignedDate: string | null;
  returnedDate: string | null;
  lastMovementDate: string | null;
  branchId: string | null;
  branchName: string | null;
  notes: string | null;
  movementCount: number;
  sourceRoute: string | null;
}

export interface CombinedBooksResult extends PaymentsInquiryResult {
  rows: CombinedBookRow[];
  receiptBookCount: number;
  paymentBookCount: number;
  totalRegisteredNumbers: number;
}

// ── حركات المادة - المورد

export interface SupplierItemTransactionsFilter extends PaymentsInquiryFilter {
  supplierId?: string;
  itemId?: string;
  itemType?: PurchaseItemType;
  dateFrom?: string;
  dateTo?: string;
  costCenterId?: string;
  branchId?: string;
  currencyId?: string;
  documentType?: string;
  includeUncatalogued?: boolean;
  search?: string;
}

export interface SupplierItemTransactionRow {
  lineId: string;
  documentId: string;
  documentType: string;
  documentNumber: string;
  documentDate: string;
  itemId: string | null;
  itemCode: string | null;
  itemName: string | null;
  itemNameAr: string | null;
  supplierId: string;
  supplierCode: string | null;
  supplierName: string | null;
  supplierNameAr: string | null;
  description: string;
  unit: string | null;
  /** Negative on a return — the goods went back. */
  quantity: number;
  unitPrice: number;
  discountAmount: number;
  taxAmount: number;
  netAmount: number;
  currencyCode: string | null;
  costCenterCode: string | null;
  branchName: string | null;
  reference: string | null;
  sourceRoute: string | null;
}

export interface SupplierItemTransactionsResult extends PaymentsInquiryResult {
  rows: SupplierItemTransactionRow[];
  totalQuantity: number;
  totalNet: number;
  totalTax: number;
  itemCount: number;
  uncataloguedCount: number;
}

// ── فواتير الموردين المستحقة

export interface OutstandingSupplierInvoicesFilter extends PaymentsInquiryFilter {
  supplierId?: string;
  dateFrom?: string;
  dateTo?: string;
  branchId?: string;
  currencyId?: string;
  reference?: string;
  overdueOnly?: boolean;
  outstandingOnly?: boolean;
  minimumOutstanding?: number;
  asOfDate?: string;
}

export interface OutstandingSupplierInvoiceRow {
  journalVoucherId: string;
  invoiceNumber: string;
  voucherTypeCode: string;
  documentType: string;
  invoiceDate: string;
  /** Null when the document carries no agreed due date. Never invented. */
  dueDate: string | null;
  supplierId: string;
  supplierCode: string | null;
  supplierName: string | null;
  supplierNameAr: string | null;
  currencyCode: string | null;
  exchangeRate: number;
  originalAmount: number;
  appliedAmount: number;
  outstandingAmount: number;
  originalLocalAmount: number;
  outstandingLocalAmount: number;
  daysOutstanding: number;
  daysOverdue: number | null;
  status: string;
  branchName: string | null;
  reference: string | null;
  sourceRoute: string | null;
}

export interface OutstandingSupplierInvoicesResult extends PaymentsInquiryResult {
  rows: OutstandingSupplierInvoiceRow[];
  totalOriginal: number;
  totalApplied: number;
  totalOutstanding: number;
  overdueCount: number;
  overdueAmount: number;
}

// ── الفواتير المسددة وغير المسددة

export interface PaidUnpaidSupplierInvoicesFilter extends PaymentsInquiryFilter {
  supplierId?: string;
  dateFrom?: string;
  dateTo?: string;
  currencyId?: string;
  branchId?: string;
  /** "Paid" | "PartiallyPaid" | "Unpaid". */
  paymentState?: string;
  overdueOnly?: boolean;
  asOfDate?: string;
  reference?: string;
}

export interface PaidUnpaidSupplierInvoiceRow {
  journalVoucherId: string;
  invoiceNumber: string;
  voucherTypeCode: string;
  documentType: string;
  invoiceDate: string;
  dueDate: string | null;
  supplierId: string;
  supplierCode: string | null;
  supplierName: string | null;
  supplierNameAr: string | null;
  currencyCode: string | null;
  invoiceAmount: number;
  paidAmount: number;
  remainingAmount: number;
  /** Decided by the settlement engine, never by the document's own status word. */
  paymentState: string;
  daysOverdue: number | null;
  branchName: string | null;
  reference: string | null;
  sourceRoute: string | null;
}

export interface PaidUnpaidSupplierInvoicesResult extends PaymentsInquiryResult {
  rows: PaidUnpaidSupplierInvoiceRow[];
  totalInvoiced: number;
  totalPaid: number;
  totalRemaining: number;
  paidCount: number;
  partiallyPaidCount: number;
  unpaidCount: number;
}

// ── سندات الصرف بدون فواتير مسددة

export interface UnappliedPaymentVouchersFilter extends PaymentsInquiryFilter {
  supplierId?: string;
  dateFrom?: string;
  dateTo?: string;
  currencyId?: string;
  branchId?: string;
  reference?: string;
  minimumUnapplied?: number;
  includeFullyApplied?: boolean;
}

export interface UnappliedPaymentVoucherRow {
  journalVoucherId: string;
  paymentVoucherId: string | null;
  voucherNumber: string;
  voucherTypeCode: string;
  documentType: string;
  voucherDate: string;
  supplierId: string;
  supplierCode: string | null;
  supplierName: string | null;
  supplierNameAr: string | null;
  paymentMethodSummary: string | null;
  cashboxName: string | null;
  bankAccountNumber: string | null;
  currencyCode: string | null;
  voucherAmount: number;
  appliedAmount: number;
  unappliedAmount: number;
  isReversed: boolean;
  branchName: string | null;
  reference: string | null;
  sourceRoute: string | null;
}

export interface UnappliedPaymentVouchersResult extends PaymentsInquiryResult {
  rows: UnappliedPaymentVoucherRow[];
  totalVoucherAmount: number;
  totalApplied: number;
  totalUnapplied: number;
}

// ── Purchasing master data ────────────────────────────────────────────────────

export interface PurchaseItem {
  id: string;
  code: string;
  name: string;
  nameAr: string | null;
  itemType: PurchaseItemType;
  unit: string | null;
  defaultExpenseAccountId: string | null;
  defaultExpenseAccountNumber: string | null;
  defaultExpenseAccountName: string | null;
  defaultTaxTypeId: string | null;
  defaultTaxTypeCode: string | null;
  defaultCostCenterId: string | null;
  defaultCostCenterCode: string | null;
  isActive: boolean;
  notes: string | null;
  /** How many purchase document lines name this item. Read, never stored. */
  usageCount: number;
}

export interface PurchaseItemList {
  rows: PurchaseItem[];
  totalCount: number;
}

export interface SavePurchaseItemInput {
  id?: string;
  code: string;
  name: string;
  nameAr?: string;
  itemType: PurchaseItemType;
  unit?: string;
  defaultExpenseAccountId?: string;
  defaultTaxTypeId?: string;
  defaultCostCenterId?: string;
  isActive: boolean;
  notes?: string;
}

// ── Purchase orders (أوامر الشراء) ───────────────────────────────────────────

export interface PurchaseOrderLine {
  id: string;
  lineNumber: number;
  itemId: string | null;
  itemCode: string | null;
  itemName: string | null;
  description: string;
  unit: string | null;
  quantity: number;
  unitPrice: number;
  discountAmount: number;
  taxTypeId: string | null;
  taxTypeCode: string | null;
  /** The rate agreed on this order, stored so a later master change cannot rewrite it. */
  taxPercent: number;
  taxableAmount: number;
  taxAmount: number;
  netAmount: number;
  expenseAccountId: string | null;
  expenseAccountNumber: string | null;
  costCenterId: string | null;
  costCenterCode: string | null;
}

export interface PurchaseOrder {
  id: string;
  orderNumber: string;
  orderDate: string;
  supplierId: string;
  supplierCode: string | null;
  supplierName: string | null;
  supplierNameAr: string | null;
  currencyId: string;
  currencyCode: string | null;
  exchangeRate: number;
  expectedDate: string | null;
  paymentTermDays: number | null;
  costCenterId: string | null;
  costCenterCode: string | null;
  branchId: string;
  branchName: string | null;
  supplierReference: string | null;
  referenceNumber: string | null;
  notes: string | null;
  isTaxInclusive: boolean;
  status: PurchaseOrderStatus;
  subtotal: number;
  discountTotal: number;
  taxTotal: number;
  netTotal: number;
  /** Derived from invoices naming this order. Never stored. */
  invoicedAmount: number;
  /** Derived from the settlement engine. Never stored. */
  paidAmount: number;
  remainingAmount: number;
  closureReason: string | null;
  cancellationReason: string | null;
  createdAt: string;
  createdByName: string | null;
  lines: PurchaseOrderLine[];
}

export interface PurchaseOrderList {
  rows: PurchaseOrder[];
  totalCount: number;
  totalCommitted: number;
  totalPaid: number;
  totalRemaining: number;
}

export interface PurchaseOrderLineInput {
  itemId?: string;
  description: string;
  unit?: string;
  quantity: number;
  unitPrice: number;
  discountAmount: number;
  taxTypeId?: string;
  expenseAccountId?: string;
  costCenterId?: string;
}

export interface SavePurchaseOrderInput {
  id?: string;
  orderDate: string;
  supplierId: string;
  branchId: string;
  currencyId: string;
  exchangeRate: number;
  expectedDate?: string;
  paymentTermDays?: number;
  costCenterId?: string;
  supplierReference?: string;
  referenceNumber?: string;
  notes?: string;
  isTaxInclusive: boolean;
  lines: PurchaseOrderLineInput[];
}

// ═══════════════════════════════════════════════════════════════════════════════════════════════
// Payments › Reports — the ten that need a query of their own
//
// The rest of the Payments catalogue is answered by an approved inquiry screen, so those reports
// have no types here: they render the inquiry and use its contract.
// ═══════════════════════════════════════════════════════════════════════════════════════════════

export const ORDER_SIDE = { Receipt: 1, Payment: 2 } as const;
export type OrderSide = (typeof ORDER_SIDE)[keyof typeof ORDER_SIDE];

export interface SupplierBlacklistFilter extends PaymentsInquiryFilter {
  supplierId?: string;
  includeAllowed?: boolean;
  isActive?: boolean;
  search?: string;
}

export interface SupplierBlacklistRow {
  supplierId: string;
  code: string;
  name: string;
  nameAr: string | null;
  isBlacklisted: boolean;
  /** Shown verbatim; never summarised or inferred. */
  blacklistReason: string | null;
  isActive: boolean;
  controlAccountNumber: string | null;
  phone: string | null;
  email: string | null;
  creditLimit: number;
  sourceRoute: string | null;
}

export interface SupplierBlacklistResult extends PaymentsInquiryResult {
  rows: SupplierBlacklistRow[];
  blacklistedCount: number;
  allowedCount: number;
}

export interface ExpiringDocumentsFilter extends PaymentsInquiryFilter {
  partnerId?: string;
  partyType?: number;
  withinDays?: number;
  includeExpired?: boolean;
  asOfDate?: string;
  documentType?: string;
  search?: string;
}

export interface ExpiringDocumentRow {
  id: string;
  partnerId: string;
  partnerCode: string | null;
  partnerName: string | null;
  partnerNameAr: string | null;
  partyType: number;
  documentType: string;
  documentNumber: string | null;
  issuingAuthority: string | null;
  issueDate: string | null;
  expiryDate: string | null;
  /** Negative once expired. Null when the document never expires. */
  daysToExpiry: number | null;
  isExpired: boolean;
  notes: string | null;
  sourceRoute: string | null;
}

export interface ExpiringDocumentsResult extends PaymentsInquiryResult {
  rows: ExpiringDocumentRow[];
  expiredCount: number;
  expiringSoonCount: number;
  asOfDate: string;
  withinDays: number;
}

export interface PartnerInfoChangesFilter extends PaymentsInquiryFilter {
  partnerId?: string;
  partyType?: number;
  dateFrom?: string;
  dateTo?: string;
  userName?: string;
  search?: string;
}

export interface PartnerInfoChangeRow {
  id: string;
  occurredAt: string;
  action: string;
  partnerId: string | null;
  partnerCode: string | null;
  partnerName: string | null;
  partnerNameAr: string | null;
  partyType: number | null;
  userName: string | null;
  affectedColumns: string | null;
  oldValues: string | null;
  newValues: string | null;
  sourceRoute: string | null;
}

export interface PartnerInfoChangesResult extends PaymentsInquiryResult {
  rows: PartnerInfoChangeRow[];
  createdCount: number;
  updatedCount: number;
  partnerCount: number;
}

export interface SupplierAgeingFilter extends PaymentsInquiryFilter {
  supplierId?: string;
  asOfDate?: string;
  currencyId?: string;
  branchId?: string;
  minimumOutstanding?: number;
  includeZeroBalances?: boolean;
  ageByDueDate?: boolean;
  byCurrency?: boolean;
}

/** One configured period's amount on a supplier's row. */
export interface SupplierAgeingBucket {
  /** The configured period, or null for the Not Due and Unclassified columns. */
  periodId: string | null;
  sequence: number;
  /** Stable key: the period id, "not-due" or "unclassified". Rows match columns by this. */
  key: string;
  /** Built from the range — الفترات الزمنية لعمر الذمة stores no name. */
  label: string;
  labelAr: string;
  fromDay: number | null;
  toDay: number | null;
  amount: number;
  documentCount: number;
}

export interface SupplierAgeingRow {
  supplierId: string;
  supplierCode: string | null;
  supplierName: string | null;
  supplierNameAr: string | null;
  currencyCode: string | null;
  /** One entry per configured column, in order. Σ(amounts) === total, always. */
  buckets: SupplierAgeingBucket[];
  total: number;
  documentCount: number;
  oldestDocumentDate: string | null;
  sourceRoute: string | null;
}

/**
 * The ordered ageing columns, exactly as the settings define them.
 *
 * The screen renders from THIS list and knows nothing about how many buckets there are or what
 * they are called — which is what makes changing the settings change the report.
 */
export interface SupplierAgeingColumn {
  periodId: string | null;
  sequence: number;
  key: string;
  label: string;
  labelAr: string;
  fromDay: number | null;
  toDay: number | null;
  /** This column's total over the whole authorised filtered set. */
  total: number;
}

export interface SupplierAgeingResult extends PaymentsInquiryResult {
  rows: SupplierAgeingRow[];
  columns: SupplierAgeingColumn[];
  grandTotal: number;
  asOfDate: string;
  /** "DueDate" or "DocumentDate" — shown, never implied. */
  ageingBasis: string;
  byCurrency: boolean;
  supplierCount: number;
  /** False when no period is configured: the report then ages nothing and says so. */
  periodsConfigured: boolean;
}

export interface ShortTrialBalanceFilter extends PaymentsInquiryFilter {
  dateFrom?: string;
  dateTo?: string;
  partyType?: number;
  branchId?: string;
  currencyId?: string;
  includeZeroBalances?: boolean;
  search?: string;
}

export interface ShortTrialBalanceRow {
  partyId: string;
  partyType: number;
  partyCode: string | null;
  partyName: string | null;
  partyNameAr: string | null;
  controlAccountNumber: string | null;
  openingBalance: number;
  periodDebit: number;
  periodCredit: number;
  closingBalance: number;
  closingDebit: number;
  closingCredit: number;
  sourceRoute: string | null;
}

export interface ShortTrialBalanceResult extends PaymentsInquiryResult {
  rows: ShortTrialBalanceRow[];
  totalOpening: number;
  totalPeriodDebit: number;
  totalPeriodCredit: number;
  totalClosingDebit: number;
  totalClosingCredit: number;
  customerCount: number;
  supplierCount: number;
}

export interface ReceiptPaymentOrdersFilter extends PaymentsInquiryFilter {
  side?: OrderSide;
  partyId?: string;
  dateFrom?: string;
  dateTo?: string;
  branchId?: string;
  currencyId?: string;
  status?: number;
  reference?: string;
  outstandingOnly?: boolean;
}

export interface ReceiptPaymentOrderRow {
  id: string;
  side: OrderSide;
  orderNumber: string;
  orderDate: string;
  partyId: string;
  partyCode: string | null;
  partyName: string | null;
  partyNameAr: string | null;
  orderAmount: number;
  /** From POSTED vouchers on the matching side. Derived, never stored. */
  settledAmount: number;
  remainingAmount: number;
  currencyCode: string | null;
  status: number;
  branchName: string | null;
  referenceNumber: string | null;
  sourceRoute: string | null;
}

export interface ReceiptPaymentOrdersResult extends PaymentsInquiryResult {
  rows: ReceiptPaymentOrderRow[];
  totalOrdered: number;
  totalSettled: number;
  totalRemaining: number;
  receiptOrderCount: number;
  paymentOrderCount: number;
}

export interface GuaranteesFilter extends PaymentsInquiryFilter {
  bankId?: string;
  currencyId?: string;
  isActive?: boolean;
  includeAllFacilityTypes?: boolean;
  search?: string;
}

export interface GuaranteeRow {
  id: string;
  code: string;
  facilityType: number;
  bankName: string | null;
  currencyCode: string | null;
  facilityCeiling: number;
  interestRatePercent: number;
  isActive: boolean;
  isExternalParty: boolean;
  ceilingExceededAllowed: boolean;
  sourceRoute: string | null;
}

export interface GuaranteesResult extends PaymentsInquiryResult {
  rows: GuaranteeRow[];
  totalCeiling: number;
  activeCount: number;
}

export interface PurchaseInvoicesByTypeFilter extends PaymentsInquiryFilter {
  supplierId?: string;
  dateFrom?: string;
  dateTo?: string;
  itemId?: string;
  itemType?: PurchaseItemType;
  expenseAccountId?: string;
  costCenterId?: string;
  branchId?: string;
  includeUncatalogued?: boolean;
  /** "item" or "account" — what was bought, versus where it landed. */
  groupBy?: string;
}

export interface PurchaseInvoiceTypeRow {
  groupKey: string;
  groupLabel: string;
  groupLabelAr: string | null;
  itemId: string | null;
  itemType: number | null;
  expenseAccountId: string | null;
  expenseAccountNumber: string | null;
  invoiceCount: number;
  lineCount: number;
  quantity: number;
  netAmount: number;
  taxAmount: number;
  totalAmount: number;
  sharePercent: number;
}

export interface PurchaseInvoicesByTypeResult extends PaymentsInquiryResult {
  rows: PurchaseInvoiceTypeRow[];
  totalNet: number;
  totalTax: number;
  grandTotal: number;
  totalQuantity: number;
  invoiceCount: number;
  groupedBy: string;
}

export interface CapitalInvoicesFilter extends PaymentsInquiryFilter {
  supplierId?: string;
  dateFrom?: string;
  dateTo?: string;
  accountId?: string;
  branchId?: string;
  minimumAmount?: number;
}

export interface CapitalInvoiceRow {
  invoiceId: string;
  lineId: string;
  invoiceNumber: string;
  invoiceDate: string;
  supplierId: string;
  supplierCode: string | null;
  supplierName: string | null;
  supplierNameAr: string | null;
  description: string;
  quantity: number;
  netAmount: number;
  totalAmount: number;
  /** The ASSET account the line posted to — what makes it capital. */
  accountId: string;
  accountNumber: string | null;
  accountName: string | null;
  costCenterCode: string | null;
  branchName: string | null;
  currencyCode: string | null;
  sourceRoute: string | null;
}

export interface CapitalInvoicesResult extends PaymentsInquiryResult {
  rows: CapitalInvoiceRow[];
  totalNet: number;
  totalAmount: number;
  invoiceCount: number;
}

export interface SupplierByAccountFilter extends PaymentsInquiryFilter {
  supplierId?: string;
  dateFrom?: string;
  dateTo?: string;
  accountId?: string;
  branchId?: string;
  currencyId?: string;
  includeReversed?: boolean;
  bySupplier?: boolean;
}

export interface SupplierByAccountRow {
  accountId: string;
  accountNumber: string;
  accountName: string;
  accountNameAr: string | null;
  accountType: number;
  supplierId: string | null;
  supplierCode: string | null;
  supplierName: string | null;
  supplierNameAr: string | null;
  debit: number;
  credit: number;
  net: number;
  movementCount: number;
  sourceRoute: string | null;
}

export interface SupplierByAccountResult extends PaymentsInquiryResult {
  rows: SupplierByAccountRow[];
  totalDebit: number;
  totalCredit: number;
  netMovement: number;
  accountCount: number;
  supplierCount: number;
}

// ── Payments settings: the two that needed a domain ──────────────────────────

export interface ReportSignature {
  id: string;
  code: string;
  /** The ROLE printed above the line. */
  title: string;
  titleAr: string;
  signatoryName: string | null;
  signatoryNameAr: string | null;
  /** Which module's reports carry it, or null for every module. */
  module: string | null;
  displayOrder: number;
  isActive: boolean;
  notes: string | null;
}

export interface ReportSignatureList {
  rows: ReportSignature[];
  totalCount: number;
  activeCount: number;
}

export interface SaveReportSignatureInput {
  id?: string;
  code: string;
  title: string;
  titleAr: string;
  signatoryName?: string;
  signatoryNameAr?: string;
  module?: string;
  displayOrder: number;
  isActive: boolean;
  notes?: string;
}

export interface SupplierEvaluationCriterion {
  id: string;
  code: string;
  name: string;
  nameAr: string;
  weight: number;
  maximumScore: number;
  displayOrder: number;
  isActive: boolean;
  description: string | null;
  descriptionAr: string | null;
}

export interface SupplierEvaluationCriteriaList {
  rows: SupplierEvaluationCriterion[];
  totalCount: number;
  activeCount: number;
  /** Σ of the ACTIVE weights. Shown, never enforced — see the screen's note. */
  totalActiveWeight: number;
}

export interface SaveSupplierEvaluationCriterionInput {
  id?: string;
  code: string;
  name: string;
  nameAr: string;
  weight: number;
  maximumScore: number;
  displayOrder: number;
  isActive: boolean;
  description?: string;
  descriptionAr?: string;
}

/* ──────────────────────────────────────────────────────────────────────────────────────────────
 * Cash & Banks › Daily Transactions › the cheque desk.
 *
 * ONE row shape for every cheque screen, mirroring the server's own. The screens differ in which
 * cheques they ask for and what they let you do next, not in what a cheque is.
 * ────────────────────────────────────────────────────────────────────────────────────────────── */

/** Which way the money travels. Fixed when a cheque is created and never edited. */
export const CHEQUE_DIRECTION = {
  /** شيك مقبوض - received from a customer. */
  Incoming: 1,
  /** شيك مدفوع - written by us. */
  Outgoing: 2,
} as const;

export type ChequeDirection = (typeof CHEQUE_DIRECTION)[keyof typeof CHEQUE_DIRECTION];

/** فوري أم آجل. Stored rather than derived from the dates - it is a business fact, not arithmetic. */
export const CHEQUE_TERM = {
  Immediate: 1,
  PostDated: 2,
} as const;

export type ChequeTerm = (typeof CHEQUE_TERM)[keyof typeof CHEQUE_TERM];


/** What happened to a cheque, named for what a person did rather than for a status change. */
export const CHEQUE_MOVEMENT_TYPE = {
  Received: 1,
  DepositedForCollection: 2,
  Collected: 3,
  WithdrawnFromCollection: 4,
  ReturnedByBank: 5,
  ReturnedChequeProcessed: 6,
  DepositedImmediately: 7,
  Issued: 8,
  Delivered: 9,
  Paid: 10,
  ReturnedToUs: 11,
  ReturnedPaidChequeProcessed: 12,
  Cancelled: 13,
  DueDateChanged: 14,
  WithdrawnFromChequeBox: 15,
  HandedBackToDrawer: 16,
} as const;

export type ChequeMovementType =
  (typeof CHEQUE_MOVEMENT_TYPE)[keyof typeof CHEQUE_MOVEMENT_TYPE];

/** What a returned RECEIVED cheque's owner decided to do about it. */
export const RETURNED_CHEQUE_ACTION = {
  /** إعادة تقديم - back into collection. Posts. */
  Represent: 1,
  /** تحويل للقضايا - onto the legal account. Posts. */
  TransferToLegal: 2,
  /** تسليم للعميل - the paper goes back. Posts nothing; they already owe it. */
  HandBack: 3,
} as const;

export type ReturnedChequeAction =
  (typeof RETURNED_CHEQUE_ACTION)[keyof typeof RETURNED_CHEQUE_ACTION];

/** What to do with one of OUR cheques that came back. */
export const RETURNED_OUTGOING_CHEQUE_ACTION = {
  /** إعادة التسليم - hand it over again. Re-posts the issue entry. */
  Redeliver: 1,
  /** الإلغاء - void it. Posts nothing; the return already reversed it. */
  Cancel: 2,
} as const;

export type ReturnedOutgoingChequeAction =
  (typeof RETURNED_OUTGOING_CHEQUE_ACTION)[keyof typeof RETURNED_OUTGOING_CHEQUE_ACTION];

/** Where a blank leaf is. About the PAPER, not about any cheque written on it. */
export const CHEQUE_LEAF_STATUS = {
  Unused: 1,
  Withdrawn: 2,
  Used: 3,
  Void: 4,
} as const;

export type ChequeLeafStatus = (typeof CHEQUE_LEAF_STATUS)[keyof typeof CHEQUE_LEAF_STATUS];

export interface ChequeDeskRow {
  id: string;
  chequeNumber: string;
  direction: ChequeDirection;
  term: ChequeTerm;
  status: ChequeStatus;
  chequeDate: string;
  dueDate: string;
  amount: number;
  currencyId: string;
  currencyCode: string | null;
  /** الساحب أو المستفيد - whichever end of the cheque is not us. */
  partyId: string | null;
  partyCode: string | null;
  partyName: string | null;
  partyNameAr: string | null;
  drawerBankId: string;
  bankName: string | null;
  bankNameAr: string | null;
  bankAccountId: string | null;
  bankAccountName: string | null;
  clearedToBankAccountId: string | null;
  clearedToBankAccountName: string | null;
  clearedDate: string | null;
  cashboxId: string | null;
  cashboxName: string | null;
  cashboxNameAr: string | null;
  deliveredDate: string | null;
  deliveredTo: string | null;
  returnedDate: string | null;
  returnReason: string | null;
  cancelledDate: string | null;
  cancellationReason: string | null;
  receiptVoucherId: string | null;
  paymentVoucherId: string | null;
  settlementJournalVoucherId: string | null;
  settlementVoucherNumber: string | null;
  chequeBookLeafId: string | null;
  /** Days past due today. Negative before it falls due, null once its life is over. */
  daysOverdue: number | null;
  notes: string | null;
}

/** A total per currency. Cheques in three currencies do not add up to one number. */
export interface ChequeCurrencyTotal {
  currencyId: string;
  currencyCode: string | null;
  amount: number;
  count: number;
}

export interface ChequeDeskList {
  rows: ChequeDeskRow[];
  totalCount: number;
  totals: ChequeCurrencyTotal[];
}

export interface ChequeDeskFilter {
  direction?: ChequeDirection;
  term?: ChequeTerm;
  statuses?: ChequeStatus[];
  partyId?: string;
  bankAccountId?: string;
  cashboxId?: string;
  currencyId?: string;
  dueFrom?: string;
  dueTo?: string;
  /** تاريخ الشيك — the date written on the paper, which is not its due date. */
  chequeDateFrom?: string;
  chequeDateTo?: string;
  /** Compared as text, because a cheque number may carry leading zeros or a prefix. */
  chequeNumberFrom?: string;
  chequeNumberTo?: string;
  amountFrom?: number;
  amountTo?: number;
  /** البنك المسحوب عليه. */
  drawerBankId?: string;
  search?: string;
}

/**
 * A slip: several cheques lodged with, or collected from, a bank in one trip.
 *
 * NOT a table of its own. A batch is the set of cheque movements that share a reference, which is
 * why it is addressed by that reference rather than by an id.
 */
/**
 * Every operation a cheque screen performs on cheques that already exist.
 *
 * Mirrors the server enum. This is the ONLY thing the thirteen cheque screens differ by.
 */
export const CHEQUE_BATCH_ACTION = {
  DepositForCollection: 1,
  DepositImmediately: 2,
  Collect: 3,
  WithdrawFromCollection: 4,
  ReturnUnpaid: 5,
  HandBackToDrawer: 6,
  ProcessReturn: 7,
  Deliver: 8,
  SettleOutgoing: 9,
  ReturnIssued: 10,
  ProcessIssuedReturn: 11,
  Cancel: 12,
  ChangeDueDate: 13,
} as const;

export type ChequeBatchAction =
  (typeof CHEQUE_BATCH_ACTION)[keyof typeof CHEQUE_BATCH_ACTION];

export interface ChequeBatchRequest {
  action: ChequeBatchAction;
  chequeIds: string[];
  bankAccountId?: string | null;
  date?: string | null;
  reference: string;
  notes?: string | null;
  /** سبب — cancellation, a return, or the reason a due date moved. */
  reason?: string | null;
  returnReasonId?: string | null;
  /** Who took the paper, on a delivery. */
  deliveredTo?: string | null;
  newDueDate?: string | null;
  /** Which processing was chosen, on either returned-cheque screen. */
  choice?: number;
}

export interface ChequeBatchLine {
  chequeId: string;
  chequeNumber: string;
  amount: number;
  currencyCode: string | null;
  fromStatus: ChequeStatus;
  toStatus: ChequeStatus;
  /** Null on a deposit for collection, which posts nothing. */
  journalVoucherId: string | null;
  journalVoucherNumber: string | null;
}

export interface ChequeBatchResult {
  reference: string;
  date: string;
  chequeCount: number;
  totalAmount: number;
  lines: ChequeBatchLine[];
}

export interface ChequeBatchSummary {
  reference: string;
  movementType: ChequeMovementType;
  date: string;
  chequeCount: number;
  totalAmount: number;
  currencyCode: string | null;
  bankAccountId: string | null;
  bankAccountName: string | null;
  notes: string | null;
  postedCount: number;
}

export interface ChequeBatchList {
  rows: ChequeBatchSummary[];
  totalCount: number;
}

export interface ChequeBatchDetailLine {
  movementId: string;
  chequeId: string;
  chequeNumber: string;
  chequeDate: string;
  dueDate: string;
  amount: number;
  currencyCode: string | null;
  partyName: string | null;
  partyNameAr: string | null;
  drawerBankName: string | null;
  drawerBankNameAr: string | null;
  cashboxName: string | null;
  cashboxNameAr: string | null;
  fromStatus: ChequeStatus;
  toStatus: ChequeStatus;
  /** The cheque's status NOW, which a later event may have changed. */
  currentStatus: ChequeStatus;
  journalVoucherId: string | null;
  journalVoucherNumber: string | null;
}

export interface ChequeBatchDetail {
  reference: string;
  movementType: ChequeMovementType;
  date: string;
  bankAccountId: string | null;
  bankAccountName: string | null;
  notes: string | null;
  totalAmount: number;
  lines: ChequeBatchDetailLine[];
}

export interface ChequeMovementRow {
  id: string;
  chequeId: string;
  chequeNumber: string;
  direction: ChequeDirection;
  movementType: ChequeMovementType;
  movementDate: string;
  fromStatus: ChequeStatus;
  toStatus: ChequeStatus;
  amount: number;
  currencyId: string | null;
  currencyCode: string | null;
  partyName: string | null;
  partyNameAr: string | null;
  journalVoucherId: string | null;
  journalVoucherNumber: string | null;
  bankAccountId: string | null;
  bankAccountName: string | null;
  reference: string | null;
  notes: string | null;
}

export interface ChequeMovementList {
  rows: ChequeMovementRow[];
  totalCount: number;
}

export interface IssueChequeInput {
  chequeNumber: string;
  bankAccountId: string;
  beneficiaryPartyId: string;
  term: ChequeTerm;
  chequeDate: string;
  dueDate?: string | null;
  amount: number;
  currencyId: string;
  chequeBookLeafId?: string | null;
  paymentVoucherId?: string | null;
  notes?: string | null;
}

export interface ChequeBookLeaf {
  id: string;
  chequeBookId: string;
  chequeNumber: string;
  sequence: number | null;
  status: ChequeLeafStatus;
  chequeId: string | null;
  withdrawnDate: string | null;
  withdrawnBy: string | null;
  notes: string | null;
  bookNumber?: string | null;
}

export interface ChequeBookLeafList {
  rows: ChequeBookLeaf[];
  totalCount: number;
}

export interface ChequeBook {
  id: string;
  bankAccountId: string;
  bankAccountName: string | null;
  bankName: string | null;
  bankNameAr: string | null;
  bookNumber: string | null;
  fromChequeNumber: string;
  toChequeNumber: string;
  leafCount: number;
  receivedDate: string;
  cashboxId: string | null;
  cashboxName: string | null;
  cashboxNameAr: string | null;
  isActive: boolean;
  notes: string | null;
  leaves: ChequeBookLeaf[];
  unusedCount: number;
  withdrawnCount: number;
  usedCount: number;
  voidCount: number;
}

export interface ChequeBookList {
  rows: ChequeBook[];
  totalCount: number;
}

export interface SaveChequeBookInput {
  id?: string | null;
  bankAccountId: string;
  bookNumber?: string | null;
  fromChequeNumber: string;
  toChequeNumber: string;
  receivedDate: string;
  cashboxId?: string | null;
  isActive: boolean;
  notes?: string | null;
}

/* ── Bank movements ─────────────────────────────────────────────────────────────────────────── */

/** What kind of bank movement it is. Closed, because each entry implies a different side. */
export const BANK_TRANSACTION_KIND = {
  /** عمولات ومصاريف بنكية */
  Charges: 1,
  /** فوائد دائنة */
  InterestEarned: 2,
  /** فوائد مدينة */
  InterestCharged: 3,
  OtherCredit: 4,
  OtherDebit: 5,
} as const;

export type BankTransactionKind =
  (typeof BANK_TRANSACTION_KIND)[keyof typeof BANK_TRANSACTION_KIND];

/** Which way the bank ACCOUNT moved, stated from our books' point of view — not the bank's. */
export const BANK_TRANSACTION_DIRECTION = {
  Increase: 1,
  Decrease: 2,
} as const;

export type BankTransactionDirection =
  (typeof BANK_TRANSACTION_DIRECTION)[keyof typeof BANK_TRANSACTION_DIRECTION];

export interface BankTransaction {
  id: string;
  journalVoucherId: string;
  voucherNumber: string;
  status: JournalStatus;
  documentDate: string;
  branchId: string;
  description: string | null;
  transactionKind: BankTransactionKind;
  direction: BankTransactionDirection;
  bankAccountId: string;
  bankAccountName: string | null;
  bankName: string | null;
  bankNameAr: string | null;
  counterAccountId: string;
  counterAccountNumber: string | null;
  counterAccountName: string | null;
  counterAccountNameAr: string | null;
  costCenterId: string | null;
  costCenterName: string | null;
  bankFacilityId: string | null;
  bankFacilityCode: string | null;
  currencyId: string;
  currencyCode: string | null;
  amount: number;
  exchangeRate: number;
  localAmount: number;
  bankReference: string | null;
  valueDate: string | null;
  notes: string | null;
  totalDebit: number;
  totalCredit: number;
}

export interface BankTransactionList {
  rows: BankTransaction[];
  totalCount: number;
}

export interface SaveBankTransactionInput {
  id?: string | null;
  documentDate: string;
  branchId: string;
  description?: string | null;
  transactionKind: BankTransactionKind;
  direction: BankTransactionDirection;
  bankAccountId: string;
  counterAccountId: string;
  costCenterId?: string | null;
  bankFacilityId?: string | null;
  currencyId: string;
  amount: number;
  exchangeRate: number;
  bankReference?: string | null;
  valueDate?: string | null;
  notes?: string | null;
}

export interface BankTransfer {
  id: string;
  journalVoucherId: string;
  voucherNumber: string;
  status: JournalStatus;
  documentDate: string;
  branchId: string;
  description: string | null;
  fromBankAccountId: string;
  fromBankAccountName: string | null;
  fromBankName: string | null;
  fromBankNameAr: string | null;
  fromCurrencyId: string;
  fromCurrencyCode: string | null;
  amount: number;
  fromExchangeRate: number;
  toBankAccountId: string;
  toBankAccountName: string | null;
  toBankName: string | null;
  toBankNameAr: string | null;
  toCurrencyId: string;
  toCurrencyCode: string | null;
  receivedAmount: number;
  toExchangeRate: number;
  feeAmount: number;
  feeAccountId: string | null;
  feeAccountName: string | null;
  feeCostCenterId: string | null;
  feeBorneBySource: boolean;
  bankReference: string | null;
  notes: string | null;
  totalDebit: number;
  totalCredit: number;
}

export interface BankTransferList {
  rows: BankTransfer[];
  totalCount: number;
}

export interface SaveBankTransferInput {
  id?: string | null;
  documentDate: string;
  branchId: string;
  description?: string | null;
  fromBankAccountId: string;
  amount: number;
  fromExchangeRate: number;
  toBankAccountId: string;
  receivedAmount: number;
  toExchangeRate: number;
  feeAmount: number;
  feeAccountId?: string | null;
  feeCostCenterId?: string | null;
  feeBorneBySource: boolean;
  bankReference?: string | null;
  notes?: string | null;
}

/* ── Bank reconciliation ────────────────────────────────────────────────────────────────────── */

export const BANK_RECONCILIATION_STATUS = {
  Draft: 1,
  /** مكتملة - agreed and closed. Read-only from here on. */
  Completed: 2,
} as const;

export type BankReconciliationStatus =
  (typeof BANK_RECONCILIATION_STATUS)[keyof typeof BANK_RECONCILIATION_STATUS];

/** Where a reconciliation row came from. */
export const BANK_RECONCILIATION_LINE_SOURCE = {
  /** Our ledger - read from posted movements, never typed. */
  Ledger: 1,
  /** The bank's statement, typed or imported. */
  Statement: 2,
} as const;

export type BankReconciliationLineSource =
  (typeof BANK_RECONCILIATION_LINE_SOURCE)[keyof typeof BANK_RECONCILIATION_LINE_SOURCE];


// ─── المخزون والمستودعات › الإعدادات ──────────────────────────────────────────────────────────

/** One method is implemented, and the enum says only that. */
export const COSTING_METHOD = { MovingWeightedAverage: 1 } as const;
export type CostingMethod = (typeof COSTING_METHOD)[keyof typeof COSTING_METHOD];

/**
 * Every inventory setting, as the screen reads and writes it.
 *
 * The shape is flat and matches the server DTO field for field: a settings screen that reshapes
 * its payload is a screen that can disagree with what was saved.
 */
export interface InventorySettings {
  allowNegativeStock: boolean;
  allowFractionalQuantity: boolean;
  quantityPrecision: number;
  costPrecision: number;
  defaultWarehouseId: string | null;
  defaultWarehouseName: string | null;
  blockInactiveItems: boolean;
  blockInactiveWarehouses: boolean;

  costingMethod: CostingMethod;
  allowBackdatedTransactions: boolean;
  allowBackdatedAfterMovements: boolean;

  freezeMovementsDuringCount: boolean;
  showCostDuringCount: boolean;
  createAdjustmentFromApprovedCount: boolean;

  requireSupplierInvoiceNumber: boolean;
  requireSupplierInvoiceDate: boolean;
  defaultPurchaseWarehouseId: string | null;
  defaultPurchaseWarehouseName: string | null;
  preventDuplicateSupplierInvoice: boolean;
  requirePurchaseOrderReference: boolean;
  allowPurchaseWithoutOrder: boolean;
  allowPurchaseQuantityAboveOrder: boolean;
  purchaseQuantityTolerancePercent: number;

  requireCostCenterOnIssue: boolean;
  requireStorageLocation: boolean;
  requireIssueReason: boolean;
  allowDirectIssue: boolean;

  requireTransferApproval: boolean;
  requireTransferReceipt: boolean;

  /** False until the company saves once — the screen says so rather than implying a choice. */
  hasBeenConfigured: boolean;

  /** Whether two-step transfer can be switched on at all. False until transit exists. */
  transferReceiptSupported: boolean;
}

export type SaveInventorySettingsInput = Omit<
  InventorySettings,
  "defaultWarehouseName" | "defaultPurchaseWarehouseName" | "hasBeenConfigured"
  | "transferReceiptSupported" | "costingMethod"
>;

/** One thing that must be configured, and whether it genuinely is. */
export interface InventoryConfigurationCheckRow {
  key: string;
  nameAr: string;
  name: string;
  isComplete: boolean;
  detail: string | null;
  /** True when a posting is BLOCKED without it. */
  blocksPosting: boolean;
}

export interface InventoryConfigurationStatus {
  rows: InventoryConfigurationCheckRow[];
  canPost: boolean;
  completeCount: number;
  totalCount: number;
}


// ─── المخزون والمستودعات › البطاقات التعريفية ────────────────────────────────────────────────

export const ITEM_TYPE = { Stock: 1, NonStock: 2, Service: 3 } as const;
export type ItemType = (typeof ITEM_TYPE)[keyof typeof ITEM_TYPE];

export const ADJUSTMENT_DIRECTION = { Both: 0, Increase: 1, Decrease: 2 } as const;
export type AdjustmentDirection =
  (typeof ADJUSTMENT_DIRECTION)[keyof typeof ADJUSTMENT_DIRECTION];

export const REASON_SCOPE = { Adjustment: 1, Issue: 2, Receipt: 3, Any: 9 } as const;
export type ReasonScope = (typeof REASON_SCOPE)[keyof typeof REASON_SCOPE];

export interface UnitOfMeasure {
  id: string; code: string; name: string; nameAr: string | null;
  decimalAllowed: boolean; isActive: boolean; notes: string | null;
  /** How many items are based on it — what makes the delete guard explicable. */
  itemCount: number;
}

export interface InventoryWarehouse {
  id: string; code: string; name: string; nameAr: string | null;
  branchId: string | null; branchName: string | null;
  custodianUserId: string | null; custodianName: string | null;
  inventoryAccountId: string | null; inventoryAccountLabel: string | null;
  isActive: boolean; notes: string | null;
  /** True once stock has moved here. Drives the delete guard. */
  hasMovements: boolean;
}

/**
 * A branch a warehouse may be placed in. Read from the one branch register — the same rows the
 * journal screen offers — but fetched with the warehouse card's own right, so an inventory-only
 * user is not handed an empty picker.
 */
export const PRICE_LIST_TYPE = { Consumer: 1, Wholesale: 2, Other: 3 } as const;
export type PriceListType = (typeof PRICE_LIST_TYPE)[keyof typeof PRICE_LIST_TYPE];

/** قائمة أسعار — a named set of SELLING prices. Never inventory cost. */
export interface PriceList {
  id: string; code: string; name: string; nameAr: string | null;
  priceListType: PriceListType;
  currencyId: string | null; currencyCode: string | null;
  isActive: boolean; notes: string | null;
  pricedItemCount: number;
}

/** One price, for one item, on one list, over one period. Effective-dated, never overwritten. */
export interface ItemPrice {
  id: string; itemId: string;
  priceListId: string; priceListCode: string; priceListName: string; priceListType: PriceListType;
  unitId: string | null; unitCode: string | null;
  price: number; currencyCode: string | null;
  effectiveFrom: string; effectiveTo: string | null;
  isActive: boolean; notes: string | null;
}

/** That an item can be bought from a supplier, and on what terms. */
export interface ItemSupplierLink {
  id: string; itemId: string;
  supplierId: string; supplierCode: string; supplierName: string;
  supplierItemCode: string | null;
  isPreferred: boolean;
  leadTimeDays: number | null;
  minimumOrderQuantity: number | null;
  /** Informational. NOT the inventory cost — that comes from the ledger. */
  lastPurchasePrice: number | null;
  currencyId: string | null; currencyCode: string | null;
  isActive: boolean; notes: string | null;
}

/** A country or a purchase tax type, as the item card offers it. */
export interface ItemLookupOption {
  id: string; code: string; name: string; nameAr: string | null;
}

/** The two masters the item card points at but does not own. One call fills both pickers. */
export interface ItemCardLookups {
  countries: ItemLookupOption[];
  purchaseTaxTypes: ItemLookupOption[];
}

export interface InventoryBranchOption {
  id: string; code: string; name: string; nameAr: string | null; isMain: boolean;
}

export interface ItemGroup {
  id: string; code: string; name: string; nameAr: string | null;
  parentId: string | null; parentCode: string | null;
  path: string; level: number;
  isActive: boolean; notes: string | null; itemCount: number;
}

export interface ItemCategory {
  id: string; code: string; name: string; nameAr: string | null;
  /** Which of the three source classification columns this value belongs in. Not a tree. */
  level: number;
  isActive: boolean; notes: string | null; itemCount: number;
}

export interface StorageLocation {
  id: string; code: string; name: string; nameAr: string | null;
  warehouseId: string; warehouseCode: string | null;
  parentLocationId: string | null; path: string; level: number;
  isActive: boolean; notes: string | null;
}

export interface InventoryReason {
  id: string; code: string; name: string; nameAr: string | null;
  direction: AdjustmentDirection; scope: ReasonScope;
  requiresNotes: boolean; requiresApproval: boolean;
  accountId: string | null; accountLabel: string | null; isActive: boolean;
}

export interface ItemConversion {
  unitId: string; unitCode: string; factorToBase: number;
}

export interface InventoryItem {
  id: string; code: string; barcode: string | null;
  name: string; nameAr: string | null;
  itemGroupId: string | null; itemGroupName: string | null;
  itemCategoryId: string | null; itemCategoryName: string | null;
  itemCategory2Id: string | null; itemCategory2Name: string | null;
  itemCategory3Id: string | null; itemCategory3Name: string | null;
  baseUnitId: string; baseUnitCode: string | null;
  purchaseUnitId: string | null; salesUnitId: string | null;
  itemType: ItemType;
  /** Derived from itemType by the server; never sent back. */
  isStockItem: boolean;
  isPurchasable: boolean; isSellable: boolean;
  minimumStock: number; reorderLevel: number;
  inventoryAccountId: string | null;
  costOfGoodsSoldAccountId: string | null;
  inventoryAdjustmentAccountId: string | null;
  defaultWarehouseId: string | null; defaultWarehouseCode: string | null;
  purchaseItemId: string | null; purchaseTaxTypeId: string | null;
  purchaseTaxTypeName: string | null;
  /** بلد المنشأ — a row in the company's one country register, never free text. */
  countryOfOriginId: string | null; countryOfOriginName: string | null;
  isActive: boolean; notes: string | null;
  conversions: ItemConversion[];
  /** Read from the stock ledger. NEVER editable from the card. */
  onHandQuantity: number;
  onHandValue: number;
  hasMovements: boolean;
}

export interface BankReconciliationLine {
  id: string;
  source: BankReconciliationLineSource;
  lineDate: string;
  /** Positive money into the account, negative money out. One signed figure. */
  amount: number;
  journalVoucherLineId: string | null;
  voucherNumber: string | null;
  bankReference: string | null;
  chequeNumber: string | null;
  description: string | null;
  isMatched: boolean;
  reconcilingReason: string | null;

  /** WHAT the row ended as. The grid's colour and its «الحالة» column read this. */
  matchType: ReconciliationMatchType;
  matchedLineId: string | null;
  matchConfidence: ReconciliationMatchConfidence;
  /** Set when a person made the match rather than the engine. */
  matchedBy: string | null;
  matchedAt: string | null;
  statementFileId: string | null;
  statementRowNumber: number | null;
  valueDate: string | null;
  /** The original cells as read from the file, so a figure can be traced. */
  rawRow: string | null;
}

/** How a reconciliation row ended up. Mirrors the server enum. */
export const RECONCILIATION_MATCH_TYPE = {
  Unmatched: 0,
  Matched: 1,
  AmountDifference: 2,
  DateDifference: 3,
  ReferenceDifference: 4,
  ErpOnly: 5,
  StatementOnly: 6,
  DuplicateCandidate: 7,
  Ambiguous: 8,
} as const;

export type ReconciliationMatchType =
  (typeof RECONCILIATION_MATCH_TYPE)[keyof typeof RECONCILIATION_MATCH_TYPE];

export const RECONCILIATION_MATCH_CONFIDENCE = {
  None: 0,
  Exact: 1,
  Strong: 2,
  Possible: 3,
} as const;

export type ReconciliationMatchConfidence =
  (typeof RECONCILIATION_MATCH_CONFIDENCE)[keyof typeof RECONCILIATION_MATCH_CONFIDENCE];

/** Where an uploaded statement got to. */
export const STATEMENT_IMPORT_STATUS = {
  Pending: 1,
  Parsed: 2,
  Empty: 3,
  Failed: 4,
} as const;

export type StatementImportStatus =
  (typeof STATEMENT_IMPORT_STATUS)[keyof typeof STATEMENT_IMPORT_STATUS];

/** The ERP meanings a statement column can carry. */
export const STATEMENT_COLUMN = {
  Ignored: 0,
  TransactionDate: 1,
  ValueDate: 2,
  Reference: 3,
  ChequeNumber: 4,
  Description: 5,
  Debit: 6,
  Credit: 7,
  Amount: 8,
  Balance: 9,
} as const;

export type StatementColumn = (typeof STATEMENT_COLUMN)[keyof typeof STATEMENT_COLUMN];

/** An imported statement file, as the reconciliation lists it. */
export interface BankReconciliationFile {
  id: string;
  fileName: string;
  contentType: string | null;
  fileSizeBytes: number;
  uploadedAt: string;
  uploadedByName: string | null;
  parseStatus: StatementImportStatus;
  parseMessage: string | null;
  rowsParsed: number;
}

export interface StatementColumnMapping {
  caption: string;
  meaning: StatementColumn;
}

export interface BankStatementImport {
  fileId: string;
  fileName: string;
  contentType: string | null;
  fileSizeBytes: number;
  uploadedAt: string;
  parseStatus: StatementImportStatus;
  parseMessage: string | null;
  rowsParsed: number;
  columns: StatementColumnMapping[];
}

/** What one run of the matcher produced, counted by outcome. */
export interface BankReconciliationMatchSummary {
  statementRows: number;
  ledgerRows: number;
  matched: number;
  amountDifferences: number;
  dateDifferences: number;
  referenceDifferences: number;
  erpOnly: number;
  statementOnly: number;
  ambiguous: number;
  duplicateCandidates: number;
  statementTotal: number;
  ledgerTotal: number;
  difference: number;
}

export interface BankReconciliation {
  id: string;
  reconciliationNumber: string;
  bankAccountId: string;
  bankAccountName: string | null;
  bankName: string | null;
  bankNameAr: string | null;
  currencyCode: string | null;
  fromDate: string;
  toDate: string;
  statementDate: string;
  statementBalance: number;
  ledgerBalance: number;
  reconciledBalance: number;
  /** Left over once everything explained is accounted for. A finding, not an error. */
  difference: number;
  status: BankReconciliationStatus;
  completedAt: string | null;
  notes: string | null;
  lines: BankReconciliationLine[];
  /** كشف البنك — the statement files attached to this reconciliation. */
  files: BankReconciliationFile[];
  matchedTotal: number;
  outstandingDeposits: number;
  outstandingPayments: number;
  unrecordedStatementTotal: number;
}

export interface BankReconciliationList {
  rows: BankReconciliation[];
  totalCount: number;
}

export interface StatementLineInput {
  lineDate: string;
  amount: number;
  bankReference?: string | null;
  chequeNumber?: string | null;
  description?: string | null;
  isMatched: boolean;
  reconcilingReason?: string | null;
}

export interface SaveBankReconciliationInput {
  id?: string | null;
  reconciliationNumber: string;
  bankAccountId: string;
  fromDate: string;
  toDate: string;
  statementDate: string;
  statementBalance: number;
  notes?: string | null;
  statementLines: StatementLineInput[];
  matchedJournalVoucherLineIds: string[];
}

/* ── Letters of credit ──────────────────────────────────────────────────────────────────────── */

export const LETTER_OF_CREDIT_STATUS = {
  /** طلب - raised with us, not yet with the bank. */
  Requested: 1,
  /** مفتوح - the bank opened it and the margin is held. */
  Opened: 2,
  Settled: 3,
  Cancelled: 4,
} as const;

export type LetterOfCreditStatus =
  (typeof LETTER_OF_CREDIT_STATUS)[keyof typeof LETTER_OF_CREDIT_STATUS];

export interface LetterOfCredit {
  id: string;
  creditNumber: string;
  status: LetterOfCreditStatus;
  bankFacilityId: string;
  bankFacilityCode: string | null;
  bankId: string;
  bankName: string | null;
  bankNameAr: string | null;
  bankBranchId: string | null;
  bankBranchName: string | null;
  beneficiaryPartyId: string;
  beneficiaryCode: string | null;
  beneficiaryName: string | null;
  beneficiaryNameAr: string | null;
  currencyId: string;
  currencyCode: string | null;
  exchangeRate: number;
  amount: number;
  marginAmount: number;
  marginAccountId: string | null;
  marginAccountName: string | null;
  marginPaidFromBankAccountId: string | null;
  marginPaidFromBankAccountName: string | null;
  requestDate: string;
  openedDate: string | null;
  expiryDate: string | null;
  latestShipmentDate: string | null;
  settledDate: string | null;
  cancelledDate: string | null;
  goodsDescription: string | null;
  cancellationReason: string | null;
  notes: string | null;
  marginJournalVoucherId: string | null;
  marginVoucherNumber: string | null;
  settlementJournalVoucherId: string | null;
  settlementVoucherNumber: string | null;
  daysToExpiry: number | null;
}

export interface LetterOfCreditList {
  rows: LetterOfCredit[];
  totalCount: number;
}

export interface SaveLetterOfCreditInput {
  id?: string | null;
  creditNumber: string;
  bankFacilityId: string;
  bankId: string;
  bankBranchId?: string | null;
  beneficiaryPartyId: string;
  currencyId: string;
  exchangeRate: number;
  amount: number;
  marginAmount: number;
  requestDate: string;
  expiryDate?: string | null;
  latestShipmentDate?: string | null;
  goodsDescription?: string | null;
  notes?: string | null;
}

/* ── Bank loans ─────────────────────────────────────────────────────────────────────────────── */

export const LOAN_STATUS = {
  /** Recorded and scheduled, not yet drawn. Nothing has posted. */
  Draft: 1,
  /** مسحوب - the money arrived and the debt is on the books. */
  Active: 2,
  Settled: 3,
} as const;

export type LoanStatus = (typeof LOAN_STATUS)[keyof typeof LOAN_STATUS];


export interface BankLoanInstallment {
  id: string;
  sequenceNumber: number;
  dueDate: string;
  principalAmount: number;
  interestAmount: number;
  totalAmount: number;
  isPaid: boolean;
  paidDate: string | null;
  paidFromBankAccountId: string | null;
  paidFromBankAccountName: string | null;
  paymentJournalVoucherId: string | null;
  paymentVoucherNumber: string | null;
  daysOverdue: number | null;
  notes: string | null;
}

export interface BankLoan {
  id: string;
  loanNumber: string;
  status: LoanStatus;
  bankFacilityId: string;
  bankFacilityCode: string | null;
  bankAccountId: string;
  bankAccountName: string | null;
  bankName: string | null;
  bankNameAr: string | null;
  loanAccountId: string;
  loanAccountName: string | null;
  interestAccountId: string;
  interestAccountName: string | null;
  currencyId: string;
  currencyCode: string | null;
  exchangeRate: number;
  principalAmount: number;
  interestRatePercent: number;
  repaymentType: LoanRepaymentType;
  numberOfPayments: number;
  approvedYearDays: number;
  drawdownDate: string;
  firstInstallmentDate: string;
  drawdownJournalVoucherId: string | null;
  drawdownVoucherNumber: string | null;
  notes: string | null;
  installments: BankLoanInstallment[];
  totalPrincipalScheduled: number;
  totalInterestScheduled: number;
  outstandingPrincipal: number;
  paidPrincipal: number;
  paidInstallments: number;
}

export interface BankLoanList {
  rows: BankLoan[];
  totalCount: number;
}

export interface SaveBankLoanInput {
  id?: string | null;
  loanNumber: string;
  bankFacilityId: string;
  bankAccountId: string;
  interestAccountId: string;
  currencyId: string;
  exchangeRate: number;
  principalAmount: number;
  drawdownDate: string;
  firstInstallmentDate?: string | null;
  notes?: string | null;
}

/* ──────────────────────────────────────────────────────────────────────────────────────────────
 * Cash & Banks › Inquiries.
 *
 * Seventeen registered screens over seven query families. Every one is READ-ONLY: nothing in this
 * block has a save shape, because no inquiry writes.
 * ────────────────────────────────────────────────────────────────────────────────────────────── */

export interface CashBanksInquiryFilter extends PagedInquiryFilter {
  dateFrom?: string;
  dateTo?: string;
  branchId?: string;
  currencyId?: string;
  search?: string;
}

/** Which accounts a ledger-driven inquiry reads. */
export const LEDGER_SCOPE = {
  CashAndBank: 0,
  Cashboxes: 1,
  BankAccounts: 2,
} as const;

export type LedgerScope = (typeof LEDGER_SCOPE)[keyof typeof LEDGER_SCOPE];

export interface LedgerMovementRow {
  id: string;
  entryDate: string;
  voucherNumber: string;
  voucherKind: number;
  journalVoucherId: string;
  accountId: string;
  accountNumber: string;
  accountName: string;
  cashboxId: string | null;
  cashboxName: string | null;
  cashboxNameAr: string | null;
  bankAccountId: string | null;
  bankAccountName: string | null;
  branchId: string;
  branchName: string | null;
  costCenterId: string | null;
  costCenterName: string | null;
  currencyId: string;
  currencyCode: string;
  exchangeRate: number;
  debit: number;
  credit: number;
  localDebit: number;
  localCredit: number;
  /** Server-side, from the opening balance, in the query's own deterministic ordering. */
  runningBalance: number;
  description: string | null;
  referenceNumber: string | null;
  isReversal: boolean;
}

export interface CurrencyTotal {
  currencyId: string;
  currencyCode: string;
  debit: number;
  credit: number;
  localDebit: number;
  localCredit: number;
  balance: number;
  count: number;
}

export interface LedgerMovementsResult extends PagedInquiryResult {
  rows: LedgerMovementRow[];
  openingBalance: number;
  totalDebit: number;
  totalCredit: number;
  closingBalance: number;
  asOfBalance: number | null;
  currencyTotals: CurrencyTotal[];
}

export interface LedgerMovementsFilter extends CashBanksInquiryFilter {
  scope?: LedgerScope;
  bankAccountId?: string;
  cashboxId?: string;
  costCenterId?: string;
  asOfDate?: string;
}

export interface BankAccountCurrencyRow {
  bankAccountId: string;
  bankAccountName: string;
  bankName: string | null;
  bankNameAr: string | null;
  accountNumber: string | null;
  accountId: string;
  glAccountNumber: string;
  currencyId: string;
  currencyCode: string;
  debit: number;
  credit: number;
  balance: number;
  localDebit: number;
  localCredit: number;
  localBalance: number;
  movementCount: number;
}

export interface BankAccountCurrencyResult extends PagedInquiryResult {
  rows: BankAccountCurrencyRow[];
  /** The only figure across currencies that means anything. */
  totalLocalBalance: number;
  currencyTotals: CurrencyTotal[];
  asOfDate: string | null;
}

export interface BankAccountCurrencyFilter extends CashBanksInquiryFilter {
  bankId?: string;
  bankAccountId?: string;
  asOfDate?: string;
}

export interface CashboxMovementRow {
  id: string;
  journalVoucherId: string;
  voucherNumber: string;
  status: JournalStatus;
  documentDate: string;
  cashboxId: string;
  cashboxCode: string;
  cashboxName: string;
  cashboxNameAr: string;
  movementType: CashMovementType;
  direction: CashDirection;
  partyType: number;
  counterpartyName: string | null;
  counterpartyNameAr: string | null;
  currencyId: string;
  currencyCode: string | null;
  amount: number;
  exchangeRate: number;
  localAmount: number;
  signedLocalAmount: number;
  branchId: string;
  branchName: string | null;
  description: string | null;
}

export interface CashboxMovementsResult extends PagedInquiryResult {
  rows: CashboxMovementRow[];
  totalIn: number;
  totalOut: number;
  netMovement: number;
  currencyTotals: CurrencyTotal[];
}

export interface CashboxMovementsFilter extends CashBanksInquiryFilter {
  cashboxId?: string;
  movementType?: CashMovementType;
  direction?: CashDirection;
  status?: JournalStatus;
}

export interface CashboxClosingFollowUpRow {
  cashboxId: string;
  code: string;
  name: string;
  nameAr: string;
  isClosingCashbox: boolean;
  isActive: boolean;
  cashAccountId: string | null;
  cashAccountNumber: string | null;
  lastZeroingDate: string | null;
  lastZeroingVoucherNumber: string | null;
  zeroingCount: number;
  lastCountDate: string | null;
  countCount: number;
  /** From posted entries, not from a stored figure. */
  balanceAsOf: number;
  outstandingBalance: boolean;
}

export interface CashboxClosingFollowUpResult extends PagedInquiryResult {
  rows: CashboxClosingFollowUpRow[];
  closedCount: number;
  outstandingCount: number;
  totalOutstanding: number;
  asOfDate: string;
}

export interface CashboxClosingFollowUpFilter extends CashBanksInquiryFilter {
  cashboxId?: string;
  closingCashboxesOnly?: boolean;
  outstandingOnly?: boolean;
  asOfDate?: string;
}

/** Which of the cheque register inquiries is asking. Fixed per screen, server-side. */
export const CHEQUE_INQUIRY_VIEW = {
  Received: 1,
  Paid: 2,
  Delivered: 3,
  Undelivered: 4,
  UnderCollection: 5,
  Returned: 6,
} as const;

export type ChequeInquiryView = (typeof CHEQUE_INQUIRY_VIEW)[keyof typeof CHEQUE_INQUIRY_VIEW];

export interface ChequeInquiryRow {
  id: string;
  chequeNumber: string;
  direction: ChequeDirection;
  term: ChequeTerm;
  status: ChequeStatus;
  chequeDate: string;
  dueDate: string;
  amount: number;
  currencyId: string;
  currencyCode: string | null;
  partyId: string | null;
  partyCode: string | null;
  partyName: string | null;
  partyNameAr: string | null;
  drawerBankId: string;
  drawerBankName: string | null;
  drawerBankNameAr: string | null;
  bankAccountId: string | null;
  bankAccountName: string | null;
  clearedToBankAccountId: string | null;
  clearedToBankAccountName: string | null;
  clearedDate: string | null;
  cashboxId: string | null;
  cashboxName: string | null;
  cashboxNameAr: string | null;
  deliveredDate: string | null;
  deliveredTo: string | null;
  returnedDate: string | null;
  returnReason: string | null;
  cancelledDate: string | null;
  cancellationReason: string | null;
  receiptVoucherId: string | null;
  paymentVoucherId: string | null;
  sourceVoucherNumber: string | null;
  settlementJournalVoucherId: string | null;
  settlementVoucherNumber: string | null;
  depositedDate: string | null;
  daysUnderCollection: number | null;
  daysOverdue: number | null;
  movementCount: number;
  notes: string | null;
}

export interface ChequeInquiryResult extends PagedInquiryResult {
  rows: ChequeInquiryRow[];
  totalAmount: number;
  chequeCount: number;
  currencyTotals: CurrencyTotal[];
  asOfDate: string;
}

export interface ChequeInquiryFilter extends CashBanksInquiryFilter {
  view?: ChequeInquiryView;
  term?: ChequeTerm;
  status?: ChequeStatus;
  partyId?: string;
  drawerBankId?: string;
  bankAccountId?: string;
  cashboxId?: string;
  dueFrom?: string;
  dueTo?: string;
  asOfDate?: string;
}

export interface ChequeMovementInquiryRow {
  id: string;
  chequeId: string;
  chequeNumber: string;
  direction: ChequeDirection;
  term: ChequeTerm;
  movementType: ChequeMovementType;
  movementDate: string;
  fromStatus: ChequeStatus;
  toStatus: ChequeStatus;
  amount: number;
  currencyCode: string | null;
  partyName: string | null;
  partyNameAr: string | null;
  journalVoucherId: string | null;
  journalVoucherNumber: string | null;
  bankAccountId: string | null;
  bankAccountName: string | null;
  cashboxId: string | null;
  cashboxName: string | null;
  reference: string | null;
  notes: string | null;
  recordedAt: string;
  recordedBy: string | null;
}

export interface ChequeMovementInquiryResult extends PagedInquiryResult {
  rows: ChequeMovementInquiryRow[];
  totalAmount: number;
  postedCount: number;
  /** The events that posted nothing — lodging paper, delivering it. */
  operationalCount: number;
}

export interface ChequeMovementInquiryFilter extends CashBanksInquiryFilter {
  chequeId?: string;
  direction?: ChequeDirection;
  movementType?: ChequeMovementType;
  partyId?: string;
  bankAccountId?: string;
  cashboxId?: string;
  postedOnly?: boolean;
}

/** What kind of gap the sequence inquiry found. Never a claim that a cheque was written and lost. */
export const CHEQUE_SEQUENCE_GAP_KIND = {
  /** A leaf still unused while a higher-numbered leaf in the same book was written. */
  SkippedLeaf: 1,
  /** Numbers between two registered books that belong to no book. */
  UnregisteredRange: 2,
} as const;

export type ChequeSequenceGapKind =
  (typeof CHEQUE_SEQUENCE_GAP_KIND)[keyof typeof CHEQUE_SEQUENCE_GAP_KIND];

export interface ChequeSequenceGapRow {
  kind: ChequeSequenceGapKind;
  bankAccountId: string;
  bankAccountName: string;
  bankName: string | null;
  bankNameAr: string | null;
  chequeBookId: string | null;
  bookNumber: string | null;
  fromNumber: string;
  toNumber: string;
  count: number;
  leafStatus: ChequeLeafStatus | null;
  leafId: string | null;
  withdrawnDate: string | null;
  withdrawnBy: string | null;
}

export interface ChequeSequenceGapsResult extends PagedInquiryResult {
  rows: ChequeSequenceGapRow[];
  totalLeavesAffected: number;
  skippedLeafCount: number;
  unregisteredRangeCount: number;
}

export interface ChequeSequenceGapsFilter extends CashBanksInquiryFilter {
  bankAccountId?: string;
  chequeBookId?: string;
  kind?: ChequeSequenceGapKind;
}

export const CASH_BANKS_DOCUMENT_KIND = {
  CashDeposit: 1,
  CashWithdrawal: 2,
  CashboxTransaction: 3,
  BankTransaction: 4,
  BankTransfer: 5,
} as const;

export type CashBanksDocumentKind =
  (typeof CASH_BANKS_DOCUMENT_KIND)[keyof typeof CASH_BANKS_DOCUMENT_KIND];

export interface CashBanksDocumentRow {
  id: string;
  kind: CashBanksDocumentKind;
  journalVoucherId: string;
  voucherNumber: string;
  status: JournalStatus;
  documentDate: string;
  branchId: string;
  branchName: string | null;
  description: string | null;
  source: string | null;
  destination: string | null;
  amount: number;
  currencyId: string;
  currencyCode: string | null;
  localAmount: number;
  totalDebit: number;
  totalCredit: number;
  /** A real screen. Nothing invented for the inquiry's sake. */
  detailRoute: string;
}

export interface CashBanksDocumentsResult extends PagedInquiryResult {
  rows: CashBanksDocumentRow[];
  totalLocalAmount: number;
  postedCount: number;
  draftCount: number;
  currencyTotals: CurrencyTotal[];
}

export interface CashBanksDocumentsFilter extends CashBanksInquiryFilter {
  kind?: CashBanksDocumentKind;
  status?: JournalStatus;
  cashboxId?: string;
  bankAccountId?: string;
}

export interface LetterOfCreditInquiryRow {
  id: string;
  creditNumber: string;
  status: LetterOfCreditStatus;
  bankId: string;
  bankName: string | null;
  bankNameAr: string | null;
  bankFacilityId: string;
  facilityCode: string | null;
  facilityCeiling: number;
  /** The FACILITY's commitment across its live credits — not a per-credit drawdown. */
  facilityCommitted: number;
  facilityRemaining: number;
  beneficiaryPartyId: string;
  beneficiaryCode: string | null;
  beneficiaryName: string | null;
  beneficiaryNameAr: string | null;
  currencyId: string;
  currencyCode: string | null;
  amount: number;
  marginAmount: number;
  requestDate: string;
  openedDate: string | null;
  expiryDate: string | null;
  latestShipmentDate: string | null;
  settledDate: string | null;
  cancelledDate: string | null;
  daysToExpiry: number | null;
  isExpired: boolean;
  goodsDescription: string | null;
  marginJournalVoucherId: string | null;
  marginVoucherNumber: string | null;
  settlementJournalVoucherId: string | null;
  settlementVoucherNumber: string | null;
}

export interface LetterOfCreditInquiryResult extends PagedInquiryResult {
  rows: LetterOfCreditInquiryRow[];
  totalAmount: number;
  totalMargin: number;
  openCount: number;
  expiredCount: number;
  currencyTotals: CurrencyTotal[];
  asOfDate: string;
}

export interface LetterOfCreditInquiryFilter extends CashBanksInquiryFilter {
  status?: LetterOfCreditStatus;
  bankId?: string;
  beneficiaryPartyId?: string;
  bankFacilityId?: string;
  openOnly?: boolean;
  expiredOnly?: boolean;
  asOfDate?: string;
}

export interface BankLoanInquiryRow {
  id: string;
  loanNumber: string;
  status: LoanStatus;
  bankFacilityId: string;
  facilityCode: string | null;
  bankAccountId: string;
  bankAccountName: string | null;
  bankName: string | null;
  bankNameAr: string | null;
  currencyId: string;
  currencyCode: string | null;
  principalAmount: number;
  interestRatePercent: number;
  repaymentType: LoanRepaymentType;
  numberOfPayments: number;
  drawdownDate: string;
  firstInstallmentDate: string;
  /** From the persisted schedule, never recomputed from the rate. */
  paidInstallments: number;
  remainingInstallments: number;
  overdueInstallments: number;
  paidPrincipal: number;
  outstandingPrincipal: number;
  scheduledInterest: number;
  paidInterest: number;
  nextDueDate: string | null;
  nextDueAmount: number | null;
  drawdownJournalVoucherId: string | null;
  drawdownVoucherNumber: string | null;
}

export interface BankLoanInquiryResult extends PagedInquiryResult {
  rows: BankLoanInquiryRow[];
  totalPrincipal: number;
  totalOutstanding: number;
  totalPaidPrincipal: number;
  totalScheduledInterest: number;
  activeCount: number;
  overdueCount: number;
  currencyTotals: CurrencyTotal[];
  asOfDate: string;
}

export interface BankLoanInquiryFilter extends CashBanksInquiryFilter {
  status?: LoanStatus;
  bankFacilityId?: string;
  bankAccountId?: string;
  overdueOnly?: boolean;
  asOfDate?: string;
}

/* ──────────────────────────────────────────────────────────────────────────────────────────────
 * Cash & Banks › Reports.
 *
 * A report describes its own columns, totals and filter controls, so ONE screen renders any of
 * them. The shape is normalised; the queries behind it are not — each is a typed query over the
 * authoritative domain, exactly like the inquiries.
 * ────────────────────────────────────────────────────────────────────────────────────────────── */

export interface ReportColumn {
  key: string;
  label: string;
  labelAr: string;
  /** Right-aligned, tabular, and exported as a number rather than as text. */
  numeric: boolean;
  date: boolean;
  width: number;
}

export interface ReportRow {
  text: Record<string, string | null>;
  numbers: Record<string, number | null>;
  /** The authoritative screen this row opens. Null when the row has no document. */
  drillRoute: string | null;
}

export interface ReportTotal {
  label: string;
  labelAr: string;
  value: number | null;
  text: string | null;
  emphasis: boolean;
}

/** Which filter controls a report offers. Only the ones it actually uses. */
export const REPORT_PARAMETER = {
  DateRange: 1,
  AsOfDate: 2,
  DueRange: 3,
  Cashbox: 4,
  BankAccount: 5,
  Bank: 6,
  Branch: 7,
  Currency: 8,
  CostCenter: 9,
  Party: 10,
  ChequeDirection: 11,
  ChequeTerm: 12,
  ChequeStatus: 13,
  PaymentMethod: 14,
  ReconciliationStatus: 15,
  Search: 16,

  // ── Fixed Assets ────────────────────────────────────────────────────────
  //
  // One vocabulary across both modules: a parameter is a CONTROL the report offers, and the shared
  // report shell draws whichever ones the server declares.
  Asset: 17,
  AssetGroup: 18,
  IncludeChildGroups: 19,
  AssetLocation: 20,
  IncludeChildLocations: 21,
  Supplier: 22,
  Custodian: 23,
  AssetStatus: 24,
  DepreciationPeriod: 25,
  ExpenseTreatment: 26,
  InsuranceView: 27,
  IncludeClosedAssets: 28,

  // ── General Ledger ────────────────────────────────────────────────────────────────────────
  Account: 29,
  AccountRange: 30,
  AccountLevel: 31,
  FiscalYear: 32,
  VoucherType: 33,
  VoucherStatus: 34,
  User: 35,
  Reference: 36,
  CompareRange: 37,
  PostingAccountsOnly: 38,
  IncludeZeroBalances: 39,

  // ── Inventory & Warehouses ────────────────────────────────────────────────────────────────
  //
  // Same vocabulary again: the server declares which controls a report offers, and the one
  // Inventory report shell draws exactly those. A control a report ignores is never shown, because
  // a filter that changes nothing is worse than no filter at all.
  Item: 40,
  ItemGroup: 41,
  ItemCategory: 42,
  Warehouse: 43,
  ToWarehouse: 44,
  StorageLocation: 45,
  MovementType: 46,
  AdjustmentReason: 47,
  Customer: 48,
  PurchaseOrder: 49,
  StockCount: 50,
  InventoryStatus: 51,
  Days: 52,
  Top: 53,
  MarginBelow: 54,
  ValueAbove: 55,
  ActiveOnly: 56,
  IncludeDraft: 57,
  EmploymentStatus: 58,
  GroupBy: 59,

  // ── شؤون الموظفين › الوثائق والعقود ───────────────────────────────────────────────────
  ContractType: 60,
  ContractStatus: 61,
  DocumentType: 62,
  ExpiryRange: 63,
} as const;

export type ReportParameter = (typeof REPORT_PARAMETER)[keyof typeof REPORT_PARAMETER];

export interface CashBanksReportResult extends PagedInquiryResult {
  code: string;
  title: string;
  titleAr: string;
  columns: ReportColumn[];
  rows: ReportRow[];
  totals: ReportTotal[];
  parameters: ReportParameter[];
}

/** One General Ledger report's answer: the server describes its own shape. */
export interface GeneralLedgerReportResult {
  code: string;
  title: string;
  titleAr: string;
  columns: ReportColumn[];
  rows: ReportRow[];
  totals: ReportTotal[];
  parameters: ReportParameter[];
  totalCount: number;
  page: number;
  pageSize: number;
  baseCurrencyCode: string;
  warnings: string[];
}

/** Everything any General Ledger report might be filtered by. Reports declare what they use. */
/**
 * How widely a report is drawn.
 *
 * 0 الشركة كاملة · 1 فرع محدد · 2 تفصيل جميع الفروع.
 * Numeric because the server binds the enum by value; omitting it means Company, which is what
 * every existing caller already sends.
 */
export const REPORT_SCOPE = { Company: 0, Branch: 1, AllBranchesDetail: 2 } as const;

export interface GeneralLedgerReportFilter {
  scope?: number;
  dateFrom?: string;
  dateTo?: string;
  asOfDate?: string;
  compareFrom?: string;
  compareTo?: string;
  fiscalYearId?: string;
  branchId?: string;
  currencyId?: string;
  costCenterId?: string;
  accountId?: string;
  accountFrom?: string;
  accountTo?: string;
  accountLevel?: number;
  voucherTypeId?: string;
  voucherStatus?: number;
  userId?: string;
  partyId?: string;
  reference?: string;
  search?: string;
  postingAccountsOnly?: boolean;
  includeZeroBalances?: boolean;
  page?: number;
  pageSize?: number;
}

export interface CashBanksReportFilter extends CashBanksInquiryFilter {
  asOfDate?: string;
  dueFrom?: string;
  dueTo?: string;
  cashboxId?: string;
  bankAccountId?: string;
  bankId?: string;
  costCenterId?: string;
  partyId?: string;
  paymentMethodId?: string;
  bankFacilityId?: string;
  direction?: number;
  term?: number;
  status?: number;
}

// ═══════════════════════════════════════ Fixed Assets › Settings

/**
 * Which Fixed Assets vocabulary a settings entry belongs to.
 *
 * Thirteen registered settings over one table, exactly as the server holds them. The numbers are
 * the server's enum and must not be renumbered — they are stored on every document that names one.
 */
export const FIXED_ASSET_LOOKUP = {
  AssetModel: 1,
  Brand: 2,
  AssetClass: 3,
  AssetCategory: 4,
  AssetProperty: 5,
  ShippingMethod: 6,
  ExpenseType: 7,
  SalesType: 8,
  DisposalReason: 9,
  AssetType: 10,
  DeliveryMethod: 11,
  PurchaseRoute: 12,
  QuotationTerm: 13,
} as const;

export type FixedAssetLookupType =
  (typeof FIXED_ASSET_LOOKUP)[keyof typeof FIXED_ASSET_LOOKUP];

export interface FixedAssetLookupEntry {
  id: string;
  lookupType: FixedAssetLookupType;
  code: string;
  name: string;
  nameAr: string;
  /** Expense types only: 1 charged to expense, 2 capitalised. */
  treatment: number | null;
  /** Disposal reasons only: the authoritative reason it classifies as. */
  classification: number | null;
  defaultAccountId: string | null;
  defaultAccountNumber: string | null;
  defaultAccountName: string | null;
  text: string | null;
  notes: string | null;
  isActive: boolean;
  /** A Fixed Assets document already names this entry, so it cannot be removed — only withdrawn. */
  inUse: boolean;
}

export interface SaveFixedAssetLookupInput {
  id?: string;
  lookupType: FixedAssetLookupType;
  code: string;
  name: string;
  nameAr: string;
  treatment?: number | null;
  classification?: number | null;
  defaultAccountId?: string | null;
  text?: string | null;
  notes?: string | null;
  isActive: boolean;
}

/** The company's Fixed Assets configuration. One row, two screens. */
export interface FixedAssetsSettings {
  depreciationFrequency: number;
  defaultUsefulLifeMonths: number | null;
  defaultSalvagePercent: number | null;
  requireDisposalReason: boolean;
  requireExpenseType: boolean;
  autoNumberAssets: boolean;
  assetNumberPrefix: string | null;
  assetNumberPadding: number;
  assetNumberNextValue: number;
  /** Rendered by the server, never assembled in the browser. */
  nextAssetNumberPreview: string;
  /** False until the company has saved once. Zero rows is a valid state. */
  isConfigured: boolean;
}

export interface SaveFixedAssetsSystemSettingsInput {
  depreciationFrequency: number;
  defaultUsefulLifeMonths?: number | null;
  defaultSalvagePercent?: number | null;
  requireDisposalReason: boolean;
  requireExpenseType: boolean;
}

export interface SaveFixedAssetCodingSettingsInput {
  autoNumberAssets: boolean;
  assetNumberPrefix?: string | null;
  assetNumberPadding: number;
  assetNumberNextValue: number;
}

// ═══════════════════════════════════════ Fixed Assets › Reports

/**
 * What one Fixed Assets report answers with.
 *
 * The same carrier the Cash & Banks reports use — columns, rows and totals described by the
 * server — so one report shell renders either module's thirteen or fifty-eight.
 */
export interface FixedAssetsReportResult extends PagedInquiryResult {
  code: string;
  title: string;
  titleAr: string;
  columns: ReportColumn[];
  rows: ReportRow[];
  totals: ReportTotal[];
  parameters: ReportParameter[];
  /** Echoed back so the header can say which date the figures are as at. */
  asOfDate: string | null;
}

export interface FixedAssetsReportFilter extends FixedAssetInquiryFilter {
  includeChildGroups?: boolean;
  includeChildLocations?: boolean;
  includeClosedAssets?: boolean;
  custodianUserId?: string;
  costCenterId?: string;
  status?: number;
  treatment?: number;
  periodYear?: number;
  periodMonth?: number;
  toPeriodYear?: number;
  toPeriodMonth?: number;
  detailed?: boolean;
}

/** How the Fixed Assets report catalogue and its executors line up. Both lists must be empty. */
export interface FixedAssetsReportCoverage {
  registered: string[];
  mapped: string[];
  withoutExecutor: string[];
  notInCatalogue: string[];
}

// ═════════════════════════════════════════════ Cash & Banks › Settings

/** مجموعة صناديق. Classification only — a group holds no account and no balance. */
export interface CashboxGroup {
  id: string;
  code: string;
  name: string;
  nameAr: string;
  displayOrder: number;
  isActive: boolean;
  notes: string | null;
  /** How many cashboxes name this group; the reason it cannot be deleted. */
  cashboxCount: number;
}

export interface CashboxGroupList {
  rows: CashboxGroup[];
  totalCount: number;
  activeCount: number;
}

export interface SaveCashboxGroupInput {
  id?: string;
  code: string;
  name: string;
  nameAr: string;
  displayOrder: number;
  isActive: boolean;
  notes?: string | null;
}

/**
 * العهدة النقدية للصندوق.
 *
 * `floatAmount` is what the company AUTHORISED; `currentBalance` is what the ledger says today.
 * They are separate figures and neither is derived from the other. A null balance means the
 * cashbox has no cash account mapped — shown blank, never as zero.
 */
export interface CashboxCashFloat {
  id: string;
  cashboxId: string;
  cashboxCode: string;
  cashboxName: string;
  cashboxNameAr: string;
  currencyId: string;
  currencyCode: string;
  currencyName: string;
  currencyNameAr: string;
  floatAmount: number;
  effectiveFrom: string;
  custodianName: string | null;
  custodianNameAr: string | null;
  isActive: boolean;
  notes: string | null;
  currentBalance: number | null;
  variance: number | null;
}

export interface CashboxCashFloatList {
  rows: CashboxCashFloat[];
  totalCount: number;
  activeCount: number;
  totalAuthorised: number;
  overFloatCount: number;
}

export interface SaveCashboxCashFloatInput {
  id?: string;
  cashboxId: string;
  currencyId: string;
  floatAmount: number;
  effectiveFrom?: string | null;
  custodianName?: string | null;
  custodianNameAr?: string | null;
  isActive: boolean;
  notes?: string | null;
}

/** نوع اعتماد مستندي. A classification, never a lifecycle. */
export interface LetterOfCreditType {
  id: string;
  code: string;
  name: string;
  nameAr: string;
  defaultMarginPercentage: number | null;
  displayOrder: number;
  isActive: boolean;
  notes: string | null;
  usageCount: number;
}

export interface LetterOfCreditTypeList {
  rows: LetterOfCreditType[];
  totalCount: number;
  activeCount: number;
}

export interface SaveLetterOfCreditTypeInput {
  id?: string;
  code: string;
  name: string;
  nameAr: string;
  defaultMarginPercentage?: number | null;
  displayOrder: number;
  isActive: boolean;
  notes?: string | null;
}

/** سبب إرجاع شيك. The coded reason; the free text recorded at the time is kept alongside. */
export interface ChequeReturnReason {
  id: string;
  code: string;
  name: string;
  nameAr: string;
  isDrawerFault: boolean;
  displayOrder: number;
  isActive: boolean;
  notes: string | null;
  usageCount: number;
}

export interface ChequeReturnReasonList {
  rows: ChequeReturnReason[];
  totalCount: number;
  activeCount: number;
}

export interface SaveChequeReturnReasonInput {
  id?: string;
  code: string;
  name: string;
  nameAr: string;
  isDrawerFault: boolean;
  displayOrder: number;
  isActive: boolean;
  notes?: string | null;
}

/** One cashbox as the user-access grid shows it, granted or not. */
export interface CashboxGrant {
  id: string | null;
  cashboxId: string;
  cashboxCode: string;
  cashboxName: string;
  cashboxNameAr: string;
  level: number;
  cashboxIsActive: boolean;
  isGranted: boolean;
  branchId: string | null;
  branchName: string | null;
  grantedAt: string | null;
}

export interface UserCashboxAccess {
  userId: string;
  userName: string;
  fullName: string;
  fullNameAr: string | null;
  userIsActive: boolean;
  /** False when the company has not switched enforcement on: the grants restrict nothing yet. */
  enforcementEnabled: boolean;
  cashboxes: CashboxGrant[];
  grantedCount: number;
}

export interface UserCashboxAccessSummary {
  userId: string;
  userName: string;
  fullName: string;
  fullNameAr: string | null;
  userIsActive: boolean;
  grantedCount: number;
}

/** إعدادات نظام النقدية والبنوك. Every field here is read by an operational path. */
export interface CashAndBanksSettings {
  preventNegativeCashboxBalance: boolean;
  enforceUserCashboxAccess: boolean;
  defaultCashboxId: string | null;
  defaultCashboxCode: string | null;
  defaultCashboxName: string | null;
  defaultCashboxNameAr: string | null;
  requireChequeBookLeafForOutgoing: boolean;
  requireChequeReturnReason: boolean;
  defaultChequeTerm: number;
  defaultBankAccountId: string | null;
  defaultBankAccountName: string | null;
  defaultBankAccountNameAr: string | null;
  /** Shown beside the "require a reason" switch so an empty list is visible before it is turned on. */
  chequeReturnReasonCount: number;
  /** Shown beside the enforcement switch for the same reason. */
  usersWithCashboxGrants: number;
}

export interface SaveCashAndBanksSettingsInput {
  preventNegativeCashboxBalance: boolean;
  enforceUserCashboxAccess: boolean;
  defaultCashboxId?: string | null;
  requireChequeBookLeafForOutgoing: boolean;
  requireChequeReturnReason: boolean;
  defaultChequeTerm: number;
  defaultBankAccountId?: string | null;
}

// ═════════════════════════════════════════════ Fixed Assets › Master Data

/** طريقة الإهلاك. An enum, not a table: each member names a different arithmetic. */
export const DEPRECIATION_METHOD = { StraightLine: 1, DecliningBalance: 2 } as const;
export type DepreciationMethod =
  (typeof DEPRECIATION_METHOD)[keyof typeof DEPRECIATION_METHOD];

/** مصدر الاقتناء. Recorded explicitly, never inferred from a missing document. */
export const ASSET_ACQUISITION_SOURCE = { Purchase: 1, OpeningBalance: 2 } as const;
export type AssetAcquisitionSource =
  (typeof ASSET_ACQUISITION_SOURCE)[keyof typeof ASSET_ACQUISITION_SOURCE];

/**
 * حالة الأصل.
 *
 * The first three are the CARD's to set. The last four are set by the operation that accounts
 * for them — the card refuses them, and the server enforces that refusal.
 */
export const FIXED_ASSET_STATUS = {
  Draft: 1,
  InService: 2,
  Suspended: 3,
  FullyDepreciated: 4,
  Sold: 5,
  Disposed: 6,
  WrittenOff: 7,
} as const;
export type FixedAssetStatus = (typeof FIXED_ASSET_STATUS)[keyof typeof FIXED_ASSET_STATUS];

/** The three statuses an asset card may set itself. */
export const CARD_SETTABLE_STATUSES: readonly FixedAssetStatus[] = [
  FIXED_ASSET_STATUS.Draft,
  FIXED_ASSET_STATUS.InService,
  FIXED_ASSET_STATUS.Suspended,
];

/** One node of a Fixed Assets hierarchy — groups and locations share this shape. */
export interface FixedAssetTreeNode {
  id: string;
  parentId: string | null;
  code: string;
  name: string;
  nameAr: string;
  level: number;
  isActive: boolean;
  /** True when an ancestor is deactivated, so the row can say why it is unusable. */
  isEffectivelyInactive: boolean;
  hasChildren: boolean;
  /** How many asset cards point here. The reason deletion is refused. */
  assetCount: number;
}

/** One account slot on a group, with what it resolves to and where from. */
export interface GroupAccountSlot {
  accountId: string | null;
  accountNumber: string | null;
  accountName: string | null;
  accountNameAr: string | null;
  /** The account actually in force — this group's own, or the nearest ancestor's. */
  effectiveAccountId: string | null;
  effectiveAccountNumber: string | null;
  effectiveAccountName: string | null;
  /** Which ancestor supplied it, when inherited rather than set here. */
  inheritedFromCode: string | null;
}

export interface FixedAssetGroup {
  id: string;
  parentId: string | null;
  parentCode: string | null;
  parentName: string | null;
  parentNameAr: string | null;
  level: number;
  path: string | null;
  code: string;
  name: string;
  nameAr: string;
  assetAccount: GroupAccountSlot;
  accumulatedDepreciationAccount: GroupAccountSlot;
  depreciationExpenseAccount: GroupAccountSlot;
  disposalGainAccount: GroupAccountSlot;
  disposalLossAccount: GroupAccountSlot;
  defaultDepreciationMethod: DepreciationMethod;
  defaultUsefulLifeMonths: number | null;
  defaultDepreciationRatePercent: number | null;
  defaultSalvagePercent: number | null;
  isActive: boolean;
  notes: string | null;
  hasChildren: boolean;
  assetCount: number;
  updatedAt: string | null;
}

export interface SaveFixedAssetGroupInput {
  parentId: string | null;
  code: string;
  name: string;
  nameAr: string;
  assetAccountId: string | null;
  accumulatedDepreciationAccountId: string | null;
  depreciationExpenseAccountId: string | null;
  disposalGainAccountId: string | null;
  disposalLossAccountId: string | null;
  defaultDepreciationMethod: DepreciationMethod;
  defaultUsefulLifeMonths: number | null;
  defaultDepreciationRatePercent: number | null;
  defaultSalvagePercent: number | null;
  isActive: boolean;
  notes: string | null;
}

export interface FixedAssetLocation {
  id: string;
  parentId: string | null;
  parentCode: string | null;
  parentName: string | null;
  parentNameAr: string | null;
  level: number;
  path: string | null;
  code: string;
  name: string;
  nameAr: string;
  branchId: string | null;
  branchName: string | null;
  isActive: boolean;
  notes: string | null;
  hasChildren: boolean;
  assetCount: number;
  updatedAt: string | null;
}

export interface SaveFixedAssetLocationInput {
  parentId: string | null;
  code: string;
  name: string;
  nameAr: string;
  branchId: string | null;
  isActive: boolean;
  notes: string | null;
}

export interface FixedAssetListRow {
  id: string;
  assetNumber: string;
  name: string;
  nameAr: string;
  fixedAssetGroupId: string;
  groupCode: string;
  groupName: string;
  groupNameAr: string;
  fixedAssetLocationId: string | null;
  locationCode: string | null;
  locationName: string | null;
  locationNameAr: string | null;
  supplierId: string | null;
  supplierCode: string | null;
  supplierName: string | null;
  supplierNameAr: string | null;
  branchId: string | null;
  branchName: string | null;
  purchaseDate: string | null;
  localAcquisitionCost: number;
  status: FixedAssetStatus;
  isActive: boolean;
  /** Derived on the SERVER. Never recomputed in the browser. */
  accumulatedDepreciation: number;
  bookValue: number;
}

export interface FixedAssetList {
  rows: FixedAssetListRow[];
  totalCount: number;
  activeCount: number;
  totalAcquisitionCost: number;
  totalBookValue: number;
  /** True while no depreciation has been posted, so the screen can say why book value = cost. */
  noDepreciationPostedYet: boolean;
}

export interface FixedAssetAttachment {
  id: string;
  fileName: string;
  contentType: string | null;
  fileSizeBytes: number;
  description: string | null;
  uploadedAt: string;
}

export interface FixedAssetCard {
  id: string;
  assetNumber: string;
  name: string;
  nameAr: string;
  fixedAssetGroupId: string;
  groupCode: string;
  groupName: string;
  groupNameAr: string;
  fixedAssetLocationId: string | null;
  locationCode: string | null;
  locationName: string | null;
  locationNameAr: string | null;
  branchId: string | null;
  branchName: string | null;
  status: FixedAssetStatus;
  isActive: boolean;
  description: string | null;
  notes: string | null;
  acquisitionSource: AssetAcquisitionSource;
  supplierId: string | null;
  supplierCode: string | null;
  supplierName: string | null;
  supplierNameAr: string | null;
  purchaseDate: string | null;
  inServiceDate: string | null;
  purchaseDocumentReference: string | null;
  acquisitionCost: number;
  currencyId: string | null;
  currencyCode: string | null;
  exchangeRate: number;
  localAcquisitionCost: number;
  serialNumber: string | null;
  manufacturer: string | null;
  model: string | null;
  warrantyExpiryDate: string | null;
  depreciationMethod: DepreciationMethod;
  usefulLifeMonths: number | null;
  depreciationRatePercent: number | null;
  salvageValue: number;
  depreciationStartDate: string | null;
  openingAccumulatedDepreciation: number;
  /** Cost less salvage. Shown, never entered. */
  depreciableAmount: number;
  accumulatedDepreciation: number;
  bookValue: number;
  /** False while the depreciation engine has posted nothing. Stated rather than implied. */
  accumulatedDepreciationIsLedgerBacked: boolean;
  custodianUserId: string | null;
  custodianUserName: string | null;
  custodianFullName: string | null;
  department: string | null;
  /** The accounts this asset will post to, inherited from its group. Read-only here. */
  assetAccount: GroupAccountSlot;
  accumulatedDepreciationAccount: GroupAccountSlot;
  depreciationExpenseAccount: GroupAccountSlot;
  attachments: FixedAssetAttachment[];
  /** True once an operation took the asset off the books. Locks the money fields. */
  isClosedByOperation: boolean;
  updatedAt: string | null;
}

export interface SaveFixedAssetInput {
  id?: string | null;
  assetNumber: string;
  name: string;
  nameAr: string;
  fixedAssetGroupId: string;
  fixedAssetLocationId: string | null;
  branchId: string | null;
  status: FixedAssetStatus;
  isActive: boolean;
  description: string | null;
  notes: string | null;
  acquisitionSource: AssetAcquisitionSource;
  supplierId: string | null;
  purchaseDate: string | null;
  inServiceDate: string | null;
  purchaseDocumentReference: string | null;
  acquisitionCost: number;
  currencyId: string | null;
  exchangeRate: number;
  serialNumber: string | null;
  manufacturer: string | null;
  model: string | null;
  warrantyExpiryDate: string | null;
  depreciationMethod: DepreciationMethod;
  usefulLifeMonths: number | null;
  depreciationRatePercent: number | null;
  salvageValue: number;
  depreciationStartDate: string | null;
  openingAccumulatedDepreciation: number;
  custodianUserId: string | null;
  department: string | null;
  /** Create only: copies the group's depreciation defaults instead of the values sent here. */
  inheritDepreciationFromGroup: boolean;
}

/** One option in a Fixed Assets picker. */
export interface FixedAssetLookupOption {
  id: string;
  code: string;
  name: string;
  nameAr: string;
  level: number;
  secondary: string | null;
}

// ═════════════════════════════════════ Fixed Assets › Daily Transactions

/** The three-state lifecycle every Fixed Assets financial document uses. */
export const ASSET_DOCUMENT_STATUS = {
  Draft: 1,
  Approved: 2,
  Posted: 3,
  Cancelled: 4,
  Reversed: 5,
} as const;
export type AssetDocumentStatus =
  (typeof ASSET_DOCUMENT_STATUS)[keyof typeof ASSET_DOCUMENT_STATUS];

/** Which document a shared workflow call (approve / cancel / reverse) is acting on. */
export const ASSET_DOCUMENT_KIND = {
  PurchaseInvoice: "PurchaseInvoice",
  Expense: "Expense",
  Revenue: "Revenue",
  Sale: "Sale",
  Disposal: "Disposal",
  Revaluation: "Revaluation",
  /** The returns share the document workflow: approve, cancel and reverse all apply. */
  PurchaseReturn: "PurchaseReturn",
  ExpenseReturn: "ExpenseReturn",
} as const;
export type AssetDocumentKind =
  (typeof ASSET_DOCUMENT_KIND)[keyof typeof ASSET_DOCUMENT_KIND];

export const ASSET_CONDITION = { Good: 1, Fair: 2, Damaged: 3, OutOfService: 4 } as const;
export type AssetCondition = (typeof ASSET_CONDITION)[keyof typeof ASSET_CONDITION];

export const ASSET_CUSTODY_MOVEMENT_TYPE = { Issue: 1, Return: 2, Transfer: 3 } as const;
export type AssetCustodyMovementType =
  (typeof ASSET_CUSTODY_MOVEMENT_TYPE)[keyof typeof ASSET_CUSTODY_MOVEMENT_TYPE];

/** Where a cost lands: charged to profit, or added to the asset's cost basis. */
export const ASSET_EXPENSE_TREATMENT = { Expensed: 1, Capitalised: 2 } as const;
export type AssetExpenseTreatment =
  (typeof ASSET_EXPENSE_TREATMENT)[keyof typeof ASSET_EXPENSE_TREATMENT];

/** Whether the counterpart is a partner balance or a cash/bank account. */
export const ASSET_SETTLEMENT_KIND = { Supplier: 1, CashOrBank: 2 } as const;
export type AssetSettlementKind =
  (typeof ASSET_SETTLEMENT_KIND)[keyof typeof ASSET_SETTLEMENT_KIND];

export const ASSET_INSURANCE_STATUS = { Active: 1, Expired: 2, Cancelled: 3 } as const;
export type AssetInsuranceStatus =
  (typeof ASSET_INSURANCE_STATUS)[keyof typeof ASSET_INSURANCE_STATUS];

export const ASSET_MAINTENANCE_TYPE = {
  Preventive: 1, Corrective: 2, Emergency: 3, Improvement: 4,
} as const;
export type AssetMaintenanceType =
  (typeof ASSET_MAINTENANCE_TYPE)[keyof typeof ASSET_MAINTENANCE_TYPE];

export const ASSET_COUNT_STATUS = { Draft: 1, Completed: 2, Cancelled: 3 } as const;
export type AssetCountStatus = (typeof ASSET_COUNT_STATUS)[keyof typeof ASSET_COUNT_STATUS];

export const ASSET_COUNT_RESULT = {
  NotCounted: 0, Found: 1, FoundElsewhere: 2, Missing: 3,
} as const;
export type AssetCountResult = (typeof ASSET_COUNT_RESULT)[keyof typeof ASSET_COUNT_RESULT];

export const ASSET_DISPOSAL_REASON = {
  Damaged: 1, Obsolete: 2, Lost: 3, Stolen: 4, Donated: 5, Other: 99,
} as const;
export type AssetDisposalReason =
  (typeof ASSET_DISPOSAL_REASON)[keyof typeof ASSET_DISPOSAL_REASON];

// ── Custody ────────────────────────────────────────────────────────────────

export interface AssetCustody {
  id: string;
  fixedAssetId: string;
  assetNumber: string;
  assetName: string;
  assetNameAr: string;
  custodianUserId: string;
  custodianUserName: string;
  custodianFullName: string;
  issuedOn: string;
  expectedReturnOn: string | null;
  returnedOn: string | null;
  conditionOnIssue: AssetCondition;
  conditionOnReturn: AssetCondition | null;
  isOpen: boolean;
  notes: string | null;
  isOverdue: boolean;
}

export interface AssetCustodyList {
  rows: AssetCustody[];
  totalCount: number;
  openCount: number;
  overdueCount: number;
}

export interface AssetCustodyMovement {
  id: string;
  movementType: AssetCustodyMovementType;
  movementDate: string;
  fromUserId: string | null;
  fromUserName: string | null;
  toUserId: string | null;
  toUserName: string | null;
  condition: AssetCondition | null;
  notes: string | null;
  recordedAt: string;
}

// ── Location ───────────────────────────────────────────────────────────────

export interface AssetLocationMovement {
  id: string;
  fixedAssetId: string;
  assetNumber: string;
  assetName: string;
  assetNameAr: string;
  fromLocationId: string | null;
  fromLocationCode: string | null;
  fromLocationName: string | null;
  toLocationId: string | null;
  toLocationCode: string | null;
  toLocationName: string | null;
  movementDate: string;
  reason: string | null;
  notes: string | null;
  recordedAt: string;
}

export interface AssetLocationMovementList {
  rows: AssetLocationMovement[];
  totalCount: number;
}

// ── Insurance ──────────────────────────────────────────────────────────────

export interface AssetInsurancePolicy {
  id: string;
  fixedAssetId: string;
  assetNumber: string;
  assetName: string;
  assetNameAr: string;
  insurerPartnerId: string;
  insurerCode: string;
  insurerName: string;
  insurerNameAr: string | null;
  policyNumber: string;
  startDate: string;
  endDate: string;
  insuredValue: number;
  premiumAmount: number;
  currencyId: string | null;
  currencyCode: string | null;
  coverageType: string | null;
  premiumExpenseId: string | null;
  premiumExpenseNumber: string | null;
  status: AssetInsuranceStatus;
  notes: string | null;
  isExpired: boolean;
  daysToExpiry: number | null;
}

export interface AssetInsuranceList {
  rows: AssetInsurancePolicy[];
  totalCount: number;
  activeCount: number;
  expiringSoonCount: number;
  totalInsuredValue: number;
}

// ── Maintenance ────────────────────────────────────────────────────────────

export interface AssetMaintenance {
  id: string;
  maintenanceNumber: string;
  fixedAssetId: string;
  assetNumber: string;
  assetName: string;
  assetNameAr: string;
  maintenanceType: AssetMaintenanceType;
  maintenanceDate: string;
  serviceProviderPartnerId: string | null;
  serviceProviderName: string | null;
  description: string | null;
  cost: number;
  currencyId: string | null;
  currencyCode: string | null;
  costExpenseId: string | null;
  costExpenseNumber: string | null;
  nextMaintenanceDate: string | null;
  meterReading: number | null;
  conditionAfter: AssetCondition | null;
  notes: string | null;
  isDue: boolean;
}

export interface AssetMaintenanceList {
  rows: AssetMaintenance[];
  totalCount: number;
  dueCount: number;
  totalCost: number;
}

// ── Inventory count ────────────────────────────────────────────────────────

export interface AssetInventoryCountLine {
  id: string;
  lineNumber: number;
  fixedAssetId: string;
  assetNumber: string;
  assetName: string;
  assetNameAr: string;
  expectedLocationId: string | null;
  expectedLocationCode: string | null;
  expectedLocationName: string | null;
  actualLocationId: string | null;
  actualLocationCode: string | null;
  actualLocationName: string | null;
  result: AssetCountResult;
  condition: AssetCondition | null;
  discrepancyResolved: boolean;
  isDiscrepancy: boolean;
  notes: string | null;
}

export interface AssetInventoryCount {
  id: string;
  countNumber: string;
  countDate: string;
  locationId: string | null;
  locationCode: string | null;
  locationName: string | null;
  branchId: string | null;
  branchName: string | null;
  responsibleUserId: string | null;
  responsibleUserName: string | null;
  status: AssetCountStatus;
  completedAt: string | null;
  cancellationReason: string | null;
  notes: string | null;
  isEditable: boolean;
  lines: AssetInventoryCountLine[];
  totalLines: number;
  countedLines: number;
  foundCount: number;
  foundElsewhereCount: number;
  missingCount: number;
  unresolvedDiscrepancies: number;
}

export interface AssetInventoryCountListRow {
  id: string;
  countNumber: string;
  countDate: string;
  locationName: string | null;
  responsibleUserName: string | null;
  status: AssetCountStatus;
  totalLines: number;
  missingCount: number;
  unresolvedDiscrepancies: number;
}

export interface AssetInventoryCountList {
  rows: AssetInventoryCountListRow[];
  totalCount: number;
  draftCount: number;
}

// ── Depreciation ───────────────────────────────────────────────────────────

export interface AssetDepreciationLine {
  id: string;
  lineNumber: number;
  fixedAssetId: string;
  assetNumber: string;
  assetName: string;
  assetNameAr: string;
  costBasis: number;
  salvageValue: number;
  method: DepreciationMethod;
  usefulLifeMonths: number | null;
  ratePercent: number | null;
  openingAccumulated: number;
  depreciationAmount: number;
  closingAccumulated: number;
  closingBookValue: number;
  wasCappedAtDepreciableAmount: boolean;
}

export interface AssetDepreciationRun {
  id: string;
  runNumber: string;
  periodYear: number;
  periodMonth: number;
  runDate: string;
  fixedAssetGroupId: string | null;
  groupCode: string | null;
  groupName: string | null;
  branchId: string | null;
  totalDepreciation: number;
  assetCount: number;
  status: AssetDocumentStatus;
  journalVoucherId: string | null;
  journalVoucherNumber: string | null;
  reversalJournalVoucherId: string | null;
  notes: string | null;
  isEditable: boolean;
  lines: AssetDepreciationLine[];
}

export interface AssetDepreciationRunListRow {
  id: string;
  runNumber: string;
  periodYear: number;
  periodMonth: number;
  runDate: string;
  groupName: string | null;
  totalDepreciation: number;
  assetCount: number;
  status: AssetDocumentStatus;
}

export interface AssetDepreciationRunList {
  rows: AssetDepreciationRunListRow[];
  totalCount: number;
  postedCount: number;
  totalPostedDepreciation: number;
}

// ── Annual depreciation — احتساب الاهتلاك السنوي ───────────────────────────

export interface AnnualAssetDepreciationPreviewLine {
  fixedAssetId: string;
  assetNumber: string;
  assetName: string;
  assetNameAr: string;
  groupCode: string;
  groupName: string;
  groupNameAr: string;
  costBasis: number;
  salvageValue: number;
  method: DepreciationMethod;
  usefulLifeMonths: number | null;
  ratePercent: number | null;
  monthsCharged: number;
  firstMonth: number;
  lastMonth: number;
  openingAccumulated: number;
  depreciationAmount: number;
  closingAccumulated: number;
  closingBookValue: number;
  wasCapped: boolean;
}

export interface AnnualAssetDepreciationVoucherLine {
  accountId: string;
  accountNumber: string;
  accountName: string;
  accountNameAr: string | null;
  debit: number;
  credit: number;
}

export interface AnnualAssetDepreciationPreview {
  periodYear: number;
  fromMonth: number;
  toMonth: number;
  voucherDate: string;
  fixedAssetGroupId: string | null;
  branchId: string | null;
  assetCount: number;
  runCount: number;
  totalDepreciation: number;
  lines: AnnualAssetDepreciationPreviewLine[];
  voucherLines: AnnualAssetDepreciationVoucherLine[];
}

export interface AnnualAssetDepreciationResult {
  journalVoucherId: string;
  journalVoucherNumber: string | null;
  voucherDate: string;
  totalDepreciation: number;
  assetCount: number;
  runNumbers: string[];
}

// ── Sale / disposal ────────────────────────────────────────────────────────

export interface AssetSale {
  id: string;
  saleNumber: string;
  saleDate: string;
  fixedAssetId: string;
  assetNumber: string;
  assetName: string;
  assetNameAr: string;
  customerId: string | null;
  customerName: string | null;
  settlementKind: AssetSettlementKind;
  settlementAccountId: string | null;
  settlementAccountName: string | null;
  saleAmount: number;
  currencyId: string | null;
  currencyCode: string | null;
  exchangeRate: number;
  localSaleAmount: number;
  assetCostAtSale: number;
  accumulatedDepreciationAtSale: number;
  netBookValueAtSale: number;
  gainOrLoss: number;
  status: AssetDocumentStatus;
  journalVoucherId: string | null;
  journalVoucherNumber: string | null;
  reversalJournalVoucherId: string | null;
  notes: string | null;
  isEditable: boolean;
}

export interface AssetSaleList {
  rows: AssetSale[];
  totalCount: number;
  postedCount: number;
  totalProceeds: number;
  totalGainOrLoss: number;
}

export interface AssetDisposal {
  id: string;
  disposalNumber: string;
  disposalDate: string;
  fixedAssetId: string;
  assetNumber: string;
  assetName: string;
  assetNameAr: string;
  reason: AssetDisposalReason;
  condition: AssetCondition | null;
  assetCostAtDisposal: number;
  accumulatedDepreciationAtDisposal: number;
  netBookValueAtDisposal: number;
  status: AssetDocumentStatus;
  journalVoucherId: string | null;
  journalVoucherNumber: string | null;
  reversalJournalVoucherId: string | null;
  notes: string | null;
  isEditable: boolean;
}

export interface AssetDisposalList {
  rows: AssetDisposal[];
  totalCount: number;
  postedCount: number;
  totalWrittenOff: number;
}

// ── Expense / revenue ──────────────────────────────────────────────────────

export interface AssetExpense {
  id: string;
  expenseNumber: string;
  expenseDate: string;
  fixedAssetId: string;
  assetNumber: string;
  assetName: string;
  assetNameAr: string;
  expenseAccountId: string | null;
  expenseAccountName: string | null;
  treatment: AssetExpenseTreatment;
  settlementKind: AssetSettlementKind;
  supplierId: string | null;
  supplierName: string | null;
  settlementAccountId: string | null;
  settlementAccountName: string | null;
  amount: number;
  currencyId: string | null;
  currencyCode: string | null;
  exchangeRate: number;
  localAmount: number;
  costCenterId: string | null;
  description: string | null;
  status: AssetDocumentStatus;
  journalVoucherId: string | null;
  journalVoucherNumber: string | null;
  reversalJournalVoucherId: string | null;
  notes: string | null;
  isEditable: boolean;
}

export interface AssetExpenseList {
  rows: AssetExpense[];
  totalCount: number;
  postedCount: number;
  totalExpensed: number;
  totalCapitalised: number;
}

export interface AssetRevenue {
  id: string;
  revenueNumber: string;
  revenueDate: string;
  fixedAssetId: string;
  assetNumber: string;
  assetName: string;
  assetNameAr: string;
  revenueAccountId: string | null;
  revenueAccountName: string | null;
  customerId: string | null;
  customerName: string | null;
  settlementKind: AssetSettlementKind;
  settlementAccountId: string | null;
  settlementAccountName: string | null;
  amount: number;
  currencyId: string | null;
  currencyCode: string | null;
  exchangeRate: number;
  localAmount: number;
  costCenterId: string | null;
  description: string | null;
  status: AssetDocumentStatus;
  journalVoucherId: string | null;
  journalVoucherNumber: string | null;
  reversalJournalVoucherId: string | null;
  notes: string | null;
  isEditable: boolean;
}

export interface AssetRevenueList {
  rows: AssetRevenue[];
  totalCount: number;
  postedCount: number;
  totalRevenue: number;
}

// ── Revaluation / split ────────────────────────────────────────────────────

/** Cost, accumulated depreciation and book value, all derived on the server. */
export interface AssetValuationSnapshot {
  cost: number;
  accumulatedDepreciation: number;
  netBookValue: number;
  salvageValue: number;
  depreciableAmount: number;
}

export interface AssetRevaluation {
  id: string;
  revaluationNumber: string;
  revaluationDate: string;
  fixedAssetId: string;
  assetNumber: string;
  assetName: string;
  assetNameAr: string;
  previousCarryingAmount: number;
  revaluedAmount: number;
  difference: number;
  assetCostAtRevaluation: number;
  accumulatedDepreciationAtRevaluation: number;
  isUpward: boolean;
  reason: string | null;
  status: AssetDocumentStatus;
  journalVoucherId: string | null;
  journalVoucherNumber: string | null;
  reversalJournalVoucherId: string | null;
  notes: string | null;
  isEditable: boolean;
}

export interface AssetRevaluationList {
  rows: AssetRevaluation[];
  totalCount: number;
  postedCount: number;
  totalUpward: number;
  totalDownward: number;
}

export interface AssetSplitLine {
  id: string;
  lineNumber: number;
  childFixedAssetId: string | null;
  childAssetNumber: string;
  childName: string;
  childNameAr: string;
  fixedAssetGroupId: string;
  groupName: string | null;
  fixedAssetLocationId: string | null;
  allocatedCost: number;
  allocatedAccumulatedDepreciation: number;
  allocatedBookValue: number;
  notes: string | null;
}

export interface AssetSplit {
  id: string;
  splitNumber: string;
  splitDate: string;
  parentFixedAssetId: string;
  parentAssetNumber: string;
  parentAssetName: string;
  parentAssetNameAr: string;
  parentCostAtSplit: number;
  parentAccumulatedDepreciationAtSplit: number;
  parentNetBookValueAtSplit: number;
  reason: string | null;
  status: AssetDocumentStatus;
  journalVoucherId: string | null;
  journalVoucherNumber: string | null;
  notes: string | null;
  isEditable: boolean;
  lines: AssetSplitLine[];
  allocatedCost: number;
  allocatedAccumulatedDepreciation: number;
  allocatedBookValue: number;
  /** True only when both allocations match the parent exactly. No tolerance. */
  isBalanced: boolean;
}

export interface AssetSplitListRow {
  id: string;
  splitNumber: string;
  splitDate: string;
  parentAssetNumber: string;
  parentAssetName: string;
  parentNetBookValueAtSplit: number;
  childCount: number;
  status: AssetDocumentStatus;
}

export interface AssetSplitList {
  rows: AssetSplitListRow[];
  totalCount: number;
  postedCount: number;
}

// ── Purchase ───────────────────────────────────────────────────────────────

export interface AssetPurchaseOrder {
  id: string;
  orderNumber: string;
  orderDate: string;
  supplierId: string;
  supplierCode: string;
  supplierName: string;
  currencyId: string;
  currencyCode: string | null;
  exchangeRate: number;
  expectedDate: string | null;
  netTotal: number;
  status: number;
  notes: string | null;
  assetPurchaseInvoiceId: string | null;
  assetPurchaseInvoiceNumber: string | null;
  isAvailableForInvoicing: boolean;
}

export interface AssetPurchaseOrderList {
  rows: AssetPurchaseOrder[];
  totalCount: number;
  availableCount: number;
  totalOnOrder: number;
}

export interface AssetPurchaseInvoiceLine {
  id: string;
  lineNumber: number;
  fixedAssetId: string | null;
  assetNumber: string | null;
  assetName: string;
  assetNameAr: string;
  fixedAssetGroupId: string;
  groupName: string | null;
  fixedAssetLocationId: string | null;
  locationName: string | null;
  serialNumber: string | null;
  manufacturer: string | null;
  model: string | null;
  unitCost: number;
  lineTotal: number;
  notes: string | null;
}

export interface AssetPurchaseInvoice {
  id: string;
  invoiceNumber: string;
  invoiceDate: string;
  supplierInvoiceNumber: string | null;
  supplierId: string;
  supplierCode: string;
  supplierName: string;
  purchaseOrderId: string | null;
  purchaseOrderNumber: string | null;
  currencyId: string;
  currencyCode: string | null;
  exchangeRate: number;
  branchId: string | null;
  costCenterId: string | null;
  settlementKind: AssetSettlementKind;
  settlementAccountId: string | null;
  settlementAccountName: string | null;
  subtotal: number;
  totalAmount: number;
  localTotalAmount: number;
  status: AssetDocumentStatus;
  journalVoucherId: string | null;
  journalVoucherNumber: string | null;
  reversalJournalVoucherId: string | null;
  notes: string | null;
  isEditable: boolean;
  lines: AssetPurchaseInvoiceLine[];
}

export interface AssetPurchaseInvoiceListRow {
  id: string;
  invoiceNumber: string;
  invoiceDate: string;
  supplierName: string;
  purchaseOrderNumber: string | null;
  localTotalAmount: number;
  assetCount: number;
  status: AssetDocumentStatus;
}

export interface AssetPurchaseInvoiceList {
  rows: AssetPurchaseInvoiceListRow[];
  totalCount: number;
  postedCount: number;
  totalAcquired: number;
}

// ═════════════════════════════ Fixed Assets › Daily Transactions (the final eight)

/** إيداع عهدة joins the custody history as a fourth kind of entry, not a fourth table. */
export const ASSET_CUSTODY_MOVEMENT_DEPOSIT = 4;

export const ASSET_RETURN_REASON = {
  Defective: 1,
  NotAsSpecified: 2,
  OverDelivered: 3,
  PurchaseCancelled: 4,
  Other: 99,
} as const;
export type AssetReturnReason =
  (typeof ASSET_RETURN_REASON)[keyof typeof ASSET_RETURN_REASON];

export const ASSET_TRANSFER_STATUS = {
  Draft: 1,
  Approved: 2,
  Dispatched: 3,
  Received: 4,
  Cancelled: 5,
} as const;
export type AssetTransferStatus =
  (typeof ASSET_TRANSFER_STATUS)[keyof typeof ASSET_TRANSFER_STATUS];

export const QUOTATION_REQUEST_STATUS = {
  Draft: 1,
  Issued: 2,
  Awarded: 3,
  Cancelled: 4,
} as const;
export type QuotationRequestStatus =
  (typeof QUOTATION_REQUEST_STATUS)[keyof typeof QUOTATION_REQUEST_STATUS];

export const QUOTATION_PURPOSE = { General: 1, FixedAsset: 2 } as const;
export type QuotationPurpose = (typeof QUOTATION_PURPOSE)[keyof typeof QUOTATION_PURPOSE];

export const SUPPLIER_QUOTATION_STATUS = { Received: 1, Withdrawn: 2 } as const;
export type SupplierQuotationStatus =
  (typeof SUPPLIER_QUOTATION_STATUS)[keyof typeof SUPPLIER_QUOTATION_STATUS];

// ── Asset purchase return ──────────────────────────────────────────────────

/** One asset on a posted invoice, and whether it can still go back. */
export interface ReturnableAsset {
  sourceInvoiceLineId: string;
  fixedAssetId: string;
  assetNumber: string;
  assetName: string;
  assetNameAr: string;
  serialNumber: string | null;
  originalCost: number;
  assetStatus: FixedAssetStatus;
  isReturnable: boolean;
  /** Why not. The screen shows this instead of a disabled checkbox with no explanation. */
  blockedReason: string | null;
}

export interface ReturnableAssetInvoice {
  invoiceId: string;
  invoiceNumber: string;
  invoiceDate: string;
  supplierId: string;
  supplierName: string;
  currencyId: string;
  currencyCode: string | null;
  exchangeRate: number;
  totalAmount: number;
  assets: ReturnableAsset[];
  returnableCount: number;
}

export interface AssetPurchaseReturnLine {
  id: string;
  lineNumber: number;
  sourceInvoiceLineId: string;
  fixedAssetId: string;
  assetNumber: string;
  assetName: string;
  assetNameAr: string;
  serialNumber: string | null;
  originalCost: number;
  returnAmount: number;
  assetAccountId: string;
  assetAccountName: string | null;
  notes: string | null;
}

export interface AssetPurchaseReturn {
  id: string;
  returnNumber: string;
  returnDate: string;
  sourceInvoiceId: string;
  sourceInvoiceNumber: string;
  sourceInvoiceDate: string;
  supplierId: string;
  supplierName: string;
  currencyId: string;
  currencyCode: string | null;
  exchangeRate: number;
  settlementKind: AssetSettlementKind;
  settlementAccountId: string | null;
  settlementAccountName: string | null;
  branchId: string | null;
  costCenterId: string | null;
  reason: AssetReturnReason;
  referenceNumber: string | null;
  returnAmount: number;
  localReturnAmount: number;
  status: AssetDocumentStatus;
  journalVoucherId: string | null;
  journalVoucherNumber: string | null;
  reversalJournalVoucherId: string | null;
  notes: string | null;
  isEditable: boolean;
  lines: AssetPurchaseReturnLine[];
  assetCount: number;
}

export interface AssetPurchaseReturnListRow {
  id: string;
  returnNumber: string;
  returnDate: string;
  sourceInvoiceNumber: string;
  supplierName: string;
  reason: AssetReturnReason;
  localReturnAmount: number;
  assetCount: number;
  status: AssetDocumentStatus;
  journalVoucherNumber: string | null;
}

export interface AssetPurchaseReturnList {
  rows: AssetPurchaseReturnListRow[];
  totalCount: number;
  postedCount: number;
  totalReturned: number;
}

// ── Asset expense return ───────────────────────────────────────────────────

export interface ReturnableExpense {
  expenseId: string;
  expenseNumber: string;
  expenseDate: string;
  fixedAssetId: string;
  assetNumber: string;
  assetName: string;
  treatment: AssetExpenseTreatment;
  currencyId: string;
  currencyCode: string | null;
  exchangeRate: number;
  amount: number;
  alreadyReturned: number;
  remainingReturnable: number;
}

export interface AssetExpenseReturn {
  id: string;
  returnNumber: string;
  returnDate: string;
  sourceExpenseId: string;
  sourceExpenseNumber: string;
  sourceExpenseDate: string;
  sourceAmount: number;
  remainingReturnable: number;
  fixedAssetId: string;
  assetNumber: string;
  assetName: string;
  assetNameAr: string;
  treatment: AssetExpenseTreatment;
  expenseAccountId: string | null;
  expenseAccountName: string | null;
  settlementKind: AssetSettlementKind;
  supplierId: string | null;
  supplierName: string | null;
  settlementAccountId: string | null;
  settlementAccountName: string | null;
  currencyId: string;
  currencyCode: string | null;
  exchangeRate: number;
  branchId: string | null;
  costCenterId: string | null;
  reason: AssetReturnReason;
  description: string | null;
  returnAmount: number;
  localReturnAmount: number;
  status: AssetDocumentStatus;
  journalVoucherId: string | null;
  journalVoucherNumber: string | null;
  reversalJournalVoucherId: string | null;
  notes: string | null;
  isEditable: boolean;
}

export interface AssetExpenseReturnList {
  rows: AssetExpenseReturn[];
  totalCount: number;
  postedCount: number;
  totalReturned: number;
  totalCapitalisedReturned: number;
}

// ── Asset / item transfer ──────────────────────────────────────────────────

export interface AssetTransfer {
  id: string;
  transferNumber: string;
  transferDate: string;
  fixedAssetId: string;
  assetNumber: string;
  assetName: string;
  assetNameAr: string;
  fromLocationId: string | null;
  fromLocationName: string | null;
  fromBranchId: string | null;
  fromGroupId: string;
  fromGroupName: string | null;
  fromCustodianUserId: string | null;
  fromCustodianName: string | null;
  toLocationId: string | null;
  toLocationName: string | null;
  toBranchId: string | null;
  toGroupId: string;
  toGroupName: string | null;
  toCustodianUserId: string | null;
  toCustodianName: string | null;
  reason: string | null;
  notes: string | null;
  status: AssetTransferStatus;
  dispatchedOn: string | null;
  receivedOn: string | null;
  conditionOnDispatch: AssetCondition | null;
  conditionOnReceipt: AssetCondition | null;
  cancellationReason: string | null;
  journalVoucherId: string | null;
  journalVoucherNumber: string | null;
  costAtTransfer: number;
  accumulatedDepreciationAtTransfer: number;
  requiredReclassification: boolean;
  /** True when the destination group's accounts differ, so a receipt WILL post. */
  willReclassify: boolean;
  isEditable: boolean;
  isInTransit: boolean;
  isReclassification: boolean;
}

export interface AssetTransferList {
  rows: AssetTransfer[];
  totalCount: number;
  inTransitCount: number;
  receivedCount: number;
  reclassifiedCount: number;
}

// ── Inventory worksheet ────────────────────────────────────────────────────

export interface AssetInventoryWorksheetLine {
  lineNumber: number;
  assetNumber: string;
  assetName: string;
  assetNameAr: string;
  serialNumber: string | null;
  groupName: string | null;
  expectedLocationName: string | null;
  actualLocationName: string | null;
  result: AssetCountResult;
  condition: AssetCondition | null;
  custodianName: string | null;
  isDiscrepancy: boolean;
  discrepancyResolved: boolean;
  /** What the difference IS, in words. Null when the line agrees with the sheet. */
  difference: string | null;
  notes: string | null;
}

export interface AssetInventoryWorksheet {
  countId: string;
  countNumber: string;
  countDate: string;
  locationName: string | null;
  branchName: string | null;
  responsibleUserName: string | null;
  status: AssetCountStatus;
  completedAt: string | null;
  notes: string | null;
  lines: AssetInventoryWorksheetLine[];
  totalLines: number;
  countedLines: number;
  notCountedLines: number;
  foundCount: number;
  foundElsewhereCount: number;
  missingCount: number;
  discrepancyCount: number;
  unresolvedDiscrepancies: number;
  companyName: string;
  companyNameAr: string | null;
}

// ── Procurement: request, quotations, comparison, award ────────────────────

export interface QuotationRequestLine {
  id: string;
  lineNumber: number;
  itemId: string | null;
  fixedAssetGroupId: string | null;
  fixedAssetGroupName: string | null;
  description: string;
  specification: string | null;
  unit: string | null;
  quantity: number;
  requiredDate: string | null;
  notes: string | null;
  awardedSupplierQuotationLineId: string | null;
  awardedSupplierId: string | null;
  awardedSupplierName: string | null;
  awardedQuantity: number | null;
  quotationCount: number;
}

export interface QuotationRequestSupplier {
  id: string;
  supplierId: string;
  supplierCode: string;
  supplierName: string;
  invitedOn: string | null;
  hasResponded: boolean;
  notes: string | null;
}

export interface QuotationRequest {
  id: string;
  requestNumber: string;
  requestDate: string;
  branchId: string;
  requiredDate: string | null;
  closingDate: string | null;
  currencyId: string;
  currencyCode: string | null;
  purpose: QuotationPurpose;
  fixedAssetGroupId: string | null;
  fixedAssetGroupName: string | null;
  fixedAssetLocationId: string | null;
  costCenterId: string | null;
  subject: string | null;
  terms: string | null;
  notes: string | null;
  status: QuotationRequestStatus;
  awardedSupplierQuotationId: string | null;
  awardedSupplierName: string | null;
  awardDate: string | null;
  awardedByName: string | null;
  awardReason: string | null;
  cancellationReason: string | null;
  isEditable: boolean;
  acceptsQuotations: boolean;
  lines: QuotationRequestLine[];
  suppliers: QuotationRequestSupplier[];
  quotationCount: number;
}

export interface QuotationRequestListRow {
  id: string;
  requestNumber: string;
  requestDate: string;
  requiredDate: string | null;
  subject: string | null;
  purpose: QuotationPurpose;
  status: QuotationRequestStatus;
  lineCount: number;
  supplierCount: number;
  quotationCount: number;
  awardedSupplierName: string | null;
}

export interface QuotationRequestList {
  rows: QuotationRequestListRow[];
  totalCount: number;
  draftCount: number;
  issuedCount: number;
  awardedCount: number;
}

export interface SupplierQuotationLine {
  id: string;
  lineNumber: number;
  purchaseQuotationRequestLineId: string;
  requestLineDescription: string;
  offeredDescription: string | null;
  quantity: number;
  unitPrice: number;
  discountAmount: number;
  taxTypeId: string | null;
  taxPercent: number;
  netAmount: number;
  taxAmount: number;
  lineTotal: number;
  deliveryDays: number | null;
  notes: string | null;
  isAwarded: boolean;
}

export interface SupplierQuotation {
  id: string;
  purchaseQuotationRequestId: string;
  requestNumber: string;
  supplierId: string;
  supplierCode: string;
  supplierName: string;
  supplierReference: string | null;
  quotationDate: string;
  validUntil: string | null;
  hasLapsed: boolean;
  currencyId: string;
  currencyCode: string | null;
  exchangeRate: number;
  deliveryDays: number | null;
  paymentTermDays: number | null;
  commercialNotes: string | null;
  technicalNotes: string | null;
  status: SupplierQuotationStatus;
  subtotal: number;
  discountTotal: number;
  taxTotal: number;
  netTotal: number;
  isAwarded: boolean;
  lines: SupplierQuotationLine[];
}

export interface SupplierQuotationList {
  rows: SupplierQuotation[];
  totalCount: number;
  liveCount: number;
}

export interface ComparisonCell {
  supplierQuotationId: string;
  supplierQuotationLineId: string;
  supplierId: string;
  hasQuoted: boolean;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
  /** Null when the offer's currency cannot be converted — shown apart, never faked. */
  comparableTotal: number | null;
  currencyCode: string | null;
  deliveryDays: number | null;
  offeredDescription: string | null;
  notes: string | null;
  isLowest: boolean;
  isAwarded: boolean;
}

export interface ComparisonRow {
  requestLineId: string;
  lineNumber: number;
  description: string;
  specification: string | null;
  unit: string | null;
  quantity: number;
  cells: ComparisonCell[];
  awardedSupplierQuotationLineId: string | null;
}

export interface ComparisonColumn {
  supplierQuotationId: string;
  supplierId: string;
  supplierCode: string;
  supplierName: string;
  supplierReference: string | null;
  quotationDate: string;
  validUntil: string | null;
  hasLapsed: boolean;
  currencyCode: string | null;
  exchangeRate: number;
  isComparableCurrency: boolean;
  deliveryDays: number | null;
  paymentTermDays: number | null;
  commercialNotes: string | null;
  technicalNotes: string | null;
  netTotal: number;
  comparableNetTotal: number | null;
  quotedLineCount: number;
  isAwarded: boolean;
  isLowestOverall: boolean;
}

export interface QuotationComparison {
  requestId: string;
  requestNumber: string;
  requestDate: string;
  subject: string | null;
  status: QuotationRequestStatus;
  currencyId: string;
  currencyCode: string | null;
  lineCount: number;
  invitedSupplierCount: number;
  respondedSupplierCount: number;
  allOneCurrency: boolean;
  columns: ComparisonColumn[];
  rows: ComparisonRow[];
  companyName: string;
  companyNameAr: string | null;
}

// ═══════════════════════════════════ Fixed Assets › Inquiries (the registered eight)

/** What every Fixed Assets inquiry accepts, on top of the shared paging contract. */
export interface FixedAssetInquiryFilter extends PagedInquiryFilter {
  dateFrom?: string;
  dateTo?: string;
  /** The date the position is reconstructed as of. A lens, not a filter. */
  asOfDate?: string;
  fixedAssetId?: string;
  fixedAssetGroupId?: string;
  fixedAssetLocationId?: string;
  branchId?: string;
  supplierId?: string;
  searchTerm?: string;
}

export const ASSET_EVENT_KIND = {
  Acquisition: 1,
  PurchaseReturn: 2,
  CustodyIssue: 3,
  CustodyDeposit: 4,
  CustodyReturn: 5,
  CustodyTransfer: 6,
  LocationMovement: 7,
  Transfer: 8,
  Insurance: 9,
  Maintenance: 10,
  InventoryCount: 11,
  Depreciation: 12,
  Expense: 13,
  ExpenseReturn: 14,
  Revenue: 15,
  Revaluation: 16,
  Sale: 17,
  Disposal: 18,
  Split: 19,
} as const;
export type AssetEventKind = (typeof ASSET_EVENT_KIND)[keyof typeof ASSET_EVENT_KIND];

/** One thing that happened to one asset, whatever table it came out of. */
export interface AssetEventRow {
  id: string;
  fixedAssetId: string;
  assetNumber: string;
  assetName: string;
  assetNameAr: string;
  kind: AssetEventKind;
  effectiveDate: string;
  documentNumber: string;
  statusLabel: string | null;
  statusLabelAr: string | null;
  isEffective: boolean;
  description: string | null;
  descriptionAr: string | null;
  fromLabel: string | null;
  toLabel: string | null;
  debit: number;
  credit: number;
  costChange: number;
  depreciationCharge: number;
  journalVoucherId: string | null;
  journalVoucherNumber: string | null;
  /** The voucher's own series when it disagrees with the document's type. Reported, not fixed. */
  voucherSeriesMismatch: string | null;
  reversalJournalVoucherId: string | null;
  reversalVoucherNumber: string | null;
  reversalSeriesMismatch: string | null;
  /** The route family a row drills into. Mapped to a real screen by the frontend. */
  drillKey: string;
  drillId: string | null;
  runningBookValue: number | null;
}

export interface AssetLifeCycleResult extends PagedInquiryResult {
  rows: AssetEventRow[];
  eventCount: number;
  fixedAssetId: string;
  assetNumber: string;
  assetName: string;
  assetNameAr: string;
  acquiredOn: string | null;
  inServiceOn: string | null;
  closedOn: string | null;
  currentStatus: FixedAssetStatus;
  cost: number;
  accumulatedDepreciation: number;
  netBookValue: number;
  daysHeld: number | null;
}

export type AssetLifeCycleFilter = FixedAssetInquiryFilter;

export interface AssetFinancialMovementsResult extends PagedInquiryResult {
  rows: AssetEventRow[];
  eventCount: number;
  totalDebit: number;
  totalCredit: number;
  totalCostIncrease: number;
  totalCostDecrease: number;
  totalDepreciation: number;
  closingBookValue: number;
  /** A running book value needs one subject; false when many assets were asked for. */
  hasRunningBalance: boolean;
}

export interface AssetFinancialMovementsFilter extends FixedAssetInquiryFilter {
  includeUnposted?: boolean;
  kind?: number;
}

export interface AssetMovementsResult extends PagedInquiryResult {
  rows: AssetEventRow[];
  eventCount: number;
  custodyEvents: number;
  locationEvents: number;
  transferEvents: number;
  inventoryEvents: number;
}

export interface AssetMovementsInquiryFilter extends FixedAssetInquiryFilter {
  kind?: number;
  custodianUserId?: string;
}

export interface AssetDailyTransactionsResult extends PagedInquiryResult {
  rows: AssetEventRow[];
  eventCount: number;
  totalDebit: number;
  totalCredit: number;
  postedCount: number;
  unpostedCount: number;
  operationalCount: number;
}

export interface AssetDailyTransactionsInquiryFilter extends FixedAssetInquiryFilter {
  kind?: number;
  postedOnly?: boolean;
}

export interface AssetRecordRow {
  id: string;
  assetNumber: string;
  assetName: string;
  assetNameAr: string;
  serialNumber: string | null;
  fixedAssetGroupId: string;
  groupCode: string | null;
  groupName: string | null;
  fixedAssetLocationId: string | null;
  locationCode: string | null;
  locationName: string | null;
  supplierId: string | null;
  supplierName: string | null;
  branchId: string | null;
  branchName: string | null;
  custodianUserId: string | null;
  custodianName: string | null;
  acquisitionDate: string | null;
  inServiceDate: string | null;
  acquisitionCost: number;
  capitalisedAdditions: number;
  revaluationAdjustment: number;
  derecognisedCost: number;
  cost: number;
  accumulatedDepreciation: number;
  netBookValue: number;
  salvageValue: number;
  /** The status ON THE DATE ASKED ABOUT, never today's. */
  status: FixedAssetStatus;
  currencyCode: string | null;
  isActive: boolean;
}

export interface AssetRecordsResult extends PagedInquiryResult {
  rows: AssetRecordRow[];
  asOfDate: string | null;
  assetCount: number;
  totalAcquisitionCost: number;
  totalCapitalisedAdditions: number;
  totalRevaluation: number;
  totalCost: number;
  totalAccumulatedDepreciation: number;
  totalNetBookValue: number;
}

export interface AssetRecordsFilter extends FixedAssetInquiryFilter {
  status?: number;
  includeChildGroups?: boolean;
  includeChildLocations?: boolean;
  includeClosed?: boolean;
}

export interface AssetTreeRow {
  groupId: string;
  parentGroupId: string | null;
  code: string;
  name: string;
  nameAr: string;
  level: number;
  hasChildren: boolean;
  ownAssetCount: number;
  ownCost: number;
  ownAccumulatedDepreciation: number;
  ownNetBookValue: number;
  totalAssetCount: number;
  totalCost: number;
  totalAccumulatedDepreciation: number;
  totalNetBookValue: number;
}

export interface AssetTreeResult extends PagedInquiryResult {
  rows: AssetTreeRow[];
  asOfDate: string | null;
  groupCount: number;
  assetCount: number;
  totalCost: number;
  totalAccumulatedDepreciation: number;
  totalNetBookValue: number;
}

export interface AssetTreeInquiryFilter extends FixedAssetInquiryFilter {
  includeEmptyGroups?: boolean;
  includeClosed?: boolean;
}

export interface AssetExpenseInquiryRow {
  id: string;
  expenseNumber: string;
  expenseDate: string;
  fixedAssetId: string;
  assetNumber: string;
  assetName: string;
  assetNameAr: string;
  groupName: string | null;
  treatment: AssetExpenseTreatment;
  expenseAccountId: string | null;
  expenseAccountName: string | null;
  supplierId: string | null;
  supplierName: string | null;
  costCenterId: string | null;
  costCenterName: string | null;
  currencyCode: string | null;
  exchangeRate: number;
  originalAmount: number;
  returnedAmount: number;
  netAmount: number;
  status: AssetDocumentStatus;
  journalVoucherId: string | null;
  journalVoucherNumber: string | null;
  description: string | null;
}

export interface AssetExpensesInquiryResult extends PagedInquiryResult {
  rows: AssetExpenseInquiryRow[];
  expenseCount: number;
  totalOriginal: number;
  totalReturned: number;
  totalNet: number;
  totalOperating: number;
  totalCapitalised: number;
}

export interface AssetExpensesInquiryFilter extends FixedAssetInquiryFilter {
  treatment?: number;
  status?: number;
  costCenterId?: string;
  postedOnly?: boolean;
}

export interface AssetTransferStatusRow {
  id: string;
  transferNumber: string;
  transferDate: string;
  fixedAssetId: string;
  assetNumber: string;
  assetName: string;
  assetNameAr: string;
  fromLocationName: string | null;
  toLocationName: string | null;
  fromBranchName: string | null;
  toBranchName: string | null;
  fromGroupName: string | null;
  toGroupName: string | null;
  fromCustodianName: string | null;
  toCustodianName: string | null;
  status: AssetTransferStatus;
  dispatchedOn: string | null;
  receivedOn: string | null;
  daysInTransit: number | null;
  isReclassification: boolean;
  requiredReclassification: boolean;
  costAtTransfer: number;
  accumulatedDepreciationAtTransfer: number;
  journalVoucherId: string | null;
  journalVoucherNumber: string | null;
  reason: string | null;
}

export interface AssetTransferStatusResult extends PagedInquiryResult {
  rows: AssetTransferStatusRow[];
  transferCount: number;
  draftCount: number;
  approvedCount: number;
  inTransitCount: number;
  receivedCount: number;
  cancelledCount: number;
  reclassifiedCount: number;
  totalCostMoved: number;
}

export interface AssetTransferStatusInquiryFilter extends FixedAssetInquiryFilter {
  status?: number;
  inTransitOnly?: boolean;
}

// ── Human Resources › Employee Affairs › Basic Data › Employee Card ─────────────────────────
//
// HR MASTER DATA. No salary, no contract value, no leave balance, no attendance — none of those
// is specified for this card. `userId` is the SAME `users.id` the rest of the system already
// names, exactly as the personnel lists carry it; it is never a second identity for a document
// to point at.

/** The HR vocabularies. ONE table, three lists — the shape `PartnerLookup` already uses. */

/* ══════════════════════════════════════════════════════════════════════════════════════════
 * System Settings — إعدادات النظام
 *
 * `User` and `CompanyAccess` above are the SAME shapes the administration screens bind to:
 * `UserDto` and `CompanyAccessDto` are what both the sign-in response and `api/users` return,
 * so nothing here re-declares them.
 * ══════════════════════════════════════════════════════════════════════════════════════════ */

/** `User.status`; the backend enum. */
export const UserStatus = {
  Pending: 0,
  Active: 1,
  Suspended: 2,
  Locked: 3,
  Disabled: 4,
} as const;

export type UserStatusValue = (typeof UserStatus)[keyof typeof UserStatus];

/** Envelope every paged administration endpoint returns. */
export interface SystemPagedResult<T> {
  items: T[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

export interface SystemUserRole {
  roleId: string;
  name: string;
  nameAr: string | null;
  isSystemRole: boolean;
}

export interface SystemUserDetail {
  user: User;
  roles: SystemUserRole[];
  companies: CompanyAccess[];
  /** Effective permission keys, already unioned across the user's roles. */
  permissions: string[];
}

export interface SystemUserFilter {
  /** Maps to PagedRequest.SearchTerm — the API ignores any other name. */
  searchTerm?: string;
  status?: number;
  roleId?: string;
  pageNumber?: number;
  pageSize?: number;
}

export interface CreateSystemUserInput {
  userName: string;
  email: string;
  password: string;
  fullName: string;
  fullNameAr?: string | null;
  phoneNumber?: string | null;
  preferredLanguage: number;
  status: number;
  mustChangePassword: boolean;
  roleIds: string[];
  /** EMPTY means every company in the tenant — absence of a restriction, not absence of access. */
  companyIds: string[];
  defaultCompanyId?: string | null;
  /** Branch selected for the user at sign-in. Independent of the company-access rows. */
  defaultBranchId?: string | null;
}

export interface UpdateSystemUserInput {
  email: string;
  fullName: string;
  fullNameAr?: string | null;
  phoneNumber?: string | null;
  preferredLanguage: number;
  status: number;
  /** `null` leaves company access untouched; an empty array clears every restriction. */
  companyIds?: string[] | null;
  defaultCompanyId?: string | null;
  defaultBranchId?: string | null;
}

export interface ResetSystemUserPasswordInput {
  newPassword: string;
  mustChangePassword: boolean;
}

export interface SystemRole {
  id: string;
  name: string;
  nameAr: string | null;
  description: string | null;
  isSystemRole: boolean;
  isActive: boolean;
  userCount: number;
  permissionCount: number;
}

export interface SystemRoleDetail {
  role: SystemRole;
  permissions: string[];
}

export interface SystemRoleFilter {
  /** Maps to PagedRequest.SearchTerm — the API ignores any other name. */
  searchTerm?: string;
  isActive?: boolean;
  isSystemRole?: boolean;
  pageNumber?: number;
  pageSize?: number;
}

export interface SaveSystemRoleInput {
  name: string;
  nameAr?: string | null;
  description?: string | null;
  isActive: boolean;
  /** Create only. Editing an existing role's grants goes through `assignRolePermissions`. */
  permissions?: string[];
}

/**
 * One grantable capability. `grantableByCurrentUser` is false when the signed-in administrator
 * does not hold it themselves — the picker greys those out, and the handler refuses them.
 */
export interface PermissionEntry {
  key: string;
  module: string;
  displayName: string;
  displayNameAr: string | null;
  description: string | null;
  grantableByCurrentUser: boolean;
}

export interface PermissionModule {
  module: string;
  permissions: PermissionEntry[];
}

export interface PermissionCatalog {
  modules: PermissionModule[];
  totalCount: number;
}

/** `AuditTrailEntry.action`; the backend enum. */
export const AuditAction = {
  Created: 1,
  Updated: 2,
  Deleted: 3,
  Login: 4,
  LoginFailed: 5,
  Logout: 6,
  PermissionDenied: 7,
  JournalAudited: 8,
  JournalPosted: 9,
  JournalRejected: 10,
  JournalReversed: 11,
  Exported: 12,
  JournalReopened: 13,
} as const;

export type AuditActionValue = (typeof AuditAction)[keyof typeof AuditAction];

export interface AuditTrailEntry {
  id: string;
  userId: string | null;
  userName: string | null;
  action: number;
  entityName: string;
  entityId: string | null;
  entityKey: string | null;
  summary: string | null;
  affectedColumns: string | null;
  /** Raw JSON as the interceptor captured it — shape varies by entity. */
  oldValues: string | null;
  newValues: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  correlationId: string | null;
  occurredAt: string;
}

export interface AuditTrailEntityOption {
  entityName: string;
  entryCount: number;
}

export interface AuditTrailFilter {
  /** Maps to PagedRequest.SearchTerm — the API ignores any other name. */
  searchTerm?: string;
  action?: number;
  entityName?: string;
  userId?: string;
  dateFrom?: string;
  dateTo?: string;
  pageNumber?: number;
  pageSize?: number;
}

export interface SystemCompany {
  id: string;
  code: string;
  name: string;
  nameAr: string | null;
  legalName: string | null;
  taxNumber: string | null;
  commercialRegistrationNumber: string | null;
  address: string | null;
  city: string | null;
  country: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  logoUrl: string | null;
  localCurrencyId: string | null;
  localCurrencyCode: string | null;
  /** Month 1-12 the fiscal year opens on. */
  fiscalYearStartMonth: number;
  isActive: boolean;
  branchCount: number;
  createdAt: string;
  updatedAt: string | null;
}

export interface SaveSystemCompanyInput {
  code: string;
  name: string;
  nameAr?: string | null;
  legalName?: string | null;
  taxNumber?: string | null;
  commercialRegistrationNumber?: string | null;
  address?: string | null;
  city?: string | null;
  country?: string | null;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  localCurrencyId?: string | null;
  fiscalYearStartMonth: number;
  isActive: boolean;
}

export interface SystemBranch {
  id: string;
  companyId: string;
  companyCode: string;
  companyName: string;
  code: string;
  name: string;
  nameAr: string | null;
  address: string | null;
  city: string | null;
  country: string | null;
  phone: string | null;
  email: string | null;
  /** Exactly one branch per company carries this. */
  isHeadOffice: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string | null;
}

export interface SaveSystemBranchInput {
  companyId: string;
  code: string;
  name: string;
  nameAr?: string | null;
  address?: string | null;
  city?: string | null;
  country?: string | null;
  phone?: string | null;
  email?: string | null;
  isHeadOffice: boolean;
  isActive: boolean;
}

/* ══════════════════════════════════════════════════════════════════════════════════════════
 * Human Resources — شؤون الموظفين
 *
 * Every classification is an ID, never a label: nationality and the address hierarchy come from
 * `country_regions`, the four small vocabularies from `hr_lookups`, and the responsible entity
 * from its own master. Renaming any of them reaches every employee at once.
 * ══════════════════════════════════════════════════════════════════════════════════════════ */

export const HR_GENDER = { Male: 1, Female: 2 } as const;
export type HrGender = (typeof HR_GENDER)[keyof typeof HR_GENDER];

export const HR_MARITAL_STATUS = { Single: 1, Married: 2, Divorced: 3, Widowed: 4 } as const;
export type HrMaritalStatus = (typeof HR_MARITAL_STATUS)[keyof typeof HR_MARITAL_STATUS];

/** Where the employee stands with the company. Separate from `isActive`, which is about the RECORD. */
export const EMPLOYMENT_STATUS = {
  Active: 1,
  OnLeave: 2,
  Suspended: 3,
  Terminated: 4,
  Resigned: 5,
} as const;
export type EmploymentStatus = (typeof EMPLOYMENT_STATUS)[keyof typeof EMPLOYMENT_STATUS];

/** The four vocabularies the employee card picks from. One table, discriminated. */
export const HR_LOOKUP_TYPE = {
  Religion: 1,
  Department: 2,
  JobTitle: 3,
  EmploymentType: 4,
  TechnicalClassification: 5,
  Grade: 6,
  EmployeeClassification: 7,
  ContractType: 8,
  GosiPolicy: 10,
  HealthInsurancePolicy: 11,

  // 9 was «فئة الإجازة» and is retired: the leave TYPE master owns that vocabulary now.
  // The daily transactions add their own — each one a list the company states for itself,
  // and none of them seeded.
  DepartureType: 12,
  TerminationType: 13,
  ViolationType: 14,
  DisciplinaryActionType: 15,
  EmployeeDocumentType: 16,
  LetterType: 17,
  InjuryType: 18,
  MedicalRecordType: 19,
  CompanyDocumentType: 20,
  PolicyCategory: 21,
  ClearanceDepartment: 22,
} as const;
export type HrLookupType = (typeof HR_LOOKUP_TYPE)[keyof typeof HR_LOOKUP_TYPE];

export interface HrLookup {
  id: string;
  lookupType: HrLookupType;
  code: string;
  nameAr: string;
  name: string;
  isActive: boolean;
}

/**
 * One entry of one HR vocabulary, as its settings screen lists it.
 *
 * Separate from `HrLookup` — which is what the employee card's picker receives — because the
 * picker only ever sees the ACTIVE entries and the settings screen must see both.
 */
export interface HrLookupEntry {
  id: string;
  lookupType: HrLookupType;
  code: string;
  nameAr: string;
  name: string;
  isActive: boolean;
}

export interface ResponsibleEntity {
  id: string;
  code: string;
  nameAr: string;
  name: string;
  isActive: boolean;
  notes: string | null;
  /** How many employees name it. Zero is the only state in which it may be deleted. */
  employeeCount: number;
}

export interface SaveResponsibleEntityInput {
  id?: string | null;
  code: string;
  nameAr: string;
  name: string;
  isActive: boolean;
  notes?: string | null;
}

export interface HrRegionOption {
  id: string;
  parentId: string | null;
  code: string;
  nameAr: string;
  name: string;
  nationalityAr: string | null;
}

export interface HrNamedOption {
  id: string;
  code: string;
  nameAr: string;
  name: string;
}

/** Everything the card's dropdowns need, in one call. */
export interface EmployeeFormContext {
  countries: HrRegionOption[];
  regions: HrRegionOption[];
  lookups: HrLookup[];
  responsibleEntities: ResponsibleEntity[];
  branches: HrNamedOption[];
  costCenters: HrNamedOption[];
  /** Candidates for المدير المباشر and المشرف — active employees of this company. */
  managers: EmployeeOption[];
  /** «فئة الإجازة», read from the leave type master rather than a lookup of its own. */
  leaveTypes: HrNamedOption[];
}

export interface EmployeeListItem {
  id: string;
  employeeNumber: string;
  serial: number;
  fullNameAr: string;
  fullName: string | null;
  departmentAr: string | null;
  jobTitleAr: string | null;
  responsibleEntityAr: string | null;
  employmentStatus: EmploymentStatus;
  isActive: boolean;
  hireDate: string | null;
  hasPhoto: boolean;
}

export interface Employee {
  id: string;
  employeeNumber: string;
  serial: number;

  firstNameAr: string;
  secondNameAr: string | null;
  thirdNameAr: string | null;
  familyNameAr: string;

  firstName: string | null;
  secondName: string | null;
  thirdName: string | null;
  familyName: string | null;

  nationalityRegionId: string | null;
  nationalityAr: string | null;
  religionLookupId: string | null;
  birthPlace: string | null;
  motherName: string | null;
  nationalId: string | null;
  gender: HrGender | null;
  maritalStatus: HrMaritalStatus | null;
  birthDate: string | null;
  /** The Hijri date as written on the document — text, never converted from the Gregorian one. */
  birthDateHijri: string | null;
  hasSpecialNeeds: boolean;

  hasPhoto: boolean;
  photoFileName: string | null;

  countryRegionId: string | null;
  governorateRegionId: string | null;
  cityRegionId: string | null;
  areaRegionId: string | null;
  addressLine: string | null;
  phone: string | null;
  alternatePhone: string | null;
  email: string | null;

  hireDate: string | null;
  departmentLookupId: string | null;
  jobTitleLookupId: string | null;
  employmentTypeLookupId: string | null;
  responsibleEntityId: string | null;
  responsibleEntityAr: string | null;
  branchId: string | null;
  costCenterId: string | null;
  employmentStatus: EmploymentStatus;
  employmentStatusDate: string | null;

  // ── المعلومات الوظيفية ────────────────────────────────────────────────────────────────
  secondaryJobTitleLookupId: string | null;
  technicalClassificationLookupId: string | null;
  gradeLookupId: string | null;
  employeeClassificationLookupId: string | null;
  contractTypeLookupId: string | null;
  defaultLeaveTypeId: string | null;

  directManagerEmployeeId: string | null;
  /** Resolved server-side so the card can name the manager without a second round trip. */
  directManagerNameAr: string | null;
  directManagerNumber: string | null;
  supervisorEmployeeId: string | null;
  supervisorNameAr: string | null;
  supervisorNumber: string | null;

  isHazardousOccupation: boolean;
  dailyWorkingHours: number | null;
  probationPeriodDays: number | null;
  allowsOvertime: boolean;
  payFrequency: number | null;

  // ── العقد الحالي ──────────────────────────────────────────────────────────────────────
  isFixedTermContract: boolean;
  currentContractStartDate: string | null;
  currentContractEndDate: string | null;
  contractRenewalCalculationMethod: number | null;

  // ── الاشتراكات والتأمينات ─────────────────────────────────────────────────────────────
  isGosiRegistered: boolean;
  gosiRegistrationDate: string | null;
  gosiNumber: string | null;
  gosiPolicyLookupId: string | null;
  hasHealthInsurance: boolean;
  healthInsurancePolicyLookupId: string | null;

  spouseIsEmployed: boolean;
  isFamilyProvider: boolean;

  userId: string | null;
  isActive: boolean;
  notes: string | null;

  createdAt: string;
  updatedAt: string | null;
}

/** طبيعة تعاطي الأجر — 1 شهري · 2 يومي · 3 بالساعة. */
export const PAY_FREQUENCY = { Monthly: 1, Daily: 2, Hourly: 3 } as const;

/** آلية احتساب تجديد العقد — 1 بالأيام · 2 بالأشهر · 3 حسب التقويم. */
export const CONTRACT_RENEWAL_METHOD = { Daily: 1, Monthly: 2, Calendar: 3 } as const;

/** An employee as the manager/supervisor pickers list them. */
export interface EmployeeOption {
  id: string;
  employeeNumber: string;
  fullNameAr: string;
  fullName: string | null;
  departmentAr: string | null;
  jobTitleAr: string | null;
  branchAr: string | null;
  employmentStatus: EmploymentStatus;
}

export interface SaveEmployeeInput {
  id?: string | null;
  /** Blank on create — the server assigns the next number. */
  employeeNumber?: string | null;

  firstNameAr: string;
  secondNameAr?: string | null;
  thirdNameAr?: string | null;
  familyNameAr: string;

  firstName?: string | null;
  secondName?: string | null;
  thirdName?: string | null;
  familyName?: string | null;

  nationalityRegionId?: string | null;
  religionLookupId?: string | null;
  birthPlace?: string | null;
  motherName?: string | null;
  nationalId?: string | null;
  gender?: number | null;
  maritalStatus?: number | null;
  birthDate?: string | null;
  birthDateHijri?: string | null;
  hasSpecialNeeds: boolean;

  countryRegionId?: string | null;
  governorateRegionId?: string | null;
  cityRegionId?: string | null;
  areaRegionId?: string | null;
  addressLine?: string | null;
  phone?: string | null;
  alternatePhone?: string | null;
  email?: string | null;

  hireDate?: string | null;
  departmentLookupId?: string | null;
  jobTitleLookupId?: string | null;
  employmentTypeLookupId?: string | null;
  responsibleEntityId?: string | null;
  branchId?: string | null;
  costCenterId?: string | null;
  employmentStatus: number;
  isActive: boolean;
  notes?: string | null;
  employmentStatusDate: string | null;
  secondaryJobTitleLookupId: string | null;
  technicalClassificationLookupId: string | null;
  gradeLookupId: string | null;
  employeeClassificationLookupId: string | null;
  contractTypeLookupId: string | null;
  defaultLeaveTypeId: string | null;
  directManagerEmployeeId: string | null;
  supervisorEmployeeId: string | null;
  isHazardousOccupation: boolean;
  dailyWorkingHours: number | null;
  probationPeriodDays: number | null;
  allowsOvertime: boolean;
  payFrequency: number | null;
  isFixedTermContract: boolean;
  currentContractStartDate: string | null;
  currentContractEndDate: string | null;
  contractRenewalCalculationMethod: number | null;
  isGosiRegistered: boolean;
  gosiRegistrationDate: string | null;
  gosiNumber: string | null;
  gosiPolicyLookupId: string | null;
  hasHealthInsurance: boolean;
  healthInsurancePolicyLookupId: string | null;
  spouseIsEmployed: boolean;
  isFamilyProvider: boolean;
}

export interface SaveHrLookupInput {
  id?: string | null;
  lookupType: number;
  code: string;
  nameAr: string;
  name: string;
  isActive: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────────────────────
// الموارد البشرية › شؤون الموظفين › الإجازات
//
// The document, the amendment that changes it after approval, and the ledger underneath both.
// A leave request moves a balance ONLY when it is approved; everything before that — drafting,
// editing, submitting — leaves the employee's entitlement exactly as it was.
// ─────────────────────────────────────────────────────────────────────────────────────────────

/** Draft = 1, Submitted = 2, Approved = 3, Rejected = 4, Cancelled = 5. */
export type LeaveDocumentStatus = 1 | 2 | 3 | 4 | 5;

/** OpeningBalance = 1, Entitlement = 2, Usage = 3, Adjustment = 4, Reversal = 5. */
export type LeaveMovementType = 1 | 2 | 3 | 4 | 5;

/** Cancel = 1, Extend = 2, Interrupt = 3, ChangeCategory = 4. */
export type LeaveAmendmentAction = 1 | 2 | 3 | 4;

/** Increase = 1, Decrease = 2. */
export type LeaveAdjustmentDirection = 1 | 2;

export interface LeaveType {
  id: string;
  code: string;
  nameAr: string;
  name: string;
  isActive: boolean;
  isPaid: boolean;
  requiresBalance: boolean;
  allowsNegativeBalance: boolean;
  requiresAttachment: boolean;
  /** Whether the submitter may also approve. Off by default — separation of duties. */
  allowSelfApproval: boolean;
  minimumDays: number | null;
  maximumDays: number | null;
  noticeDays: number | null;
  annualEntitlementDays: number | null;
  carryForwardAllowed: boolean;
  maximumCarryForwardDays: number | null;
  notes: string | null;
  /** Set when anything already refers to it — the screen offers deactivation instead of delete. */
  inUse: boolean;
}

export interface SaveLeaveTypeInput {
  id?: string | null;
  code: string;
  nameAr: string;
  name: string;
  isActive: boolean;
  isPaid: boolean;
  requiresBalance: boolean;
  allowsNegativeBalance: boolean;
  requiresAttachment: boolean;
  allowSelfApproval: boolean;
  minimumDays?: number | null;
  maximumDays?: number | null;
  noticeDays?: number | null;
  annualEntitlementDays?: number | null;
  carryForwardAllowed: boolean;
  maximumCarryForwardDays?: number | null;
  notes?: string | null;
}

export interface LeaveRequestListItem {
  id: string;
  number: string;
  employeeId: string;
  employeeNumber: string;
  employeeNameAr: string;
  leaveTypeId: string;
  leaveTypeNameAr: string;
  requestDate: string;
  fromDate: string;
  toDate: string;
  returnDate: string | null;
  requestedDays: number;
  approvedDays: number;
  status: LeaveDocumentStatus;
  branchNameAr: string | null;
  departmentNameAr: string | null;
  replacementEmployeeNameAr: string | null;
  hasAttachments: boolean;
  isAmended: boolean;
}

export interface LeaveAttachment {
  id: string;
  fileName: string;
  contentType: string | null;
  fileSize: number;
  notes: string | null;
  createdAt: string;
}

export interface LeaveAmendment {
  id: string;
  number: string;
  leaveRequestId: string;
  leaveRequestNumber: string;
  action: LeaveAmendmentAction;
  effectiveDate: string;
  oldFromDate: string;
  oldToDate: string;
  newFromDate: string | null;
  newToDate: string | null;
  oldDays: number;
  newDays: number;
  daysDifference: number;
  oldLeaveTypeNameAr: string | null;
  newLeaveTypeNameAr: string | null;
  reason: string | null;
  notes: string | null;
  approvedAt: string | null;
  approvedByName: string | null;
}

export interface LeaveRequestDetail {
  id: string;
  number: string;
  employeeId: string;
  employeeNumber: string;
  employeeNameAr: string;
  leaveTypeId: string;
  leaveTypeNameAr: string;
  requiresAttachment: boolean;
  requestDate: string;
  fromDate: string;
  toDate: string;
  returnDate: string | null;
  requestedDays: number;
  approvedDays: number;
  replacementEmployeeId: string | null;
  replacementEmployeeNameAr: string | null;
  branchId: string | null;
  branchNameAr: string | null;
  departmentLookupId: string | null;
  departmentNameAr: string | null;
  reason: string | null;
  notes: string | null;
  status: LeaveDocumentStatus;
  submittedAt: string | null;
  submittedByName: string | null;
  approvedAt: string | null;
  approvedByName: string | null;
  rejectedAt: string | null;
  rejectedByName: string | null;
  rejectionReason: string | null;
  cancelledAt: string | null;
  cancelledByName: string | null;
  /** Read live, not stored: the approver needs to know what is available today. */
  availableDays: number;
  attachments: LeaveAttachment[];
  amendments: LeaveAmendment[];
}

export interface SaveLeaveRequestInput {
  id?: string | null;
  employeeId: string;
  leaveTypeId: string;
  requestDate: string;
  fromDate: string;
  toDate: string;
  replacementEmployeeId?: string | null;
  reason?: string | null;
  notes?: string | null;
}

export interface SaveLeaveAmendmentInput {
  leaveRequestId: string;
  action: LeaveAmendmentAction;
  effectiveDate: string;
  newToDate?: string | null;
  newLeaveTypeId?: string | null;
  reason?: string | null;
  notes?: string | null;
}

export interface SaveLeaveBalanceAdjustmentInput {
  employeeId: string;
  leaveTypeId: string;
  direction: LeaveAdjustmentDirection;
  days: number;
  isOpeningBalance: boolean;
  effectiveDate: string;
  reason: string;
  notes?: string | null;
}

export interface LeaveMovement {
  id: string;
  employeeId: string;
  employeeNumber: string;
  employeeNameAr: string;
  leaveTypeId: string;
  leaveTypeNameAr: string;
  movementType: LeaveMovementType;
  /** Signed: negative consumed days, positive granted or returned them. */
  days: number;
  effectiveDate: string;
  sourceDocumentType: string;
  sourceDocumentId: string;
  sourceDocumentNumber: string | null;
  balanceBefore: number;
  balanceAfter: number;
  notes: string | null;
  createdAt: string;
}

export interface LeaveBalanceRow {
  employeeId: string;
  employeeNumber: string;
  employeeNameAr: string;
  departmentNameAr: string | null;
  branchNameAr: string | null;
  leaveTypeId: string;
  leaveTypeNameAr: string;
  entitled: number;
  used: number;
  adjusted: number;
  available: number;
  /** Already deducted from `available` — shown beside it, never added to it. */
  upcoming: number;
}

export interface LeaveTypeOption {
  id: string;
  code: string;
  nameAr: string;
  name: string;
  isPaid: boolean;
  requiresBalance: boolean;
  allowsNegativeBalance: boolean;
  requiresAttachment: boolean;
  minimumDays: number | null;
  maximumDays: number | null;
  noticeDays: number | null;
}

export interface LeaveEmployeeOption {
  id: string;
  employeeNumber: string;
  nameAr: string;
  departmentNameAr: string | null;
  branchNameAr: string | null;
  employmentStatus: EmploymentStatus;
}

export interface LeaveNamedOption {
  id: string;
  nameAr: string;
  name: string | null;
}

export interface LeaveFormContext {
  leaveTypes: LeaveTypeOption[];
  employees: LeaveEmployeeOption[];
  departments: LeaveNamedOption[];
  branches: LeaveNamedOption[];
}

/** Everything any Leave report might be filtered by. One shape, seven reports. */
export interface LeaveReportFilter {
  employeeId?: string;
  leaveTypeId?: string;
  branchId?: string;
  departmentLookupId?: string;
  status?: number;
  /** EmploymentStatus: Active 1, OnLeave 2, Suspended 3, Terminated 4, Resigned 5. */
  employmentStatus?: number;
  /** LeaveType 1, Employee 2, Department 3, Branch 4, Month 5. Statistics only. */
  groupBy?: number;
  fromDate?: string;
  toDate?: string;
  asOfDate?: string;
  activeEmployeesOnly?: boolean;
  includeDraft?: boolean;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDescending?: boolean;
}

/** Everything any employee register report might be filtered by. */
export interface EmployeeReportFilter {
  employeeId?: string;
  branchId?: string;
  departmentLookupId?: string;
  jobTitleLookupId?: string;
  gradeLookupId?: string;
  employeeClassificationLookupId?: string;
  /** EmploymentStatus: Active 1, OnLeave 2, Suspended 3, Terminated 4, Resigned 5. */
  employmentStatus?: number;
  activeEmployeesOnly?: boolean;
  fromDate?: string;
  toDate?: string;
  /** The day age and length of service are measured from. Defaults to today, server-side. */
  asOfDate?: string;
  /** How far ahead «قاربت على الانتهاء» looks. Defaults to 30. */
  withinDays?: number;
  search?: string;
  /** الوثائق والعقود — only the reports that declare these controls ever send them. */
  contractTypeLookupId?: string;
  /** EmployeeContractStatus: Pending 1, Active 2, Expired 3, Terminated 4, Renewed 5. */
  contractStatus?: number;
  /** A row of the company's OWN document vocabulary — never a hard-coded id. */
  documentTypeLookupId?: string;
  expiryFrom?: string;
  expiryTo?: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDescending?: boolean;
}

/** What one employee register report answers with. Same shape the Leave reports use. */
export interface EmployeeReportResult extends PagedInquiryResult {
  code: string;
  title: string;
  titleAr: string;
  columns: ReportColumn[];
  rows: ReportRow[];
  totals: ReportTotal[];
  parameters: ReportParameter[];
  asOfDate: string | null;
}

export interface LeaveReportResult extends PagedInquiryResult {
  code: string;
  title: string;
  titleAr: string;
  columns: ReportColumn[];
  rows: ReportRow[];
  totals: ReportTotal[];
  parameters: ReportParameter[];
  /** Echoed back so the header can say which day the figures are measured from. */
  asOfDate: string | null;
}

// ─────────────────────────────────────────────────────────────────────────────────────────────
// الموارد البشرية › شؤون الموظفين › الحركات اليومية
//
// One shape serves every HR document: the common part is typed, and each document's own fields
// arrive in `fields`, keyed by the save command's property names. That is what lets a field added
// on the server appear on its screen without a second declaration here.
// ─────────────────────────────────────────────────────────────────────────────────────────────

/** Draft 1 · Submitted 2 · Approved 3 · Rejected 4 · Cancelled 5 · Executed 6. */
export type HrTransactionStatus = 1 | 2 | 3 | 4 | 5 | 6;

export const HR_TRANSACTION_STATUS = {
  Draft: 1,
  Submitted: 2,
  Approved: 3,
  Rejected: 4,
  Cancelled: 5,
  Executed: 6,
} as const;

/** The document kinds, matching HrDocumentKind on the server. */
export const HR_DOCUMENT_KIND = {
  LeaveRequest: 1,
  LeaveAmendment: 2,
  LeaveBalanceAdjustment: 3,
  DepartureRequest: 10,
  DepartureAmendment: 11,
  EmployeeTransferRequest: 20,
  EmployeeTransfer: 21,
  EmployeePromotion: 22,
  JobTitleChange: 23,
  ManagerAssignment: 24,
  EmployeeContract: 30,
  ContractRenewal: 31,
  ContractExtension: 32,
  ContractAmendment: 33,
  ContractTermination: 34,
  EndOfService: 35,
  EmployeeClearance: 36,
  EmployeeDocumentMovement: 40,
  OvertimeRequest: 50,
  OvertimeAssignment: 51,
  BusinessTrip: 52,
  TravelTicket: 53,
  DisciplinaryAction: 60,
  WorkInjury: 61,
  MedicalRecord: 62,
  AdministrativeLetter: 70,
  EmployeeNotice: 71,
  Communication: 72,
  CompanyDocument: 80,
  PolicyRegulation: 81,
} as const;

export type HrDocumentKind = (typeof HR_DOCUMENT_KIND)[keyof typeof HR_DOCUMENT_KIND];

export interface HrDocumentListItem {
  id: string;
  number: string;
  documentDate: string;
  employeeId: string;
  employeeNumber: string;
  employeeNameAr: string;
  branchNameAr: string | null;
  departmentNameAr: string | null;
  status: HrTransactionStatus;
  reason: string | null;
  hasAttachments: boolean;
}

/** One company document, as its inquiry lists it. Negative `daysToExpiry` means it is past. */
export interface CompanyDocumentInquiryRow {
  id: string;
  number: string;
  title: string;
  documentNumber: string | null;
  documentTypeNameAr: string | null;
  issueDate: string | null;
  expiryDate: string | null;
  issuer: string | null;
  responsibleEntityNameAr: string | null;
  daysToExpiry: number | null;
}

/** One row of the employee state ledger — what changed, when, and which document did it. */
export interface EmployeeStateChangeRow {
  id: string;
  employeeId: string;
  employeeNumber: string;
  employeeNameAr: string;
  changeKind: number;
  effectiveDate: string;
  oldValueNumber: number | null;
  newValueNumber: number | null;
  sourceDocumentKind: HrDocumentKind | null;
  sourceDocumentNumber: string | null;
  notes: string | null;
}

/** The unified daily register: the shared list shape plus the kind that produced the row. */
export interface HrDailyRegisterRow {
  kind: HrDocumentKind;
  id: string;
  number: string;
  documentDate: string;
  employeeId: string;
  employeeNumber: string;
  employeeNameAr: string;
  branchNameAr: string | null;
  departmentNameAr: string | null;
  status: HrTransactionStatus;
  reason: string | null;
}

export interface HrAttachment {
  id: string;
  fileName: string;
  contentType: string | null;
  fileSize: number;
  notes: string | null;
  createdAt: string;
}

export interface HrDocument {
  id: string;
  kind: HrDocumentKind;
  number: string;
  documentDate: string;
  employeeId: string;
  employeeNumber: string;
  employeeNameAr: string;
  branchId: string | null;
  branchNameAr: string | null;
  departmentLookupId: string | null;
  departmentNameAr: string | null;
  reason: string | null;
  notes: string | null;
  status: HrTransactionStatus;
  submittedAt: string | null;
  submittedByName: string | null;
  approvedAt: string | null;
  approvedByName: string | null;
  rejectedAt: string | null;
  rejectedByName: string | null;
  rejectionReason: string | null;
  cancelledAt: string | null;
  cancelledByName: string | null;
  executedAt: string | null;
  executedByName: string | null;
  /** The document's own fields, keyed by the save command's property names. */
  fields: Record<string, unknown>;
  attachments: HrAttachment[];
}

export interface HrDocumentSearchFilter {
  number?: string;
  employeeNumber?: string;
  employeeId?: string;
  branchId?: string;
  departmentLookupId?: string;
  status?: number;
  fromDate?: string;
  toDate?: string;
  /** Honoured by حركات وثائق الموظف alone — an iqama is a document type, not a table. */
  documentTypeLookupId?: string;
}

/** إرسال · اعتماد · رفض · إلغاء · تنفيذ. */
export type HrWorkflowAction = 1 | 2 | 3 | 4 | 5;

export const HR_WORKFLOW_ACTION = {
  Submit: 1,
  Approve: 2,
  Reject: 3,
  Cancel: 4,
  Execute: 5,
} as const;

/** Everything the HR daily transaction forms need to fill their pickers. */
export interface HrDailyFormContext {
  employees: LeaveEmployeeOption[];
  branches: LeaveNamedOption[];
  departments: LeaveNamedOption[];
  lookups: HrLookup[];
  costCenters: HrNamedOption[];
  currencies: HrNamedOption[];
}

export interface CompanyDocumentRow {
  id: string;
  number: string;
  documentTypeLookupId: string | null;
  documentTypeNameAr: string | null;
  title: string;
  documentNumber: string | null;
  issueDate: string | null;
  expiryDate: string | null;
  issuer: string | null;
  responsibleEntityId: string | null;
  responsibleEntityNameAr: string | null;
  documentStatus: number;
  version: string | null;
  notes: string | null;
}

export interface PolicyRegulationRow {
  id: string;
  number: string;
  title: string;
  categoryLookupId: string | null;
  categoryNameAr: string | null;
  version: string | null;
  effectiveDate: string | null;
  reviewDate: string | null;
  expiryDate: string | null;
  documentStatus: number;
  notes: string | null;
}

/** One employee's custody, as the Fixed Assets module already records it. */
export interface EmployeeCustodyRow {
  custodyId: string;
  employeeId: string;
  employeeNumber: string;
  employeeNameAr: string;
  fixedAssetId: string;
  assetCode: string;
  assetNameAr: string;
  issuedOn: string;
  expectedReturnOn: string | null;
  returnedOn: string | null;
  isOpen: boolean;
  notes: string | null;
}

/** Which dimension «إحصائيات إجازات الموظفين» totals by. */
export const LEAVE_STATISTICS_GROUPING = {
  LeaveType: 1,
  Employee: 2,
  Department: 3,
  Branch: 4,
  Month: 5,
} as const;

/** How the Leave report catalogue and its executors line up. Both gap lists must be empty. */
export interface LeaveReportCoverage {
  registered: string[];
  mapped: string[];
  withoutExecutor: string[];
  notInCatalogue: string[];
}

// ─────────────────────────────────────────────────────────────────────────────────────────────
// المخزون والمستودعات › الحركات اليومية
//
// The procurement chain, in the order the goods and the money travel:
//   طلب شراء مواد → أمر شراء مواد → سند استلام مواد → المخزون
//                                       ↓
//                           فاتورة مشتريات مواد → ذمة المورد
// ─────────────────────────────────────────────────────────────────────────────────────────────

/** Draft → مدقق → مرحّل, with ملغى reachable only before posting. */
export const INVENTORY_DOC_STATUS = { Draft: 1, Audited: 2, Posted: 3, Cancelled: 4 } as const;

/** The five directions one stock document takes. */
export const STOCK_DOCUMENT_KIND = {
  Receipt: 1,
  Issue: 2,
  Transfer: 3,
  Adjustment: 4,
  CountAdjustment: 5,
  Opening: 6,
} as const;

export type StockDocumentKind =
  (typeof STOCK_DOCUMENT_KIND)[keyof typeof STOCK_DOCUMENT_KIND];

/** مسودة / معتمد / مغلق / ملغى — the request's own four states. */
export const MATERIAL_REQUEST_STATUS = { Draft: 1, Approved: 2, Closed: 3, Cancelled: 4 } as const;

export interface MaterialRequestLine {
  id: string;
  lineNumber: number;
  itemId: string;
  itemCode: string;
  itemName: string;
  unitId: string;
  unitCode: string;
  factorToBase: number;
  quantity: number;
  /** On the shelf right now, in base units. Read-only, from the ledger. */
  availableQuantity: number;
  /** Already turned into orders, in base units. Derived from the orders themselves. */
  orderedQuantity: number;
  requiredDate: string | null;
  costCenterId: string | null;
  notes: string | null;
}

export interface MaterialRequest {
  id: string;
  requestNumber: string;
  requestDate: string;
  branchId: string;
  warehouseId: string;
  warehouseName: string;
  requestedByUserId: string | null;
  requestedByName: string | null;
  requestingDepartment: string | null;
  requiredDate: string | null;
  costCenterId: string | null;
  reference: string | null;
  notes: string | null;
  status: number;
  statusNameAr: string;
  conversionStateAr: string;
  lines: MaterialRequestLine[];
}

export interface MaterialOrderLine {
  id: string;
  lineNumber: number;
  itemId: string;
  itemCode: string;
  description: string;
  unitId: string;
  unitCode: string;
  factorToBase: number;
  orderedQuantity: number;
  orderedQuantityBase: number;
  receivedQuantityBase: number;
  remainingQuantityBase: number;
  invoicedQuantityBase: number;
  unitPrice: number;
  unitCostBase: number;
  taxTypeId: string | null;
  taxPercent: number;
  warehouseId: string | null;
  costCenterId: string | null;
}

export interface MaterialOrder {
  id: string;
  orderNumber: string;
  orderDate: string;
  supplierId: string;
  supplierName: string;
  currencyId: string;
  exchangeRate: number;
  warehouseId: string | null;
  warehouseName: string | null;
  expectedDate: string | null;
  paymentTermDays: number | null;
  reference: string | null;
  netTotal: number;
  status: number;
  orderedQuantity: number;
  receivedQuantity: number;
  remainingQuantity: number;
  invoicedQuantity: number;
  remainingInvoiceQuantity: number;
  returnedQuantity: number;
  progressAr: string;
  lines: MaterialOrderLine[];
}

/** مطابق / نقص / زيادة / اختلاف مادة / اختلاف وصف / مستلم بالكامل. */
export const RECEIPT_COMPARISON = {
  Matched: 1,
  Shortage: 2,
  OverReceipt: 3,
  ItemMismatch: 4,
  DescriptionMismatch: 5,
  AlreadyFullyReceived: 6,
} as const;

export interface GoodsReceiptLine {
  id: string;
  lineNumber: number;
  purchaseOrderLineId: string;
  itemId: string;
  itemCode: string;
  description: string;
  receivedDescription: string | null;
  unitId: string;
  unitCode: string;
  factorToBase: number;
  quantity: number;
  orderedQuantityBase: number;
  previouslyReceivedBase: number;
  remainingBase: number;
  differenceBase: number;
  unitCost: number;
  lineValue: number;
  warehouseId: string | null;
  warehouseName: string | null;
  storageLocationId: string | null;
  costCenterId: string | null;
  notes: string | null;
  comparison: number;
  comparisonAr: string;
  invoicedBase: number;
}

export interface GoodsReceipt {
  id: string;
  voucherNumber: string;
  receiptDate: string;
  purchaseOrderId: string;
  purchaseOrderNumber: string;
  purchaseOrderDate: string | null;
  supplierId: string;
  supplierName: string;
  warehouseId: string;
  warehouseName: string;
  storageLocationId: string | null;
  branchId: string;
  currencyId: string;
  exchangeRate: number;
  deliveryNoteNumber: string | null;
  reference: string | null;
  notes: string | null;
  totalValue: number;
  status: number;
  statusNameAr: string;
  journalVoucherId: string | null;
  isReversed: boolean;
  postedAt: string | null;
  hasShortage: boolean;
  lines: GoodsReceiptLine[];
}

export interface MaterialInvoiceLine {
  id: string;
  lineNumber: number;
  itemId: string;
  itemCode: string;
  description: string;
  unitId: string;
  unitCode: string;
  factorToBase: number;
  quantity: number;
  unitPrice: number;
  receiptUnitCost: number;
  priceDifference: number;
  discountAmount: number;
  taxTypeId: string | null;
  taxRate: number;
  taxableAmount: number;
  taxAmount: number;
  lineTotal: number;
  goodsReceiptNoteLineId: string | null;
  purchaseOrderLineId: string | null;
  warehouseId: string | null;
  costCenterId: string | null;
  returnedBase: number;
}

export interface MaterialInvoice {
  id: string;
  voucherNumber: string;
  invoiceDate: string;
  supplierId: string;
  supplierName: string;
  supplierInvoiceNumber: string | null;
  supplierInvoiceDate: string | null;
  dueDate: string | null;
  goodsReceiptNoteId: string | null;
  goodsReceiptNumber: string | null;
  purchaseOrderId: string | null;
  purchaseOrderNumber: string | null;
  warehouseId: string;
  warehouseName: string;
  branchId: string;
  currencyId: string;
  exchangeRate: number;
  reference: string | null;
  notes: string | null;
  subTotal: number;
  discountTotal: number;
  taxTotal: number;
  grandTotal: number;
  status: number;
  statusNameAr: string;
  journalVoucherId: string | null;
  isReversed: boolean;
  lines: MaterialInvoiceLine[];
}

export interface MaterialReturnLine {
  id: string;
  lineNumber: number;
  itemId: string;
  itemCode: string;
  materialPurchaseInvoiceLineId: string;
  unitId: string;
  unitCode: string;
  factorToBase: number;
  quantity: number;
  unitPrice: number;
  receiptUnitCost: number;
  taxRate: number;
  taxAmount: number;
  lineTotal: number;
  warehouseId: string | null;
  costCenterId: string | null;
}

export interface MaterialReturn {
  id: string;
  voucherNumber: string;
  returnDate: string;
  supplierId: string;
  supplierName: string;
  materialPurchaseInvoiceId: string;
  invoiceNumber: string;
  goodsReceiptNoteId: string | null;
  purchaseOrderId: string | null;
  warehouseId: string;
  warehouseName: string;
  branchId: string;
  reason: string | null;
  notes: string | null;
  subTotal: number;
  taxTotal: number;
  grandTotal: number;
  status: number;
  statusNameAr: string;
  journalVoucherId: string | null;
  isReversed: boolean;
  lines: MaterialReturnLine[];
}

export interface StockDocumentLine {
  id: string;
  lineNumber: number;
  itemId: string;
  itemCode: string;
  itemName: string;
  unitId: string;
  unitCode: string;
  factorToBase: number;
  quantity: number;
  unitCost: number;
  lineValue: number;
  /** From the ledger, never typed: what is on the shelf and what it averages. */
  availableQuantity: number;
  averageCost: number;
  warehouseId: string | null;
  storageLocationId: string | null;
  toStorageLocationId: string | null;
  costCenterId: string | null;
  adjustmentReasonId: string | null;
  notes: string | null;
}

export interface StockDocument {
  id: string;
  kind: number;
  voucherNumber: string;
  documentDate: string;
  warehouseId: string;
  warehouseName: string;
  toWarehouseId: string | null;
  toWarehouseName: string | null;
  storageLocationId: string | null;
  toStorageLocationId: string | null;
  branchId: string;
  adjustmentReasonId: string | null;
  adjustmentReasonName: string | null;
  counterAccountId: string | null;
  costCenterId: string | null;
  stockCountId: string | null;
  stockCountNumber: string | null;
  isIncrease: boolean;
  reference: string | null;
  notes: string | null;
  totalValue: number;
  status: number;
  statusNameAr: string;
  journalVoucherId: string | null;
  isReversed: boolean;
  lines: StockDocumentLine[];
}

export interface StockCountLine {
  id: string;
  lineNumber: number;
  itemId: string;
  itemCode: string;
  itemName: string;
  baseUnitCode: string;
  systemQuantity: number;
  countedQuantity: number | null;
  difference: number;
  unitCost: number;
  differenceValue: number;
  storageLocationId: string | null;
  notes: string | null;
}

export interface StockCount {
  id: string;
  voucherNumber: string;
  countDate: string;
  warehouseId: string;
  warehouseName: string;
  storageLocationId: string | null;
  responsibleUserId: string | null;
  responsibleUserName: string | null;
  branchId: string;
  notes: string | null;
  status: number;
  statusNameAr: string;
  adjustmentDocumentId: string | null;
  adjustmentDocumentNumber: string | null;
  showCost: boolean;
  lines: StockCountLine[];
}

// ─────────────────────────────────────────────────────────────────────────────────────────────
// المخزون والمستودعات › الاستعلامات
//
// Twenty-three catalogue entries plus the reconciliation check, over ten server queries. Every
// balance on every one of them comes from the SAME stock-ledger reader; nothing here computes a
// quantity in the browser.
// ─────────────────────────────────────────────────────────────────────────────────────────────

export interface InventoryInquiryFilterBase extends PagedInquiryFilter {
  dateFrom?: string;
  dateTo?: string;
  warehouseId?: string;
  itemId?: string;
  itemGroupId?: string;
  itemCategoryId?: string;
  branchId?: string;
  costCenterId?: string;
  searchTerm?: string;
  includeDraft?: boolean;
}

/**
 * Every filter any of the 101 Inventory reports might use.
 *
 * ONE filter type across all of them, because the SCREEN is one: a report declares which controls
 * it offers and the ones it does not declare are simply never sent.
 */
export interface InventoryReportFilter extends InventoryInquiryFilterBase {
  asOfDate?: string;
  supplierId?: string;
  customerId?: string;
  storageLocationId?: string;
  adjustmentReasonId?: string;
  purchaseOrderId?: string;
  stockCountId?: string;
  status?: number;
  movementType?: number;
  days?: number;
  top?: number;
  marginBelow?: number;
  valueAbove?: number;
  activeOnly?: boolean;
}

/** One Inventory report's answer: the server describes its own shape. */
export interface InventoryReportResult extends PagedInquiryResult {
  code: string;
  title: string;
  titleAr: string;
  columns: ReportColumn[];
  rows: ReportRow[];
  totals: ReportTotal[];
  parameters: ReportParameter[];

  /**
   * True when cost, value, COGS and margin were REMOVED from this response because the reader may
   * not see them. Said out loud on the page: a person looking at a stripped report must know it is
   * stripped, or they will read a quantity page as though it were the whole answer.
   */
  costsHidden: boolean;
}

export interface StockBalanceFilter extends InventoryInquiryFilterBase {
  asOfDate?: string;
  activeOnly?: boolean;
  stockItemsOnly?: boolean;
  balanceSign?: "positive" | "zero" | "negative";
  groupByItem?: boolean;
}

export interface StockBalanceRow {
  id: string;
  itemId: string;
  itemCode: string;
  itemName: string;
  baseUnitCode: string;
  warehouseId: string | null;
  warehouseName: string;
  groupName: string | null;
  categoryName: string | null;
  quantity: number;
  averageCost: number;
  inventoryValue: number;
  minimumStock: number;
  reorderLevel: number;
  availableQuantity: number;
  lastMovementDate: string | null;
  isActive: boolean;
}

export interface StockBalanceResult extends PagedInquiryResult {
  rows: StockBalanceRow[];
  totalQuantity: number;
  totalValue: number;
  itemCount: number;
  warehouseCount: number;
  asOfDate: string | null;
}

export interface StockMovementInquiryFilter extends InventoryInquiryFilterBase {
  documentType?: number;
  documentNumber?: string;
  direction?: "in" | "out";
  createdByUserId?: string;
  withRunningBalance?: boolean;
}

export interface StockMovementInquiryRow {
  id: string;
  movementDate: string;
  createdAt: string;
  documentType: number;
  documentTypeName: string;
  documentId: string;
  documentNumber: string;
  documentRoute: string | null;
  itemId: string;
  itemCode: string;
  itemName: string;
  baseUnitCode: string;
  warehouseId: string;
  warehouseName: string;
  quantityIn: number;
  quantityOut: number;
  unitCost: number;
  valueIn: number;
  valueOut: number;
  runningQuantity: number;
  runningValue: number;
  costCenterId: string | null;
  costCenterName: string | null;
  journalVoucherId: string | null;
  notes: string | null;
}

export interface StockMovementInquiryResult extends PagedInquiryResult {
  rows: StockMovementInquiryRow[];
  totalQuantityIn: number;
  totalQuantityOut: number;
  totalValueIn: number;
  totalValueOut: number;
  openingQuantity: number | null;
  openingValue: number | null;
  closingQuantity: number | null;
  closingValue: number | null;
}

export interface ReorderFilter extends InventoryInquiryFilterBase {
  againstMinimum?: boolean;
}

export interface ReorderRow {
  id: string;
  itemId: string;
  itemCode: string;
  itemName: string;
  baseUnitCode: string;
  warehouseId: string;
  warehouseName: string;
  currentQuantity: number;
  minimumStock: number;
  reorderLevel: number;
  shortageQuantity: number;
  groupName: string | null;
}

export interface ReorderResult extends PagedInquiryResult {
  rows: ReorderRow[];
  totalShortage: number;
  itemsBelow: number;
}

export interface NonMovingFilter extends InventoryInquiryFilterBase {
  asOfDate?: string;
  days?: number;
  includeNeverMoved?: boolean;
}

export interface NonMovingRow {
  id: string;
  itemId: string;
  itemCode: string;
  itemName: string;
  baseUnitCode: string;
  warehouseId: string | null;
  warehouseName: string;
  quantity: number;
  inventoryValue: number;
  lastMovementDate: string | null;
  daysSinceLastMovement: number | null;
  groupName: string | null;
}

export interface NonMovingResult extends PagedInquiryResult {
  rows: NonMovingRow[];
  totalQuantity: number;
  totalValue: number;
  days: number;
}

export interface PurchaseOrderInquiryFilter extends InventoryInquiryFilterBase {
  supplierId?: string;
  purchaseOrderId?: string;
  status?: number;
  expectedFrom?: string;
  expectedTo?: string;
  outstandingOnly?: boolean;
  overdueOnly?: boolean;
  byLine?: boolean;
}

export interface PurchaseOrderInquiryRow {
  id: string;
  purchaseOrderId: string;
  orderNumber: string;
  orderDate: string;
  supplierId: string;
  supplierName: string;
  warehouseId: string | null;
  warehouseName: string | null;
  expectedDate: string | null;
  status: number;
  statusName: string;
  netTotal: number;
  purchaseOrderLineId: string | null;
  itemId: string | null;
  itemCode: string | null;
  itemName: string | null;
  unitCode: string | null;
  orderedQuantity: number;
  receivedQuantity: number;
  remainingReceiptQuantity: number;
  invoicedQuantity: number;
  remainingInvoiceQuantity: number;
  returnedQuantity: number;
  lastReceiptDate: string | null;
  lastReceiptNumber: string | null;
  progressAr: string;
  progressEn: string;
  isOverdue: boolean;
}

export interface PurchaseOrderInquiryResult extends PagedInquiryResult {
  rows: PurchaseOrderInquiryRow[];
  totalOrdered: number;
  totalReceived: number;
  totalRemaining: number;
  totalInvoiced: number;
  totalOrderedAmount: number;
  overdueCount: number;
}

export interface GoodsReceiptInquiryFilter extends InventoryInquiryFilterBase {
  supplierId?: string;
  purchaseOrderId?: string;
  goodsReceiptNoteId?: string;
  status?: number;
  comparison?: "matched" | "shortage" | "over";
  uninvoicedOnly?: boolean;
  minimumAgeDays?: number;
}

export interface GoodsReceiptInquiryRow {
  id: string;
  goodsReceiptNoteId: string;
  receiptNumber: string;
  receiptDate: string;
  purchaseOrderId: string;
  purchaseOrderNumber: string;
  supplierId: string;
  supplierName: string;
  warehouseId: string;
  warehouseName: string;
  itemId: string;
  itemCode: string;
  itemName: string;
  unitCode: string;
  orderedQuantity: number;
  previouslyReceived: number;
  receivedNow: number;
  remainingOnOrder: number;
  differenceQuantity: number;
  comparisonAr: string;
  unitCost: number;
  receiptValue: number;
  invoicedQuantity: number;
  invoicedValue: number;
  remainingUninvoicedQuantity: number;
  remainingUninvoicedValue: number;
  status: number;
  statusNameAr: string;
  isReversed: boolean;
  daysSinceReceipt: number;
  journalVoucherId: string | null;
}

export interface GoodsReceiptInquiryResult extends PagedInquiryResult {
  rows: GoodsReceiptInquiryRow[];
  totalReceivedQuantity: number;
  totalReceiptValue: number;
  totalInvoicedQuantity: number;
  totalInvoicedValue: number;
  totalRemainingQuantity: number;
  totalRemainingValue: number;
}

export interface MaterialInvoiceInquiryFilter extends InventoryInquiryFilterBase {
  supplierId?: string;
  purchaseOrderId?: string;
  goodsReceiptNoteId?: string;
  status?: number;
  supplierInvoiceNumber?: string;
  dueFrom?: string;
  dueTo?: string;
  paymentStatus?: "outstanding" | "settled";
}

export interface MaterialInvoiceInquiryRow {
  id: string;
  invoiceId: string;
  invoiceNumber: string;
  supplierInvoiceNumber: string | null;
  invoiceDate: string;
  dueDate: string | null;
  supplierId: string;
  supplierName: string;
  purchaseOrderId: string | null;
  purchaseOrderNumber: string | null;
  goodsReceiptNoteId: string | null;
  goodsReceiptNumber: string | null;
  subTotal: number;
  taxTotal: number;
  grandTotal: number;
  paidAmount: number;
  outstandingAmount: number;
  paymentStatusAr: string;
  status: number;
  statusNameAr: string;
  isReversed: boolean;
  journalVoucherId: string | null;
  daysOverdue: number;
}

export interface MaterialInvoiceInquiryResult extends PagedInquiryResult {
  rows: MaterialInvoiceInquiryRow[];
  totalSubTotal: number;
  totalTax: number;
  totalGrand: number;
  totalPaid: number;
  totalOutstanding: number;
}

export interface MaterialReturnInquiryFilter extends InventoryInquiryFilterBase {
  supplierId?: string;
  materialPurchaseInvoiceId?: string;
  status?: number;
}

export interface MaterialReturnInquiryRow {
  id: string;
  returnId: string;
  returnNumber: string;
  returnDate: string;
  supplierId: string;
  supplierName: string;
  invoiceId: string;
  invoiceNumber: string;
  goodsReceiptNoteId: string | null;
  goodsReceiptNumber: string | null;
  itemId: string;
  itemCode: string;
  itemName: string;
  unitCode: string;
  warehouseId: string;
  warehouseName: string;
  returnQuantity: number;
  unitPrice: number;
  receiptUnitCost: number;
  returnValue: number;
  taxReversal: number;
  status: number;
  statusNameAr: string;
  isReversed: boolean;
  journalVoucherId: string | null;
}

export interface MaterialReturnInquiryResult extends PagedInquiryResult {
  rows: MaterialReturnInquiryRow[];
  totalQuantity: number;
  totalValue: number;
  totalTaxReversal: number;
}

export interface StockDocumentInquiryFilter extends InventoryInquiryFilterBase {
  kind?: number;
  toWarehouseId?: string;
  adjustmentReasonId?: string;
  status?: number;
  direction?: "increase" | "decrease";
}

export interface StockDocumentInquiryRow {
  id: string;
  documentId: string;
  kind: number;
  voucherNumber: string;
  documentDate: string;
  itemId: string;
  itemCode: string;
  itemName: string;
  unitCode: string;
  warehouseId: string;
  warehouseName: string;
  toWarehouseId: string | null;
  toWarehouseName: string | null;
  quantity: number;
  unitCost: number;
  lineValue: number;
  isIncrease: boolean;
  reasonName: string | null;
  costCenterId: string | null;
  costCenterName: string | null;
  status: number;
  statusNameAr: string;
  isReversed: boolean;
  journalVoucherId: string | null;
  stockCountNumber: string | null;
  notes: string | null;
}

export interface StockDocumentInquiryResult extends PagedInquiryResult {
  rows: StockDocumentInquiryRow[];
  totalQuantity: number;
  totalValue: number;
  transferOutQuantity: number;
  transferInQuantity: number;
  transferDifference: number;
}

export interface StockCountInquiryFilter extends InventoryInquiryFilterBase {
  stockCountId?: string;
  status?: number;
  responsibleUserId?: string;
  byLine?: boolean;
  differenceSign?: "positive" | "negative";
  unadjustedOnly?: boolean;
}

export interface StockCountInquiryRow {
  id: string;
  stockCountId: string;
  countNumber: string;
  countDate: string;
  warehouseId: string;
  warehouseName: string;
  responsibleUserName: string | null;
  status: number;
  statusNameAr: string;
  itemId: string | null;
  itemCode: string | null;
  itemName: string | null;
  baseUnitCode: string | null;
  systemQuantity: number;
  countedQuantity: number | null;
  difference: number;
  unitCost: number;
  differenceValue: number;
  lineCount: number;
  adjustmentDocumentId: string | null;
  adjustmentDocumentNumber: string | null;
  adjustmentStatusAr: string;
}

export interface StockCountInquiryResult extends PagedInquiryResult {
  rows: StockCountInquiryRow[];
  totalSystemQuantity: number;
  totalCountedQuantity: number;
  positiveDifferenceValue: number;
  negativeDifferenceValue: number;
  netDifferenceValue: number;
  unadjustedCount: number;
}

export interface ItemBySupplierFilter extends InventoryInquiryFilterBase {
  supplierId?: string;
}

export interface ItemBySupplierRow {
  id: string;
  supplierId: string;
  supplierName: string;
  itemId: string;
  itemCode: string;
  itemName: string;
  baseUnitCode: string;
  orderCount: number;
  receiptCount: number;
  invoiceCount: number;
  returnCount: number;
  orderedQuantity: number;
  receivedQuantity: number;
  invoicedQuantity: number;
  returnedQuantity: number;
  receivedValue: number;
  invoicedValue: number;
  lastReceiptDate: string | null;
}

export interface ItemBySupplierResult extends PagedInquiryResult {
  rows: ItemBySupplierRow[];
  totalReceived: number;
  totalInvoiced: number;
  totalReturned: number;
  totalReceivedValue: number;
}

/**
 * سجل أصناف المواد — the item CARD register.
 *
 * `itemType`, `isActive` and the two «only» flags are its own; everything else comes from the
 * shared inventory filter base. There is no `asOfDate`: a card has no history to stand «as at».
 */
export interface ItemRegisterFilter extends InventoryInquiryFilterBase {
  baseUnitId?: string;
  /** Classification 1 is `itemCategoryId` on the base filter; these are columns 2 and 3. */
  itemCategory2Id?: string;
  itemCategory3Id?: string;
  supplierId?: string;
  countryOfOriginId?: string;
  purchaseTaxTypeId?: string;
  defaultWarehouseId?: string;
  itemType?: number;
  /** Undefined means BOTH — a retired card is still part of the register. */
  isActive?: boolean;
  neverMovedOnly?: boolean;
  /** Cards missing a group, a category, an origin or a default purchase tax. */
  incompleteOnly?: boolean;
}

export interface ItemRegisterRow {
  id: string;
  itemId: string;
  code: string;
  barcode: string | null;
  name: string;
  nameAr: string | null;
  groupCode: string | null;
  groupName: string | null;
  groupPath: string | null;
  /** The item's depth in the GROUP tree — its group's level plus one. */
  level: number;
  /** تصنيف مادة 1 / 2 / 3 — three independent columns, never merged. */
  classification1Code: string | null;
  classification1Name: string | null;
  classification2Code: string | null;
  classification2Name: string | null;
  classification3Code: string | null;
  classification3Name: string | null;
  baseUnitCode: string;
  purchaseUnitCode: string | null;
  salesUnitCode: string | null;
  conversionCount: number;
  itemType: ItemType;
  isStockItem: boolean;
  isPurchasable: boolean;
  isSellable: boolean;
  countryOfOriginCode: string | null;
  countryOfOriginName: string | null;
  purchaseTaxTypeCode: string | null;
  purchaseTaxTypeName: string | null;
  defaultWarehouseCode: string | null;
  /** From the CONSUMER price list. A SELLING price — never the inventory cost. Null = no price. */
  consumerPrice: number | null;
  preferredSupplierCode: string | null;
  preferredSupplierName: string | null;
  minimumStock: number;
  reorderLevel: number;
  isActive: boolean;
  notes: string | null;
  /** From the ledger, per row. The base unit is locked once this is true. */
  hasMovements: boolean;
  isIncomplete: boolean;
}

/** One node of the group tree the register hangs its items from. A group is never an item. */
export interface ItemRegisterGroup {
  id: string;
  parentId: string | null;
  code: string;
  name: string;
  path: string;
  level: number;
  directItemCount: number;
  descendantItemCount: number;
}

export interface ItemRegisterResult extends PagedInquiryResult {
  rows: ItemRegisterRow[];
  /** The WHOLE tree, unfiltered — so a filtered item never hangs under a missing ancestor. */
  groups: ItemRegisterGroup[];
  activeCount: number;
  stockCount: number;
  neverMovedCount: number;
  withGroup: number;
  withCategory: number;
  withCountry: number;
  withPurchaseTax: number;
  incompleteCount: number;
}

export interface InventoryReconciliationFilter extends InventoryInquiryFilterBase {
  asOfDate?: string;
  mismatchesOnly?: boolean;
}

export interface ReconciliationRow {
  id: string;
  itemId: string;
  itemCode: string;
  itemName: string;
  warehouseId: string;
  warehouseName: string;
  runningQuantity: number;
  runningValue: number;
  summedQuantity: number;
  summedValue: number;
  quantityDifference: number;
  valueDifference: number;
  isReconciled: boolean;
  movementCount: number;
}

export interface InventoryReconciliationResult extends PagedInquiryResult {
  rows: ReconciliationRow[];
  checkedPairs: number;
  mismatchCount: number;
  totalQuantityDifference: number;
  totalValueDifference: number;
  isClean: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════════════════════════
// المخزون والمستودعات › مبيعات المواد
//
// ONE header shape and ONE line shape across all four documents. They differ in which fields they
// fill, not in what a reader needs from them, and a single type is what keeps the four screens
// showing the same figure for the same thing.
// ═══════════════════════════════════════════════════════════════════════════════════════════════

export interface SalesLine {
  id: string;
  lineNumber: number;
  itemId: string;
  itemCode: string;
  itemName: string;
  itemNameAr: string | null;
  barcode: string | null;
  description: string | null;
  warehouseId: string | null;
  warehouseName: string | null;
  storageLocationId: string | null;
  unitId: string;
  unitCode: string;
  factorToBase: number;
  quantity: number;
  unitPrice: number;
  grossAmount: number;
  discountTypeId: string | null;
  discountPercent: number;
  discountAmount: number;
  taxTypeId: string | null;
  taxRate: number;
  taxableAmount: number;
  taxAmount: number;
  lineTotal: number;
  costCenterId: string | null;
  notes: string | null;

  /** The line one link upstream, so a sold item can be traced order → delivery → invoice → return. */
  sourceLineId: string | null;

  deliveredQuantity: number;
  invoicedQuantity: number;
  returnedQuantity: number;
  remainingQuantity: number;

  /** What the goods actually cost when they left. Zero on anything not yet posted. */
  issueUnitCost: number;
  issueCostTotal: number;
}

export interface SalesDocument {
  id: string;
  voucherNumber: string;
  documentDate: string;
  customerId: string;
  customerCode: string;
  customerName: string;
  customerNameAr: string | null;
  customerTaxNumber: string | null;

  salesOrderId: string | null;
  salesOrderNumber: string | null;
  deliveryNoteId: string | null;
  deliveryNoteNumber: string | null;
  materialSalesInvoiceId: string | null;
  materialSalesInvoiceNumber: string | null;

  branchId: string;
  branchName: string | null;
  warehouseId: string;
  warehouseName: string | null;
  currencyId: string;
  currencyCode: string | null;
  exchangeRate: number;
  paymentMethodId: string | null;
  paymentMethodName: string | null;
  paymentTerms: string | null;
  dueDate: string | null;
  salespersonUserId: string | null;
  salespersonName: string | null;
  driverUserId: string | null;
  driverName: string | null;
  vehicleNumber: string | null;
  deliveryReference: string | null;
  returnReasonId: string | null;
  returnReasonName: string | null;
  costCenterId: string | null;
  costCenterName: string | null;

  customerReference: string | null;
  customerPoNumber: string | null;
  customerPoDate: string | null;
  billingAddress: string | null;
  deliveryAddress: string | null;
  contactPerson: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
  notes: string | null;
  internalNotes: string | null;
  /** Sales order only: «العرض جزء لا يتجزأ» — decides whether the price condition prints. */
  indivisibleOffer: boolean;
  /** Sales order only: «مدة التسليم». */
  deliveryPeriod: string | null;
  /** Invoice only: its delivery note was raised by posting it (a direct invoice). */
  autoDelivery: boolean;

  subTotal: number;
  discountTotal: number;
  taxTotal: number;
  grandTotal: number;
  baseCurrencyTotal: number;
  costTotal: number;

  status: number;
  statusNameAr: string;

  /** Derived on a sales order only — read off the deliveries, never stored. */
  fulfilmentAr: string | null;
  isClosed: boolean;

  journalVoucherId: string | null;
  reversalJournalVoucherId: string | null;
  isReversed: boolean;
  reversalReason: string | null;

  createdAt: string;
  createdBy: string | null;
  createdByName: string | null;
  auditedAt: string | null;
  auditedBy: string | null;
  auditedByName: string | null;
  postedAt: string | null;
  postedBy: string | null;
  postedByName: string | null;
  reversedAt: string | null;

  attachmentCount: number;

  lines: SalesLine[];
}

/** A line of the SOURCE document a new one is being built from, with what is left on it. */
export interface SalesSourceLine {
  sourceLineId: string;
  lineNumber: number;
  itemId: string;
  itemCode: string;
  itemName: string;
  itemNameAr: string | null;
  barcode: string | null;
  description: string | null;
  unitId: string;
  unitCode: string;
  factorToBase: number;
  warehouseId: string | null;
  storageLocationId: string | null;
  costCenterId: string | null;

  sourceQuantity: number;
  consumedQuantity: number;
  remainingQuantity: number;

  unitPrice: number;
  discountTypeId: string | null;
  discountPercent: number;
  discountAmount: number;
  taxTypeId: string | null;
  taxRate: number;
  issueUnitCost: number;

  /** On-hand in the line's warehouse, in base units. Null where there is no warehouse to ask. */
  availableBase: number | null;
}

/** One bank account on the dashboard, with the balance read from its ledger account. */
export interface DashboardBankBalance {
  bankAccountId: string;
  nameAr: string;
  name: string;
  bankNameAr: string | null;
  accountNumber: string | null;
  currencyCode: string | null;
  isMain: boolean;
  localBalance: number;
  glAccountId: string;
  glAccountNumber: string | null;
}

/** What was sold on one day. */
export interface DashboardSalesDay {
  date: string;
  materialSales: number;
  materialInvoiceCount: number;
  serviceSales: number;
  serviceInvoiceCount: number;
  total: number;
  invoiceCount: number;
}

export interface DashboardSummary {
  asOfDate: string;
  bankBalances: DashboardBankBalance[];
  totalBankLocalBalance: number;
  /** True when the viewer may not see the bank block. Never the same as an empty list. */
  bankBalancesRestricted: boolean;
  sales: DashboardSalesDay[];
  salesRestricted: boolean;
  financial: DashboardFinancialSummary | null;
  /** True when the viewer may not see the financial block. Never the same as a null summary. */
  financialRestricted: boolean;
  baseCurrencyCode: string | null;
}

/** Revenue/expenses/cash, fiscal-year-to-date through DashboardSummary.asOfDate. */
export interface DashboardFinancialSummary {
  fiscalYearCode: string;
  fiscalYearStartDate: string;
  revenue: number;
  expenses: number;
  netResult: number;
  cash: number;
}

/** One report in the flat search index. */
export interface ReportSearchItem {
  code: string;
  name: string;
  nameAr: string;
  categoryCode: string;
  categoryName: string;
  categoryNameAr: string;
}

export interface ReportSearchIndex {
  reports: ReportSearchItem[];
}

/**
 * One reason a voucher cannot be posted.
 *
 * The CODE is what the screen keys its wording off; `message` is the server's English and is only
 * shown when there is no translation for the code. `detail` is the variable part — a voucher
 * number, an amount — kept separate so Arabic can place it where its own grammar wants it.
 */
export interface JournalPostingBlocker {
  code: string;
  message: string;
  detail: string | null;
}

/* ─── Hotel Front Desk ────────────────────────────────────────────────────────────────────
 * Restored verbatim from commit 6bb3be8 on 2026-09-20. The four front-desk screens in this
 * tree import these and they were absent here, so NOTHING in the web app could build. Added
 * as one block at the end of the file; not one existing line was touched.
 * ─────────────────────────────────────────────────────────────────────────────────────── */

export interface RoomBoardTileDto {
  roomId: string;
  roomNumber: string;
  floor: number;
  roomTypeName: string | null;
  isOutOfService: boolean;
  outOfServiceReason: string | null;
  isOccupied: boolean;
  currentStayId: string | null;
  guestName: string | null;
  checkInDate: string | null;
  checkOutDate: string | null;
  /** Only meaningful when isOccupied: true on the stay's first night, false afterwards. */
  isFirstNight: boolean;
  /** Vacant right now, but a future stay is Reserved against this room (direct or an OTA). */
  hasUpcomingReservation: boolean;
  upcomingReservationGuestName: string | null;
  upcomingReservationCheckInDate: string | null;
  upcomingReservationCheckOutDate: string | null;
  /** Only meaningful when isOccupied: today's daily-service cleaning is due and not done yet. */
  needsCleaning: boolean;
  /** True whenever the room is dirty for any reason (checkout or a manual flag) — regardless of statusKey. */
  isFlaggedDirty: boolean;
  /**
   * Server-computed colour state: "outOfService" | "occupiedCheckoutToday" | "occupiedFirstNight"
   * | "occupiedContinuing" | "vacantDirtyToday" | "vacantDirtyStale" | "reserved" | "vacant".
   */
  statusKey: string;
}

export interface RoomDto {
  id: string;
  roomNumber: string;
  floor: number;
  roomTypeId: string;
  roomTypeCode: string | null;
  roomTypeName: string | null;
  roomTypePricePerNight: number;
  isOutOfService: boolean;
  outOfServiceReason: string | null;
  lastCleanedAt: string | null;
  isActive: boolean;
  notes: string | null;
}

export interface RoomTypeDto {
  id: string;
  code: string;
  name: string;
  nameAr: string | null;
  pricePerNight: number;
  serviceSalesTypeId: string;
  serviceSalesTypeName: string | null;
  isActive: boolean;
  notes: string | null;
}

export interface HotelSettingsDto {
  defaultCostCenterId: string | null;
  defaultCostCenterName: string | null;
  defaultGuestControlAccountId: string | null;
  defaultGuestControlAccountName: string | null;
  discountTypeId: string | null;
  discountTypeName: string | null;
  cleaningDueTime: string;
}

export interface QuickAddGuestResultDto {
  guestId: string;
  wasExisting: boolean;
}
